import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Badge, Spinner, Modal, Dropdown, ProgressBar, Pagination, Toast, ToastContainer } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

const DataClaimPasien = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('Administrator');
  const [menus, setMenus] = useState([]);
  const [, setActiveMenu] = useState('');
  const [time] = useState({ date: '', clock: '' });
  const [isNavOpen, setIsNavOpen] = useState(false);

  // ==========================================
  // HELPER DATE (YYYY-MM-DD for Input, DD/MM/YYYY for API)
  // ==========================================
  const getTodayYmd = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getSevenDaysAgoYmd = () => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatYmdToDdmmyyyy = (ymdStr) => {
    if (!ymdStr) return '';
    if (ymdStr.includes('/')) return ymdStr;
    const parts = ymdStr.split('-');
    if (parts.length === 3) {
      const [yyyy, mm, dd] = parts;
      return `${dd}/${mm}/${yyyy}`;
    }
    return ymdStr;
  };

  // Helper Format Rupiah
  const formatRp = (val) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(val || 0);
  };

  // Helper Validasi Nomor SEP
  const isInvalidSep = (val) => {
    const s = String(val || '').trim().toUpperCase();
    return !s || s === '-' || s === '0' || s === 'PASIEN BAYAR TUNAI' || s === 'NULL' || s === 'UNDEFINED';
  };


  // ==========================================
  // STATE DATA KLAIM
  // ==========================================
  const [periode1, setPeriode1] = useState(getSevenDaysAgoYmd());
  const [periode2, setPeriode2] = useState(getTodayYmd());
  const [claimList, setClaimList] = useState([]);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [searchSepInput, setSearchSepInput] = useState('');
  const [filterIdrg, setFilterIdrg] = useState('all');
  const [activeTabRawat, setActiveTabRawat] = useState('RI'); // 'RI' = Rawat Inap, 'RJ' = Rawat Jalan, 'ALL' = Semua Pasien
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [bridgingConfig, setBridgingConfig] = useState(null);
  
  // Export NCC State
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(getSevenDaysAgoYmd());
  const [exportEndDate, setExportEndDate] = useState(getTodayYmd());
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });

  // Laporan Klaim Individual / Rekap E-Klaim State
  const [showFilterLaporanModal, setShowFilterLaporanModal] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [exportExcelStatusMsg, setExportExcelStatusMsg] = useState('');
  const [filterDialog, setFilterDialog] = useState({
    jenisRawat: 'Semua',
    periodeTipe: 'Waktu Pulang',
    periodeTglAwal: getSevenDaysAgoYmd(),
    periodeJamAwal: '00:00',
    periodeTglAkhir: getTodayYmd(),
    periodeJamAkhir: '23:59',
    metodePembayaran: 'Semua',
    kelasRawat: 'Semua Kelas',
    caraPulang: 'Semua',
    severity: 'Semua',
    jenisTarif: 'TARIF RS KELAS C SWASTA',
    cob: 'Semua',
    groupingTglAwal: getSevenDaysAgoYmd(),
    groupingJamAwal: '00:00',
    groupingTglAkhir: getTodayYmd(),
    groupingJamAkhir: '23:59'
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);

  const toggleRow = (idx) => {
    if (expandedRow === idx) setExpandedRow(null);
    else setExpandedRow(idx);
  };

  // ==========================================
  // MODAL EDIT SEP
  // ==========================================
  const [showEditSepModal, setShowEditSepModal] = useState(false);
  const [selectedPatient, setSelectedPatient]   = useState(null);
  const [inputNoSep, setInputNoSep]             = useState('');
  const [isSavingSep, setIsSavingSep]           = useState(false);
  const [sepFeedback, setSepFeedback]           = useState(null);

  const handleOpenEditSep = (item) => {
    setSelectedPatient(item);
    setInputNoSep(item?.no_sep && !isInvalidSep(item.no_sep) ? item.no_sep : '');
    setSepFeedback(null);
    setShowEditSepModal(true);
  };

  const handleSaveSep = async (e) => {
    e.preventDefault();
    if (!selectedPatient) return;

    const newSepVal = inputNoSep.trim() || '-';
    setIsSavingSep(true);
    setSepFeedback(null);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    const token = localStorage.getItem('token');

    const payload = {
      no_nota: selectedPatient.no_nota,
      no_sep: newSepVal
    };

    try {
      const res = await fetch(`${apiUrl}/isidata_sep_eclaim`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();
      
      setSepFeedback({
        type: json?.status === 1 || res.ok ? 'success' : 'warning',
        text: json?.msg || json?.message || `No. SEP (${newSepVal}) berhasil disimpan!`
      });

      // Update state lokal
      setClaimList(prev => prev.map(p =>
        p.no_nota === selectedPatient.no_nota 
          ? { ...p, no_sep: newSepVal } 
          : p
      ));

      setTimeout(() => setShowEditSepModal(false), 1200);
    } catch (err) {
      console.warn('Gagal simpan SEP:', err);
      // Mode simulasi jika gagal
      setClaimList(prev => prev.map(p =>
        p.no_nota === selectedPatient.no_nota 
          ? { ...p, no_sep: newSepVal } 
          : p
      ));
      setSepFeedback({
        type: 'warning',
        text: `[Demo Mode] No. SEP (${newSepVal}) diperbarui di tampilan lokal.`
      });
      setTimeout(() => setShowEditSepModal(false), 1500);
    } finally {
      setIsSavingSep(false);
    }
  };

  // ==========================================
  // MODAL GROUPER E-CLAIM
  // ==========================================
  const [showGrouperModal, setShowGrouperModal] = useState(false);
  const [grouperTarget, setGrouperTarget]       = useState(null);
  const [isGrouping, setIsGrouping]             = useState(false);
  const [grouperResult, setGrouperResult]       = useState(null);
  const [inputSitb, setInputSitb]               = useState('');
  const [isValidatingSitb, setIsValidatingSitb] = useState(false);
  const [sitbValidationResult, setSitbValidationResult] = useState(null);
  
  const [showSepWarningModal, setShowSepWarningModal]   = useState(false);
  const [warningPatientTarget, setWarningPatientTarget] = useState(null);

  const [selectedDiagnosaItem, setSelectedDiagnosaItem] = useState(null);
  const [diagnosaData, setDiagnosaData]                 = useState({ list_diagnosa: [], list_prosedur: [] });
  const [isLoadingDiagnosa, setIsLoadingDiagnosa]       = useState(false);
  const [activeTabDiagnosa, setActiveTabDiagnosa]       = useState('diagnosa');
  const [idrgProcedureList, setIdrgProcedureList]       = useState([]);
  const [, setIdrgProcedureData]                        = useState(null);

  // ── State Diagnosa & Prosedur INACBG (hdiagnosa_icd & hdiagnosa_icd2) ──
  const [diagnosaInacbgData, setDiagnosaInacbgData]     = useState({ list_diagnosa: [], list_prosedur: [] });
  const [isLoadingDiagnosaInacbg, setIsLoadingDiagnosaInacbg] = useState(false);
  const [activeTabDiagnosaInacbg, setActiveTabDiagnosaInacbg] = useState('diagnosa');

  // ── State Grouping Tarif SIMRS (hasil_tarif) ──
  const [hasilTarifData, setHasilTarifData]             = useState(null);
  const [hasilTarifStr, setHasilTarifStr]               = useState('');
  const [isLoadingHasilTarif, setIsLoadingHasilTarif]   = useState(false);
  const [copiedReadableTarif, setCopiedReadableTarif]   = useState(false);
  const [tarifViewMode, setTarifViewMode]               = useState('grid'); // 'grid' | 'text'

  // ── State Integrasi EHR ──
  const [ehrData, setEhrData]                           = useState(null);

  // ── State Input Diagnosa & Prosedur Modal ──
  const [showInputDiagnosaModal, setShowInputDiagnosaModal] = useState(false);
  const [showInlineInputIdrg, setShowInlineInputIdrg]       = useState(false);
  const [showInlineInputInacbg, setShowInlineInputInacbg]   = useState(false);
  const [showTableInacbg, setShowTableInacbg]               = useState(false);
  const [inputDiagnosaType, setInputDiagnosaType]           = useState('diagnosa'); // 'diagnosa' | 'prosedur'
  const [inputDiagnosaTarget, setInputDiagnosaTarget]       = useState('idrg'); // 'idrg' | 'inacbg'
  const [masterIcd10, setMasterIcd10]                       = useState([]);
  const [masterIcd9, setMasterIcd9]                         = useState([]);
  const [isLoadingMasterIcd, setIsLoadingMasterIcd]         = useState(false);
  const [isSavingDiagnosa, setIsSavingDiagnosa]             = useState(false);

  // ── State Edit Kode ICD Modal ──
  const [editIcdModal, setEditIcdModal] = useState({
    show: false,
    item: null,
    type: 'diagnosa',
    target: 'idrg',
    urutan: 1,
    old_kd_icd: '',
    old_nm_icd: '',
    new_kd_icd: '',
    new_nm_icd: '',
    multiplicity: 1,
    isSaving: false,
    validcode: null,
    accpdx: null
  });
  const [editIcdSearchInput, setEditIcdSearchInput] = useState('');
  const [editIcdSuggestions, setEditIcdSuggestions] = useState([]);
  const [isCheckingIcd, setIsCheckingIcd]                   = useState(false);
  const [icdCheckMsg, setIcdCheckMsg]                       = useState(null);
  const [showMasterIcdModal, setShowMasterIcdModal]         = useState(false);
  const [masterIcdModalSearch, setMasterIcdModalSearch]     = useState('');
  const [masterIcdCurrentPage, setMasterIcdCurrentPage]     = useState(1);
  const [masterIcdItemsPerPage]                             = useState(10);
  const [activeAutocomplete, setActiveAutocomplete]         = useState(null); // { target: 'idrg' | 'inacbg' | 'modal', type: 'icd10' | 'icd9' }
  const [showIcd9Autocomplete, setShowIcd9Autocomplete]     = useState(false);
  const [showIcd10Autocomplete, setShowIcd10Autocomplete]   = useState(false);

  const openAutocomplete = (target, type) => {
    setActiveAutocomplete({ target, type });
    if (type === 'icd10') {
      setShowIcd10Autocomplete(true);
      setShowIcd9Autocomplete(false);
    } else {
      setShowIcd9Autocomplete(true);
      setShowIcd10Autocomplete(false);
    }
  };

  const closeAutocomplete = () => {
    setActiveAutocomplete(null);
    setShowIcd10Autocomplete(false);
    setShowIcd9Autocomplete(false);
  };

  const initialFormDiagnosaState = {
    kd_icd: '',
    nm_icd: '',
    ket: 'Utama',
    utama: 1,
    multiplicity: 1,
    kd_dokter: '',
    nm_dokter: '',
    tanggal: '',
    jam: '',
    lokasi: 'Rawat Inap',
    petugas: '',
    isHeader: false,
    validcode: undefined,
    accpdx: undefined
  };

  const [formInputDiagnosa, setFormInputDiagnosa]             = useState(initialFormDiagnosaState);
  const [formInputDiagnosaIdrg, setFormInputDiagnosaIdrg]     = useState(initialFormDiagnosaState);
  const [formInputDiagnosaInacbg, setFormInputDiagnosaInacbg] = useState(initialFormDiagnosaState);

  // Filter autocomplete ICD-9 IM
  const getFilteredIcd9 = useCallback((rawQuery) => {
    if (masterIcd9.length === 0) return [];
    const query = (rawQuery || '').split('+')[0].split('#')[0].trim().toLowerCase();
    if (!query) return masterIcd9.slice(0, 50);
    return masterIcd9.filter(item => {
      const code = String(item.icd || item.kode || item.kd_icd || '').toLowerCase();
      const desc = String(item.deskripsi || item.nm_icd || '').toLowerCase();
      const idrg = String(item.idrg || '').toLowerCase();
      return code.includes(query) || desc.includes(query) || idrg.includes(query);
    }).slice(0, 50);
  }, [masterIcd9]);

  // Filter autocomplete ICD-10 IM
  const getFilteredIcd10 = useCallback((rawQuery) => {
    if (masterIcd10.length === 0) return [];
    const query = (rawQuery || '').trim().toLowerCase();
    if (!query) return masterIcd10.slice(0, 50);
    return masterIcd10.filter(item => {
      const code = (item.icd || item.kode || item.kd_icd || '').toLowerCase();
      const desc = (item.deskripsi || item.nm_icd || item.penyakit || '').toLowerCase();
      const pyk = (item.penyakit || '').toLowerCase();
      const dtd = (item.dtd || '').toLowerCase();
      return code.includes(query) || desc.includes(query) || pyk.includes(query) || dtd.includes(query);
    }).slice(0, 50);
  }, [masterIcd10]);

  const filteredIcd9SuggestionsIdrg = useMemo(() => getFilteredIcd9(formInputDiagnosaIdrg?.kd_icd), [getFilteredIcd9, formInputDiagnosaIdrg?.kd_icd]);
  const filteredIcd9SuggestionsInacbg = useMemo(() => getFilteredIcd9(formInputDiagnosaInacbg?.kd_icd), [getFilteredIcd9, formInputDiagnosaInacbg?.kd_icd]);
  const filteredIcd9Suggestions = useMemo(() => getFilteredIcd9(formInputDiagnosa?.kd_icd), [getFilteredIcd9, formInputDiagnosa?.kd_icd]);

  const filteredIcd10SuggestionsIdrg = useMemo(() => getFilteredIcd10(formInputDiagnosaIdrg?.kd_icd), [getFilteredIcd10, formInputDiagnosaIdrg?.kd_icd]);
  const filteredIcd10SuggestionsInacbg = useMemo(() => getFilteredIcd10(formInputDiagnosaInacbg?.kd_icd), [getFilteredIcd10, formInputDiagnosaInacbg?.kd_icd]);
  const filteredIcd10Suggestions = useMemo(() => getFilteredIcd10(formInputDiagnosa?.kd_icd), [getFilteredIcd10, formInputDiagnosa?.kd_icd]);

  const isHeaderIcd10Code = useCallback((code) => {
    if (!code) return false;
    const clean = String(code).trim().toLowerCase();
    if (!clean) return false;
    const cleanNoDot = clean.replace(/\./g, '');
    const matched = (masterIcd10 || []).find(x => {
      const c = String(x.icd || x.kode || x.kd_icd || '').trim().toLowerCase();
      return c === clean || c.replace(/\./g, '') === cleanNoDot;
    });
    if (matched) {
      return matched.validcode === 0 || matched.validcode === '0' || matched.validcode === false;
    }
    return false;
  }, [masterIcd10]);

  const isAccpdxNoProcedure = useCallback((code) => {
    if (!code) return false;
    const clean = String(code).split('+')[0].split('#')[0].trim().toLowerCase();
    if (!clean) return false;
    const cleanNoDot = clean.replace(/\./g, '');
    const matched = (masterIcd9 || []).find(x => {
      const c = String(x.icd || x.kode || x.kd_icd || '').trim().toLowerCase();
      return c === clean || c.replace(/\./g, '') === cleanNoDot;
    });
    if (matched) {
      return String(matched.accpdx || '').trim().toUpperCase() === 'N';
    }
    return false;
  }, [masterIcd9]);

  const isIdrgHeaderDiagnosis = useMemo(() => {
    if (inputDiagnosaType !== 'diagnosa') return false;
    if (formInputDiagnosaIdrg?.isHeader) return true;
    if (formInputDiagnosaIdrg?.validcode === 0 || formInputDiagnosaIdrg?.validcode === '0' || formInputDiagnosaIdrg?.validcode === false) return true;
    return isHeaderIcd10Code(formInputDiagnosaIdrg?.kd_icd);
  }, [inputDiagnosaType, formInputDiagnosaIdrg?.isHeader, formInputDiagnosaIdrg?.validcode, formInputDiagnosaIdrg?.kd_icd, isHeaderIcd10Code]);

  const isIdrgProcedureAccpdxNo = useMemo(() => {
    if (inputDiagnosaType !== 'prosedur') return false;
    if (String(formInputDiagnosaIdrg?.accpdx || '').trim().toUpperCase() === 'N') return true;
    return isAccpdxNoProcedure(formInputDiagnosaIdrg?.kd_icd);
  }, [inputDiagnosaType, formInputDiagnosaIdrg?.accpdx, formInputDiagnosaIdrg?.kd_icd, isAccpdxNoProcedure]);

  const isModalIdrgHeaderDiagnosis = useMemo(() => {
    if (inputDiagnosaTarget !== 'idrg' || inputDiagnosaType !== 'diagnosa') return false;
    if (formInputDiagnosa?.isHeader) return true;
    if (formInputDiagnosa?.validcode === 0 || formInputDiagnosa?.validcode === '0' || formInputDiagnosa?.validcode === false) return true;
    return isHeaderIcd10Code(formInputDiagnosa?.kd_icd);
  }, [inputDiagnosaTarget, inputDiagnosaType, formInputDiagnosa?.isHeader, formInputDiagnosa?.validcode, formInputDiagnosa?.kd_icd, isHeaderIcd10Code]);

  const isModalIdrgProcedureAccpdxNo = useMemo(() => {
    if (inputDiagnosaTarget !== 'idrg' || inputDiagnosaType !== 'prosedur') return false;
    if (String(formInputDiagnosa?.accpdx || '').trim().toUpperCase() === 'N') return true;
    return isAccpdxNoProcedure(formInputDiagnosa?.kd_icd);
  }, [inputDiagnosaTarget, inputDiagnosaType, formInputDiagnosa?.accpdx, formInputDiagnosa?.kd_icd, isAccpdxNoProcedure]);

  const handleSelectIcd9FromDropdown = (itemOrCode, explicitTarget = null) => {
    if (!itemOrCode) {
      if (explicitTarget === 'inacbg') setFormInputDiagnosaInacbg(prev => ({ ...prev, kd_icd: '', nm_icd: '' }));
      else setFormInputDiagnosaIdrg(prev => ({ ...prev, kd_icd: '', nm_icd: '' }));
      setFormInputDiagnosa(prev => ({ ...prev, kd_icd: '', nm_icd: '' }));
      setIcdCheckMsg(null);
      return;
    }
    const targetToUse = explicitTarget || inputDiagnosaTarget;
    if (explicitTarget && explicitTarget !== inputDiagnosaTarget) {
      setInputDiagnosaTarget(explicitTarget);
    }
    let matched = null;
    if (typeof itemOrCode === 'object') {
      matched = itemOrCode;
    } else {
      matched = masterIcd9.find(x => (x.icd || x.kode || x.kd_icd) === itemOrCode);
    }
    if (matched) {
      const c = matched.icd || matched.kode || matched.kd_icd || itemOrCode;
      const d = matched.deskripsi || matched.nm_icd || '';
      const isIm = matched.im === 1 || matched.im === '1';
      const isAccpdxNo = String(matched.accpdx || '').trim().toUpperCase() === 'N';

      if (isAccpdxNo) {
        setIcdCheckMsg({ type: 'danger', text: `Prosedur ${c} tidak bisa dipilih karena accpdx = N (tidak dapat disimpan).` });
        notify('warning', 'Prosedur Tidak Valid', `Prosedur ${c} tidak bisa dipilih karena accpdx = N`);
        if (targetToUse === 'inacbg') {
          setFormInputDiagnosaInacbg(prev => ({ ...prev, kd_icd: c, nm_icd: d, accpdx: 'N' }));
        } else {
          setFormInputDiagnosaIdrg(prev => ({ ...prev, kd_icd: c, nm_icd: d, accpdx: 'N' }));
        }
        setFormInputDiagnosa(prev => ({ ...prev, kd_icd: c, nm_icd: d, accpdx: 'N' }));
        closeAutocomplete();
        return;
      }

      if (targetToUse === 'inacbg') {
        setFormInputDiagnosaInacbg(prev => ({ ...prev, kd_icd: c, nm_icd: d }));
      } else {
        setFormInputDiagnosaIdrg(prev => ({ ...prev, kd_icd: c, nm_icd: d }));
      }
      setFormInputDiagnosa(prev => ({ ...prev, kd_icd: c, nm_icd: d }));
      setIcdCheckMsg({ type: 'success', text: `Ditemukan di Master ICD-9 IM (_idrg): ${d}${isIm ? ' [IM]' : ''}` });

      closeAutocomplete();

      // Otomatis simpan saat kode ditemukan/dipilih
      handleSaveInputDiagnosa(null, {
        kd_icd: c,
        nm_icd: d,
        validcode: matched.validcode,
        accpdx: matched.accpdx,
        target: targetToUse
      });
    }
  };

  const handleSelectIcd10FromDropdown = (itemOrCode, explicitTarget = null) => {
    if (!itemOrCode) {
      if (explicitTarget === 'inacbg') setFormInputDiagnosaInacbg(prev => ({ ...prev, kd_icd: '', nm_icd: '' }));
      else setFormInputDiagnosaIdrg(prev => ({ ...prev, kd_icd: '', nm_icd: '' }));
      setFormInputDiagnosa(prev => ({ ...prev, kd_icd: '', nm_icd: '' }));
      setIcdCheckMsg(null);
      return;
    }
    const targetToUse = explicitTarget || inputDiagnosaTarget;
    if (explicitTarget && explicitTarget !== inputDiagnosaTarget) {
      setInputDiagnosaTarget(explicitTarget);
    }
    let matched = null;
    if (typeof itemOrCode === 'object') {
      matched = itemOrCode;
    } else {
      matched = masterIcd10.find(x => (x.icd || x.kode || x.kd_icd) === itemOrCode);
    }
    if (matched) {
      const c = matched.icd || matched.kode || matched.kd_icd || itemOrCode;
      const d = matched.deskripsi || (matched.penyakit ? `${matched.penyakit} (${matched.nm_icd || ''})` : (matched.nm_icd || ''));
      const isIm = matched.im === 1 || matched.im === '1';
      const isHeader = matched.validcode === 0 || matched.validcode === '0' || matched.validcode === false;
      const isAccpdxNo = String(matched.accpdx || '').trim().toUpperCase() === 'N';

      if (isHeader && targetToUse !== 'inacbg') {
        setIcdCheckMsg({ type: 'danger', text: `Diagnosa ${c} merupakan Header Code (tidak valid untuk iDRG dan tidak dapat disimpan).` });
        notify('warning', 'Header Code Tidak Dapat Dipilih', `Kode ${c} adalah Header Code dan tidak dapat dipilih untuk pengklaiman iDRG.`);
        setFormInputDiagnosaIdrg(prev => ({ ...prev, kd_icd: c, nm_icd: d, isHeader: true, validcode: matched.validcode }));
        setFormInputDiagnosa(prev => ({ ...prev, kd_icd: c, nm_icd: d, isHeader: true, validcode: matched.validcode }));
        closeAutocomplete();
        return;
      }

      const curFormState = targetToUse === 'inacbg' ? formInputDiagnosaInacbg : formInputDiagnosaIdrg;
      const wasPrimary = curFormState.ket === 'Utama' || curFormState.utama === 1 || curFormState.utama === '1';
      const targetKet = isAccpdxNo && wasPrimary ? 'Sekunder' : curFormState.ket;
      const targetUtama = isAccpdxNo && wasPrimary ? 0 : curFormState.utama;

      const nextFormValues = {
        kd_icd: c,
        nm_icd: d,
        ket: targetKet,
        utama: targetUtama,
        isHeader: false,
        validcode: matched.validcode
      };

      if (targetToUse === 'inacbg') {
        setFormInputDiagnosaInacbg(prev => ({ ...prev, ...nextFormValues, isHeader: !!isHeader }));
      } else {
        setFormInputDiagnosaIdrg(prev => ({ ...prev, ...nextFormValues }));
      }
      setFormInputDiagnosa(prev => ({ ...prev, ...nextFormValues, isHeader: targetToUse === 'inacbg' ? !!isHeader : false }));

      closeAutocomplete();

      if (isHeader) {
        setIcdCheckMsg({ type: 'info', text: `Ditemukan: ${d} [Header Code - INACBG]` });
        notify('info', 'Diagnosa Terpilih', `${c} - ${d}`);
      } else if (isAccpdxNo && wasPrimary) {
        setIcdCheckMsg({ type: 'warning', text: `Ditemukan: ${d} (Catatan: accpdx = N, tidak bisa diinput sebagai primary, dialihkan ke Sekunder)` });
        notify('info', 'Status Diagnosa Sekunder', 'Diagnosa tidak bisa diinput sebagai primary karena accpdx = N. Status dialihkan ke Sekunder agar tetap bisa diinput.');
      } else {
        setIcdCheckMsg({ type: 'success', text: `Ditemukan: ${d}${isIm ? ' [Indonesian Modification]' : ''}` });
      }

      // Otomatis simpan saat kode ditemukan/dipilih
      handleSaveInputDiagnosa(null, {
        kd_icd: c,
        nm_icd: d,
        ket: targetKet,
        utama: targetUtama,
        validcode: matched.validcode,
        accpdx: matched.accpdx,
        target: targetToUse
      });
    }
  };

  const fetchMasterIcd = async (type = 'diagnosa') => {
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    if (type === 'diagnosa' && masterIcd10.length === 0 && !isLoadingMasterIcd) {
      setIsLoadingMasterIcd(true);
      try {
        const res = await fetch(`${apiUrl}/show_icdxim`);
        const json = await res.json();
        const list = Array.isArray(json) ? json : (json?.list_icdxim || json?.list_icdx || json?.data || []);
        setMasterIcd10(list);
      } catch (err) {
        console.warn('Gagal fetch /show_icdxim:', err);
      } finally {
        setIsLoadingMasterIcd(false);
      }
    } else if (type === 'prosedur' && masterIcd9.length === 0 && !isLoadingMasterIcd) {
      setIsLoadingMasterIcd(true);
      try {
        const res = await fetch(`${apiUrl}/show_icdixim`);
        const json = await res.json();
        const list = Array.isArray(json) ? json : (json?.list_icdixim || json?.list_icdix || json?.data || []);
        setMasterIcd9(list);
      } catch (err) {
        console.warn('Gagal fetch /show_icdixim:', err);
      } finally {
        setIsLoadingMasterIcd(false);
      }
    }
  };
  // ── Toast Notification State ──
  const [toastNotif, setToastNotif] = useState({
    show: false,
    type: 'info',
    title: '',
    message: ''
  });

  const notify = (type, title, message) => {
    if (/header code/i.test(title || '') || /header code/i.test(message || '')) {
      return;
    }
    setToastNotif({
      show: true,
      type,
      title,
      message
    });
  };

  const [actionModal, setActionModal] = useState({ show: false, type: '', title: '', item: null, loading: false, result: null });
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printPdfUrl, setPrintPdfUrl] = useState('');



  const handlePrintClaim = (no_sep) => {
    if (!no_sep || no_sep === '-') return;
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    let url = `${apiUrl}/cetak-klaim/${no_sep}`;
    if (bridgingConfig) {
      const params = new URLSearchParams(getEklaimParams());
      url += `?${params.toString()}`;
    }
    setPrintPdfUrl(url);
    setShowPrintModal(true);
  };

  // ── Standarisasi Kemenkes iDRG & INA-CBG Workflow States ──
  const [idrgStatus, setIdrgStatus] = useState({ isGrouped: false, isFinal: false, code: '', desc: '', costWeight: 0 });
  const [inacbgStatus, setInacbgStatus] = useState({ isGrouped: false, isFinal: false, code: '', desc: '', tariff: 0 });
  const [claimFinalStatus, setClaimFinalStatus] = useState(false);
  const [isFinalizingIdrg, setIsFinalizingIdrg] = useState(false);
  const [isFinalizingInacbg, setIsFinalizingInacbg] = useState(false);
  const [isImportingInacbg, setIsImportingInacbg] = useState(false);
  const [isSendingOnline, setIsSendingOnline] = useState(false);
  const [isSentOnline, setIsSentOnline] = useState(false);
  const [imWarningList, setImWarningList] = useState([]);
  const [hasRunGrouperInacbg, setHasRunGrouperInacbg] = useState(false);
  const [isGroupingInacbg, setIsGroupingInacbg] = useState(false);
  const [inacbgGroupingData, setInacbgGroupingData] = useState(null);
  const [selectedSpecialProcedure, setSelectedSpecialProcedure] = useState('');
  const [selectedSpecialProsthesis, setSelectedSpecialProsthesis] = useState('');
  const [selectedSpecialInvestigation, setSelectedSpecialInvestigation] = useState('');
  const [selectedSpecialDrug, setSelectedSpecialDrug] = useState('');

  // ── State Top-Up Payment iDRG (Stage 2) & Kalkulasi Bobot Biaya (Cost Weight) ──
  const [idrgTopupOptions, setIdrgTopupOptions] = useState([]);
  const [selectedIdrgTopupMap, setSelectedIdrgTopupMap] = useState({});
  const [idrgCostWeightData, setIdrgCostWeightData] = useState({
    drgCostWeight: 0,
    topupCostWeight: 0,
    totalCostWeight: 0,
    tariff: 0
  });
  const [isGroupingIdrgStage2, setIsGroupingIdrgStage2] = useState(false);

  const handleFinalisasiIdrg = async (targetItem) => {
    const item = targetItem || grouperTarget;
    if (!item?.no_sep) return;
    setIsFinalizingIdrg(true);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    try {
      const res = await fetch(`${apiUrl}/finalisasi_idrg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ no_sep: item.no_sep, ...getEklaimParams() })
      });
      const json = await res.json();
      if (json.status === 1 || json.status === '1') {
        notify('success', 'Finalisasi iDRG Berhasil (Poin 9)', `iDRG untuk No. SEP ${item.no_sep} telah berhasil difinalisasi.`);
        setIdrgStatus(prev => ({ ...prev, isFinal: true }));
        setShowTableInacbg(true);
        fetchDiagnosaInacbgData(item);
        fetchDataClaim();
        handleOpenInputDiagnosa('diagnosa', 'inacbg');
        setShowInlineInputInacbg(true);
      } else {
        notify('danger', 'Gagal Finalisasi iDRG', json.msg || 'Terjadi kesalahan saat finalisasi iDRG.');
      }
    } catch (err) {
      notify('danger', 'Error', `Gagal menghubungi server: ${err.message}`);
    } finally {
      setIsFinalizingIdrg(false);
    }
  };

  const handleReeditIdrg = async (targetItem) => {
    const item = targetItem || grouperTarget;
    if (!item?.no_sep) return;
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    try {
      if (idrgStatus.isFinal) {
        const res = await fetch(`${apiUrl}/reedit_idrg`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ no_sep: item.no_sep, ...getEklaimParams() })
        });
        const json = await res.json();
        if (json.status === 1 || json.status === '1') {
          notify('warning', 'Re-Edit iDRG Berhasil (Poin 11)', `Kunci iDRG untuk No. SEP ${item.no_sep} telah dibuka.`);
          setIdrgStatus(prev => ({ ...prev, isFinal: false, isGrouped: false }));
          setShowTableInacbg(false);
          setShowInlineInputInacbg(false);
          fetchDataClaim();
        } else {
          notify('danger', 'Gagal Re-Edit iDRG', json.msg || 'Terjadi kesalahan saat buka kunci iDRG.');
          return;
        }
      } else {
        setIdrgStatus(prev => ({ ...prev, isGrouped: false, isFinal: false }));
        setShowTableInacbg(false);
        setShowInlineInputInacbg(false);
        notify('info', 'Edit Ulang iDRG Aktif', 'Kunci Grouper iDRG telah dibuka. Form input diagnosa & prosedur aktif kembali.');
      }
    } catch (err) {
      notify('danger', 'Error', `Gagal menghubungi server: ${err.message}`);
    }
  };

  const handleImportIdrgToInacbg = async (targetItem) => {
    const item = targetItem || grouperTarget;
    if (!item?.no_sep) return;
    setIsImportingInacbg(true);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    try {
      const res = await fetch(`${apiUrl}/import_idrg_to_inacbg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ no_sep: item.no_sep, ...getEklaimParams() })
      });
      const json = await res.json();
      if (json.status === 1 || json.status === '1') {
        notify('success', 'Impor Koding Berhasil (Poin 13)', 'Koding diagnosis & prosedur dari iDRG berhasil disalin ke INA-CBG.');
        // Periksa apakah ada kode IM yang tidak berlaku di INA-CBG (Poin 14)
        const diags = diagnosaData?.list_diagnosa || [];
        const foundIm = diags.filter(d => {
          const code = (d.kd_icd || '').trim();
          return code.length > 5 || (code.includes('.') && code.split('.')[1].length > 2);
        });
        setImWarningList(foundIm);
      } else {
        notify('danger', 'Gagal Impor Koding', json.msg || 'Terjadi kesalahan saat impor koding.');
      }
    } catch (err) {
      notify('danger', 'Error', `Gagal menghubungi server: ${err.message}`);
    } finally {
      setIsImportingInacbg(false);
    }
  };

  // ── Handlers Grouping iDRG Stage 2 (Top-Up Payment) & INA-CBG Stage 2 (Special CMG) ──
  const handleProcessGrouperIdrgStage2 = async (targetItem, topupCodes = null) => {
    const item = targetItem || grouperTarget;
    if (!item?.no_sep) return;
    setIsGroupingIdrgStage2(true);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    // Standar E-Klaim v5.10.x: Jika topup dibatalkan/dikosongkan, kirim nilai topup_codes = "#"
    const codesToSend = (topupCodes !== null && topupCodes !== undefined && topupCodes !== '' && (!Array.isArray(topupCodes) || topupCodes.length > 0)) ? topupCodes : '#';

    try {
      const res = await fetch(`${apiUrl}/grouping_idrg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nomor_sep: item.no_sep,
          nomorsep: item.no_sep,
          stage: 2,
          topup_codes: codesToSend,
          ...getEklaimParams()
        })
      });
      let json = {};
      try { json = await res.json(); } catch(e) {}
      if (json?.status === 1 || json?.status === '1' || json?.metadata?.code === 200 || json?.metadata?.code === '200') {
        const respIdrg = json?.response_idrg || json?.eklaim_response?.response_idrg || {};
        const idrgCode = json?.idrg_code || respIdrg.drg_code || respIdrg.code || idrgStatus.code;
        const idrgDesc = json?.idrg_desc || respIdrg.drg_description || respIdrg.description || idrgStatus.desc;
        const drgCw = json?.drg_cost_weight ?? respIdrg.drg_cost_weight ?? idrgCostWeightData.drgCostWeight;
        const topupCw = json?.topup_cost_weight ?? respIdrg.topup_cost_weight ?? 0;
        const totalCw = json?.total_cost_weight ?? respIdrg.total_cost_weight ?? respIdrg.cost_weight ?? (Number(drgCw) + Number(topupCw));
        const idrgTariff = json?.tarif_idrg ?? respIdrg.total_tarif ?? respIdrg.tariff ?? 0;

        setIdrgCostWeightData({
          drgCostWeight: drgCw,
          topupCostWeight: topupCw,
          totalCostWeight: totalCw,
          tariff: idrgTariff
        });

        if (json?.topup_options || respIdrg?.topup_options) {
          setIdrgTopupOptions(json?.topup_options || respIdrg?.topup_options || []);
        }

        const isIdrgUngroup = String(idrgCode || '').toLowerCase().includes('ungroupable') || String(respIdrg.mdc_number) === '36' || String(idrgCode || '').startsWith('36');

        setIdrgStatus(prev => ({
          ...prev,
          code: idrgCode,
          desc: idrgDesc,
          costWeight: totalCw,
          isUngroupable: isIdrgUngroup
        }));

        setGrouperResult(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            data: {
              ...prev.data,
              idrg_code: idrgCode,
              idrg_desc: idrgDesc,
              drg_cost_weight: drgCw,
              topup_cost_weight: topupCw,
              total_cost_weight: totalCw,
              idrg_cost_weight: totalCw,
              cost_weight: totalCw,
              tarif_idrg: idrgTariff,
              response_idrg: respIdrg
            }
          };
        });

        notify('success', 'Top-Up iDRG Berhasil Diperbarui', `Cost Weight Total: ${totalCw} (DRG: ${drgCw}, Top-Up: ${topupCw})`);
      } else {
        notify('danger', 'Gagal Top-Up iDRG', json?.msg || json?.metadata?.message || 'Terjadi kesalahan saat memproses Stage 2 Top-up iDRG.');
      }
    } catch (err) {
      notify('danger', 'Error', `Gagal memproses Top-Up iDRG: ${err.message}`);
    } finally {
      setIsGroupingIdrgStage2(false);
    }
  };

  const handleIdrgTopupChange = async (type, code) => {
    const updatedMap = {
      ...selectedIdrgTopupMap,
      [type]: code || ''
    };
    setSelectedIdrgTopupMap(updatedMap);

    const activeCodes = Object.values(updatedMap).filter(Boolean);
    const topupPayload = activeCodes.length > 0 ? activeCodes : '#';
    await handleProcessGrouperIdrgStage2(grouperTarget, topupPayload);
  };

  const handleProcessGrouperInacbg = async (targetItem, specialCmgCode = null) => {
    const item = targetItem || grouperTarget;
    if (!item?.no_sep) return;
    setIsGroupingInacbg(true);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    // Stage 2 jika specialCmgCode disertakan (termasuk reset '#'), Stage 1 jika null
    const stageToUse = (specialCmgCode !== null && specialCmgCode !== undefined) ? 2 : 1;
    const specialCmgPayload = (specialCmgCode !== null && specialCmgCode !== undefined) ? specialCmgCode : undefined;

    // Susun diagnosa_str & prosedur_str sesuai urutan tampilan tabel
    const diagInacbgStr = (diagnosaInacbgData?.list_diagnosa || [])
      .map(d => String(d.kd_icd || '').trim())
      .filter(Boolean)
      .join('#');

    const procInacbgStr = (diagnosaInacbgData?.list_prosedur || [])
      .map(p => {
        const code = String(p.kd_icd || '').trim();
        if (!code) return '';
        const qty = parseInt(p.qty || p.multiplicity || 1);
        return qty > 1 && !code.includes('+') ? `${code}+${qty}` : code;
      })
      .filter(Boolean)
      .join('#');

    try {
      const res = await fetch(`${apiUrl}/groupinginacbs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nomorsep: item.no_sep,
          nomedrec: item.no_medrec,
          no_nota: item.no_nota || item.no_daftar || item.no_sep,
          stage: stageToUse,
          special_cmg: specialCmgPayload,
          diagnosa_str: diagInacbgStr || undefined,
          prosedur_str: procInacbgStr || undefined,
          ...getEklaimParams()
        })
      });
      let json = {};
      try { json = await res.json(); } catch(e) {}
      if (json?.status === 1 || json?.status === '1' || json?.metadata?.code === 200 || json?.metadata?.code === '200') {
        const cbgCode = json.kode_cbg || json.cbg || json.response_inacbg?.cbg?.code;
        const cbgDesc = json.deskripsi_cbg || json.cbg_desc || json.response_inacbg?.cbg?.description;
        const cbgTarif = json.tarif_cbg || json.tarif_inacbg || json.response_inacbg?.cbg?.tariff || 0;
        const totalTariff = json.response_inacbg?.tariff || json.tarif_cbg || json.tarif_inacbg || cbgTarif;
        const specOptions = json.special_cmg_option || json.response_inacbg?.special_cmg_option || [];
        setInacbgGroupingData(prev => {
          const merged = { ...json };
          // Tangkap dan pertahankan special_cmg_option dari stage 1 maupun stage 2
          const finalOpts = (Array.isArray(specOptions) && specOptions.length > 0)
            ? specOptions
            : (prev?.special_cmg_option || prev?.response_inacbg?.special_cmg_option || []);
          merged.special_cmg_option = finalOpts;
          if (merged.response_inacbg) {
            merged.response_inacbg.special_cmg_option = finalOpts;
          }
          return merged;
        });
        setHasRunGrouperInacbg(true);
        setInacbgStatus({
          isGrouped: Boolean(cbgCode && cbgCode !== 'X-0-99-X' && !String(cbgCode).startsWith('X')),
          isFinal: false,
          isUngroupable: !cbgCode || cbgCode === 'X-0-99-X' || String(cbgCode).startsWith('X'),
          code: cbgCode || '',
          desc: cbgDesc || '',
          tariff: totalTariff
        });
        setGrouperResult(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            data: {
              ...prev.data,
              cbg: cbgCode,
              cbg_desc: cbgDesc,
              tarif_inacbg: totalTariff,
              response_inacbg: json.response_inacbg || prev.data?.response_inacbg,
              special_cmg: json.special_cmg_option || json.special_cmg || prev.data?.special_cmg
            }
          };
        });
        setClaimList(prev => prev.map(p => {
          if (p.no_sep === item.no_sep || (item.no_nota && p.no_nota === item.no_nota)) {
            return {
              ...p,
              cbg: cbgCode || p.cbg,
              cbg_desc: cbgDesc || p.cbg_desc,
              tarif_inacbg: totalTariff || p.tarif_inacbg
            };
          }
          return p;
        }));
        notify('success', 'Grouper INA-CBG Berhasil', `Kode CBG: ${cbgCode || '-'} - ${cbgDesc || '-'} (Tarif: ${formatCurrencyInacbg(totalTariff)})`);
      } else {
        notify('danger', 'Gagal Grouper INA-CBG', json?.msg || json?.metadata?.message || 'Gagal memproses Grouper INA-CBG.');
      }
    } catch (err) {
      notify('danger', 'Error', `Gagal memproses grouper INA-CBG: ${err.message}`);
    } finally {
      setIsGroupingInacbg(false);
    }
  };

  const handleFinalisasiInacbg = async (targetItem) => {
    const item = targetItem || grouperTarget;
    if (!item?.no_sep) return;
    setIsFinalizingInacbg(true);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    try {
      const res = await fetch(`${apiUrl}/finalisasi_inacbg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ no_sep: item.no_sep, ...getEklaimParams() })
      });
      const json = await res.json();
      if (json.status === 1 || json.status === '1') {
        notify('success', 'Finalisasi INA-CBG Berhasil (Poin 18)', `Koding INA-CBG untuk No. SEP ${item.no_sep} telah berhasil dikunci.`);
        setInacbgStatus(prev => ({ ...prev, isFinal: true }));
        fetchDataClaim();
      } else {
        notify('danger', 'Gagal Finalisasi INA-CBG', json.msg || 'Terjadi kesalahan saat finalisasi INA-CBG.');
      }
    } catch (err) {
      notify('danger', 'Error', `Gagal menghubungi server: ${err.message}`);
    } finally {
      setIsFinalizingInacbg(false);
    }
  };

  const handleReeditInacbg = async (targetItem) => {
    const item = targetItem || grouperTarget;
    if (!item?.no_sep) return;
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    try {
      const res = await fetch(`${apiUrl}/reedit_inacbg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ no_sep: item.no_sep, ...getEklaimParams() })
      });
      const json = await res.json();
      if (json.status === 1 || json.status === '1') {
        notify('warning', 'Re-Edit INA-CBG Berhasil (Poin 19)', `Kunci INA-CBG untuk No. SEP ${item.no_sep} telah dibuka.`);
        setInacbgStatus(prev => ({ ...prev, isFinal: false }));
        fetchDataClaim();
      } else {
        notify('danger', 'Gagal Re-Edit INA-CBG', json.msg || 'Terjadi kesalahan saat buka kunci INA-CBG.');
      }
    } catch (err) {
      notify('danger', 'Error', `Gagal menghubungi server: ${err.message}`);
    }
  };

  const handleSendClaimOnline = async (targetItem) => {
    const item = targetItem || actionModal.item || grouperTarget;
    if (!item?.no_sep) {
      notify('warning', 'Peringatan', 'Nomor SEP tidak ditemukan untuk proses Kirim Klaim Online.');
      return;
    }
    setIsSendingOnline(true);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    try {
      const res = await fetch(`${apiUrl}/send_claim_individual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ no_sep: item.no_sep, ...getEklaimParams() })
      });
      const json = await res.json();
      const isOk = json.status === 1 || json.status === '1' || (json.metadata && (json.metadata.code === 200 || json.metadata.code === '200'));
      if (isOk) {
        setIsSentOnline(true);
        notify('success', 'Kirim Klaim Online Berhasil (Poin 22)', `Klaim No. SEP ${item.no_sep} berhasil dikirim ke Data Center Kemenkes!`);
        setActionModal(prev => {
          if (!prev.show) return prev;
          const currentResult = prev.result || {};
          return {
            ...prev,
            result: {
              ...currentResult,
              success: true,
              status_dc: `Terkirim ke Data Center Kemenkes (${new Date().toLocaleTimeString('id-ID')})`,
              is_dc_terkirim: true,
              tgl_kirim_dc: new Date().toLocaleString('id-ID'),
              message: `Klaim No. SEP ${item.no_sep} berhasil dikirim ke Data Center Kemenkes!`
            }
          };
        });
      } else {
        notify('danger', 'Gagal Kirim Klaim Online', json.msg || 'Terjadi kesalahan saat mengirim klaim ke Data Center Kemenkes.');
      }
    } catch (err) {
      notify('danger', 'Error', `Gagal menghubungi server: ${err.message}`);
    } finally {
      setIsSendingOnline(false);
    }
  };

  const handleOpenAction = (item, type, title) => {
    setActionModal({ show: true, type, title, item, loading: false, result: null });
  };

  const handleProcessAction = async () => {
    setActionModal(prev => ({ ...prev, loading: true, result: null }));
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    let endpoint = '';
    const bodyData = { no_sep: actionModal.item?.no_sep };
    if (actionModal.type === 'hapus') {
      endpoint = '/hapus_klaim';
      bodyData.coder_nik = localStorage.getItem('nik_coder') || '';
    }
    if (actionModal.type === 'idrg_final') {
      endpoint = '/finalisasi_idrg';
    }
    if (actionModal.type === 'finalisasi') {
      endpoint = '/finalisasi_klaim';
      bodyData.coder_nik = localStorage.getItem('nik_coder') || '';
    }
    if (actionModal.type === 'reedit') endpoint = '/reedit_klaim';
    if (actionModal.type === 'status') endpoint = '/cek_status_klaim';

    try {
      const res = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...bodyData, ...getEklaimParams() })
      });
      const json = await res.json();
      const isSuccess = json.status === 1 || json.status === '1' || json.status === 200 || json.status === '200' || (json.metadata && (json.metadata.code === 200 || json.metadata.code === '200'));
      const messageText = json.msg || (json.metadata ? json.metadata.message : (typeof json === 'string' ? json : JSON.stringify(json)));

      if (isSuccess && actionModal.type === 'finalisasi') {
        const targetSep = actionModal.item?.no_sep;
        setClaimFinalStatus(true);
        setActionModal(prev => ({ ...prev, show: false, loading: false }));
        notify('success', 'Finalisasi Klaim Berhasil (Poin 20, 21)', `Klaim berhasil difinalisasi dengan NIK Koder. Menampilkan dokumen cetak klaim untuk No. SEP: ${targetSep || '-'}`);
        fetchDataClaim();
        if (targetSep && targetSep !== '-') {
          setTimeout(() => {
            handlePrintClaim(targetSep);
          }, 350);
        }
      } else {
        if (isSuccess && actionModal.type === 'reedit') {
          setClaimFinalStatus(false);
          setInacbgStatus(prev => ({ ...prev, isFinal: false }));
        }
        setActionModal(prev => ({
          ...prev,
          loading: false,
          result: {
            success: isSuccess,
            message: messageText,
            status_klaim: json.status_klaim || 'Klaim Terproses',
            status_dc: json.status_dc || (json.is_dc_terkirim ? 'Terkirim ke Data Center Kemenkes' : 'Belum Terkirim ke Data Center Kemenkes'),
            is_dc_terkirim: Boolean(json.is_dc_terkirim),
            tgl_kirim_dc: json.tgl_kirim_dc || null,
            klaim_final: Boolean(json.klaim_final),
            raw: json
          }
        }));
        if (isSuccess) {
          fetchDataClaim();
        }
      }
    } catch (err) {
      setActionModal(prev => ({
        ...prev,
        loading: false,
        result: { success: false, message: 'Gagal menghubungi server SIMRS/E-Klaim.' }
      }));
    }
  };

  const hasPrimaryDiagnosa = (list) => {
    if (!list || !Array.isArray(list) || list.length === 0) return false;
    return list.some(d => {
      const ketStr = String(d.ket || '').trim().toLowerCase();
      return ketStr === 'utama' || ketStr.includes('utama') || d.utama === 1 || d.utama === '1' || d.prioritas === '1' || d.prioritas === 1;
    });
  };

  const fetchDiagnosaData = async (item) => {
    if (!item) return;
    setIsLoadingDiagnosa(true);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${apiUrl}/show_hdiagnosa`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ no_medrec: item.no_medrec, no_nota: item.no_nota || item.no_daftar || item.no_sep })
      });

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();
      if (json?.list_diagnosa || json?.list_prosedur) {
        const diagList = json.list_diagnosa || [];
        setDiagnosaData({
          list_diagnosa: diagList,
          list_prosedur: json.list_prosedur || []
        });
        const hasUtama = hasPrimaryDiagnosa(diagList);
        if (diagList.length === 0 || !hasUtama) {
          setFormInputDiagnosaIdrg(prev => ({ ...prev, ket: 'Utama', utama: 1 }));
          setFormInputDiagnosa(prev => ({ ...prev, ket: 'Utama', utama: 1 }));
        } else {
          setFormInputDiagnosaIdrg(prev => ({ ...prev, ket: 'Sekunder', utama: 0 }));
          setFormInputDiagnosa(prev => ({ ...prev, ket: 'Sekunder', utama: 0 }));
        }
      } else {
        setDiagnosaData({ list_diagnosa: [], list_prosedur: [] });
        setFormInputDiagnosaIdrg(prev => ({ ...prev, ket: 'Utama', utama: 1 }));
        setFormInputDiagnosa(prev => ({ ...prev, ket: 'Utama', utama: 1 }));
      }
    } catch (err) {
      console.warn('Gagal fetch /show_hdiagnosa:', err);
      setDiagnosaData({ list_diagnosa: [], list_prosedur: [] });
      setFormInputDiagnosaIdrg(prev => ({ ...prev, ket: 'Utama', utama: 1 }));
      setFormInputDiagnosa(prev => ({ ...prev, ket: 'Utama', utama: 1 }));
    } finally {
      setIsLoadingDiagnosa(false);
    }
  };

  const fetchDiagnosaInacbgData = async (item) => {
    if (!item) return;
    setIsLoadingDiagnosaInacbg(true);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${apiUrl}/show_hdiagnosa_inacbg`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ no_medrec: item.no_medrec, no_nota: item.no_nota || item.no_daftar || item.no_sep })
      });

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();
      if (json?.list_diagnosa || json?.list_prosedur) {
        const diagList = json.list_diagnosa || [];
        setDiagnosaInacbgData({
          list_diagnosa: diagList,
          list_prosedur: json.list_prosedur || []
        });
        const hasUtama = hasPrimaryDiagnosa(diagList);
        if (diagList.length === 0 || !hasUtama) {
          setFormInputDiagnosaInacbg(prev => ({ ...prev, ket: 'Utama', utama: 1 }));
          setFormInputDiagnosa(prev => ({ ...prev, ket: 'Utama', utama: 1 }));
        } else {
          setFormInputDiagnosaInacbg(prev => ({ ...prev, ket: 'Sekunder', utama: 0 }));
          setFormInputDiagnosa(prev => ({ ...prev, ket: 'Sekunder', utama: 0 }));
        }
      } else {
        setDiagnosaInacbgData({ list_diagnosa: [], list_prosedur: [] });
        setFormInputDiagnosaInacbg(prev => ({ ...prev, ket: 'Utama', utama: 1 }));
        setFormInputDiagnosa(prev => ({ ...prev, ket: 'Utama', utama: 1 }));
      }
    } catch (err) {
      console.warn('Gagal fetch /show_hdiagnosa_inacbg:', err);
      setDiagnosaInacbgData({ list_diagnosa: [], list_prosedur: [] });
      setFormInputDiagnosaInacbg(prev => ({ ...prev, ket: 'Utama', utama: 1 }));
      setFormInputDiagnosa(prev => ({ ...prev, ket: 'Utama', utama: 1 }));
    } finally {
      setIsLoadingDiagnosaInacbg(false);
    }
  };

  const handleImportToInacbg = async () => {
    if (!grouperTarget) return;
    setIsImportingInacbg(true);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${apiUrl}/import_diagnosa_inacbg`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          no_medrec: grouperTarget.no_medrec, 
          no_nota: grouperTarget.no_nota || grouperTarget.no_daftar || grouperTarget.no_sep 
        })
      });

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();
      if (json?.status === 1 || json?.success) {
        notify('success', 'Import Diagnosa INACBG Berhasil', json?.msg || 'Data diagnosa dan prosedur iDRG berhasil disalin ke tabel INACBG (hdiagnosa_icd & hdiagnosa_icd2).');
        await fetchDiagnosaInacbgData(grouperTarget);
      } else {
        notify('danger', 'Gagal Import Diagnosa INACBG', json?.msg || 'Terjadi kesalahan saat menyalin data diagnosa.');
      }
    } catch (err) {
      console.error('Error import diagnosa inacbg:', err);
      notify('danger', 'Error Server', `Gagal menghubungi server untuk import data: ${err.message}`);
    } finally {
      setIsImportingInacbg(false);
    }
  };


  const handleMultiplicityChange = (newMult, target = 'idrg') => {
    const mult = Math.max(1, parseInt(newMult) || 1);
    if (target === 'inacbg') {
      setFormInputDiagnosaInacbg(prev => {
        const cleanCode = (prev.kd_icd || '').split('+')[0].trim();
        return { ...prev, multiplicity: mult, kd_icd: cleanCode };
      });
    } else {
      setFormInputDiagnosaIdrg(prev => {
        const cleanCode = (prev.kd_icd || '').split('+')[0].trim();
        return { ...prev, multiplicity: mult, kd_icd: cleanCode };
      });
    }
    setFormInputDiagnosa(prev => {
      const cleanCode = (prev.kd_icd || '').split('+')[0].trim();
      return { ...prev, multiplicity: mult, kd_icd: cleanCode };
    });
  };

  const handleOpenInputDiagnosa = async (type = 'diagnosa', target = 'idrg') => {
    setInputDiagnosaType(type);
    setInputDiagnosaTarget(target);
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');

    const curList = target === 'inacbg' ? diagnosaInacbgData.list_diagnosa : diagnosaData.list_diagnosa;
    const hasUtama = hasPrimaryDiagnosa(curList);
    const isNoRecordOrNoUtama = !curList || curList.length === 0 || !hasUtama;

    const initialValues = {
      kd_icd: '',
      nm_icd: '',
      ket: type === 'diagnosa' ? (isNoRecordOrNoUtama ? 'Utama' : 'Sekunder') : 'Prosedur',
      utama: type === 'diagnosa' ? (isNoRecordOrNoUtama ? 1 : 0) : 0,
      multiplicity: 1,
      kd_dokter: selectedDiagnosaItem?.kd_dokter || '',
      nm_dokter: selectedDiagnosaItem?.nm_dokter || selectedDiagnosaItem?.dpjp || '',
      tanggal: `${dd}/${mm}/${yyyy}`,
      jam: `${hh}:${min}`,
      lokasi: selectedDiagnosaItem?.jenis_pelayanan === '2' ? 'Rawat Jalan' : 'Rawat Inap',
      petugas: localStorage.getItem('nama_user') || localStorage.getItem('user') || localStorage.getItem('username') || sessionStorage.getItem('nama_user') || sessionStorage.getItem('user') || sessionStorage.getItem('username') || username || 'Administrator'
    };

    if (target === 'inacbg') {
      setFormInputDiagnosaInacbg(initialValues);
    } else {
      setFormInputDiagnosaIdrg(initialValues);
    }
    setFormInputDiagnosa(initialValues);

    closeAutocomplete();

    // Buka form inline di panel grouper (bukan modal terpisah)
    if (target === 'inacbg') {
      setShowInlineInputInacbg(true);
    } else {
      setShowInlineInputIdrg(true);
    }

    const apiUrl = getApiUrl() || 'http://localhost:8000';
    if (type === 'diagnosa' && masterIcd10.length === 0) {
      setIsLoadingMasterIcd(true);
      try {
        const res = await fetch(`${apiUrl}/show_icdxim`);
        const json = await res.json();
        const list = Array.isArray(json) ? json : (json?.list_icdxim || json?.list_icdx || json?.data || []);
        setMasterIcd10(list);
      } catch (err) {
        console.warn('Gagal fetch /show_icdxim:', err);
      } finally {
        setIsLoadingMasterIcd(false);
      }
    } else if (type === 'prosedur' && masterIcd9.length === 0) {
      setIsLoadingMasterIcd(true);
      try {
        const res = await fetch(`${apiUrl}/show_icdixim`);
        const json = await res.json();
        const list = Array.isArray(json) ? json : (json?.list_icdixim || json?.list_icdix || json?.data || []);
        setMasterIcd9(list);
      } catch (err) {
        console.warn('Gagal fetch /show_icdixim:', err);
      } finally {
        setIsLoadingMasterIcd(false);
      }
    }
  };

  const handleCheckIcd = async (kode, explicitTarget = null) => {
    const targetToUse = explicitTarget || inputDiagnosaTarget;
    if (explicitTarget && explicitTarget !== inputDiagnosaTarget) {
      setInputDiagnosaTarget(explicitTarget);
    }
    const curFormState = targetToUse === 'inacbg' ? formInputDiagnosaInacbg : (targetToUse === 'idrg' ? formInputDiagnosaIdrg : formInputDiagnosa);
    const codeToTest = (kode !== undefined && kode !== null ? kode : curFormState.kd_icd || '').trim();
    if (!codeToTest) {
      setIcdCheckMsg({ type: 'warning', text: 'Silakan ketik kode terlebih dahulu' });
      return;
    }

    setIsCheckingIcd(true);
    setIcdCheckMsg(null);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    const isProsedur = inputDiagnosaType === 'prosedur';

    // Dukungan Poin 4 Standar Kemenkes RI: Multiplicity (+) dan Setting (#)
    let baseCode = codeToTest;
    let multiplicityInfo = '';
    if (isProsedur) {
      if (codeToTest.includes('+')) {
        const parts = codeToTest.split('+');
        baseCode = parts[0].trim();
        multiplicityInfo = ` (Multiplicity: +${parts[1].trim()})`;
        const parsedMult = parseInt(parts[1].trim());
        if (!isNaN(parsedMult) && parsedMult > 0) {
          if (targetToUse === 'inacbg') {
            setFormInputDiagnosaInacbg(prev => ({ ...prev, multiplicity: parsedMult }));
          } else {
            setFormInputDiagnosaIdrg(prev => ({ ...prev, multiplicity: parsedMult }));
          }
          setFormInputDiagnosa(prev => ({ ...prev, multiplicity: parsedMult }));
        }
      } else if (codeToTest.includes('#')) {
        const parts = codeToTest.split('#');
        baseCode = parts[0].trim();
        multiplicityInfo = ` (Setting Sesi: #${parts.slice(1).join('#')})`;
      }

      // ── PENCARIAN PROSEDUR ICD-9-CM: Langsung ke show_icdixim / local database table _idrg (tanpa ke /search_procedures_im) ──
      try {
        let listIcd9 = masterIcd9;
        if (!listIcd9 || listIcd9.length === 0) {
          try {
            const res9 = await fetch(`${apiUrl}/show_icdixim`);
            const json9 = await res9.json();
            listIcd9 = Array.isArray(json9) ? json9 : (json9?.list_icdixim || json9?.list_icdix || json9?.data || []);
            if (Array.isArray(listIcd9) && listIcd9.length > 0) {
              setMasterIcd9(listIcd9);
            }
          } catch (e9) {
            console.warn('Gagal fetch /show_icdixim:', e9);
          }
        }

        // 1. Cari exact match kode pada masterIcd9 yang dimuat dari show_icdixim (table _idrg)
        const matched = (listIcd9 || []).find(x => {
          const c = String(x.icd || x.kode || x.kd_icd || '').trim().toLowerCase();
          return c === baseCode.toLowerCase();
        });

        if (matched) {
          const foundDesc = (matched.deskripsi || matched.nm_icd || '') + multiplicityInfo;
          const isIm = matched.im === 1 || matched.im === '1';
          const isAccpdxNo = String(matched.accpdx || '').trim().toUpperCase() === 'N';

          if (isAccpdxNo) {
            if (targetToUse === 'inacbg') {
              setFormInputDiagnosaInacbg(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: foundDesc, accpdx: 'N' }));
            } else {
              setFormInputDiagnosaIdrg(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: foundDesc, accpdx: 'N' }));
            }
            setFormInputDiagnosa(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: foundDesc, accpdx: 'N' }));
            setIcdCheckMsg({ type: 'danger', text: `Prosedur ${codeToTest} tidak bisa dipilih karena accpdx = N (tidak dapat disimpan).` });
            notify('warning', 'Prosedur Tidak Valid', `Prosedur ${codeToTest} tidak bisa dipilih karena accpdx = N`);
            setIsCheckingIcd(false);
            return;
          }

          if (targetToUse === 'inacbg') {
            setFormInputDiagnosaInacbg(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: foundDesc }));
          } else {
            setFormInputDiagnosaIdrg(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: foundDesc }));
          }
          setFormInputDiagnosa(prev => ({
            ...prev,
            kd_icd: codeToTest,
            nm_icd: foundDesc
          }));
          setIcdCheckMsg({ type: 'success', text: `Ditemukan di Master ICD-9 IM (_idrg): ${foundDesc}` });
          notify('success', 'Kode Prosedur ICD-9 IM Ditemukan', `Kode ${codeToTest}: ${foundDesc}${isIm ? ' [Indonesian Modification]' : ''}`);
          setIsCheckingIcd(false);
          handleSaveInputDiagnosa(null, {
            kd_icd: codeToTest,
            nm_icd: foundDesc,
            validcode: matched.validcode,
            accpdx: matched.accpdx,
            target: targetToUse
          });
          return;
        }

        // 2. Jika belum ditemukan di cache memori, panggil endpoint lokal /cari_icdix (query _idrg)
        const resCari = await fetch(`${apiUrl}/cari_icdix?kodeicd=${encodeURIComponent(baseCode)}`);
        const jsonCari = await resCari.json();
        if (jsonCari && (jsonCari.status === 0 || jsonCari.status === '0')) {
          const nameResult = (jsonCari.nm_icd || jsonCari.deskripsi || '') + multiplicityInfo;
          const isAccpdxNo = String(jsonCari.accpdx || '').trim().toUpperCase() === 'N';

          if (isAccpdxNo) {
            if (targetToUse === 'inacbg') {
              setFormInputDiagnosaInacbg(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: nameResult, accpdx: 'N' }));
            } else {
              setFormInputDiagnosaIdrg(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: nameResult, accpdx: 'N' }));
            }
            setFormInputDiagnosa(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: nameResult, accpdx: 'N' }));
            setIcdCheckMsg({ type: 'danger', text: `Prosedur ${codeToTest} tidak bisa dipilih karena accpdx = N (tidak dapat disimpan).` });
            notify('warning', 'Prosedur Tidak Valid', `Prosedur ${codeToTest} tidak bisa dipilih karena accpdx = N`);
            setIsCheckingIcd(false);
            return;
          }

          if (targetToUse === 'inacbg') {
            setFormInputDiagnosaInacbg(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: nameResult }));
          } else {
            setFormInputDiagnosaIdrg(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: nameResult }));
          }
          setFormInputDiagnosa(prev => ({
            ...prev,
            kd_icd: codeToTest,
            nm_icd: nameResult
          }));
          setIcdCheckMsg({ type: 'success', text: `Ditemukan di Master ICD-9 IM (_idrg): ${nameResult}` });
          notify('success', 'Kode Prosedur ICD-9 IM Ditemukan', `Kode ${codeToTest}: ${nameResult}`);
          setIsCheckingIcd(false);
          handleSaveInputDiagnosa(null, {
            kd_icd: codeToTest,
            nm_icd: nameResult,
            validcode: jsonCari.validcode,
            accpdx: jsonCari.accpdx,
            target: targetToUse
          });
          return;
        }

        // 3. Tidak ditemukan pada table _idrg lokal
        const errMsg = `Kode Prosedur "${codeToTest}" tidak ditemukan pada Master ICD-9-CM IM lokal (tabel _idrg)`;
        setIcdCheckMsg({ type: 'danger', text: errMsg });
        notify('warning', 'Kode Prosedur Tidak Ditemukan', `${errMsg}. Mengalihkan ke tabel pencarian master prosedur.`);
        handleOpenMasterIcdModal(baseCode);
      } catch (err) {
        console.error('Error pencarian prosedur ICD-9 IM:', err);
        setIcdCheckMsg({ type: 'danger', text: `Gagal memeriksa kode prosedur: ${err.message}` });
        notify('danger', 'Koneksi Terputus', `Gagal memeriksa kode prosedur ke server: ${err.message}`);
        handleOpenMasterIcdModal(baseCode);
      } finally {
        setIsCheckingIcd(false);
      }
      return;
    }

    // ── PENCARIAN DIAGNOSA ICD-10 (cari_icdx dengan fallback search_diagnosis_im) ──
    try {
      let res = await fetch(`${apiUrl}/cari_icdx?kodeicd=${encodeURIComponent(baseCode)}`);
      let json = await res.json();

      if ((!json || json.status === 1 || json.status === '1') && apiUrl) {
        try {
          const resIm = await fetch(`${apiUrl}/search_diagnosis_im?keyword=${encodeURIComponent(baseCode)}`);
          if (resIm.ok) {
            const jsonIm = await resIm.json();
            const imList = jsonIm?.response?.data || jsonIm?.data || [];
            if (Array.isArray(imList) && imList.length > 0) {
              const matched = imList.find(x => (x[0] || x.code || '').toLowerCase() === baseCode.toLowerCase()) || imList[0];
              const imCode = matched[0] || matched.code || baseCode;
              const imDesc = matched[1] || matched.desc || matched.display || '';
              json = { status: 0, kd_icd: imCode, nm_icd: imDesc };
            }
          }
        } catch (eIm) {}
      }

      if (json && (json.status === 0 || json.status === '0')) {
        const nameResult = (json.nm_icd || json.deskripsi || '');
        if (targetToUse === 'inacbg') {
          setFormInputDiagnosaInacbg(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: nameResult }));
        } else {
          setFormInputDiagnosaIdrg(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: nameResult }));
        }
        setFormInputDiagnosa(prev => ({
          ...prev,
          kd_icd: codeToTest,
          nm_icd: nameResult
        }));

        let itemValidCode = json.validcode;
        let itemAccpdx = json.accpdx;
        if (itemValidCode === undefined || itemAccpdx === undefined) {
          const matchedMem = (masterIcd10 || []).find(x => String(x.icd || x.kode || x.kd_icd || '').trim().toLowerCase() === baseCode.toLowerCase());
          if (matchedMem) {
            if (itemValidCode === undefined) itemValidCode = matchedMem.validcode;
            if (itemAccpdx === undefined) itemAccpdx = matchedMem.accpdx;
          }
        }
        const isHeader = itemValidCode === 0 || itemValidCode === '0' || itemValidCode === false;
        const isAccpdxNo = String(itemAccpdx || '').trim().toUpperCase() === 'N';

        const wasPrimary = curFormState.ket === 'Utama' || curFormState.utama === 1 || curFormState.utama === '1';
        let targetKet = curFormState.ket;
        let targetUtama = curFormState.utama;

        if (isAccpdxNo) {
          if (wasPrimary) {
            targetKet = 'Sekunder';
            targetUtama = 0;
          }
          if (targetToUse === 'inacbg') {
            setFormInputDiagnosaInacbg(prev => {
              if (prev.ket === 'Utama' || prev.utama === 1 || prev.utama === '1') {
                return { ...prev, ket: 'Sekunder', utama: 0 };
              }
              return prev;
            });
          } else {
            setFormInputDiagnosaIdrg(prev => {
              if (prev.ket === 'Utama' || prev.utama === 1 || prev.utama === '1') {
                return { ...prev, ket: 'Sekunder', utama: 0 };
              }
              return prev;
            });
          }
          setFormInputDiagnosa(prev => {
            if (prev.ket === 'Utama' || prev.utama === 1 || prev.utama === '1') {
              return { ...prev, ket: 'Sekunder', utama: 0 };
            }
            return prev;
          });
        }

        if (isHeader) {
          if (targetToUse !== 'inacbg') {
            setFormInputDiagnosaIdrg(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: nameResult, isHeader: true, validcode: itemValidCode }));
            setFormInputDiagnosa(prev => ({ ...prev, kd_icd: codeToTest, nm_icd: nameResult, isHeader: true, validcode: itemValidCode }));
            setIcdCheckMsg({ type: 'danger', text: `Diagnosa ${codeToTest} merupakan Header Code (tidak valid untuk iDRG dan tidak dapat disimpan).` });
            notify('warning', 'Header Code Tidak Valid', `Kode ${codeToTest} adalah Header Code dan tidak dapat disimpan untuk iDRG.`);
            setIsCheckingIcd(false);
            return;
          }
          setIcdCheckMsg({ type: 'info', text: `Ditemukan: ${nameResult} [Header Code - INACBG]` });
          notify('info', 'Diagnosa Terpilih', `${codeToTest} - ${nameResult}`);
          handleSaveInputDiagnosa(null, {
            kd_icd: codeToTest,
            nm_icd: nameResult,
            ket: targetKet,
            utama: targetUtama,
            validcode: itemValidCode,
            accpdx: itemAccpdx,
            target: targetToUse
          });
        } else if (isAccpdxNo) {
          if (wasPrimary) {
            setIcdCheckMsg({ type: 'warning', text: `Ditemukan: ${nameResult} (accpdx = N, tidak bisa diinput sebagai primary, dialihkan ke Sekunder)` });
            notify('info', 'Status Diagnosa Sekunder', `Kode ${codeToTest} memiliki accpdx = N (tidak bisa diinput sebagai primary). Status dialihkan ke Sekunder agar tetap bisa diinput.`);
          } else {
            setIcdCheckMsg({ type: 'success', text: `Ditemukan: ${nameResult} [accpdx = N: diizinkan untuk diagnosa sekunder/tambahan]` });
            notify('success', 'Kode Diagnosa (ICD-10) Ditemukan', `Kode ${codeToTest}: ${nameResult}`);
          }
          handleSaveInputDiagnosa(null, {
            kd_icd: codeToTest,
            nm_icd: nameResult,
            ket: targetKet,
            utama: targetUtama,
            validcode: itemValidCode,
            accpdx: itemAccpdx,
            target: targetToUse
          });
        } else {
          setIcdCheckMsg({ type: 'success', text: `Ditemukan: ${nameResult}` });
          notify('success', 'Kode Diagnosa (ICD-10) Ditemukan', `Kode ${codeToTest}: ${nameResult}`);
          handleSaveInputDiagnosa(null, {
            kd_icd: codeToTest,
            nm_icd: nameResult,
            ket: targetKet,
            utama: targetUtama,
            validcode: itemValidCode,
            accpdx: itemAccpdx,
            target: targetToUse
          });
        } 
} else {
        const errMsg = json?.msg || `Kode Diagnosa (ICD-10) "${codeToTest}" tidak ditemukan`;
        setIcdCheckMsg({ type: 'danger', text: errMsg });
        notify('warning', 'Kode Diagnosa Tidak Ditemukan', `${errMsg}. Mengalihkan ke tabel pencarian master diagnosa.`);
        handleOpenMasterIcdModal(baseCode);
      }
    } catch (err) {
      console.error('Error cari_icdx:', err);
      setIcdCheckMsg({ type: 'danger', text: `Gagal memeriksa kode: ${err.message}` });
      notify('danger', 'Koneksi Terputus', `Gagal memeriksa kode ke server: ${err.message}`);
      handleOpenMasterIcdModal(baseCode);
    } finally {
      setIsCheckingIcd(false);
    }
  };

  const handleOpenMasterIcdModal = async (initialSearch = '') => {
    setMasterIcdModalSearch(initialSearch || '');
    setMasterIcdCurrentPage(1);
    setShowMasterIcdModal(true);

    const apiUrl = getApiUrl() || 'http://localhost:8000';
    if (inputDiagnosaType === 'prosedur') {
      if (masterIcd9.length === 0) {
        setIsLoadingMasterIcd(true);
        try {
          const res = await fetch(`${apiUrl}/show_icdixim`);
          const json = await res.json();
          const list = Array.isArray(json) ? json : (json?.list_icdixim || json?.list_icdix || json?.data || []);
          setMasterIcd9(list);
        } catch (err) {
          console.warn('Gagal fetch /show_icdixim:', err);
        } finally {
          setIsLoadingMasterIcd(false);
        }
      }
    } else {
      if (masterIcd10.length === 0) {
        setIsLoadingMasterIcd(true);
        try {
          const res = await fetch(`${apiUrl}/show_icdxim`);
          const json = await res.json();
          const list = Array.isArray(json) ? json : (json?.list_icdxim || json?.list_icdx || json?.data || []);
          setMasterIcd10(list);
        } catch (err) {
          console.warn('Gagal fetch /show_icdxim:', err);
        } finally {
          setIsLoadingMasterIcd(false);
        }
      }
    }
  };

  const handleSelectMasterIcdRow = (item) => {
    const code = item.icd || item.kode || item.kd_icd || '';
    const desc = item.nm_icd || item.deskripsi || item.penyakit || '';
    const isProsedur = inputDiagnosaType === 'prosedur';
    const isAccpdxNo = String(item.accpdx || '').trim().toUpperCase() === 'N';

    if (isProsedur) {
      if (isAccpdxNo) {
        setIcdCheckMsg({ type: 'danger', text: `Prosedur ${code} tidak bisa dipilih karena accpdx = N` });
        notify('warning', 'Prosedur Tidak Valid', `Prosedur ${code} tidak bisa dipilih karena accpdx = N`);
        return;
      }
      setFormInputDiagnosa(prev => ({
        ...prev,
        kd_icd: code,
        nm_icd: desc
      }));
      setIcdCheckMsg({ type: 'success', text: `Ditemukan di Master ICD-9 IM (_idrg): ${desc}` });
      notify('info', 'Prosedur Terpilih', `${code} - ${desc}`);
      setShowMasterIcdModal(false);
      handleSaveInputDiagnosa(null, { kd_icd: code, nm_icd: desc, validcode: item.validcode, accpdx: item.accpdx, target: inputDiagnosaTarget });
      return;
    }

    const isHeader = item.validcode === 0 || item.validcode === '0' || item.validcode === false;
    const wasPrimary = formInputDiagnosa.ket === 'Utama' || formInputDiagnosa.utama === 1 || formInputDiagnosa.utama === '1';
    let targetKet = formInputDiagnosa.ket;
    let targetUtama = formInputDiagnosa.utama;
    if (isAccpdxNo && wasPrimary) {
      targetKet = 'Sekunder';
      targetUtama = 0;
    }

    if (isHeader && inputDiagnosaTarget !== 'inacbg') {
      setIcdCheckMsg({ type: 'danger', text: `Diagnosa ${code} merupakan Header Code (tidak valid untuk iDRG dan tidak dapat dipilih).` });
      notify('warning', 'Header Code Tidak Dapat Dipilih', `Kode ${code} adalah Header Code dan tidak dapat dipilih untuk pengklaiman iDRG.`);
      return;
    }

    setFormInputDiagnosa(prev => {
      const nextState = { ...prev, kd_icd: code, nm_icd: desc, isHeader: false, validcode: item.validcode };
      if (isAccpdxNo && (prev.ket === 'Utama' || prev.utama === 1 || prev.utama === '1')) {
        nextState.ket = 'Sekunder';
        nextState.utama = 0;
      }
      return nextState;
    });

    if (isHeader) {
      setIcdCheckMsg({ type: 'info', text: `Ditemukan: ${desc} [Header Code - INACBG]` });
      notify('info', 'Diagnosa Terpilih', `${code} - ${desc}`);
      setShowMasterIcdModal(false);
      handleSaveInputDiagnosa(null, { kd_icd: code, nm_icd: desc, ket: targetKet, utama: targetUtama, validcode: item.validcode, accpdx: item.accpdx, target: inputDiagnosaTarget });
    } else if (isAccpdxNo) {
      if (wasPrimary) {
        setIcdCheckMsg({ type: 'warning', text: `Ditemukan: ${desc} (Catatan: accpdx = N, tidak bisa diinput sebagai primary, dialihkan ke Sekunder)` });
        notify('info', 'Status Diagnosa Sekunder', 'Diagnosa tidak bisa diinput sebagai primary karena accpdx = N. Status dialihkan ke Sekunder agar tetap bisa diinput.');
      } else {
        setIcdCheckMsg({ type: 'success', text: `Ditemukan: ${desc} [accpdx = N: diizinkan untuk diagnosa sekunder/tambahan]` });
      }
      setShowMasterIcdModal(false);
      handleSaveInputDiagnosa(null, { kd_icd: code, nm_icd: desc, ket: targetKet, utama: targetUtama, validcode: item.validcode, accpdx: item.accpdx, target: inputDiagnosaTarget });
    } else {
      setIcdCheckMsg({ type: 'success', text: `Ditemukan: ${desc}` });
      notify('info', 'Diagnosa Terpilih', `${code} - ${desc}`);
      setShowMasterIcdModal(false);
      handleSaveInputDiagnosa(null, { kd_icd: code, nm_icd: desc, ket: targetKet, utama: targetUtama, validcode: item.validcode, accpdx: item.accpdx, target: inputDiagnosaTarget });
    }
  };

  const handleSaveInputDiagnosa = async (e, overrideData = null) => {
    if (e && e.preventDefault) e.preventDefault();
    const currentTarget = overrideData?.target || inputDiagnosaTarget;
    if (overrideData?.target && overrideData.target !== inputDiagnosaTarget) {
      setInputDiagnosaTarget(overrideData.target);
    }
    const baseSourceForm = currentTarget === 'inacbg' ? formInputDiagnosaInacbg : (currentTarget === 'idrg' ? formInputDiagnosaIdrg : formInputDiagnosa);
    const curForm = { ...baseSourceForm, ...(overrideData || {}) };
    if (!curForm.kd_icd) {
      notify('warning', 'Validasi Form', 'Silakan pilih kode dan nama diagnosa/prosedur terlebih dahulu.');
      return;
    }

    const patient = selectedDiagnosaItem || grouperTarget;
    if (!patient) return;

    const apiUrl = getApiUrl() || 'http://localhost:8000';
    const token = localStorage.getItem('token');
    const isProsedur = inputDiagnosaType === 'prosedur';
    let itemValidCode = overrideData?.validcode !== undefined ? overrideData.validcode : undefined;
    let itemAccpdx = overrideData?.accpdx !== undefined ? overrideData.accpdx : undefined;

    // Validasi Header code & accpdx untuk diagnosa
    if (!isProsedur) {
      const curCode = (curForm.kd_icd || '').trim();
      let matchedItem = (masterIcd10 || []).find(x => {
        const code = String(x.icd || x.kode || x.kd_icd || '').trim().toLowerCase();
        return code === curCode.toLowerCase() || code.replace(/\./g, '') === curCode.toLowerCase().replace(/\./g, '');
      });
      if (itemValidCode === undefined && matchedItem) itemValidCode = matchedItem.validcode;
      if (itemAccpdx === undefined && matchedItem) itemAccpdx = matchedItem.accpdx;

      if (itemValidCode === undefined || itemAccpdx === undefined) {
        try {
          const resCari = await fetch(`${apiUrl}/cari_icdx?kodeicd=${encodeURIComponent(curCode)}`);
          const jsonCari = await resCari.json();
          if (jsonCari) {
            if (itemValidCode === undefined && jsonCari.validcode !== undefined) itemValidCode = jsonCari.validcode;
            if (itemAccpdx === undefined && (jsonCari.accpdx !== undefined || jsonCari.acc_pdx !== undefined)) {
              itemAccpdx = jsonCari.accpdx !== undefined ? jsonCari.accpdx : jsonCari.acc_pdx;
            }
          }
          if (itemAccpdx === undefined && curCode.includes('.')) {
            const resAlt = await fetch(`${apiUrl}/cari_icdx?kodeicd=${encodeURIComponent(curCode.replace(/\./g, ''))}`);
            const jsonAlt = await resAlt.json();
            if (jsonAlt) {
              if (itemValidCode === undefined && jsonAlt.validcode !== undefined) itemValidCode = jsonAlt.validcode;
              if (itemAccpdx === undefined && (jsonAlt.accpdx !== undefined || jsonAlt.acc_pdx !== undefined)) {
                itemAccpdx = jsonAlt.accpdx !== undefined ? jsonAlt.accpdx : jsonAlt.acc_pdx;
              }
            }
          }
        } catch (eChk) {}
      }

      const isHeaderDiagnosa = itemValidCode === 0 || itemValidCode === '0' || itemValidCode === false || isHeaderIcd10Code(curCode);
      if (isHeaderDiagnosa) {
        if (currentTarget !== 'inacbg') {
          setIsSavingDiagnosa(false);
          setIcdCheckMsg({ type: 'danger', text: `Diagnosa ${curCode} merupakan Header Code (tidak valid untuk iDRG dan tidak dapat disimpan).` });
          notify('warning', 'Header Code Tidak Valid', `Diagnosa ${curCode} merupakan Header Code dan tidak dapat disimpan untuk iDRG.`);
          return;
        }
      }

      const isPrimary = curForm.utama === 1 || curForm.utama === '1' || String(curForm.ket || '').trim().toLowerCase() === 'utama';
      const isAccpdxNo = String(itemAccpdx || '').trim().toUpperCase() === 'N';

      if (isPrimary && isAccpdxNo) {
        // Sesuai aturan INACBGS: diagnosa dengan accpdx = N tetap bisa dipilih, dialihkan otomatis ke Sekunder
        curForm.ket = 'Sekunder';
        curForm.utama = 0;
        if (currentTarget === 'inacbg') {
          setFormInputDiagnosaInacbg(prev => ({ ...prev, ket: 'Sekunder', utama: 0 }));
        } else {
          setFormInputDiagnosaIdrg(prev => ({ ...prev, ket: 'Sekunder', utama: 0 }));
        }
        setFormInputDiagnosa(prev => ({ ...prev, ket: 'Sekunder', utama: 0 }));
        setIcdCheckMsg({ type: 'warning', text: `Ditemukan: ${curForm.nm_icd || curCode} (accpdx = N: otomatis dialihkan ke status Sekunder)` });
        notify('info', 'Status Diagnosa Sekunder', `Kode ${curCode} memiliki accpdx = N (hanya untuk diagnosa sekunder/tambahan, otomatis disimpan sebagai Sekunder).`);
      }
      // Catatan: Jika bukan diagnosa utama (misalnya Sekunder / Tambahan), diagnosa dengan accpdx = N tetap bisa diinput / disimpan
    } else {
      // Validasi accpdx untuk prosedur (ICD-9-CM)
      const curCode = (curForm.kd_icd || '').trim();
      const baseCode = curCode.includes('+') ? curCode.split('+')[0].trim() : curCode;

      let matchedItem = (masterIcd9 || []).find(x => {
        const code = String(x.icd || x.kode || x.kd_icd || '').trim().toLowerCase();
        return code === baseCode.toLowerCase() || code.replace(/\./g, '') === baseCode.toLowerCase().replace(/\./g, '');
      });
      if (itemValidCode === undefined && matchedItem) itemValidCode = matchedItem.validcode;
      if (itemAccpdx === undefined && matchedItem) itemAccpdx = matchedItem.accpdx;

      if (itemAccpdx === undefined) {
        try {
          const resCari = await fetch(`${apiUrl}/cari_icdix?kodeicd=${encodeURIComponent(baseCode)}`);
          const jsonCari = await resCari.json();
          if (jsonCari && (jsonCari.accpdx !== undefined || jsonCari.acc_pdx !== undefined)) {
            itemAccpdx = jsonCari.accpdx !== undefined ? jsonCari.accpdx : jsonCari.acc_pdx;
            if (itemValidCode === undefined && jsonCari.validcode !== undefined) itemValidCode = jsonCari.validcode;
          }
        } catch (eChk) {}
      }

      const isAccpdxNo = String(itemAccpdx || '').trim().toUpperCase() === 'N';
      if (isAccpdxNo) {
        setIcdCheckMsg({ type: 'danger', text: 'Prosedur tidak bisa dipilih karena accpdx = N' });
        notify('warning', 'Prosedur Tidak Valid', 'Prosedur tidak bisa dipilih karena accpdx = N');
        return;
      }
    }

    setIsSavingDiagnosa(true);

    let finalKdIcd = (curForm.kd_icd || '').split('+')[0].trim();
    const mult = parseInt(curForm.multiplicity) || 1;

    // Hitung urutan penginputan berdasarkan list saat ini
    const activeList = currentTarget === 'inacbg'
      ? (isProsedur ? (diagnosaInacbgData?.list_prosedur || []) : (diagnosaInacbgData?.list_diagnosa || []))
      : (isProsedur ? (diagnosaData?.list_prosedur || []) : (diagnosaData?.list_diagnosa || []));

    let itemUrutan = activeList.length + 1;
    if (curForm.utama === 1 || curForm.utama === '1' || String(curForm.ket || '').trim().toLowerCase() === 'utama') {
      itemUrutan = 1;
    }

    const payload = {
      no_nota: patient.no_nota || patient.no_daftar || patient.no_sep || '',
      no_medrec: patient.no_medrec || '',
      tgl_kunj: patient.tgl_masuk || patient.tgl_pulang || curForm.tanggal,
      mode: 'append',
      jenis: isProsedur ? 'prosedur' : 'diagnosa',
      target: currentTarget,
      qty: isProsedur ? mult : 1,
      multiplicity: isProsedur ? mult : 1,
      urutan: itemUrutan,
      list_diagnosa: [
        {
          kd_icd: finalKdIcd,
          nm_icd: curForm.nm_icd,
          ket: curForm.ket,
          utama: curForm.utama,
          multiplicity: isProsedur ? mult : 1,
          qty: isProsedur ? mult : 1,
          urutan: itemUrutan,
          validcode: itemValidCode,
          accpdx: itemAccpdx,
          kd_dokter: curForm.kd_dokter || patient?.kd_dokter || '',
          nm_dokter: curForm.nm_dokter || patient?.nm_dokter || patient?.dpjp || '',
          tanggal: curForm.tanggal,
          jam: curForm.jam,
          lokasi: curForm.lokasi,
          petugas: curForm.petugas
        }
      ]
    };

    try {
      const res = await fetch(`${apiUrl}/simpan_hdiagnosa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (json && (json.status === 1 || json.status === '1' || json.status === 'success')) {
        setShowInputDiagnosaModal(false);
        const isPrimarySaved = curForm.utama === 1 || curForm.utama === '1' || String(curForm.ket || '').trim().toLowerCase() === 'utama';
        // Reset form inline target masing-masing agar siap input berikutnya (form tetap terbuka)
        const nextResetObj = (prev) => ({
          ...prev,
          kd_icd: '',
          nm_icd: '',
          multiplicity: 1,
          ket: !isProsedur ? (isPrimarySaved ? 'Sekunder' : prev.ket) : 'Prosedur',
          utama: !isProsedur ? (isPrimarySaved ? 0 : prev.utama) : 0
        });

        if (currentTarget === 'inacbg') {
          setFormInputDiagnosaInacbg(nextResetObj);
        } else {
          setFormInputDiagnosaIdrg(nextResetObj);
        }
        setFormInputDiagnosa(nextResetObj);
        setIcdCheckMsg(null);
        closeAutocomplete();
        notify('success', 'Berhasil Disimpan Otomatis', `${finalKdIcd} - ${curForm.nm_icd} (${currentTarget.toUpperCase()}) berhasil disimpan.`);
        if (currentTarget === 'inacbg') {
          fetchDiagnosaInacbgData(patient);
        } else {
          fetchDiagnosaData(patient);
        }
      } else {
        const errMsg = json?.msg || 'Gagal menyimpan data ke server.';
        notify('danger', 'Gagal Menyimpan', errMsg);
        setIcdCheckMsg({ type: 'danger', text: errMsg });
      }
    } catch (err) {
      console.error('Error simpan_hdiagnosa:', err);
      notify('danger', 'Kesalahan Sistem', `Terjadi kesalahan saat menyimpan: ${err.message}`);
    } finally {
      setIsSavingDiagnosa(false);
    }
  };

  // ── State & Handler Konfirmasi Hapus Diagnosa / Prosedur (Bootstrap Modal) ──
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    show: false,
    item: null,
    type: 'diagnosa',
    target: 'idrg', // 'idrg' | 'inacbg'
    isDeleting: false
  });

  const handleDeleteDiagnosa = (diag, type = 'diagnosa', target = 'idrg') => {
    if (target === 'idrg' && (idrgStatus.isGrouped || idrgStatus.isFinal || claimFinalStatus)) {
      notify('warning', 'Form Terkunci', 'Data diagnosa/prosedur tidak bisa dihapus karena status sudah Grouper Coding iDRG. Buka kunci dengan tombol "Edit Ulang iDRG" jika ingin merevisi koding.');
      return;
    }
    setDeleteConfirmModal({
      show: true,
      item: diag,
      type,
      target,
      isDeleting: false
    });
  };

  const handleExecuteDeleteDiagnosa = async () => {
    const { item: diag, type, target = 'idrg' } = deleteConfirmModal;
    if (!diag) return;
    const targetPatient = selectedDiagnosaItem || grouperTarget;
    if (!targetPatient) return;
    setDeleteConfirmModal(prev => ({ ...prev, isDeleting: true }));
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${apiUrl}/simpan_hdiagnosa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          no_nota: targetPatient.no_nota || targetPatient.no_daftar || targetPatient.no_sep,
          no_medrec: targetPatient.no_medrec,
          mode: 'delete',
          jenis: type,
          kd_icd: diag.kd_icd,
          id: diag.id,
          target
        })
      });
      const json = await res.json();
      if (json?.status === 1 || res.ok) {
        notify('success', 'Berhasil Dihapus', `Item ${diag.kd_icd} - ${diag.nm_icd || diag.nama_icd || ''} (${target.toUpperCase()}) berhasil dihapus.`);
        setDeleteConfirmModal({ show: false, item: null, type: 'diagnosa', target: 'idrg', isDeleting: false });
        if (target === 'inacbg') {
          fetchDiagnosaInacbgData(targetPatient);
        } else {
          fetchDiagnosaData(targetPatient);
        }
      } else {
        notify('danger', 'Gagal Menghapus', json?.msg || 'Gagal menghapus item.');
        setDeleteConfirmModal(prev => ({ ...prev, isDeleting: false }));
      }
    } catch (err) {
      console.error("Gagal hapus diagnosa:", err);
      notify('danger', 'Kesalahan Sistem', `Gagal menghapus: ${err.message}`);
      setDeleteConfirmModal(prev => ({ ...prev, isDeleting: false }));
    }
  };

  const handleSetDiagnosaUtama = async (diag, target = 'idrg') => {
    if (!diag) return;
    const targetPatient = selectedDiagnosaItem || grouperTarget;
    if (!targetPatient) return;

    if (target === 'idrg' && (idrgStatus.isGrouped || idrgStatus.isFinal || claimFinalStatus)) {
      notify('warning', 'Form Terkunci', 'Status diagnosa tidak bisa diubah karena status sudah Grouper Coding iDRG. Buka kunci dengan tombol "Edit Ulang iDRG" jika ingin merevisi koding.');
      return;
    }
    if (target === 'inacbg' && claimFinalStatus) {
      notify('warning', 'Form Terkunci', 'Status diagnosa tidak bisa diubah karena klaim sudah difinalisasi.');
      return;
    }

    const apiUrl = getApiUrl() || 'http://localhost:8000';
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${apiUrl}/simpan_hdiagnosa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          no_nota: targetPatient.no_nota || targetPatient.no_daftar || targetPatient.no_sep,
          no_medrec: targetPatient.no_medrec,
          mode: 'set_utama',
          jenis: 'diagnosa',
          kd_icd: diag.kd_icd,
          id: diag.id,
          target
        })
      });
      const json = await res.json();
      if (json?.status === 1 || json?.success) {
        notify('success', 'Diagnosa Utama Diperbarui', json.msg || `Diagnosa ${diag.kd_icd} berhasil diatur sebagai diagnosa Utama dan urutan pertama (${target.toUpperCase()}).`);
        if (target === 'inacbg') {
          fetchDiagnosaInacbgData(targetPatient);
        } else {
          fetchDiagnosaData(targetPatient);
        }
      } else {
        notify('danger', 'Gagal Mengubah Diagnosa Utama', json?.msg || 'Terjadi kesalahan saat mengubah diagnosa utama.');
      }
    } catch (err) {
      console.error("Gagal ubah diagnosa utama:", err);
      notify('danger', 'Kesalahan Sistem', `Gagal mengubah diagnosa utama: ${err.message}`);
    }
  };

  // ── Handler Edit Kode ICD Sesuai Nomor Urut Data ──
  const handleOpenEditIcd = async (item, type = 'diagnosa', target = 'idrg', urutanIdx = 1) => {
    if (target === 'idrg' && (idrgStatus.isGrouped || idrgStatus.isFinal || claimFinalStatus)) {
      notify('warning', 'Form Terkunci', 'Data diagnosa/prosedur tidak bisa diedit karena status sudah Grouper Coding iDRG. Buka kunci dengan tombol "Edit Ulang iDRG" jika ingin merevisi koding.');
      return;
    }
    if (target === 'inacbg' && claimFinalStatus) {
      notify('warning', 'Form Terkunci', 'Data diagnosa/prosedur tidak bisa diedit karena klaim sudah difinalisasi.');
      return;
    }

    const urutanNum = item?.urutan && parseInt(item.urutan) > 0 ? parseInt(item.urutan) : urutanIdx;
    const currentKd = item?.kd_icd || '';
    const currentNm = item?.nm_icd || item?.nama_icd || '';
    const mult = parseInt(item?.qty || item?.multiplicity) || 1;

    setEditIcdModal({
      show: true,
      item,
      type,
      target,
      urutan: urutanNum,
      old_kd_icd: currentKd,
      old_nm_icd: currentNm,
      new_kd_icd: currentKd,
      new_nm_icd: currentNm,
      multiplicity: mult,
      isSaving: false,
      validcode: item?.validcode,
      accpdx: item?.accpdx
    });
    setEditIcdSearchInput(`${currentKd} - ${currentNm}`.trim());
    setEditIcdSuggestions([]);

    const apiUrl = getApiUrl() || 'http://localhost:8000';
    if (type === 'prosedur' && masterIcd9.length === 0) {
      try {
        const res9 = await fetch(`${apiUrl}/show_icdixim`);
        const json9 = await res9.json();
        const list9 = Array.isArray(json9) ? json9 : (json9?.list_icdixim || json9?.list_icdix || json9?.data || []);
        if (Array.isArray(list9) && list9.length > 0) setMasterIcd9(list9);
      } catch (e) {}
    } else if (type === 'diagnosa' && masterIcd10.length === 0) {
      try {
        const res10 = await fetch(`${apiUrl}/show_icdxim`);
        const json10 = await res10.json();
        const list10 = Array.isArray(json10) ? json10 : (json10?.list_icdxim || json10?.list_icdx || json10?.data || []);
        if (Array.isArray(list10) && list10.length > 0) setMasterIcd10(list10);
      } catch (e) {}
    }
  };

  const handleSearchEditIcd = (query) => {
    setEditIcdSearchInput(query);
    if (!query || query.trim().length < 1) {
      setEditIcdSuggestions([]);
      return;
    }
    const q = query.toLowerCase().trim();
    const isProc = editIcdModal.type === 'prosedur';
    const sourceList = isProc ? masterIcd9 : masterIcd10;

    const filtered = (sourceList || []).filter(item => {
      const code = String(item.icd || item.kode || item.kd_icd || '').toLowerCase();
      const name = String(item.deskripsi || item.penyakit || item.nama_icd || item.nm_icd || '').toLowerCase();
      return code.includes(q) || name.includes(q);
    }).slice(0, 25);

    setEditIcdSuggestions(filtered);
  };

  const handleSelectEditIcdSuggestion = (item) => {
    const code = String(item.icd || item.kode || item.kd_icd || '').trim();
    const name = String(item.deskripsi || item.penyakit || item.nama_icd || item.nm_icd || '').trim();
    const isHdr = item.validcode === 0 || item.validcode === '0' || item.validcode === false;
    const isAccNo = String(item.accpdx || '').toUpperCase() === 'N';
    if (editIcdModal.target === 'idrg' && editIcdModal.type === 'diagnosa' && isHdr) {
      notify('warning', 'Header Code Tidak Dapat Dipilih', `Kode ${code} adalah Header Code dan tidak dapat dipilih untuk iDRG.`);
      return;
    }
    if (editIcdModal.target === 'idrg' && editIcdModal.type === 'prosedur' && isAccNo) {
      notify('warning', 'Prosedur Tidak Valid', `Prosedur ${code} tidak bisa dipilih karena accpdx = N (tidak dapat disimpan).`);
      return;
    }
    setEditIcdModal(prev => ({
      ...prev,
      new_kd_icd: code,
      new_nm_icd: name,
      validcode: item.validcode,
      accpdx: item.accpdx
    }));
    setEditIcdSearchInput(`${code} - ${name}`);
    setEditIcdSuggestions([]);
  };

  const handleSaveEditIcd = async () => {
    if (!editIcdModal.new_kd_icd) {
      notify('warning', 'Validasi Form', 'Silakan pilih kode ICD yang baru terlebih dahulu.');
      return;
    }
    if (editIcdModal.target === 'idrg' && editIcdModal.type === 'diagnosa') {
      const isHdr = editIcdModal.validcode === 0 || editIcdModal.validcode === '0' || editIcdModal.validcode === false || isHeaderIcd10Code(editIcdModal.new_kd_icd);
      if (isHdr) {
        notify('warning', 'Header Code Tidak Valid', `Kode ${editIcdModal.new_kd_icd} adalah Header Code dan tidak dapat disimpan untuk iDRG.`);
        return;
      }
    }
    if (editIcdModal.target === 'idrg' && editIcdModal.type === 'prosedur') {
      const isAccNo = String(editIcdModal.accpdx || '').toUpperCase() === 'N' || isAccpdxNoProcedure(editIcdModal.new_kd_icd);
      if (isAccNo) {
        notify('warning', 'Prosedur Tidak Valid', `Prosedur ${editIcdModal.new_kd_icd} tidak bisa disimpan karena accpdx = N.`);
        return;
      }
    }

    const targetPatient = selectedDiagnosaItem || grouperTarget;
    if (!targetPatient) return;

    setEditIcdModal(prev => ({ ...prev, isSaving: true }));
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${apiUrl}/simpan_hdiagnosa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          no_nota: targetPatient.no_nota || targetPatient.no_daftar || targetPatient.no_sep,
          no_medrec: targetPatient.no_medrec,
          mode: 'edit_icd',
          jenis: editIcdModal.type,
          target: editIcdModal.target,
          is_inacbg: editIcdModal.target === 'inacbg' ? 1 : 0,
          id: editIcdModal.item?.id,
          urutan: editIcdModal.urutan,
          old_kd_icd: editIcdModal.old_kd_icd || editIcdModal.item?.kd_icd,
          kd_icd_lama: editIcdModal.old_kd_icd || editIcdModal.item?.kd_icd,
          new_kd_icd: editIcdModal.new_kd_icd,
          kd_icd: editIcdModal.new_kd_icd,
          new_nm_icd: editIcdModal.new_nm_icd,
          nm_icd: editIcdModal.new_nm_icd,
          qty: editIcdModal.multiplicity
        })
      });

      const json = await res.json();
      if (json && (json.status === 1 || json.status === '1' || json.status === 'success')) {
        notify('success', 'Berhasil Edit Kode ICD', json.msg || `Kode ICD nomor urut #${editIcdModal.urutan} berhasil diperbarui ke ${editIcdModal.new_kd_icd} - ${editIcdModal.new_nm_icd}.`);
        setEditIcdModal(prev => ({ ...prev, show: false, isSaving: false }));
        if (editIcdModal.target === 'inacbg') {
          fetchDiagnosaInacbgData(targetPatient);
        } else {
          fetchDiagnosaData(targetPatient);
        }
      } else {
        notify('danger', 'Gagal Edit Kode ICD', json?.msg || 'Terjadi kesalahan saat menyimpan perubahan kode ICD.');
        setEditIcdModal(prev => ({ ...prev, isSaving: false }));
      }
    } catch (err) {
      console.error("Gagal edit kode ICD:", err);
      notify('danger', 'Error Server', `Gagal menyimpan perubahan kode ICD: ${err.message}`);
      setEditIcdModal(prev => ({ ...prev, isSaving: false }));
    }
  };

  // ── Handler New Claim ──
  const [showNewClaimConfirm, setShowNewClaimConfirm] = useState(false);
  const [newClaimTarget, setNewClaimTarget] = useState(null);
  const [newClaimResult, setNewClaimResult] = useState(null);
  const [isProcessingNewClaim, setIsProcessingNewClaim] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const handleOpenNewClaim = (item) => {
    setNewClaimTarget(item);
    setNewClaimResult(null);
    setInputSitb('');
    setSitbValidationResult(null);
    setShowNewClaimConfirm(true);
  };

  const handleProcessNewClaim = async () => {
    if (!newClaimTarget) return;

    setIsProcessingNewClaim(true);
    setNewClaimResult(null);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${apiUrl}/claim_inacbgs`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          norincian: newClaimTarget.no_nota,
          no_medrec: newClaimTarget.no_medrec,
          nomor_register_sitb: inputSitb,
          ...getEklaimParams()
        })
      });

      let json = {};
      try {
        json = await res.json();
      } catch (e) {}

      // response can have eklaim_response.metadata
      if (json?.eklaim_response?.metadata?.code && parseInt(json.eklaim_response.metadata.code) !== 200) {
        setNewClaimResult({
          type: 'danger',
          text: json.eklaim_response.metadata.message || json.msg || `Gagal New Claim! (Error: ${json.eklaim_response.metadata.error_no})`
        });
        return;
      }

      if (!res.ok || json?.status === 0) {
        setNewClaimResult({
          type: 'danger',
          text: json?.msg || json?.eklaim_response?.metadata?.message || `Gagal memproses New Claim! Response HTTP ${res.status}.`
        });
        return;
      }

      setNewClaimResult({
        type: 'success',
        text: json?.msg || `Pasien ${newClaimTarget.nm_pasien || newClaimTarget.nama} berhasil diproses (New Claim)!`
      });
    } catch (err) {
      console.warn('Gagal new claim:', err);
      setNewClaimResult({
        type: 'danger',
        text: `[Error] Gagal mendaftarkan pasien (Nota: ${newClaimTarget.no_nota})`
      });
    } finally {
      setIsProcessingNewClaim(false);
    }
  };

  // ── Fetch Informasi Medis dari EHR (Linimasa) ──
  // Dinonaktifkan khusus di modul DataClaimPasien sesuai permintaan pengguna
  const fetchEhrMedicalInfo = () => {
    setEhrData(null);
  };

  const formatGroupedTariffStr = (ht) => {
    if (!ht) return '';
    if (typeof ht === 'string') return ht;
    const f = (val) => Number(val || 0).toFixed(1);
    return `GroupedTariff(tglmasuk='${ht.tglmasuk || ''}', tglpulang='${ht.tglpulang || ''}', jnsrawat='${ht.jnsrawat || '1'}', klas_rawat='${ht.klas_rawat || '005'}', diagnosa_str='${ht.diagnosa_str || ''}', prosedur_str='${ht.prosedur_str || ''}', non_bedah=${f(ht.non_bedah)}, bedah=${f(ht.bedah)}, konsultasi=${f(ht.konsultasi)}, tenaga_ahli=${f(ht.tenaga_ahli)}, keperawatan=${f(ht.keperawatan)}, penunjang=${f(ht.penunjang)}, radiologi=${f(ht.radiologi)}, laboratorium=${f(ht.laboratorium)}, pelayanan_darah=${f(ht.pelayanan_darah)}, rehabilitasi=${f(ht.rehabilitasi)}, kamar=${f(ht.kamar)}, rawat_intensif=${f(ht.rawat_intensif)}, obat=${f(ht.obat)}, alkes=${f(ht.alkes)}, sewa_alat=${f(ht.sewa_alat)}, bmhp=${f(ht.bmhp)}, total_rs=${f(ht.total_rs)}, berat_badan=${ht.berat_badan || 0})`;
  };

  const fetchHasilTarif = async (item) => {
    if (!item?.no_medrec || !item?.no_nota) return;
    setIsLoadingHasilTarif(true);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    try {
      const res = await fetch(`${apiUrl}/get_hasil_tarif/${item.no_medrec}/${encodeURIComponent(item.no_nota)}`);
      const json = await res.json();
      if (json && json.status === 1 && json.hasil_tarif) {
        setHasilTarifData(json.hasil_tarif);
        setHasilTarifStr(json.hasil_tarif_str || '');
      }
    } catch (err) {
      console.warn('Gagal mengambil preview grouping tarif SIMRS:', err);
    } finally {
      setIsLoadingHasilTarif(false);
    }
  };

  const formatReadableTariffStr = (ht) => {
    if (!ht) return '';
    const f = (val) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(val || 0);
    return [
      `Non Bedah = ${f(ht.non_bedah)}`,
      `Bedah = ${f(ht.bedah)}`,
      `Konsultasi = ${f(ht.konsultasi)}`,
      `Tenaga Ahli = ${f(ht.tenaga_ahli)}`,
      `Keperawatan = ${f(ht.keperawatan)}`,
      `Penunjang = ${f(ht.penunjang)}`,
      `Radiologi = ${f(ht.radiologi)}`,
      `Laboratorium = ${f(ht.laboratorium)}`,
      `Pelayanan Darah = ${f(ht.pelayanan_darah)}`,
      `Rehabilitasi = ${f(ht.rehabilitasi)}`,
      `Kamar = ${f(ht.kamar)}`,
      `Rawat Intensif = ${f(ht.rawat_intensif)}`,
      `Obat = ${f(ht.obat)}`,
      `Alkes = ${f(ht.alkes)}`,
      `Sewa Alat = ${f(ht.sewa_alat)}`,
      `BMHP = ${f(ht.bmhp)}`,
      `----------------------------------------`,
      `Total RS = ${f(ht.total_rs)}`,
      `Berat Badan = ${ht.berat_badan || 0} gram`,
      `Tgl Masuk = ${ht.tglmasuk || '-'}`,
      `Tgl Pulang = ${ht.tglpulang || '-'}`,
      `Jenis Rawat = ${ht.jnsrawat === '1' ? '1 (Rawat Inap)' : ht.jnsrawat === '3' ? '3 (IGD)' : '2 (Rawat Jalan)'}`,
      `Kelas Rawat = ${ht.klas_rawat || '-'}`,
      `Diagnosa STR = ${ht.diagnosa_str || '-'}`,
      `Prosedur STR = ${ht.prosedur_str || '-'}`
    ].join('\n');
  };

  const renderTabTarifContent = () => {
    const activeHt = grouperResult?.data?.hasil_tarif || hasilTarifData;
    const activeStr = grouperResult?.data?.hasil_tarif_str || hasilTarifStr || (activeHt ? formatGroupedTariffStr(activeHt) : '');

    const formatCurrency = (val) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(val || 0);

    const tarifComponents = [
      { label: 'Prosedur Non Bedah', shortName: 'Non Bedah', value: activeHt?.non_bedah, icon: 'bi-bandaid', color: '#0284c7' },
      { label: 'Prosedur Bedah', shortName: 'Bedah', value: activeHt?.bedah, icon: 'bi-scissors', color: '#dc2626' },
      { label: 'Konsultasi Dokter', shortName: 'Konsultasi', value: activeHt?.konsultasi, icon: 'bi-chat-heart', color: '#2563eb' },
      { label: 'Tenaga Ahli', shortName: 'Tenaga Ahli', value: activeHt?.tenaga_ahli, icon: 'bi-person-badge', color: '#4f46e5' },
      { label: 'Keperawatan', shortName: 'Keperawatan', value: activeHt?.keperawatan, icon: 'bi-heart-pulse', color: '#059669' },
      { label: 'Penunjang Medis', shortName: 'Penunjang', value: activeHt?.penunjang, icon: 'bi-activity', color: '#0d9488' },
      { label: 'Radiologi', shortName: 'Radiologi', value: activeHt?.radiologi, icon: 'bi-radioactive', color: '#7c3aed' },
      { label: 'Laboratorium', shortName: 'Laboratorium', value: activeHt?.laboratorium, icon: 'bi-moisture', color: '#d97706' },
      { label: 'Pelayanan Darah', shortName: 'Pelayanan Darah', value: activeHt?.pelayanan_darah, icon: 'bi-droplet-fill', color: '#e11d48' },
      { label: 'Rehabilitasi', shortName: 'Rehabilitasi', value: activeHt?.rehabilitasi, icon: 'bi-person-arms-up', color: '#0891b2' },
      { label: 'Kamar Rawat', shortName: 'Kamar', value: activeHt?.kamar, icon: 'bi-hospital', color: '#65a30d' },
      { label: 'Rawat Intensif', shortName: 'Rawat Intensif', value: activeHt?.rawat_intensif, icon: 'bi-hospital-fill', color: '#c026d3' },
      { label: 'Obat & Farmasi', shortName: 'Obat', value: activeHt?.obat, icon: 'bi-capsule', color: '#ea580c' },
      { label: 'Alat Kesehatan', shortName: 'Alkes', value: activeHt?.alkes, icon: 'bi-box-seam', color: '#475569' },
      { label: 'Sewa Alat', shortName: 'Sewa Alat', value: activeHt?.sewa_alat, icon: 'bi-cpu', color: '#4338ca' },
      { label: 'BMHP', shortName: 'BMHP', value: activeHt?.bmhp, icon: 'bi-tag', color: '#64748b' }
    ];

    if (isLoadingHasilTarif) {
      return (
        <div className="text-center py-5 bg-white">
          <Spinner animation="border" size="sm" variant="warning" className="me-2" />
          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Mengambil dan menyusun rincian grouping tarif SIMRS (hasil_tarif)...</span>
        </div>
      );
    }

    if (!activeHt && !activeStr) {
      return (
        <div className="text-center py-5 bg-white text-muted" style={{ fontSize: '0.78rem' }}>
          <i className="bi bi-calculator d-block mb-2 fs-2 opacity-50 text-warning"></i>
          <div>Data tarif SIMRS belum dimuat atau belum tersedia untuk nota ini.</div>
          <button
            onClick={() => fetchHasilTarif(grouperTarget)}
            className="btn btn-sm btn-outline-warning mt-2 py-1 px-3 shadow-xs"
            style={{ fontSize: '0.72rem' }}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>Muat Ulang Tarif
          </button>
        </div>
      );
    }

    return (
      <div style={{ padding: '8px 12px', background: '#ffffff' }}>
        {/* Top Control Bar */}
        <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-warning bg-opacity-10 text-dark border border-warning border-opacity-25 px-2 py-1 d-inline-flex align-items-center gap-1">
              <i className="bi bi-cash-stack text-warning"></i>
              <strong style={{ fontSize: '0.72rem' }}>Format Keterbacaan Tarif</strong>
            </span>
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '6px', padding: '2px', gap: '2px' }}>
              <button
                type="button"
                onClick={() => setTarifViewMode('grid')}
                className={`btn btn-sm py-0 px-2 fw-semibold ${tarifViewMode === 'grid' ? 'bg-white text-primary shadow-xs' : 'text-secondary'}`}
                style={{ fontSize: '0.7rem', border: 'none', borderRadius: '5px' }}
              >
                <i className="bi bi-grid-fill me-1"></i>Kartu Modern
              </button>
              <button
                type="button"
                onClick={() => setTarifViewMode('text')}
                className={`btn btn-sm py-0 px-2 fw-semibold ${tarifViewMode === 'text' ? 'bg-white text-primary shadow-xs' : 'text-secondary'}`}
                style={{ fontSize: '0.7rem', border: 'none', borderRadius: '5px' }}
              >
                <i className="bi bi-card-text me-1"></i>Teks Baku (List)
              </button>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 font-monospace px-2 py-1 shadow-xs" style={{ fontSize: '0.75rem', fontWeight: '700' }}>
              Total RS = {formatCurrency(activeHt?.total_rs || grouperTarget?.total)}
            </span>
            <Button
              variant={copiedReadableTarif ? "success" : "outline-primary"}
              size="sm"
              className="py-0 px-2 d-inline-flex align-items-center gap-1 shadow-xs"
              style={{ fontSize: '0.7rem', height: '26px', borderRadius: '5px' }}
              onClick={() => {
                const textToCopy = formatReadableTariffStr(activeHt);
                navigator.clipboard.writeText(textToCopy);
                setCopiedReadableTarif(true);
                setTimeout(() => setCopiedReadableTarif(false), 2000);
              }}
            >
              <i className={`bi ${copiedReadableTarif ? 'bi-check2' : 'bi-clipboard'}`}></i>
              {copiedReadableTarif ? 'Tersalin!' : 'Salin Format Teks'}
            </Button>
          </div>
        </div>

        {/* View Mode 1: Modern Grid Cards */}
        {tarifViewMode === 'grid' ? (
          <div>
            <div className="row g-2 mb-2">
              {tarifComponents.map((comp, cIdx) => {
                const hasValue = (comp.value || 0) > 0;
                return (
                  <div key={cIdx} className="col-6 col-md-3">
                    <div 
                      className={`p-2 rounded border h-100 transition-all ${hasValue ? 'bg-white' : 'bg-light bg-opacity-50'}`}
                      style={{ 
                        borderColor: hasValue ? '#cbd5e1' : '#f1f5f9',
                        boxShadow: hasValue ? '0 1px 3px rgba(0,0,0,0.04)' : 'none',
                        borderLeft: hasValue ? `3.5px solid ${comp.color}` : '1px solid #f1f5f9'
                      }}
                    >
                      <div className="text-muted d-flex align-items-center justify-content-between mb-1" style={{ fontSize: '0.67rem' }}>
                        <span className="d-inline-flex align-items-center gap-1 text-truncate" title={comp.label}>
                          <i className={`bi ${comp.icon}`} style={{ color: comp.color }}></i>
                          {comp.label}
                        </span>
                      </div>
                      <div className={`font-monospace text-truncate ${hasValue ? 'fw-bold text-dark' : 'text-muted'}`} style={{ fontSize: '0.78rem' }}>
                        <span className="text-muted me-1 fw-normal" style={{ fontSize: '0.7rem' }}>{comp.shortName} =</span>
                        <span className={hasValue ? 'text-dark' : 'text-muted'}>{formatCurrency(comp.value)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Metadata Badges */}
            <div className="p-2 rounded bg-light border d-flex flex-wrap align-items-center gap-2 text-muted" style={{ fontSize: '0.71rem' }}>
              <div className="d-flex align-items-center gap-1 px-2 py-1 bg-white border rounded">
                <i className="bi bi-calendar-event text-primary"></i>
                <span><strong>Tgl Masuk =</strong> {activeHt?.tglmasuk || '-'}</span>
              </div>
              <div className="d-flex align-items-center gap-1 px-2 py-1 bg-white border rounded">
                <i className="bi bi-calendar-check text-danger"></i>
                <span><strong>Tgl Pulang =</strong> {activeHt?.tglpulang || '-'}</span>
              </div>
              <div className="d-flex align-items-center gap-1 px-2 py-1 bg-white border rounded">
                <i className="bi bi-hospital text-info"></i>
                <span><strong>Jenis Rawat =</strong> {activeHt?.jnsrawat === '1' ? '1 (Rawat Inap)' : activeHt?.jnsrawat === '3' ? '3 (IGD)' : '2 (Rawat Jalan)'}</span>
              </div>
              <div className="d-flex align-items-center gap-1 px-2 py-1 bg-white border rounded">
                <i className="bi bi-layers text-secondary"></i>
                <span><strong>Kelas Rawat =</strong> {activeHt?.klas_rawat || '-'}</span>
              </div>
              <div className="d-flex align-items-center gap-1 px-2 py-1 bg-white border rounded">
                <i className="bi bi-heart-pulse text-warning"></i>
                <span><strong>Diagnosa STR =</strong> <code className="text-dark">{activeHt?.diagnosa_str || '-'}</code></span>
              </div>
              <div className="d-flex align-items-center gap-1 px-2 py-1 bg-white border rounded">
                <i className="bi bi-scissors text-success"></i>
                <span><strong>Prosedur STR =</strong> <code className="text-dark">{activeHt?.prosedur_str || '-'}</code></span>
              </div>
              <div className="d-flex align-items-center gap-1 px-2 py-1 bg-white border rounded">
                <i className="bi bi-person text-secondary"></i>
                <span><strong>Berat Badan =</strong> {activeHt?.berat_badan || 0} g</span>
              </div>
            </div>
          </div>
        ) : (
          /* View Mode 2: Clean Textarea/Preformatted View */
          <div>
            <div className="d-flex align-items-center justify-content-between mb-1">
              <small className="text-muted fw-semibold" style={{ fontSize: '0.7rem' }}>
                Teks Rincian Tarif Baku (Format Baca Langsung):
              </small>
            </div>
            <pre
              className="p-3 rounded font-monospace m-0 border text-dark"
              style={{
                backgroundColor: '#f8fafc',
                fontSize: '0.74rem',
                lineHeight: 1.6,
                maxHeight: '260px',
                overflowY: 'auto',
                borderColor: '#e2e8f0'
              }}
            >
              {formatReadableTariffStr(activeHt)}
            </pre>
          </div>
        )}
      </div>
    );
  };

  const renderHasilTarifBanner = () => {
    const activeHt = grouperResult?.data?.hasil_tarif || hasilTarifData;
    const activeStr = grouperResult?.data?.hasil_tarif_str || hasilTarifStr || (activeHt ? formatGroupedTariffStr(activeHt) : '');
    if (!activeHt && !activeStr) return null;

    return (
      <div 
        className="p-2 px-3 rounded d-flex align-items-center justify-content-between flex-wrap gap-2"
        style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}
      >
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-calculator-fill text-warning fs-6"></i>
          <div style={{ fontSize: '0.74rem' }}>
            <span className="fw-bold text-dark me-2">Hasil Grouping Tarif SIMRS (Variable <code className="text-primary">hasil_tarif</code>):</span>
            <span className="badge bg-warning bg-opacity-25 text-dark font-monospace me-2" style={{ fontSize: '0.7rem' }}>
              Total RS: {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format((activeHt?.total_rs || grouperTarget?.total) || 0)}
            </span>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            className="py-0 px-2 fw-semibold d-inline-flex align-items-center gap-1 shadow-xs"
            style={{ fontSize: '0.7rem', height: '26px', borderRadius: '6px' }}
            onClick={() => setActiveTabDiagnosa('tarif')}
          >
            <i className="bi bi-folder-check text-primary"></i> Buka Tab Tarif SIMRS
          </Button>
        </div>
      </div>
    );
  };

  const formatCurrencyInacbg = (val) => {
    if (val === null || val === undefined || val === '') return 'Rp 0,00';
    const num = typeof val === 'number' ? val : parseFloat(String(val).replace(/[^0-9.-]/g, '')) || 0;
    return 'Rp ' + num.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatInfoDate = (tglStr, jamStr) => {
    if (!tglStr) return '-';
    let day = '', month = '', year = '';
    const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
    if (tglStr.includes('/')) {
      const parts = tglStr.split('/');
      day = parts[0];
      const mIdx = parseInt(parts[1], 10) - 1;
      month = months[mIdx] || parts[1];
      year = parts[2];
    } else if (tglStr.includes('-')) {
      const parts = tglStr.split('-');
      if (parts[0].length === 4) {
        year = parts[0];
        const mIdx = parseInt(parts[1], 10) - 1;
        month = months[mIdx] || parts[1];
        day = parts[2];
      } else {
        day = parts[0];
        const mIdx = parseInt(parts[1], 10) - 1;
        month = months[mIdx] || parts[1];
        year = parts[2];
      }
    } else {
      return tglStr;
    }
    const cleanDay = parseInt(day, 10) || day;
    const timeFormatted = jamStr ? String(jamStr).trim().replace(':', '.') : '10.09';
    return `${cleanDay} ${month} ${year} pukul ${timeFormatted}`;
  };

  const handleSpecialCmgChange = async (type, code) => {
    let newProc = selectedSpecialProcedure;
    let newProsth = selectedSpecialProsthesis;
    let newInvest = selectedSpecialInvestigation;
    let newDrug = selectedSpecialDrug;

    if (type === 'procedure') {
      setSelectedSpecialProcedure(code);
      newProc = code;
    } else if (type === 'prosthesis') {
      setSelectedSpecialProsthesis(code);
      newProsth = code;
    } else if (type === 'investigation') {
      setSelectedSpecialInvestigation(code);
      newInvest = code;
    } else if (type === 'drug') {
      setSelectedSpecialDrug(code);
      newDrug = code;
    }

    // Jika ada lebih dari satu kode yang dipilih (misal beda tipe: YY dan RR), gabungkan dengan delimiter "#" (contoh: "RR04#YY01").
    // Jika topup dibatalkan/dikosongkan, kirim nilai "#" (bukan string kosong "").
    const selectedList = [newProc, newProsth, newInvest, newDrug].filter(Boolean);
    const combinedCmg = selectedList.length > 0 ? selectedList.join('#') : '#';

    await handleProcessGrouperInacbg(grouperTarget, combinedCmg);
  };

  const renderHasilGroupingInacbgTable = () => {
    const item = grouperTarget || {};
    const resInacbg = inacbgGroupingData?.response_inacbg || grouperResult?.data?.response_inacbg || grouperResult?.data || {};
    const cbgData = resInacbg?.cbg || {
      code: inacbgGroupingData?.kode_cbg || grouperResult?.data?.cbg || inacbgStatus.code || '-',
      description: inacbgGroupingData?.deskripsi_cbg || grouperResult?.data?.cbg_desc || inacbgStatus.desc || '-',
      tariff: inacbgGroupingData?.tarif_cbg || grouperResult?.data?.tarif_inacbg || inacbgStatus.tariff || 0
    };
    const subAcuteData = resInacbg?.sub_acute || { code: '-', description: '-', tariff: 0 };
    const chronicData = resInacbg?.chronic || { code: '-', description: '-', tariff: 0 };
    // Ekstraksi Special CMG yang diterapkan (baik dari array special_cmg maupun object special_procedure dll)
    const specialCmgList = Array.isArray(resInacbg?.special_cmg)
      ? resInacbg.special_cmg
      : (resInacbg?.special_cmg ? [resInacbg.special_cmg] : []);

    const appliedProc = specialCmgList.find(c => {
      const t = String(c?.type || '').toLowerCase();
      const cd = String(c?.code || '').toUpperCase();
      return t.includes('procedure') || cd.startsWith('RR') || cd.startsWith('YY');
    });
    const appliedProsth = specialCmgList.find(c => {
      const t = String(c?.type || '').toLowerCase();
      const cd = String(c?.code || '').toUpperCase();
      return t.includes('prosthesis') || cd.startsWith('XP');
    });
    const appliedInvest = specialCmgList.find(c => {
      const t = String(c?.type || '').toLowerCase();
      const cd = String(c?.code || '').toUpperCase();
      return t.includes('investigation') || cd.startsWith('XI');
    });
    const appliedDrug = specialCmgList.find(c => {
      const t = String(c?.type || '').toLowerCase();
      const cd = String(c?.code || '').toUpperCase();
      return t.includes('drug') || cd.startsWith('XD');
    });

    const specialProcData = appliedProc || resInacbg?.special_procedure || { code: '-', description: '-', tariff: 0 };
    const specialProsthData = appliedProsth || resInacbg?.special_prosthesis || { code: '-', description: '-', tariff: 0 };
    const specialInvestData = appliedInvest || resInacbg?.special_investigation || { code: '-', description: '-', tariff: 0 };
    const specialDrugData = appliedDrug || resInacbg?.special_drug || { code: '-', description: '-', tariff: 0 };

    const specialOptions = inacbgGroupingData?.special_cmg_option || 
                           resInacbg?.special_cmg_option || 
                           grouperResult?.data?.special_cmg_option || 
                           grouperResult?.data?.special_cmg || 
                           [];

    const patientName = item.nm_pasien || item.nama || 'Pasien';
    const tglMasuk = item.tgl_masuk || item.tgl_kunjungan || getTodayYmd();
    const jamMasuk = item.jam_masuk || item.jam || '10.09';
    const dateFormatted = formatInfoDate(tglMasuk, jamMasuk);
    const kelasStr = (item.nm_klas || item.kelas_rawat || inacbgGroupingData?.kelas_cbg || 'KELAS 1').toUpperCase();
    const tarifRsStr = (item.jenis_tarif || filterDialog?.jenisTarif || 'TARIF RS KELAS A PEMERINTAH').toUpperCase();

    const isRanap = Boolean(item.jenis_rawat === 'ranap' || item.jns_rawat === '1' || item.jenis_pelayanan === '1' || String(item.asal || '').toLowerCase().includes('inap'));
    const jenisRawatText = isRanap ? 'Rawat Inap' : 'Rawat Jalan Regular';

    const isUngroupable = cbgData?.code === 'X-0-99-X' || inacbgStatus.isUngroupable || String(cbgData?.description || '').toLowerCase().includes('ungroupable');
    const statusText = isUngroupable ? 'Ungroupable' : 'Normal';

    // Dropdown filters (dukung casing "Special Procedure" maupun kode YY/RR)
    const listProcOpts = specialOptions.filter(o => {
      const t = String(o.type || '').toLowerCase();
      const cd = String(o.code || '').toUpperCase();
      return t.includes('procedure') || cd.startsWith('RR') || cd.startsWith('YY');
    });
    const listProsthOpts = specialOptions.filter(o => {
      const t = String(o.type || '').toLowerCase();
      const cd = String(o.code || '').toUpperCase();
      return t.includes('prosthesis') || cd.startsWith('XP');
    });
    const listInvestOpts = specialOptions.filter(o => {
      const t = String(o.type || '').toLowerCase();
      const cd = String(o.code || '').toUpperCase();
      return t.includes('investigation') || cd.startsWith('XI');
    });
    const listDrugOpts = specialOptions.filter(o => {
      const t = String(o.type || '').toLowerCase();
      const cd = String(o.code || '').toUpperCase();
      return t.includes('drug') || cd.startsWith('XD');
    });

    const isCodeMatch = (selCode, optCode) => {
      if (!selCode || !optCode) return false;
      const s = String(selCode).toUpperCase().replace(/[^A-Z0-9]/g, '');
      const o = String(optCode).toUpperCase().replace(/[^A-Z0-9]/g, '');
      return s === o || s.startsWith(o) || o.startsWith(s);
    };

    // Default Special CMG selalu "Tidak Ada" kecuali secara aktif dipilih oleh pengguna
    const valProc = selectedSpecialProcedure || '';
    const valProsth = selectedSpecialProsthesis || '';
    const valInvest = selectedSpecialInvestigation || '';
    const valDrug = selectedSpecialDrug || '';

    // Formula Top-Up INA-CBG & Penentuan Tarif Sesuai Permenkes No. 3 Tahun 2023
    const baseTariff = parseFloat(resInacbg?.base_tariff || cbgData?.tariff || inacbgGroupingData?.tarif_cbg || 0);

    const procOptItem = listProcOpts.find(o => isCodeMatch(valProc, o.code || o.special_cmg));
    const procTariff = !valProc ? 0 : (
      (specialProcData.tariff && parseFloat(specialProcData.tariff) > 0 && isCodeMatch(valProc, specialProcData.code))
        ? parseFloat(specialProcData.tariff)
        : (procOptItem?.tariff ? parseFloat(procOptItem.tariff) : (String(valProc).includes('YY10') && baseTariff ? baseTariff : 0))
    );

    const prosthOptItem = listProsthOpts.find(o => isCodeMatch(valProsth, o.code || o.special_cmg));
    const prosthTariff = !valProsth ? 0 : (
      (specialProsthData.tariff && parseFloat(specialProsthData.tariff) > 0 && isCodeMatch(valProsth, specialProsthData.code))
        ? parseFloat(specialProsthData.tariff)
        : (prosthOptItem?.tariff ? parseFloat(prosthOptItem.tariff) : 0)
    );

    const investOptItem = listInvestOpts.find(o => isCodeMatch(valInvest, o.code || o.special_cmg));
    const investTariff = !valInvest ? 0 : (
      (specialInvestData.tariff && parseFloat(specialInvestData.tariff) > 0 && isCodeMatch(valInvest, specialInvestData.code))
        ? parseFloat(specialInvestData.tariff)
        : (investOptItem?.tariff ? parseFloat(investOptItem.tariff) : 0)
    );

    const drugOptItem = listDrugOpts.find(o => isCodeMatch(valDrug, o.code || o.special_cmg));
    const drugTariff = !valDrug ? 0 : (
      (specialDrugData.tariff && parseFloat(specialDrugData.tariff) > 0 && isCodeMatch(valDrug, specialDrugData.code))
        ? parseFloat(specialDrugData.tariff)
        : (drugOptItem?.tariff ? parseFloat(drugOptItem.tariff) : 0)
    );

    const subAcuteTariff = parseFloat(subAcuteData?.tariff || 0);
    const chronicTariff = parseFloat(chronicData?.tariff || 0);
    const sumSpecialTariff = procTariff + prosthTariff + investTariff + drugTariff;

    const calculatedTotalClaim = baseTariff + sumSpecialTariff + subAcuteTariff + chronicTariff;

    const hasAnySpecialOptions = Boolean(Array.isArray(specialOptions) && specialOptions.length > 0);
    const hasAnySpecialActive = Boolean(
      selectedSpecialProcedure ||
      selectedSpecialProsthesis ||
      selectedSpecialInvestigation ||
      selectedSpecialDrug
    );

    const cellBorder = '1px solid #e5e7eb';
    const thStyle = {
      padding: '7px 12px',
      fontWeight: '600',
      color: '#111827',
      borderRight: cellBorder,
      borderBottom: cellBorder,
      fontSize: '0.82rem',
      backgroundColor: '#ffffff',
      width: '180px'
    };
    const tdStyle = {
      padding: '7px 12px',
      borderRight: cellBorder,
      borderBottom: cellBorder,
      fontSize: '0.82rem',
      color: '#1f2937',
      backgroundColor: '#ffffff'
    };
    const selectStyle = {
      width: '100%',
      padding: '4px 8px',
      fontSize: '0.8rem',
      color: '#374151',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      outline: 'none'
    };

    return (
      <div className="mb-3" style={{ background: '#ffffff', borderRadius: '4px' }}>
        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>
          Hasil Grouping INACBG
        </div>
        <div style={{ border: cellBorder, borderRadius: '4px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <tbody>
              {/* Row 1: Info */}
              <tr>
                <td style={thStyle}>Info</td>
                <td colSpan="2" style={tdStyle}>
                  {patientName} @ {dateFormatted} •• {kelasStr} •• Tarif : {tarifRsStr}
                </td>
                <td style={{ ...tdStyle, borderRight: 'none', width: '160px' }}></td>
              </tr>

              {/* Row 2: Jenis Rawat */}
              <tr>
                <td style={thStyle}>Jenis Rawat</td>
                <td colSpan="3" style={{ ...tdStyle, borderRight: 'none' }}>
                  {jenisRawatText}
                </td>
              </tr>

              {/* Row 3: Group */}
              <tr>
                <td style={thStyle}>Group</td>
                <td style={tdStyle}>{cbgData.description || '-'}</td>
                <td style={{ ...tdStyle, width: '120px', textAlign: 'center' }}>
                  {cbgData.code || '-'}
                </td>
                <td style={{ ...tdStyle, width: '160px', textAlign: 'right', borderRight: 'none', whiteSpace: 'nowrap' }}>
                  {formatCurrencyInacbg(baseTariff)}
                </td>
              </tr>

              {/* Row 4: Sub Acute */}
              <tr>
                <td style={thStyle}>Sub Acute</td>
                <td style={tdStyle}>{subAcuteData.description || '-'}</td>
                <td style={{ ...tdStyle, width: '120px', textAlign: 'center' }}>
                  {subAcuteData.code || '-'}
                </td>
                <td style={{ ...tdStyle, width: '160px', textAlign: 'right', borderRight: 'none', whiteSpace: 'nowrap' }}>
                  {formatCurrencyInacbg(subAcuteTariff)}
                </td>
              </tr>

              {/* Row 5: Chronic */}
              <tr>
                <td style={thStyle}>Chronic</td>
                <td style={tdStyle}>{chronicData.description || '-'}</td>
                <td style={{ ...tdStyle, width: '120px', textAlign: 'center' }}>
                  {chronicData.code || '-'}
                </td>
                <td style={{ ...tdStyle, width: '160px', textAlign: 'right', borderRight: 'none', whiteSpace: 'nowrap' }}>
                  {formatCurrencyInacbg(chronicTariff)}
                </td>
              </tr>

              {/* Special CMG Section: Tampilkan dropdown hanya jika special_cmg_option tidak kosong atau sedang aktif */}
              {!hasAnySpecialOptions && !hasAnySpecialActive ? (
                <tr>
                  <td style={thStyle}>Special CMG</td>
                  <td colSpan="3" style={{ ...tdStyle, color: '#64748b', fontStyle: 'italic', borderRight: 'none' }}>
                    <i className="bi bi-info-circle me-1"></i>Tidak ada opsi Special CMG untuk hasil grouping ini.
                  </td>
                </tr>
              ) : (
                <>
                  {/* Row 6: Special Procedure */}
                  {(listProcOpts.length > 0 || selectedSpecialProcedure || (specialProcData.code && specialProcData.code !== '-')) && (
                    <tr>
                      <td style={thStyle}>Special Procedure</td>
                      <td style={tdStyle}>
                        <select
                          style={selectStyle}
                          value={valProc}
                          disabled={isGroupingInacbg || inacbgStatus.isFinal || claimFinalStatus}
                          onChange={(e) => handleSpecialCmgChange('procedure', e.target.value)}
                        >
                          <option value="">- Tidak Ada / Reset -</option>
                          {listProcOpts.map((opt, idx) => {
                            const optTf = opt.tariff || (opt.code && String(opt.code).includes('YY10') && baseTariff ? baseTariff : 0);
                            return (
                              <option key={idx} value={opt.code || opt.special_cmg}>
                                {opt.code ? `[${opt.code}] ` : ''}{opt.description || opt.desc || opt.code}
                                {optTf ? ` (+${formatCurrencyInacbg(optTf)})` : ''}
                              </option>
                            );
                          })}
                        </select>
                      </td>
                      <td style={{ ...tdStyle, width: '120px', textAlign: 'center' }}>
                        {valProc ? (specialProcData.code && specialProcData.code !== '-' ? specialProcData.code : valProc) : '-'}
                      </td>
                      <td style={{ ...tdStyle, width: '160px', textAlign: 'right', borderRight: 'none', whiteSpace: 'nowrap', fontWeight: procTariff ? '600' : 'normal', color: procTariff ? '#0d9488' : '#64748b' }}>
                        {formatCurrencyInacbg(procTariff)}
                      </td>
                    </tr>
                  )}

                  {/* Row 7: Special Prosthesis */}
                  {(listProsthOpts.length > 0 || selectedSpecialProsthesis || (specialProsthData.code && specialProsthData.code !== '-')) && (
                    <tr>
                      <td style={thStyle}>Special Prosthesis</td>
                      <td style={tdStyle}>
                        <select
                          style={selectStyle}
                          value={valProsth}
                          disabled={isGroupingInacbg || inacbgStatus.isFinal || claimFinalStatus}
                          onChange={(e) => handleSpecialCmgChange('prosthesis', e.target.value)}
                        >
                          <option value="">- Tidak Ada / Reset -</option>
                          {listProsthOpts.map((opt, idx) => (
                            <option key={idx} value={opt.code || opt.special_cmg}>
                              {opt.code ? `[${opt.code}] ` : ''}{opt.description || opt.desc || opt.code}
                              {opt.tariff ? ` (+${formatCurrencyInacbg(opt.tariff)})` : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ ...tdStyle, width: '120px', textAlign: 'center' }}>
                        {valProsth ? (specialProsthData.code && specialProsthData.code !== '-' ? specialProsthData.code : valProsth) : '-'}
                      </td>
                      <td style={{ ...tdStyle, width: '160px', textAlign: 'right', borderRight: 'none', whiteSpace: 'nowrap', fontWeight: prosthTariff ? '600' : 'normal', color: prosthTariff ? '#0d9488' : '#64748b' }}>
                        {formatCurrencyInacbg(prosthTariff)}
                      </td>
                    </tr>
                  )}

                  {/* Row 8: Special Investigation */}
                  {(listInvestOpts.length > 0 || selectedSpecialInvestigation || (specialInvestData.code && specialInvestData.code !== '-')) && (
                    <tr>
                      <td style={thStyle}>Special Investigation</td>
                      <td style={tdStyle}>
                        <select
                          style={selectStyle}
                          value={valInvest}
                          disabled={isGroupingInacbg || inacbgStatus.isFinal || claimFinalStatus}
                          onChange={(e) => handleSpecialCmgChange('investigation', e.target.value)}
                        >
                          <option value="">- Tidak Ada / Reset -</option>
                          {listInvestOpts.map((opt, idx) => (
                            <option key={idx} value={opt.code || opt.special_cmg}>
                              {opt.code ? `[${opt.code}] ` : ''}{opt.description || opt.desc || opt.code}
                              {opt.tariff ? ` (+${formatCurrencyInacbg(opt.tariff)})` : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ ...tdStyle, width: '120px', textAlign: 'center' }}>
                        {valInvest ? (specialInvestData.code && specialInvestData.code !== '-' ? specialInvestData.code : valInvest) : '-'}
                      </td>
                      <td style={{ ...tdStyle, width: '160px', textAlign: 'right', borderRight: 'none', whiteSpace: 'nowrap', fontWeight: investTariff ? '600' : 'normal', color: investTariff ? '#0d9488' : '#64748b' }}>
                        {formatCurrencyInacbg(investTariff)}
                      </td>
                    </tr>
                  )}

                  {/* Row 9: Special Drug */}
                  {(listDrugOpts.length > 0 || selectedSpecialDrug || (specialDrugData.code && specialDrugData.code !== '-')) && (
                    <tr>
                      <td style={thStyle}>Special Drug</td>
                      <td style={tdStyle}>
                        <select
                          style={selectStyle}
                          value={valDrug}
                          disabled={isGroupingInacbg || inacbgStatus.isFinal || claimFinalStatus}
                          onChange={(e) => handleSpecialCmgChange('drug', e.target.value)}
                        >
                          <option value="">- Tidak Ada / Reset -</option>
                          {listDrugOpts.map((opt, idx) => (
                            <option key={idx} value={opt.code || opt.special_cmg}>
                              {opt.code ? `[${opt.code}] ` : ''}{opt.description || opt.desc || opt.code}
                              {opt.tariff ? ` (+${formatCurrencyInacbg(opt.tariff)})` : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ ...tdStyle, width: '120px', textAlign: 'center' }}>
                        {valDrug ? (specialDrugData.code && specialDrugData.code !== '-' ? specialDrugData.code : valDrug) : '-'}
                      </td>
                      <td style={{ ...tdStyle, width: '160px', textAlign: 'right', borderRight: 'none', whiteSpace: 'nowrap', fontWeight: drugTariff ? '600' : 'normal', color: drugTariff ? '#0d9488' : '#64748b' }}>
                        {formatCurrencyInacbg(drugTariff)}
                      </td>
                    </tr>
                  )}
                </>
              )}

              {/* Row 10: Total Klaim */}
              <tr>
                <td style={{ ...thStyle, fontWeight: '700' }}>Total Klaim</td>
                <td style={tdStyle}></td>
                <td style={{ ...tdStyle, width: '120px', textAlign: 'right', fontWeight: '500' }}>
                  Rp
                </td>
                <td style={{ ...tdStyle, width: '160px', textAlign: 'right', fontWeight: '700', borderRight: 'none', whiteSpace: 'nowrap' }}>
                  {formatCurrencyInacbg(calculatedTotalClaim)}
                </td>
              </tr>

              {/* Row 11: Status */}
              <tr>
                <td style={{ ...thStyle, borderBottom: 'none' }}>Status</td>
                <td style={{ ...tdStyle, borderBottom: 'none' }}>
                  <span style={{ color: isUngroupable ? '#dc2626' : '#111827', fontWeight: isUngroupable ? '700' : 'normal' }}>
                    {statusText}
                  </span>
                </td>
                <td style={{ ...tdStyle, width: '120px', borderBottom: 'none' }}></td>
                <td style={{ ...tdStyle, width: '160px', borderRight: 'none', borderBottom: 'none' }}></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const handleOpenGrouper = async (item) => {
    if (isInvalidSep(item?.no_sep)) {
      setWarningPatientTarget(item);
      setShowSepWarningModal(true);
      notify('warning', 'Nomor SEP Belum Diisi atau Format SEP Salah', 'Nomor SEP belum diisi atau Format SEP Salah dan tidak bisa melanjutkan grouper, silakan input SEP dulu.');
      return;
    }

    setGrouperTarget(item);
    setSelectedDiagnosaItem(item);
    setGrouperResult(null);
    setHasilTarifData(null);
    setHasilTarifStr('');
    setIdrgProcedureList([]);
    setIdrgProcedureData(null);
    setInputSitb(item.sitb || '');
    setSitbValidationResult(null);

    // Reset Top-up iDRG & Special CMG state
    setIdrgTopupOptions([]);
    setSelectedIdrgTopupMap({});
    setIdrgCostWeightData({
      drgCostWeight: item.idrg_cost_weight || item.drg_cost_weight || 0,
      topupCostWeight: item.idrg_topup_cost_weight || item.topup_cost_weight || 0,
      totalCostWeight: item.idrg_total_cost_weight || item.total_cost_weight || item.cost_weight || 0,
      tariff: item.tarif_idrg || 0
    });
    setSelectedSpecialProcedure('');
    setSelectedSpecialProsthesis('');
    setSelectedSpecialInvestigation('');
    setSelectedSpecialDrug('');

    // Inisialisasi status workflow Kemenkes
    const hasIdrg = Boolean(item.kd_idrg && item.kd_idrg !== '-');
    const hasInacbg = Boolean(item.inacbg && item.inacbg !== '-' && item.inacbg !== 'X-0-99-X');
    const isUngroupableIdrg = String(item.kd_idrg || '').toLowerCase().includes('ungroupable') || String(item.kd_idrg || '').startsWith('36');
    const isUngroupableInacbg = !hasInacbg || String(item.inacbg || '').toLowerCase().includes('ungroupable') || item.inacbg === 'X-0-99-X';
    const isIdrgFinal = Boolean(item.idrg_final || item.is_idrg_final || item.status_idrg === 'final');

    setIdrgStatus({
      isGrouped: hasIdrg,
      isFinal: isIdrgFinal,
      isUngroupable: isUngroupableIdrg,
      code: item.kd_idrg || '',
      desc: item.nm_idrg || '',
      costWeight: item.idrg_total_cost_weight || item.cost_weight || 0
    });
    setInacbgStatus({
      isGrouped: hasInacbg,
      isFinal: false,
      isUngroupable: isUngroupableInacbg,
      code: item.inacbg || '',
      desc: '',
      tariff: item.tarif_inacbg || 0
    });
    const prevInacbgCode = item.kd_inacbg || item.inacbg || item.cbg || '';
    if (prevInacbgCode && prevInacbgCode !== '-' && prevInacbgCode !== 'X-0-99-X') {
      setInacbgGroupingData({
        kode_cbg: prevInacbgCode,
        deskripsi_cbg: item.cbg_desc || item.nm_inacbg || item.deskripsi_cbg || '',
        tarif_cbg: item.tarif_inacbg || 0,
        response_inacbg: {
          cbg: {
            code: prevInacbgCode,
            description: item.cbg_desc || item.nm_inacbg || item.deskripsi_cbg || '-',
            tariff: item.tarif_inacbg || 0
          },
          sub_acute: { code: '-', description: '-', tariff: 0 },
          chronic: { code: '-', description: '-', tariff: 0 },
          special_procedure: { code: '-', description: '-', tariff: 0 },
          special_prosthesis: { code: '-', description: '-', tariff: 0 },
          special_investigation: { code: '-', description: '-', tariff: 0 },
          special_drug: { code: '-', description: '-', tariff: 0 },
          tariff: item.tarif_inacbg || 0
        }
      });
      setHasRunGrouperInacbg(true);
    } else {
      setInacbgGroupingData(null);
      setHasRunGrouperInacbg(false);
    }

    setShowGrouperModal(true);
    setClaimFinalStatus(Boolean(item.klaim_final));
    setActiveTabDiagnosa('diagnosa');
    setActiveTabDiagnosaInacbg('diagnosa');
    setDiagnosaData({ list_diagnosa: [], list_prosedur: [] });
    setDiagnosaInacbgData({ list_diagnosa: [], list_prosedur: [] });
    handleOpenInputDiagnosa('diagnosa', 'idrg');
    setShowInlineInputInacbg(isIdrgFinal);
    setShowTableInacbg(isIdrgFinal);
    fetchDiagnosaData(item);
    fetchDiagnosaInacbgData(item);
    fetchEhrMedicalInfo();
    fetchHasilTarif(item);
    fetchResponseEklaim(item);
  };

  const fetchResponseEklaim = async (item) => {
    if (!item?.no_sep || isInvalidSep(item?.no_sep)) return;
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    try {
      const res = await fetch(`${apiUrl}/get_response_eklaim?no_sep=${encodeURIComponent(item.no_sep)}`);
      if (!res.ok) return;
      const json = await res.json();
      if (json && json.status === 1) {
        const rawRes = json.response || {};
        const resInacbg = json.response_inacbg || rawRes.response_inacbg || {};
        const resIdrg = json.response_idrg || rawRes.response_idrg || {};

        // 1. Ekstraksi Opsi Special CMG
        const specOptions = rawRes.special_cmg_option || resInacbg.special_cmg_option || [];
        const baseTariffVal = resInacbg.base_tariff || resInacbg.cbg?.tariff || item.tarif_inacbg || 0;
        if (Array.isArray(specOptions) && specOptions.length > 0) {
          setInacbgGroupingData(prev => ({
            ...(prev || {}),
            ...resInacbg,
            kode_cbg: resInacbg.cbg?.code || prev?.kode_cbg || item.inacbg,
            deskripsi_cbg: resInacbg.cbg?.description || prev?.deskripsi_cbg || item.cbg_desc,
            tarif_cbg: resInacbg.cbg?.tariff || resInacbg.base_tariff || prev?.tarif_cbg || item.tarif_inacbg,
            special_cmg_option: specOptions,
            response_inacbg: {
              ...(prev?.response_inacbg || {}),
              ...resInacbg,
              base_tariff: resInacbg.base_tariff || resInacbg.cbg?.tariff || baseTariffVal,
              special_cmg_option: specOptions
            }
          }));
        }

        // 2. Default Special CMG selalu "Tidak Ada" (kosong) saat modal dibuka,
        // pengguna memilih secara eksplisit jika ingin menerapkan Top-Up Stage 2.
        setSelectedSpecialProcedure('');
        setSelectedSpecialProsthesis('');
        setSelectedSpecialInvestigation('');
        setSelectedSpecialDrug('');

        // 3. Ekstraksi iDRG Top-up jika ada
        const idrgTopups = rawRes.topup_options || resIdrg.topup_options || [];
        if (Array.isArray(idrgTopups) && idrgTopups.length > 0) {
          setIdrgTopupOptions(idrgTopups);
        }
      }
    } catch (err) {
      console.warn('Gagal memuat cache response_eklaim lokal:', err);
    }
  };

  const handleValidateSitb = async (no_sep) => {
    setSitbValidationResult(null);
    if (!inputSitb) {
      setSitbValidationResult({ type: 'danger', text: "Harap isi Nomor Register SITB terlebih dahulu." });
      return;
    }
    if (!no_sep || no_sep === '-') {
      setSitbValidationResult({ type: 'danger', text: "Nomor SEP kosong. Harap isi/edit Nomor SEP sebelum memvalidasi SITB." });
      return;
    }
    setIsValidatingSitb(true);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    try {
      const res = await fetch(`${apiUrl}/validasi_sitb`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...{ no_sep: no_sep, nomor_register_sitb: inputSitb }, ...getEklaimParams() })
      });
      const json = await res.json();
      if (json.status === 1 || json.status === '1') {
        setSitbValidationResult({ type: 'success', text: `Validasi Berhasil! Pasien: ${json.nama_pasien || '-'}` });
      } else {
        setSitbValidationResult({ type: 'danger', text: `Gagal: ${json.msg || "SITB Tidak Valid."}` });
      }
    } catch (err) {
      setSitbValidationResult({ type: 'danger', text: "Error saat menghubungi backend untuk validasi SITB." });
    } finally {
      setIsValidatingSitb(false);
    }
  };

  const handleProcessGrouper = async () => {
    if (!grouperTarget) return;

    // Periksa Nomor SEP sebelum memproses Grouper
    if (isInvalidSep(grouperTarget?.no_sep)) {
      setWarningPatientTarget(grouperTarget);
      setShowGrouperModal(false);
      setShowSepWarningModal(true);
      notify('danger', 'Nomor SEP Belum Diisi atau Format SEP Salah', 'Nomor SEP belum diisi atau Format SEP Salah dan tidak bisa melanjutkan grouper, silakan input SEP dulu.');
      return;
    }

    // Periksa diagnosa sebelum proses Grouper E-Claim dijalankan
    const hasListDiagnosa = Array.isArray(diagnosaData?.list_diagnosa) && diagnosaData.list_diagnosa.length > 0;
    const hasTargetDiagnosa = Boolean(grouperTarget?.diagnosa && grouperTarget.diagnosa.trim() !== '' && grouperTarget.diagnosa.trim() !== '-');
    const hasHasilTarifDiagnosa = Boolean(hasilTarifData?.diagnosa_str && hasilTarifData.diagnosa_str.trim() !== '' && hasilTarifData.diagnosa_str.trim() !== '-');

    if (!hasListDiagnosa && !hasTargetDiagnosa && !hasHasilTarifDiagnosa) {
      setActiveTabDiagnosa('diagnosa');
      notify('warning', 'Diagnosa Masih Kosong', 'Diagnosa utama belum diisi. Harap klik tombol "+ Input Diagnosa" terlebih dahulu sebelum memproses Grouper E-Claim.');
      return;
    }

    setIsGrouping(true);
    setGrouperResult(null);
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    const token = localStorage.getItem('token');

    const isRanapItem = !isRawatJalanItem(grouperTarget) || 
      (ehrData?.forms?.resume_medis_ranap?.length > 0) || 
      (ehrData?.forms?.ringkasan_masuk_keluar?.length > 0) || 
      Boolean(ehrData?.summary?.tanggal_keluar);

    const tglMasukEhr = isRanapItem ? (
      ehrData?.summary?.tanggal_masuk || 
      ehrData?.forms?.resume_medis_ranap?.[0]?.tanggal_masuk || 
      ehrData?.forms?.ringkasan_masuk_keluar?.[0]?.tanggal_masuk || 
      null
    ) : null;

    const tglPulangEhr = isRanapItem ? (
      ehrData?.summary?.tanggal_keluar || 
      ehrData?.forms?.resume_medis_ranap?.[0]?.tanggal_keluar || 
      ehrData?.forms?.ringkasan_masuk_keluar?.[0]?.tanggal_keluar || 
      null
    ) : null;

    // Susun diagnosa_str & prosedur_str sesuai urutan tampilan tabel
    const diagStr = (diagnosaData?.list_diagnosa || [])
      .map(d => String(d.kd_icd || '').trim())
      .filter(Boolean)
      .join('#');

    const procStr = (diagnosaData?.list_prosedur || [])
      .map(p => {
        const code = String(p.kd_icd || '').trim();
        if (!code) return '';
        const qty = parseInt(p.qty || p.multiplicity || 1);
        return qty > 1 && !code.includes('+') ? `${code}+${qty}` : code;
      })
      .filter(Boolean)
      .join('#');

    try {
      const res = await fetch(`${apiUrl}/claim_grouper`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          norincian: grouperTarget.no_nota,
          no_medrec: grouperTarget.no_medrec,
          nomor_register_sitb: inputSitb,
          is_rawat_inap: isRanapItem,
          tgl_masuk: grouperTarget.tgl_masuk,
          tgl_kunjungan: grouperTarget.tgl_masuk,
          tgl_pulang: grouperTarget.tgl_pulang || grouperTarget.tgl_masuk,
          tgl_masuk_ehr: tglMasukEhr,
          tgl_pulang_ehr: tglPulangEhr,
          ehr_summary: ehrData?.summary || null,
          ehr_data: ehrData || null,
          diagnosa_str: diagStr || undefined,
          prosedur_str: procStr || undefined,
          ...getEklaimParams()
        })
      });

      let json = {};
      try {
        json = await res.json();
      } catch (e) {}

      // Ekstraksi data grouping tarif SIMRS (variable hasil_tarif)
      if (json?.hasil_tarif) {
        setHasilTarifData(json.hasil_tarif);
      }
      if (json?.hasil_tarif_str) {
        setHasilTarifStr(json.hasil_tarif_str);
      }

      // Ekstraksi data prosedur tervalidasi iDRG
      const procExp = json?.procedure_expanded || json?.procedure_data?.expanded || json?.eklaim_response?.response_procedure?.data?.expanded || [];
      if (procExp && procExp.length > 0) {
        setIdrgProcedureList(procExp);
        setIdrgProcedureData(json?.procedure_data || null);
      }

      // response can have eklaim_response.metadata
      const metadata = json?.eklaim_response?.metadata;
      const drgDescription = json?.drg_description || json?.eklaim_response?.response_idrg?.drg_description;

      if (metadata?.code && parseInt(metadata.code) !== 200) {
        let errText = json.msg || metadata.message || `Gagal Grouper! (Error: ${metadata.error_no || metadata.code})`;
        if (drgDescription && !errText.includes(drgDescription)) {
          errText += ` [Keterangan iDRG: ${drgDescription}]`;
        }
        setGrouperResult({
          type: 'danger',
          text: errText,
          data: json
        });
        setIsGrouping(false);
        return;
      }

      if (!res.ok || json?.status === 0) {
        let errText = json?.msg || metadata?.message || `Gagal memproses Grouper! Response HTTP ${res.status}.`;
        if (drgDescription && !errText.includes(drgDescription)) {
          errText += ` [Keterangan iDRG: ${drgDescription}]`;
        }
        setGrouperResult({
          type: 'danger',
          text: errText,
          data: json
        });
        setIsGrouping(false);
        return;
      }

      setGrouperResult({
        type: 'success',
        text: json?.idrg_desc ? `Grouper Coding iDRG Berhasil: ${json.idrg_code || ''} - ${json.idrg_desc}` : (json?.msg || `Grouper Coding iDRG Berhasil untuk pasien ${grouperTarget.nm_pasien || grouperTarget.nama}!`),
        data: json
      });
      setHasRunGrouperInacbg(false);
      setInacbgGroupingData(null);

      // Helper konversi tanggal ke format Indonesia DD/MM/YYYY untuk update lokal
      const formatToIndoDate = (dateVal) => {
        if (!dateVal) return '';
        const s = String(dateVal).trim();
        if (s.includes('/')) return s.split(' ')[0];
        if (s.includes('-')) {
          const dpart = s.split('T')[0].split(' ')[0];
          const parts = dpart.split('-');
          if (parts.length === 3) {
            if (parts[0].length === 4) return `${parts[2].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[0]}`;
            if (parts[2].length === 4) return `${parts[0].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[2]}`;
          }
        }
        return s;
      };

      const updatedTglMasuk = json?.tgl_masuk ? formatToIndoDate(json.tgl_masuk) : null;
      const updatedTglPulang = json?.tgl_pulang ? formatToIndoDate(json.tgl_pulang) : null;

      if (updatedTglMasuk || updatedTglPulang) {
        setGrouperTarget(prev => prev ? ({
          ...prev,
          tgl_masuk: prev.tgl_masuk || updatedTglMasuk,
          tgl_pulang: updatedTglPulang || prev.tgl_pulang
        }) : null);
      }

      const respInacbg = json?.eklaim_response?.response_inacbg || json?.response_cbg || json?.cbg || {};
      const respIdrg = json?.eklaim_response?.response_idrg || json?.response_idrg || {};
      const cbgCode = json?.cbg || json?.cbg_code || respInacbg.code || grouperTarget.inacbg;
      const idrgCode = json?.idrg_code || respIdrg.drg_code || respIdrg.code || grouperTarget.kd_idrg;
      const idrgCostWeight = json?.idrg_cost_weight || respIdrg.cost_weight || respIdrg.weight || 0;
      const idrgMdc = json?.idrg_mdc_number || respIdrg.mdc_number;

      // Ekstraksi opsi Top-Up iDRG & Kalkulasi Cost Weight (Stage 1 Grouper)
      const topupOpts = json?.idrg_topup_options || respIdrg?.topup_options || [];
      setIdrgTopupOptions(Array.isArray(topupOpts) ? topupOpts : []);
      setSelectedIdrgTopupMap({});

      const drgCw = json?.drg_cost_weight ?? respIdrg?.drg_cost_weight ?? idrgCostWeight ?? 0;
      const topupCw = json?.topup_cost_weight ?? respIdrg?.topup_cost_weight ?? 0;
      const totalCw = json?.total_cost_weight ?? respIdrg?.total_cost_weight ?? idrgCostWeight ?? (Number(drgCw) + Number(topupCw));
      setIdrgCostWeightData({
        drgCostWeight: drgCw,
        topupCostWeight: topupCw,
        totalCostWeight: totalCw,
        tariff: json?.tarif_inadrg ?? json?.tarif_idrg ?? respIdrg?.total_tarif ?? respIdrg?.tariff ?? 0
      });

      // Reset pilihan Special CMG saat grouper awal dijalankan ulang
      setSelectedSpecialProcedure('');
      setSelectedSpecialProsthesis('');
      setSelectedSpecialInvestigation('');
      setSelectedSpecialDrug('');

      const isIdrgUngroup = String(idrgCode || '').toLowerCase().includes('ungroupable') || String(idrgMdc) === '36' || String(idrgCode || '').startsWith('36');
      const isInacbgUngroup = !cbgCode || cbgCode === 'X-0-99-X' || String(cbgCode).toLowerCase().includes('ungroupable') || String(cbgCode).startsWith('X');

      setIdrgStatus({
        isGrouped: Boolean(idrgCode),
        isFinal: false,
        isUngroupable: isIdrgUngroup,
        code: idrgCode || '',
        desc: json?.idrg_desc || respIdrg.drg_description || respIdrg.description || '',
        costWeight: totalCw
      });

      setInacbgStatus({
        isGrouped: false,
        isFinal: false,
        isUngroupable: isInacbgUngroup,
        code: cbgCode || '',
        desc: json?.cbg_desc || respInacbg.description || '',
        tariff: json?.tarif_inacbg ?? respInacbg.tariff ?? 0
      });

      // Deteksi Poin 14: Peringatan Kode Indonesian Modification (IM)
      const currentDiags = diagnosaData?.list_diagnosa || [];
      const imDiags = currentDiags.filter(d => {
        const c = String(d.kd_icd || '').trim();
        return c.length > 5 || (c.includes('.') && c.split('.')[1].length > 2);
      });
      setImWarningList(imDiags);

      // Update state lokal claimList
      setClaimList(prev => prev.map(p => {
        if (p.no_nota === grouperTarget.no_nota) {
          const tarifInacbg = json?.tarif_inacbg ?? respInacbg.tariff ?? p.tarif_inacbg;
          const tarifInadrg = json?.tarif_inadrg ?? json?.tarif_idrg ?? respIdrg.total_tarif ?? respIdrg.tariff ?? p.tarif_inadrg;
          return {
            ...p,
            tgl_masuk: updatedTglMasuk || p.tgl_masuk,
            tgl_pulang: updatedTglPulang || p.tgl_pulang,
            inacbg: cbgCode,
            kd_idrg: idrgCode,
            nm_idrg: json?.idrg_desc || respIdrg.drg_description || respIdrg.description || p.nm_idrg,
            tarif_inacbg: tarifInacbg,
            tarif_inadrg: tarifInadrg
          };
        }
        return p;
      }));
    } catch (err) {
      console.warn('Gagal grouper backend:', err);
      setGrouperResult({
        type: 'danger',
        text: `[Error] Gagal melakukan proses Grouper E-Klaim (Nota: ${grouperTarget.no_nota})`
      });
    } finally {
      setIsGrouping(false);
    }
  };

  // ==========================================
  // EFFECTS
  // ==========================================
  useEffect(() => {
    const savedUser = localStorage.getItem('nama_user') || localStorage.getItem('user');
    if (savedUser) setUsername(savedUser);
    const m = localStorage.getItem('menus');
    if (m) setMenus(JSON.parse(m));
    const am = localStorage.getItem('activeMenu');
    if (am) setActiveMenu(am);

    // Fetch Setup Bridging (E-Klaim config)
    fetchSetupBridging();

    // Preload master ICD-9 IM dari /show_icdixim untuk pemilihan cepat prosedur
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    fetch(`${apiUrl}/show_icdixim`)
      .then(res => res.json())
      .then(json => {
        const list = Array.isArray(json) ? json : (json?.list_icdixim || json?.list_icdix || json?.data || []);
        if (Array.isArray(list) && list.length > 0) {
          setMasterIcd9(list);
        }
      })
      .catch(err => console.warn('Gagal preload /show_icdixim:', err));

    // Listener pesan dari iframe cetak klaim jika user klik tombol Tutup
    const handleWindowMessage = (event) => {
      if (event.data === 'close-print-modal') {
        setShowPrintModal(false);
      }
    };
    window.addEventListener('message', handleWindowMessage);
    return () => {
      window.removeEventListener('message', handleWindowMessage);
    };
  }, []);

  const fetchSetupBridging = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${getApiUrl()}/setup_bridging`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (res.ok) {
        const json = await res.json();
        const configData = json.data || json;
        if (configData && !configData.msg && typeof configData === 'object' && Object.keys(configData).length > 0) {
          setBridgingConfig(configData);
          const ep = configData.ECLAIM_URL || configData.ENDPOINT_URL || configData.endpoint_url;
          const key = configData.ECLAIM_KEY || configData.ENCRYPTION_KEY || configData.encryption_key;
          const tarif = configData.KODE_TARIF || configData.kode_tarif;
          const coder = configData.ECLAIM_CODER_NIK || configData.CODER_NIK || configData.coder_nik;
          if (ep) {
            localStorage.setItem('endpoint_url', ep);
            localStorage.setItem('ENDPOINT_URL', ep);
            localStorage.setItem('ECLAIM_URL', ep);
          }
          if (key) {
            localStorage.setItem('encryption_key', key);
            localStorage.setItem('ENCRYPTION_KEY', key);
            localStorage.setItem('ECLAIM_KEY', key);
          }
          if (tarif) {
            localStorage.setItem('kode_tarif', tarif);
            localStorage.setItem('KODE_TARIF', tarif);
          }
          if (coder) {
            localStorage.setItem('nik_coder', coder);
            localStorage.setItem('coder_nik', coder);
            localStorage.setItem('ECLAIM_CODER_NIK', coder);
          }
        }
      }
    } catch (err) {
      console.error('Gagal memuat setup bridging:', err);
    }
  };

  const getEklaimParams = () => {
    const config = bridgingConfig || {};
    const endpoint_url = config.ECLAIM_URL || config.ENDPOINT_URL || config.endpoint_url || localStorage.getItem('endpoint_url') || localStorage.getItem('ENDPOINT_URL') || localStorage.getItem('ECLAIM_URL') || '';
    
    let raw_key = config.ECLAIM_KEY || config.ENCRYPTION_KEY || config.encryption_key || localStorage.getItem('encryption_key') || localStorage.getItem('ENCRYPTION_KEY') || localStorage.getItem('ECLAIM_KEY') || '';
    // Validasi apakah key berformat hex 64-karakter, jika tidak valid (misal berisi URL) gunakan default hex key
    const defaultHexKey = '8fcfe26a649cc6fc2fcc430fb104edfdd29fb80b4e6181eda0bfa3b9fb90a708';
    let encryption_key = (raw_key && typeof raw_key === 'string' && /^[0-9a-fA-F]{64}$/.test(raw_key.trim())) 
      ? raw_key.trim() 
      : defaultHexKey;

    const kode_tarif = config.KODE_TARIF || config.kode_tarif || localStorage.getItem('kode_tarif') || localStorage.getItem('KODE_TARIF') || '';
    const coder_nik = config.ECLAIM_CODER_NIK || config.CODER_NIK || config.coder_nik || localStorage.getItem('nik_coder') || localStorage.getItem('coder_nik') || '';

    const params = {};
    if (endpoint_url) {
      params.endpoint_url = endpoint_url;
      params.ENDPOINT_URL = endpoint_url;
    }
    if (encryption_key) {
      params.encryption_key = encryption_key;
      params.ENCRYPTION_KEY = encryption_key;
    }
    if (kode_tarif) {
      params.kode_tarif = kode_tarif;
      params.KODE_TARIF = kode_tarif;
    }
    if (coder_nik) {
      params.coder_nik = coder_nik;
      params.ECLAIM_CODER_NIK = coder_nik;
    }
    return params;
  };

  // ==========================================
  // FETCH DATA
  // ==========================================
  const fetchDataClaim = async (customParams = {}) => {
    setIsLoading(true);
    setFetchError('');
    setClaimList([]);

    const token = localStorage.getItem('token');
    if (!token) {
      setFetchError('Token tidak tersedia. Silakan login kembali.');
      setIsLoading(false);
      return;
    }

    const targetSep = customParams.no_sep !== undefined ? customParams.no_sep : searchSepInput.trim();
    const p1 = customParams.periode1 !== undefined ? customParams.periode1 : periode1;
    const p2 = customParams.periode2 !== undefined ? customParams.periode2 : periode2;

    const reqBody = {};
    if (targetSep) {
      reqBody.no_sep = targetSep;
    } else {
      if (!p1 || !p2) {
        setIsLoading(false);
        return;
      }
      reqBody.periode1 = formatYmdToDdmmyyyy(p1);
      reqBody.periode2 = formatYmdToDdmmyyyy(p2);
    }

    try {
      const apiUrl = getApiUrl() || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/show_dataclaimpasienri`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(reqBody)
      });

      const data = await response.json();
      if (response.ok && data.data) {
        setClaimList(data.data);
        setHasLoadedData(true);
        setCurrentPage(1);

        if (targetSep) {
          if (data.data.length === 0) {
            notify('warning', 'Pencarian No. SEP', `Data klaim untuk No. SEP "${targetSep}" tidak ditemukan.`);
          } else {
            notify('success', 'Pencarian No. SEP Berhasil', `Ditemukan ${data.data.length} data klaim untuk No. SEP "${targetSep}".`);
            const hasRI = data.data.some(x => !isRawatJalanItem(x));
            const hasRJ = data.data.some(x => isRawatJalanItem(x));
            if (hasRI && !hasRJ) setActiveTabRawat('RI');
            else if (hasRJ && !hasRI) setActiveTabRawat('RJ');
            else setActiveTabRawat('ALL');
          }
        }
      } else {
        setFetchError(data.message || 'Gagal mengambil data dari server.');
        setHasLoadedData(true);
      }
    } catch (error) {
      console.error('Error fetching data claim:', error);
      setFetchError('Terjadi kesalahan jaringan atau server mati.');
      setHasLoadedData(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePeriode1Change = (newVal) => {
    setPeriode1(newVal);
    if (newVal && periode2) {
      fetchDataClaim({ periode1: newVal, periode2: periode2, no_sep: '' });
    }
  };

  const handlePeriode2Change = (newVal) => {
    setPeriode2(newVal);
    if (periode1 && newVal) {
      fetchDataClaim({ periode1: periode1, periode2: newVal, no_sep: '' });
    }
  };

  const handleSearchBySep = () => {
    const sep = searchSepInput.trim();
    if (!sep) {
      notify('warning', 'Pencarian SEP', 'Silakan masukkan Nomor SEP yang ingin dicari.');
      return;
    }
    fetchDataClaim({ no_sep: sep });
  };

  const handleExportNcc = async () => {
    setIsExporting(true);
    try {
      // 1. Ambil data pasien di rentang tanggal yang dipilih yang SUDAH iDRG
      const reqBody = {
        periode1: formatYmdToDdmmyyyy(exportStartDate),
        periode2: formatYmdToDdmmyyyy(exportEndDate)
      };
      const token = localStorage.getItem('token');
      const apiUrl = getApiUrl() || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/show_dataclaimpasienri`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(reqBody)
      });
      const data = await response.json();
      let targetList = (data.data || []).filter(x => x.kd_idrg && x.kd_idrg !== '-' && x.kd_idrg !== 'null' && x.no_sep && x.no_sep !== '-');
      
      if (targetList.length === 0) {
        notify('warning', 'Data Kosong', 'Tidak ada data pasien yang sudah di-Grouper iDRG pada periode tersebut.');
        setIsExporting(false);
        return;
      }

      setExportProgress({ current: 0, total: targetList.length });
      
      // 2. Loop and fetch pull_claim
      const allExtractedData = [];
      for (let i = 0; i < targetList.length; i++) {
        const item = targetList[i];
        let extraEklaimData = {};
        try {
          const resPull = await fetch(`${apiUrl}/pull_claim_ncc`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ no_sep: item.no_sep, ...getEklaimParams() })
          });
          const pullData = await resPull.json();
          if (pullData.status === 1 && pullData.data && typeof pullData.data === 'object') {
            extraEklaimData = pullData.data;
          }
        } catch (e) {
          console.error("Error pull_claim for", item.no_sep, e);
        }

        allExtractedData.push({
          "Nomor SEP": item.no_sep,
          "Nomor RM": item.no_medrec,
          "Nama Pasien": item.nm_pasien,
          "Tanggal Masuk": item.tgl_masuk,
          "Tanggal Pulang": item.tgl_pulang,
          "Dokter DPJP": item.nm_dokter,
          "Kode iDRG": item.kd_idrg,
          "Deskripsi iDRG": item.nm_idrg,
          "Tarif RS": item.total,
          ...extraEklaimData
        });

        setExportProgress({ current: i + 1, total: targetList.length });
      }

      if (allExtractedData.length === 0) {
        notify('warning', 'Export Batal', 'Tidak ada data untuk diexport.');
        setIsExporting(false);
        return;
      }

      // 3. Send to backend to generate Excel
      const resExcel = await fetch(`${apiUrl}/generate_excel_ncc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ export_data: allExtractedData })
      });
      if (resExcel.ok) {
        const blob = await resExcel.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Export_NCC_${exportStartDate}_sd_${exportEndDate}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        notify('success', 'Export Berhasil', 'File Excel NCC berhasil di-generate dan didownload.');
      } else {
        notify('danger', 'Export Gagal', 'Gagal men-generate file Excel.');
      }
    } catch (err) {
      notify('danger', 'Kesalahan Sistem', 'Terjadi kesalahan sistem: ' + err.message);
    }
    setIsExporting(false);
    setShowExportModal(false);
  };

  // ==========================================
  // FILTER & PAGINATION
  // ==========================================
  // Helper identifikasi Rawat Jalan vs Rawat Inap
  const isRawatJalanItem = (item) => {
    return String(item?.no_nota || '').toUpperCase().startsWith('RJ') || item?.asal === 'POLI';
  };

  // List pasien berdasarkan tab yang aktif (Rawat Inap / Rawat Jalan / Semua)
  const currentTabList = useMemo(() => {
    if (activeTabRawat === 'RI') {
      return claimList.filter(item => !isRawatJalanItem(item));
    } else if (activeTabRawat === 'RJ') {
      return claimList.filter(item => isRawatJalanItem(item));
    }
    return claimList;
  }, [claimList, activeTabRawat]);

  const countRI = useMemo(() => {
    return claimList.filter(x => !isRawatJalanItem(x)).length;
  }, [claimList]);

  const countRJ = useMemo(() => {
    return claimList.filter(x => isRawatJalanItem(x)).length;
  }, [claimList]);

  const filteredData = useMemo(() => {
    return currentTabList.filter((item) => {
      const q = searchQuery.toLowerCase();
      const searchMatch = 
        (item.nm_pasien && item.nm_pasien.toLowerCase().includes(q)) ||
        (item.no_medrec && item.no_medrec.toLowerCase().includes(q)) ||
        (item.no_sep && item.no_sep.toLowerCase().includes(q)) ||
        (item.no_nota && item.no_nota.toLowerCase().includes(q)) ||
        (item.kd_klas && String(item.kd_klas).toLowerCase().includes(q)) ||
        (item.nm_klas && String(item.nm_klas).toLowerCase().includes(q)) ||
        (item.diagnosa && item.diagnosa.toLowerCase().includes(q));
      
      let idrgMatch = true;
      if (filterIdrg === 'sudah') {
        idrgMatch = item.kd_idrg && item.kd_idrg !== '-' && item.kd_idrg !== 'null';
      } else if (filterIdrg === 'belum') {
        idrgMatch = !item.kd_idrg || item.kd_idrg === '-' || item.kd_idrg === 'null';
      }

      return searchMatch && idrgMatch;
    });
  }, [currentTabList, searchQuery, filterIdrg]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirst, indexOfLast);

  // Helper untuk mendapatkan ikon menu yang sesuai
  const getMenuIcon = (menuName) => {
    const name = menuName.toLowerCase();
    if (name.includes('dashboard')) return 'bi-speedometer2';
    if (name.includes('kamar')) return 'bi-hospital';
    if (name.includes('dokter')) return 'bi-person-badge';
    if (name.includes('pasien')) return 'bi-people';
    if (name.includes('tarif')) return 'bi-cash-stack';
    if (name.includes('icd')) return 'bi-journal-medical';
    if (name.includes('tagihan')) return 'bi-receipt';
    if (name.includes('laporan')) return 'bi-file-earmark-bar-graph';
    if (name.includes('klaim')) return 'bi-shield-check';
    return 'bi-folder2';
  };

  // Helper Format Tanggal ke ISO YYYY-MM-DD
  const formatDateToIso = (dateStr) => {
    if (!dateStr) return '';
    const s = String(dateStr).trim();
    if (s.includes('/')) {
      const parts = s.split('/');
      if (parts.length === 3) {
        const [d, m, y] = parts;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }
    }
    if (s.includes('-')) {
      const parts = s.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        return s;
      }
    }
    return s;
  };

  // Quick Preset Tanggal Filter Dialog
  const handleSetPresetDate = (type) => {
    const today = getTodayYmd();
    if (type === 'today') {
      setFilterDialog(prev => ({
        ...prev,
        periodeTglAwal: today,
        periodeTglAkhir: today,
        groupingTglAwal: today,
        groupingTglAkhir: today
      }));
    } else if (type === 'thisMonth') {
      const now = new Date();
      const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
      setFilterDialog(prev => ({
        ...prev,
        periodeTglAwal: firstDay,
        periodeTglAkhir: today,
        groupingTglAwal: firstDay,
        groupingTglAkhir: today
      }));
    } else if (type === 'reset') {
      setFilterDialog({
        jenisRawat: 'Semua',
        periodeTipe: 'Waktu Pulang',
        periodeTglAwal: getSevenDaysAgoYmd(),
        periodeJamAwal: '00:00',
        periodeTglAkhir: getTodayYmd(),
        periodeJamAkhir: '23:59',
        metodePembayaran: 'Semua',
        kelasRawat: 'Semua Kelas',
        caraPulang: 'Semua',
        severity: 'Semua',
        jenisTarif: 'TARIF RS KELAS C SWASTA',
        cob: 'Semua',
        groupingTglAwal: getSevenDaysAgoYmd(),
        groupingJamAwal: '00:00',
        groupingTglAkhir: getTodayYmd(),
        groupingJamAkhir: '23:59'
      });
    }
  };

  // Handler Ekspor Rekap Excel E-Klaim
  const handleExportEklaimExcel = async (filterOverride = null) => {
    setIsExportingExcel(true);
    setExportExcelStatusMsg('Menyusun Berkas Excel E-Klaim...');
    const activeFilter = filterOverride || filterDialog;
    const apiUrl = getApiUrl() || 'http://localhost:8000';
    const token = localStorage.getItem('token');

    try {
      let targetList = [...claimList];

      if (filterOverride) {
        if (activeFilter.jenisRawat === 'RI' || activeFilter.jenisRawat === 'Rawat Inap') {
          targetList = targetList.filter(x => !isRawatJalanItem(x));
        } else if (activeFilter.jenisRawat === 'RJ' || activeFilter.jenisRawat === 'Rawat Jalan') {
          targetList = targetList.filter(x => isRawatJalanItem(x));
        }

        if (activeFilter.kelasRawat && activeFilter.kelasRawat !== 'Semua Kelas' && activeFilter.kelasRawat !== 'Semua') {
          const kSearch = activeFilter.kelasRawat.toLowerCase().replace('kelas', '').trim();
          targetList = targetList.filter(x => {
            const nm = String(x.nm_klas || '').toLowerCase();
            const kd = String(x.kd_klas || '').toLowerCase();
            return nm.includes(kSearch) || kd === kSearch;
          });
        }
      } else {
        targetList = filteredData.length > 0 ? filteredData : currentTabList;
      }

      if (targetList.length === 0) {
        notify('warning', 'Pemberitahuan', 'Tidak ada data klaim yang sesuai filter untuk diekspor.');
        setIsExportingExcel(false);
        return;
      }

      const mappedData = targetList.map(item => ({
        tgl_masuk: formatDateToIso(item.tgl_masuk),
        tgl_pulang: formatDateToIso(item.tgl_pulang || item.tgl_masuk),
        no_medrec: item.no_medrec || item.no_rm || '',
        nm_pasien: item.nm_pasien || item.nama_pasien || '',
        no_sep: item.no_sep || '',
        inacbg: item.kd_cbg && item.kd_cbg !== '-' ? item.kd_cbg : (item.kd_idrg && item.kd_idrg !== '-' ? item.kd_idrg : '-'),
        top_up: item.top_up || '',
        total_tarif: parseFloat(item.tarif_inacbg || item.tarif_inadrg || item.total_tarif || 0),
        tarif_rs: parseFloat(item.total || item.tarif_rs || 0),
        jenis: isRawatJalanItem(item) ? 'RJ' : 'RI'
      }));

      const payload = {
        periode1: activeFilter.periodeTglAwal || periode1,
        periode2: activeFilter.periodeTglAkhir || periode2,
        jenis_rawat: activeFilter.jenisRawat === 'Semua' ? 'semua' : activeFilter.jenisRawat,
        nama_rs: 'RSU MARY CILEUNGSIH HIJAU',
        kode_rs: '3201152',
        jenis_tarif: activeFilter.jenisTarif || 'TARIF RS KELAS C SWASTA',
        kelas_rawat: activeFilter.kelasRawat || 'Semua',
        severity: activeFilter.severity || 'Semua',
        cara_pulang: activeFilter.caraPulang || 'Semua',
        data: mappedData
      };

      const res = await fetch(`${apiUrl}/rekap_eklaim_excel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      const resData = await res.json();

      if (res.ok && resData.status === 1 && resData.download_url) {
        // Unduh otomatis tanpa membuka tab browser baru
        const fileUrl = `${apiUrl}${resData.download_url.startsWith('/') ? '' : '/'}${resData.download_url}`;
        const downloadLink = document.createElement('a');
        downloadLink.href = fileUrl;
        downloadLink.setAttribute('download', resData.filename || `Rekap_Klaim_Eklaim_${activeFilter.periodeTglAwal}_${activeFilter.periodeTglAkhir}.xlsx`);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();

        notify(
          'success',
          'Ekspor Excel Berhasil',
          `Berkas Excel E-Klaim berhasil diunduh (${resData.count || mappedData.length} baris klaim).`
        );
        setShowFilterLaporanModal(false);
      } else {
        notify('danger', 'Ekspor Gagal', resData.msg || resData.message || 'Gagal menyusun berkas Excel E-Klaim.');
      }
    } catch (err) {
      console.error('Error export eklaim excel:', err);
      notify('danger', 'Kesalahan Sistem', 'Terjadi gangguan saat menghubungi server: ' + err.message);
    } finally {
      setIsExportingExcel(false);
      setExportExcelStatusMsg('');
    }
  };

  // ==========================================
  // RENDER
  // ==========================================
  const isIdrgGrouperLocked = Boolean(idrgStatus.isGrouped || idrgStatus.isFinal || claimFinalStatus);

  return (
    <div style={styles.appContainer}>
      {/* ══════════════ NAVBAR ══════════════ */}
      <nav className="navbar navbar-expand-xl navbar-dark shadow-sm" style={styles.navbarCustom}>
        <Container fluid>
          <a className="navbar-brand d-flex align-items-center me-3" href="#home" onClick={(e) => { e.preventDefault(); navigate('/home'); }}>
            <div className="d-flex align-items-center justify-content-center me-2 rounded bg-white bg-opacity-10 p-1">
              <i className="bi bi-shield-check text-warning fs-4"></i>
            </div>
            <div>
              <div className="fw-bold text-white lh-1" style={{ fontSize: '0.95rem' }}>SIMRS2 <span className="text-teal" style={{ fontSize: '0.78rem' }}>Finance</span></div>
              <div className="text-white-50 small" style={{ fontSize: '0.62rem' }}>Data Klaim Pasien RI</div>
            </div>
          </a>

          <button 
            className="navbar-toggler" 
            type="button" 
            onClick={() => setIsNavOpen(!isNavOpen)}
            style={{ border: 'none', outline: 'none' }}
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className={`collapse navbar-collapse ${isNavOpen ? 'show' : ''}`} id="navbarNav">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0 flex-row gap-2">
              {menus.map((item) => {
                const isActive = location.pathname.toLowerCase() === (item.lnk || '').toLowerCase();
                return (
                  <li className="nav-item" key={item.id_mn}>
                    <a
                      className={`nav-link-custom ${isActive ? 'active' : ''}`}
                      href={`#${(item.des_mn || '').toLowerCase().replace(/s+/g, '-')}`}
                      onClick={(e) => { 
                        e.preventDefault();
                        setActiveMenu(item.id_mn); 
                        setIsNavOpen(false);
                        if(item.lnk) navigate(item.lnk);
                      }}
                      style={{ ...styles.navLinkCustom, ...(isActive ? styles.navLinkActive : {}) }}
                    >
                      <i className={`bi ${getMenuIcon(item.des_mn || '')}`}></i>
                      {(item.des_mn || '').toLowerCase().replace(/bw/g, s => s.toUpperCase())}
                    </a>
                  </li>
                );
              })}
            </ul>

            <div className="d-flex align-items-center text-white text-end gap-3 ms-auto">
              <div className="small border-end pe-3 border-light border-opacity-25 d-none d-lg-block">
                <div className="fw-semibold text-white-50" style={{ fontSize: '0.73rem' }}>{time.date}</div>
                <div className="fw-bold text-teal" style={{ fontSize: '0.78rem' }}>{time.clock}</div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="rounded-circle bg-white bg-opacity-25 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                  <i className="bi bi-person-fill text-white"></i>
                </div>
                <div className="text-start d-none d-md-block">
                  <div className="fw-bold lh-1" style={{ fontSize: '0.8rem' }}>{username}</div>
                  <div className="text-white-50" style={{ fontSize: '0.65rem' }}>Administrator</div>
                </div>
              </div>
              <Button
                variant="warning"
                size="sm"
                className="fw-semibold text-dark shadow-sm d-flex align-items-center gap-1"
                style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', borderRadius: '6px' }}
                onClick={() => navigate('/home')}
              >
                <i className="bi bi-house-door-fill"></i> <span className="d-none d-sm-inline">Kembali ke Home</span>
              </Button>
            </div>
          </div>
        </Container>
      </nav>

      {/* ══════════════ CORE MAIN CONTENT ══════════════ */}
      <div style={styles.mainContent} className="d-flex flex-column">
        <Container fluid className="px-3 py-3 flex-grow-1 d-flex flex-column">
          
          <Card className="shadow-sm border-0 flex-grow-1 d-flex flex-column" style={{ borderRadius: '12px', overflow: 'hidden' }}>
            {/* Header & Filter Card */}
            <Card.Header className="border-bottom py-2 px-3" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' }}>
              <Row className="align-items-center gy-2">
                <Col xl={4} lg={4} md={12}>
                  <div className="d-flex align-items-center gap-2">
                    <div className="bg-white text-primary p-2 rounded shadow-sm border border-primary border-opacity-10">
                      <i className="bi bi-shield-check" style={{ fontSize: '1.2rem' }}></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem', letterSpacing: '0.5px', color: '#0f172a' }}>
                        Data Klaim Pasien BPJS
                      </h6>
                      <div className="text-secondary fw-semibold" style={{ fontSize: '0.68rem', marginTop: '1px' }}>
                        Monitoring dan validasi klaim pasien BPJS (Rawat Inap &amp; Rawat Jalan)
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Filter Tanggal & Pencarian Nomor SEP */}
                <Col xl={8} lg={8} md={12}>
                  <div className="d-flex flex-wrap gap-2 justify-content-lg-end align-items-center">
                    {/* Pencarian Khusus Nomor SEP */}
                    <div className="d-flex align-items-center bg-white rounded border shadow-sm px-1 py-0" style={{ maxWidth: '240px' }}>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-transparent border-0 text-muted px-2 py-0" style={{ fontSize: '0.72rem' }}>
                          <i className="bi bi-card-text"></i>
                        </InputGroup.Text>
                        <Form.Control
                          placeholder="Cari No. SEP..."
                          value={searchSepInput}
                          onChange={(e) => setSearchSepInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSearchBySep();
                            }
                          }}
                          className="border-0 bg-transparent shadow-none p-0 text-dark"
                          style={{ fontSize: '0.72rem' }}
                          title="Ketikkan Nomor SEP lengkap atau sebagian lalu tekan Enter atau klik Cari"
                        />
                        {searchSepInput && (
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-muted p-0 me-1"
                            style={{ textDecoration: 'none', fontSize: '0.85rem', lineHeight: 1 }}
                            onClick={() => {
                              setSearchSepInput('');
                              if (hasLoadedData) fetchDataClaim({ no_sep: '' });
                            }}
                            title="Reset pencarian SEP"
                          >
                            &times;
                          </button>
                        )}
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={handleSearchBySep}
                          disabled={isLoading || !searchSepInput.trim()}
                          className="py-0 px-2 fw-semibold"
                          style={{ fontSize: '0.68rem', borderRadius: '4px', height: '24px', margin: 'auto 0' }}
                          title="Cari Pasien Berdasarkan Nomor SEP"
                        >
                          Cari SEP
                        </Button>
                      </InputGroup>
                    </div>

                    {/* Filter Periode Tanggal */}
                    <div className="d-flex align-items-center gap-2 bg-light px-2 py-1 rounded border">
                      <i className="bi bi-calendar3 text-muted" style={{ fontSize: '0.7rem' }}></i>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={periode1}
                        onChange={(e) => handlePeriode1Change(e.target.value)}
                        className="border-0 bg-transparent shadow-none p-0 text-dark"
                        style={{ fontSize: '0.72rem', width: '95px', cursor: 'pointer' }}
                        title="Pilih Tanggal Pulang Awal"
                      />
                      <span className="text-muted" style={{ fontSize: '0.7rem' }}>s/d</span>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={periode2}
                        onChange={(e) => handlePeriode2Change(e.target.value)}
                        className="border-0 bg-transparent shadow-none p-0 text-dark"
                        style={{ fontSize: '0.72rem', width: '95px', cursor: 'pointer' }}
                        title="Pilih Tanggal Pulang Akhir"
                      />
                    </div>

                    <Button 
                      size="sm" 
                      variant="dark" 
                      onClick={() => {
                        setSearchSepInput('');
                        fetchDataClaim({ periode1, periode2, no_sep: '' });
                      }}
                      disabled={isLoading}
                      className="d-flex align-items-center fw-semibold shadow-sm"
                      style={{ padding: '0.3rem 0.75rem', fontSize: '0.72rem', borderRadius: '6px' }}
                      title="Tampilkan data klaim pasien sesuai periode tanggal terpilih"
                    >
                      {isLoading ? <Spinner as="span" animation="border" size="sm" className="me-2" /> : <i className="bi bi-calendar-check me-1"></i>}
                      Tampilkan Data
                    </Button>

                    <Button 
                      size="sm" 
                      variant="info" 
                      onClick={() => window.open((getApiUrl() || 'http://localhost:8000') + '/download_panduan', '_blank')}
                      className="d-flex align-items-center fw-semibold text-white shadow-sm"
                      style={{ padding: '0.3rem 0.75rem', fontSize: '0.72rem', borderRadius: '6px' }}
                    >
                      <i className="bi bi-file-earmark-word-fill me-1"></i>
                      Panduan E-Klaim
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Header>

            {/* Nav Tabs Pemisah Rawat Inap & Rawat Jalan */}
            <div className="bg-white border-bottom px-3 pt-2">
              <ul className="nav nav-tabs border-0 gap-2" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link border-0 d-flex align-items-center gap-2 py-2 px-3 rounded-top ${activeTabRawat === 'RI' ? 'active bg-primary text-white shadow-sm' : 'text-secondary bg-light'}`}
                    onClick={() => { setActiveTabRawat('RI'); setCurrentPage(1); }}
                    style={{ cursor: 'pointer', transition: 'all 0.2s', borderBottom: activeTabRawat === 'RI' ? 'none' : '1px solid #dee2e6' }}
                  >
                    <i className="bi bi-hospital fs-6"></i>
                    <span>Rawat Inap (RI)</span>
                    <Badge bg={activeTabRawat === 'RI' ? 'light' : 'primary'} text={activeTabRawat === 'RI' ? 'dark' : 'white'} pill className="ms-1" style={{ fontSize: '0.68rem' }}>
                      {countRI}
                    </Badge>
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link border-0 d-flex align-items-center gap-2 py-2 px-3 rounded-top ${activeTabRawat === 'RJ' ? 'active bg-success text-white shadow-sm' : 'text-secondary bg-light'}`}
                    onClick={() => { setActiveTabRawat('RJ'); setCurrentPage(1); }}
                    style={{ cursor: 'pointer', transition: 'all 0.2s', borderBottom: activeTabRawat === 'RJ' ? 'none' : '1px solid #dee2e6' }}
                  >
                    <i className="bi bi-person-walking fs-6"></i>
                    <span>Rawat Jalan (RJ)</span>
                    <Badge bg={activeTabRawat === 'RJ' ? 'light' : 'success'} text={activeTabRawat === 'RJ' ? 'dark' : 'white'} pill className="ms-1" style={{ fontSize: '0.68rem' }}>
                      {countRJ}
                    </Badge>
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link border-0 d-flex align-items-center gap-2 py-2 px-3 rounded-top ${activeTabRawat === 'ALL' ? 'active bg-dark text-white shadow-sm' : 'text-secondary bg-light'}`}
                    onClick={() => { setActiveTabRawat('ALL'); setCurrentPage(1); }}
                    style={{ cursor: 'pointer', transition: 'all 0.2s', borderBottom: activeTabRawat === 'ALL' ? 'none' : '1px solid #dee2e6' }}
                  >
                    <i className="bi bi-people fs-6"></i>
                    <span>Semua Data</span>
                    <Badge bg={activeTabRawat === 'ALL' ? 'light' : 'secondary'} text={activeTabRawat === 'ALL' ? 'dark' : 'white'} pill className="ms-1" style={{ fontSize: '0.68rem' }}>
                      {claimList.length}
                    </Badge>
                  </button>
                </li>
              </ul>
            </div>

            {/* Body Card */}
            <Card.Body className="p-0 d-flex flex-column bg-white">
              
              {/* Summary Badges */}
              <div className="bg-light px-3 py-2 border-bottom d-flex gap-3 overflow-auto custom-scrollbar" style={{ fontSize: '0.72rem' }}>
                <div className="d-flex align-items-center gap-2 pe-3 border-end border-secondary border-opacity-25">
                  <div className="text-muted fw-medium"><i className="bi bi-people-fill me-1"></i>Total Pasien:</div>
                  <Badge bg="dark" className="fw-semibold px-2 py-1 shadow-sm">{currentTabList.length}</Badge>
                </div>
                <div className="d-flex align-items-center gap-2 pe-3 border-end border-secondary border-opacity-25">
                  <div className="text-muted fw-medium"><i className="bi bi-check-circle-fill text-success me-1"></i>Sudah SEP:</div>
                  <Badge bg="success" className="fw-semibold px-2 py-1 shadow-sm">
                    {currentTabList.filter(x => !isInvalidSep(x.no_sep)).length}
                  </Badge>
                </div>
                <div className="d-flex align-items-center gap-2 pe-3 border-end border-secondary border-opacity-25">
                  <div className="text-muted fw-medium"><i className="bi bi-exclamation-circle-fill text-warning me-1"></i>Belum SEP:</div>
                  <Badge bg="warning" text="dark" className="fw-semibold px-2 py-1 shadow-sm">
                    {currentTabList.filter(x => isInvalidSep(x.no_sep)).length}
                  </Badge>
                </div>
                <div className="d-flex align-items-center gap-2 pe-3 border-end border-secondary border-opacity-25">
                  <div className="text-muted fw-medium"><i className="bi bi-gear-fill text-info me-1"></i>Sudah iDRG:</div>
                  <Badge bg="info" className="fw-semibold px-2 py-1 shadow-sm text-white">
                    {currentTabList.filter(x => x.kd_idrg && x.kd_idrg !== '-' && x.kd_idrg !== 'null').length}
                  </Badge>
                </div>
                <div className="d-flex align-items-center gap-2 pe-3 border-end border-secondary border-opacity-25">
                  <div className="text-muted fw-medium"><i className="bi bi-gear text-danger me-1"></i>Belum iDRG:</div>
                  <Badge bg="danger" className="fw-semibold px-2 py-1 shadow-sm text-white">
                    {currentTabList.filter(x => !x.kd_idrg || x.kd_idrg === '-' || x.kd_idrg === 'null').length}
                  </Badge>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="text-muted fw-medium"><i className="bi bi-cash-stack text-primary me-1"></i>Estimasi Tagihan:</div>
                  <Badge bg="primary" className="fw-semibold px-2 py-1 shadow-sm text-white">
                    {formatRp(currentTabList.reduce((acc, curr) => acc + (parseFloat(curr.total) || 0), 0))}
                  </Badge>
                </div>
                <div className="d-flex align-items-center ms-auto gap-2">
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="fw-bold d-flex align-items-center shadow-sm"
                    style={{ padding: '0.25rem 0.65rem', fontSize: '0.72rem' }}
                    onClick={() => {
                      setFilterDialog(prev => ({
                        ...prev,
                        periodeTglAwal: periode1,
                        periodeTglAkhir: periode2,
                        groupingTglAwal: periode1,
                        groupingTglAkhir: periode2,
                        jenisRawat: activeTabRawat === 'RI' ? 'RI' : activeTabRawat === 'RJ' ? 'RJ' : 'Semua'
                      }));
                      setShowFilterLaporanModal(true);
                    }}
                    title="Buka dialog filter dan opsi Laporan Klaim Individual"
                  >
                    <i className="bi bi-sliders me-1"></i> Laporan Klaim Individual
                  </Button>
                  <Button 
                    variant="success" 
                    size="sm" 
                    className="fw-bold d-flex align-items-center shadow-sm text-white"
                    style={{ padding: '0.25rem 0.65rem', fontSize: '0.72rem', background: '#10b981', borderColor: '#10b981' }}
                    onClick={() => handleExportEklaimExcel()}
                    disabled={isExportingExcel}
                    title="Ekspor rekap klaim saat ini langsung ke format baku E-Klaim"
                  >
                    {isExportingExcel ? (
                      <><Spinner size="sm" animation="border" className="me-1" /> Mengekspor...</>
                    ) : (
                      <><i className="bi bi-file-earmark-excel-fill me-1"></i> Ekspor Excel E-Klaim</>
                    )}
                  </Button>
                  <Button 
                    variant="outline-info" 
                    size="sm" 
                    className="fw-bold d-flex align-items-center shadow-sm"
                    style={{ padding: '0.25rem 0.65rem', fontSize: '0.72rem', borderColor: '#0d9488', color: '#0f766e', background: '#f0fdfa' }}
                    onClick={() => navigate('/BillingGroup')}
                    title="Buka Master Mapping Template Billing Group E-Klaim (Kemenkes)"
                  >
                    <i className="bi bi-file-earmark-spreadsheet me-1" style={{ color: '#0d9488' }}></i> Template Billing Group
                  </Button>
                  <Button 
                    variant="success" 
                    size="sm" 
                    className="fw-bold d-flex align-items-center shadow-sm"
                    style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem', background: '#059669', border: 'none' }}
                    onClick={() => setShowExportModal(true)}
                  >
                    <i className="bi bi-file-earmark-excel-fill me-1"></i> Export NCC
                  </Button>
                </div>
              </div>

              <div className="px-3 py-2 border-bottom bg-light bg-opacity-50 d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div style={{ width: '280px', maxWidth: '100%' }}>
                  <InputGroup size="sm">
                    <InputGroup.Text className="bg-white border-end-0 text-muted px-2">
                      <i className="bi bi-search" style={{ fontSize: '0.75rem' }}></i>
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Cari No. RM, SEP, Nota, Nama..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="border-start-0 bg-white focus-ring-none"
                      style={{ fontSize: '0.72rem', boxShadow: 'none' }}
                    />
                  </InputGroup>
                </div>
                
                <div className="d-flex align-items-center gap-2">
                  <Badge bg="secondary" className="text-white fw-medium shadow-sm" style={{ fontSize: '0.68rem', padding: '0.35rem 0.6rem', borderRadius: '6px', background: '#475569' }}>
                    {filteredData.length} Data Klaim
                  </Badge>
                  <Form.Select
                    size="sm"
                    value={filterIdrg}
                    onChange={e => { setFilterIdrg(e.target.value); setCurrentPage(1); }}
                    className="shadow-sm border"
                    style={{ fontSize: '0.72rem', width: '130px', cursor: 'pointer' }}
                  >
                    <option value="all">Semua iDRG</option>
                    <option value="sudah">Sudah iDRG</option>
                    <option value="belum">Belum iDRG</option>
                  </Form.Select>
                  <Form.Select
                    size="sm"
                    value={itemsPerPage}
                    onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="shadow-sm border"
                    style={{ fontSize: '0.72rem', width: '100px', cursor: 'pointer' }}
                  >
                    <option value={10}>10 Baris</option>
                    <option value={20}>20 Baris</option>
                    <option value={50}>50 Baris</option>
                    <option value={100}>100 Baris</option>
                  </Form.Select>
                </div>
              </div>

              {fetchError && (
                <div className="alert alert-warning m-3 py-2 px-3" style={{ fontSize: '0.75rem' }}>
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>{fetchError}
                </div>
              )}

              {/* ── Table Container ── */}
              <div className="table-responsive flex-grow-1 custom-scrollbar" style={{ overflowY: 'auto' }}>
                <Table hover striped bordered size="sm" className="align-middle mb-0 table-compact" style={{ fontSize: '0.72rem' }}>
                  <thead style={{ background: '#0f172a', color: '#fff', position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr>
                      <th className="text-center align-middle" rowSpan={2} style={{ width: '50px' }}>No</th>
                      <th className="text-center align-middle" rowSpan={2} style={{ width: '90px' }}>Tgl. Masuk</th>
                      <th className="text-center align-middle" rowSpan={2} style={{ width: '90px' }}>Tgl. Pulang</th>
                      <th className="text-center align-middle" rowSpan={2} style={{ width: '150px' }}>No. SEP</th>
                      <th className="align-middle" rowSpan={2} style={{ minWidth: '170px' }}>Pasien</th>
                      <th className="text-center border-bottom border-light border-opacity-25" colSpan={2}>INACBG</th>
                      <th className="text-center border-bottom border-light border-opacity-25" colSpan={2}>iDRG</th>
                      <th className="text-end align-middle" rowSpan={2} style={{ width: '115px' }}>Billing RS</th>
                      <th className="text-center align-middle" rowSpan={2} style={{ width: '100px' }}>Rawat</th>
                      <th className="align-middle" rowSpan={2} style={{ width: '95px' }}>Petugas</th>
                      <th className="text-center align-middle" rowSpan={2} style={{ width: '115px' }}>Aksi</th>
                    </tr>
                    <tr>
                      <th className="text-center" style={{ width: '80px', fontSize: '0.68rem' }}>Kode</th>
                      <th className="text-end" style={{ width: '105px', fontSize: '0.68rem' }}>Tarif Total</th>
                      <th className="text-center" style={{ width: '80px', fontSize: '0.68rem' }}>Kode</th>
                      <th className="text-end" style={{ width: '105px', fontSize: '0.68rem' }}>Tarif Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="13" className="text-center py-5 text-muted">
                          <Spinner animation="border" variant="teal" className="mb-2" />
                          <div>Memuat data klaim pasien...</div>
                        </td>
                      </tr>
                    ) : currentItems.length > 0 ? (
                      currentItems.map((item, idx) => (
                        <React.Fragment key={idx}>
                          <tr 
                            style={{ cursor: 'pointer', backgroundColor: expandedRow === idx ? '#f8fafc' : 'inherit' }}
                            onClick={() => toggleRow(idx)}
                          >
                            {/* No */}
                            <td className="text-center align-middle">
                              <div className="d-flex align-items-center justify-content-center gap-1">
                                <Button 
                                  variant="link" 
                                  size="sm" 
                                  className="p-0 text-decoration-none text-muted"
                                >
                                  <i className={`bi ${expandedRow === idx ? 'bi-chevron-up text-primary' : 'bi-chevron-down'}`} style={{ fontSize: '0.7rem' }}></i>
                                </Button>
                                <span className="text-muted">{indexOfFirst + idx + 1}</span>
                              </div>
                            </td>

                            {/* Tgl. Masuk */}
                            <td className="text-center align-middle font-monospace" style={{ fontSize: '0.72rem' }}>
                              {item.tgl_masuk || '-'}
                            </td>

                            {/* Tgl. Pulang */}
                            <td className="text-center align-middle font-monospace" style={{ fontSize: '0.72rem' }}>
                              {item.tgl_pulang || '-'}
                            </td>
                            
                            {/* No. SEP */}
                            <td className="align-middle">
                              <div className="font-monospace text-primary fw-bold" style={{ fontSize: '0.72rem' }}>
                                {item.no_sep || item.nrp || '-'}
                              </div>
                              <div className="d-flex align-items-center gap-1 text-muted mt-1" style={{ fontSize: '0.65rem' }}>
                                {isRawatJalanItem(item) ? (
                                  <Badge bg="success" className="px-1 py-0 fw-semibold" style={{ fontSize: '0.58rem' }}>RJ</Badge>
                                ) : (
                                  <Badge bg="primary" className="px-1 py-0 fw-semibold" style={{ fontSize: '0.58rem' }}>RI</Badge>
                                )}
                                <span>{item.no_nota || '-'}</span>
                              </div>
                            </td>

                            {/* Pasien */}
                            <td className="align-middle">
                              <div className="fw-bold text-dark" style={{ fontSize: '0.78rem' }}>{item.nm_pasien || item.nama}</div>
                              <div className="font-monospace text-muted" style={{ fontSize: '0.68rem' }}>
                                RM: <strong className="text-dark">{item.no_medrec}</strong>
                              </div>
                            </td>

                            {/* INACBG - Kode */}
                            <td className="text-center align-middle">
                              <span className="font-monospace fw-bold text-dark" style={{ fontSize: '0.72rem' }}>
                                {item.inacbg || '-'}
                              </span>
                            </td>

                            {/* INACBG - Tarif Total */}
                            <td className="text-end align-middle font-monospace" style={{ fontSize: '0.72rem' }}>
                              {item.tarif_inacbg && parseFloat(item.tarif_inacbg) > 0 ? (
                                <span className="fw-semibold text-dark">{formatRp(item.tarif_inacbg)}</span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>

                            {/* iDRG - Kode */}
                            <td className="text-center align-middle">
                              <span className="font-monospace fw-bold text-primary" style={{ fontSize: '0.72rem' }}>
                                {item.kd_idrg || '-'}
                              </span>
                            </td>

                            {/* iDRG - Tarif Total */}
                            <td className="text-end align-middle font-monospace" style={{ fontSize: '0.72rem' }}>
                              {(item.tarif_inadrg || item.tarif_idrg) && parseFloat(item.tarif_inadrg || item.tarif_idrg) > 0 ? (
                                <span className="fw-semibold text-primary">{formatRp(item.tarif_inadrg || item.tarif_idrg)}</span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>

                            {/* Billing RS */}
                            <td className="text-end fw-bold text-success align-middle font-monospace" style={{ fontSize: '0.8rem' }}>
                              {formatRp(item.total)}
                            </td>

                            {/* Rawat */}
                            <td className="text-center align-middle">
                              <span className="badge bg-light text-dark border fw-medium" style={{ fontSize: '0.7rem' }}>
                                {item.nm_klas || (isRawatJalanItem(item) ? 'Rawat Jalan' : 'Rawat Inap')}
                              </span>
                            </td>

                            {/* Petugas */}
                            <td className="align-middle">
                              <div className="text-truncate text-muted fw-medium" style={{ fontSize: '0.7rem', maxWidth: '95px' }} title={item.usr_validasi || item.petugas || item.kasir}>
                                {item.usr_validasi || item.petugas || item.kasir || '-'}
                              </div>
                            </td>

                            {/* Aksi */}
                            <td className="text-center align-middle" style={{ width: '115px' }} onClick={(e) => e.stopPropagation()}>
                              <div className="d-inline-flex align-items-center justify-content-center gap-1">
                                <Button
                                  size="sm"
                                  className="d-inline-flex align-items-center gap-1 fw-bold text-white shadow-xs"
                                  style={{
                                    background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '2px 6px',
                                    fontSize: '0.68rem'
                                  }}
                                  onClick={(e) => { e.stopPropagation(); handleOpenGrouper(item); }}
                                  title="Proses Grouper E-Claim"
                                >
                                  <i className="bi bi-cpu-fill" style={{ fontSize: '0.7rem' }}></i>
                                  <span>Grouper</span>
                                </Button>

                                <Dropdown align="end">
                                  <Dropdown.Toggle 
                                    variant="light" 
                                    size="sm" 
                                    className="border shadow-xs p-1 d-inline-flex align-items-center justify-content-center"
                                    style={{
                                      width: '24px',
                                      height: '24px',
                                      borderRadius: '6px',
                                      background: '#f8fafc',
                                      borderColor: '#cbd5e1',
                                      color: '#475569'
                                    }}
                                    title="Pilihan Aksi Lainnya"
                                  >
                                    <i className="bi bi-three-dots-vertical" style={{ fontSize: '0.72rem' }}></i>
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu 
                                    style={{ 
                                      zIndex: 1050, 
                                      fontSize: '0.75rem', 
                                      borderRadius: '10px', 
                                      padding: '6px', 
                                      minWidth: '185px' 
                                    }} 
                                    className="shadow-lg border-0"
                                  >
                                    {item.klaim_final && (
                                      <>
                                        <Dropdown.Item onClick={(e) => { e.stopPropagation(); handlePrintClaim(item.no_sep); }} className="fw-semibold text-dark rounded px-2 py-1">
                                          <i className="bi bi-printer-fill text-secondary me-2"></i>Cetak Klaim
                                        </Dropdown.Item>
                                        <Dropdown.Divider className="my-1" />
                                      </>
                                    )}
                                    <Dropdown.Item onClick={(e) => { e.stopPropagation(); handleOpenAction(item, 'status', 'Cek Status Klaim'); }} className="rounded px-2 py-1">
                                      <i className="bi bi-info-circle text-info me-2"></i>Cek Status Klaim
                                    </Dropdown.Item>
                                    <Dropdown.Item 
                                      onClick={(e) => { 
                                        e.stopPropagation(); 
                                        if (item.klaim_final) handleSendClaimOnline(item); 
                                      }} 
                                      disabled={!item.klaim_final || isSendingOnline}
                                      className={`rounded px-2 py-1 ${item.klaim_final ? 'text-primary fw-semibold' : 'text-muted'}`}
                                      style={{ cursor: item.klaim_final && !isSendingOnline ? 'pointer' : 'not-allowed', opacity: item.klaim_final ? 1 : 0.5 }}
                                      title={item.klaim_final ? "Kirim Klaim Online ke Data Center Kemenkes" : "Klaim belum final (Tombol nonaktif)"}
                                    >
                                      <i className={`bi bi-cloud-arrow-up-fill me-2 ${item.klaim_final ? 'text-primary' : 'text-secondary'}`}></i>Kirim Klaim Online
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={(e) => { e.stopPropagation(); handleOpenAction(item, 'reedit', 'Re-Edit Klaim (Buka Kunci)'); }} className="rounded px-2 py-1">
                                      <i className="bi bi-unlock text-warning me-2"></i>Re-Edit Klaim
                                    </Dropdown.Item>
                                    <Dropdown.Divider className="my-1" />
                                    <Dropdown.Item onClick={(e) => { e.stopPropagation(); handleOpenAction(item, 'hapus', 'Hapus Klaim (Batal)'); }} className="text-danger fw-semibold rounded px-2 py-1">
                                      <i className="bi bi-trash text-danger me-2"></i>Hapus Klaim
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded Detail Row */}
                          {expandedRow === idx && (
                            <tr style={{ backgroundColor: '#f8fafc' }}>
                              <td colSpan="13" className="p-0 border-bottom-0">
                                <div className="p-3 border-start border-4 border-primary m-2 bg-white rounded shadow-sm" style={{ fontSize: '0.72rem' }}>
                                  <Row className="gy-2">
                                    <Col md={4} className="border-end-md">
                                      <h6 className="fw-bold text-primary mb-2" style={{ fontSize: '0.75rem' }}><i className="bi bi-file-medical me-1"></i>Detail Medis</h6>
                                      <div className="mb-1"><span className="text-muted d-inline-block" style={{ width: '85px' }}>Diagnosa:</span><span className="fw-semibold">{item.diagnosa || '-'}</span></div>
                                      <div className="mb-1"><span className="text-muted d-inline-block" style={{ width: '85px' }}>Kelas Rawat:</span><span className="fw-semibold text-primary">{item.kd_klas ? `[${item.kd_klas}] ${item.nm_klas || ''}` : (item.nm_klas || '-')}</span></div>
                                      <div className="mb-1"><span className="text-muted d-inline-block" style={{ width: '85px' }}>Deskripsi iDrg:</span><span className="fw-semibold text-dark">{item.nm_idrg || '-'}</span></div>
                                      <div className="mb-1"><span className="text-muted d-inline-block" style={{ width: '85px' }}>Asal Poli:</span><Badge bg="secondary" style={{ fontSize: '0.65rem' }}>{item.asal || '-'}</Badge></div>
                                      <div className="mb-1"><span className="text-muted d-inline-block" style={{ width: '85px' }}>Dokter:</span><span className="fw-semibold">{item.nm_dokter || '-'}</span></div>
                                      <div className="mb-1"><span className="text-muted d-inline-block" style={{ width: '85px' }}>Perujuk:</span><span>{item.nm_perujuk || '-'}</span></div>
                                    </Col>
                                    <Col md={4} className="border-end-md">
                                      <h6 className="fw-bold text-teal mb-2" style={{ fontSize: '0.75rem' }}><i className="bi bi-calendar-range me-1"></i>Waktu Perawatan</h6>
                                      <div className="mb-1"><span className="text-muted d-inline-block" style={{ width: '70px' }}>Tgl Masuk:</span><span className="fw-semibold text-success"><i className="bi bi-box-arrow-in-right me-1"></i>{item.tgl_masuk || '-'}</span></div>
                                      <div className="mb-1"><span className="text-muted d-inline-block" style={{ width: '70px' }}>Tgl Pulang:</span><span className="fw-semibold text-danger"><i className="bi bi-box-arrow-right me-1"></i>{item.tgl_pulang || '-'}</span></div>
                                      <div className="mt-2 text-muted fst-italic">Klaim diproses oleh sistem SIMRS.</div>
                                    </Col>
                                    <Col md={4}>
                                      <h6 className="fw-bold text-dark mb-2" style={{ fontSize: '0.75rem' }}><i className="bi bi-check-circle me-1"></i>Validasi & Status Klaim</h6>
                                      <div className="mb-1"><span className="text-muted d-inline-block" style={{ width: '70px' }}>Tgl Tagih:</span><span className="fw-semibold">{item.tgl_tagih || '-'}</span></div>
                                      <div className="mb-1"><span className="text-muted d-inline-block" style={{ width: '70px' }}>Validasi:</span><span className="fw-semibold">{item.tgl_validasi || '-'} <span className="text-muted">({item.jamvalidasi || '-'})</span></span></div>
                                      <div className="mb-1"><span className="text-muted d-inline-block" style={{ width: '70px' }}>Petugas:</span><span className="fw-semibold text-primary">{item.usr_validasi || item.petugas || '-'}</span></div>
                                      <div className="mt-2 pt-2 border-top d-flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="outline-info"
                                          className="flex-fill fw-bold d-flex align-items-center justify-content-center gap-1 shadow-sm"
                                          style={{ fontSize: '0.68rem', padding: '3px 6px' }}
                                          onClick={(e) => { e.stopPropagation(); handleOpenAction(item, 'status', 'Cek Status Klaim'); }}
                                          title="Cek Status Klaim & Status DC Kemenkes"
                                        >
                                          <i className="bi bi-info-circle"></i> Status Klaim
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant={item.klaim_final ? "primary" : "secondary"}
                                          className="flex-fill fw-bold text-white d-flex align-items-center justify-content-center gap-1 shadow-sm"
                                          style={{ 
                                            background: item.klaim_final ? '#2563eb' : '#64748b', 
                                            border: 'none', 
                                            fontSize: '0.68rem', 
                                            padding: '3px 6px',
                                            cursor: item.klaim_final && !isSendingOnline ? 'pointer' : 'not-allowed',
                                            opacity: item.klaim_final && !isSendingOnline ? 1 : 0.65
                                          }}
                                          onClick={(e) => { e.stopPropagation(); handleSendClaimOnline(item); }}
                                          disabled={!item.klaim_final || isSendingOnline}
                                          title={item.klaim_final ? "Kirim Klaim ke Data Center Kemenkes (Poin 22)" : "Klaim belum final. Silakan lakukan Finalisasi Klaim terlebih dahulu."}
                                        >
                                          <i className="bi bi-cloud-arrow-up-fill"></i> Kirim Klaim Online
                                        </Button>
                                      </div>
                                    </Col>
                                    <Col xs={12} className="d-lg-none mt-3 pt-3 border-top" onClick={(e) => e.stopPropagation()}>
                                       <h6 className="fw-bold text-dark mb-2" style={{ fontSize: '0.75rem' }}><i className="bi bi-tools me-1"></i>Aksi Klaim</h6>
                                       <div className="d-flex justify-content-start align-items-center gap-2">
                                         <Button 
                                           size="sm" 
                                           className="d-inline-flex align-items-center gap-1 fw-bold text-white shadow-sm"
                                           style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', border: 'none', padding: '0.35rem 0.75rem', borderRadius: '6px', fontSize: '0.75rem' }}
                                           onClick={(e) => { e.stopPropagation(); handleOpenGrouper(item); }}
                                         >
                                           <i className="bi bi-cpu-fill"></i>
                                           <span>Proses Grouper</span>
                                         </Button>

                                         <Dropdown align="start">
                                           <Dropdown.Toggle 
                                             variant="light" 
                                             size="sm" 
                                             className="border shadow-sm d-inline-flex align-items-center justify-content-center"
                                             style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', background: '#f8fafc', borderColor: '#cbd5e1', color: '#475569', fontSize: '0.75rem' }}
                                           >
                                             <i className="bi bi-three-dots-vertical me-1"></i> Menu Lain
                                           </Dropdown.Toggle>
                                           <Dropdown.Menu 
                                             style={{ zIndex: 1050, fontSize: '0.75rem', borderRadius: '10px', padding: '6px', minWidth: '190px' }} 
                                             className="shadow-lg border-0"
                                           >
                                             {item.klaim_final && (
                                               <>
                                                 <Dropdown.Item onClick={(e) => { e.stopPropagation(); handlePrintClaim(item.no_sep); }} className="fw-semibold text-dark rounded px-2 py-1">
                                                   <i className="bi bi-printer-fill text-secondary me-2"></i>Cetak Dokumen Klaim
                                                 </Dropdown.Item>
                                                 <Dropdown.Divider className="my-1" />
                                               </>
                                             )}
                                             <Dropdown.Item onClick={(e) => { e.stopPropagation(); handleOpenAction(item, 'status', 'Cek Status Klaim'); }} className="rounded px-2 py-1"><i className="bi bi-info-circle text-info me-2"></i>Cek Status Klaim</Dropdown.Item>
                                             <Dropdown.Item 
                                               onClick={(e) => { 
                                                 e.stopPropagation(); 
                                                 if (item.klaim_final) handleSendClaimOnline(item); 
                                               }} 
                                               disabled={!item.klaim_final || isSendingOnline}
                                               className={`rounded px-2 py-1 ${item.klaim_final ? 'text-primary fw-semibold' : 'text-muted'}`}
                                               style={{ cursor: item.klaim_final && !isSendingOnline ? 'pointer' : 'not-allowed', opacity: item.klaim_final ? 1 : 0.5 }}
                                               title={item.klaim_final ? "Kirim Klaim ke Data Center Kemenkes" : "Klaim belum final (Tombol nonaktif)"}
                                             >
                                               <i className={`bi bi-cloud-arrow-up-fill me-2 ${item.klaim_final ? 'text-primary' : 'text-secondary'}`}></i>Kirim Klaim Online
                                             </Dropdown.Item>
                                             <Dropdown.Item onClick={(e) => { e.stopPropagation(); handleOpenAction(item, 'reedit', 'Re-Edit Klaim (Buka Kunci)'); }} className="rounded px-2 py-1"><i className="bi bi-unlock text-warning me-2"></i>Re-Edit Klaim</Dropdown.Item>
                                             <Dropdown.Divider className="my-1" />
                                             <Dropdown.Item onClick={(e) => { e.stopPropagation(); handleOpenAction(item, 'hapus', 'Hapus Klaim (Batal)'); }} className="text-danger fw-semibold rounded px-2 py-1"><i className="bi bi-trash text-danger me-2"></i>Hapus Klaim</Dropdown.Item>
                                           </Dropdown.Menu>
                                         </Dropdown>
                                       </div>
                                     </Col>
                                  </Row>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="13" className="text-center py-5 text-muted">
                          {!hasLoadedData ? (
                            <div className="py-4">
                              <div className="mb-2">
                                <i className="bi bi-inbox text-secondary" style={{ fontSize: '2.5rem', opacity: 0.4 }}></i>
                              </div>
                              <h6 className="fw-bold text-dark mb-1" style={{ fontSize: '0.85rem' }}>
                                Data Tabel Masih Kosong
                              </h6>
                              <div className="text-muted" style={{ fontSize: '0.74rem', maxWidth: '460px', margin: '0 auto' }}>
                                Silakan pilih <strong>Periode Tanggal</strong> dan klik <strong>"Tampilkan Data"</strong>, atau lakukan <strong>Pencarian Nomor SEP</strong> untuk memunculkan data pasien.
                              </div>
                            </div>
                          ) : searchQuery || searchSepInput ? (
                            'Tidak ada data klaim yang cocok dengan pencarian Anda.'
                          ) : (
                            'Tidak ada data klaim pasien di rentang tanggal ini.'
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-4 py-3 bg-white border-top d-flex justify-content-between align-items-center">
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Menampilkan {indexOfFirst + 1} - {Math.min(indexOfLast, filteredData.length)} dari {filteredData.length} klaim
                  </div>
                  <div className="d-flex gap-1">
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }}
                    >
                      <i className="bi bi-chevron-double-left"></i>
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(curr => curr - 1)}
                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </Button>
                    <div className="d-flex align-items-center px-2 fw-bold text-teal" style={{ fontSize: '0.75rem' }}>
                      {currentPage} / {totalPages}
                    </div>
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(curr => curr + 1)}
                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }}
                    >
                      <i className="bi bi-chevron-double-right"></i>
                    </Button>
                  </div>
                </div>
              )}

            </Card.Body>
          </Card>
        </Container>
      </div>

      {/* ══ MODAL EDIT NOMOR SEP ══ */}
      <Modal show={showEditSepModal} onHide={() => setShowEditSepModal(false)} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-pencil-square text-warning fs-5"></i>
            <span>Edit Nomor SEP Pasien</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          {selectedPatient && (
            <Form onSubmit={handleSaveSep}>
              <div className="alert alert-primary py-2 px-3 mb-3" style={{ fontSize: '0.75rem', borderRadius: '8px' }}>
                <div><strong>Nama Pasien:</strong> {selectedPatient.nm_pasien || selectedPatient.nama}</div>
                <div><strong>No. Medrec:</strong> {selectedPatient.no_medrec} | <strong>Asal Poli:</strong> {selectedPatient.asal}</div>
                <div><strong>Diagnosa:</strong> {selectedPatient.diagnosa}</div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.78rem' }}>
                  Nomor SEP BPJS Pasien
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Contoh: 0117R0750726V000102"
                  value={inputNoSep}
                  onChange={e => setInputNoSep(e.target.value)}
                  style={{ fontSize: '0.85rem', fontWeight: '600' }}
                  required
                />
                <Form.Text className="text-muted" style={{ fontSize: '0.68rem' }}>
                  Pastikan Nomor SEP Valid agar proses Grouper dapat berjalan.
                </Form.Text>
              </Form.Group>

              {sepFeedback && (
                <div className={`alert alert-${sepFeedback.type} py-1 px-2 mb-3`} style={{ fontSize: '0.73rem' }}>
                  <i className="bi bi-info-circle-fill me-1"></i>
                  {sepFeedback.text}
                </div>
              )}

              <div className="d-flex justify-content-end gap-2 border-top pt-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => setShowEditSepModal(false)} disabled={isSavingSep}>
                  Batal
                </Button>
                <Button type="submit" size="sm" variant="primary" className="fw-bold" disabled={isSavingSep}>
                  {isSavingSep ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-save me-1"></i>}
                  Simpan Nomor SEP
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>



      {/* ══ MODAL INPUT DIAGNOSA & PROSEDUR (SUB-MODAL) ══ */}
       <Modal show={showInputDiagnosaModal} onHide={() => { setShowInputDiagnosaModal(false); setShowIcd10Autocomplete(false); setShowIcd9Autocomplete(false); }} centered backdrop="static" size="lg">
        <Modal.Header closeButton style={{ background: inputDiagnosaType === 'diagnosa' ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'linear-gradient(135deg, #059669 0%, #047857 100%)', color: '#fff', padding: '10px 16px' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: '700', width: '100%' }} className="d-flex align-items-center gap-2">
            <i className={`bi ${inputDiagnosaType === 'diagnosa' ? 'bi-clipboard2-pulse-fill' : 'bi-activity'} fs-5`}></i>
            <span>{inputDiagnosaType === 'diagnosa' ? 'Input Diagnosa Pasien (ICD-10 IM)' : 'Input Prosedur Tindakan (ICD-9-CM IM)'}</span>
            <div className="d-flex gap-1 ms-auto">
              <Badge bg="light" className="text-dark" style={{ fontSize: '0.68rem', padding: '3px 8px', fontWeight: 700 }}>
                <i className={`bi ${inputDiagnosaTarget === 'inacbg' ? 'bi-hospital' : 'bi-cpu'} me-1`}></i>
                {inputDiagnosaTarget === 'inacbg' ? 'INACBG' : 'iDRG'}
              </Badge>
              {isLoadingMasterIcd && (
                <Badge bg="warning" text="dark" style={{ fontSize: '0.68rem', padding: '3px 8px' }}>
                  <Spinner animation="border" size="sm" className="me-1" style={{ width: '10px', height: '10px' }} />
                  Memuat Master...
                </Badge>
              )}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 bg-light">
          <Form onSubmit={handleSaveInputDiagnosa}>

            {/* ── Info Pasien Banner ── */}
            {selectedDiagnosaItem && (
              <div style={{ background: inputDiagnosaType === 'diagnosa' ? 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)' : 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', borderBottom: `2px solid ${inputDiagnosaType === 'diagnosa' ? '#38bdf8' : '#4ade80'}`, padding: '8px 16px' }}>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2" style={{ fontSize: '0.78rem' }}>
                  <div className="d-flex align-items-center gap-2">
                    <i className={`bi bi-person-badge-fill ${inputDiagnosaType === 'diagnosa' ? 'text-primary' : 'text-success'} fs-5`}></i>
                    <div>
                      <div className="fw-bold text-dark" style={{ fontSize: '0.82rem' }}>{selectedDiagnosaItem.nm_pasien || selectedDiagnosaItem.nama}</div>
                      <div className="text-muted" style={{ fontSize: '0.72rem' }}>No. MR: <span className="font-monospace fw-semibold">{selectedDiagnosaItem.no_medrec}</span> &nbsp;|&nbsp; DPJP: {selectedDiagnosaItem.nm_dokter || selectedDiagnosaItem.dpjp || '-'}</div>
                    </div>
                  </div>
                  <Badge bg={inputDiagnosaType === 'diagnosa' ? 'primary' : 'success'} style={{ fontSize: '0.72rem', padding: '4px 10px' }}>
                    <i className="bi bi-receipt me-1"></i>
                    {selectedDiagnosaItem.no_nota || '-'}
                  </Badge>
                </div>
              </div>
            )}

            <div className="p-3">
              {/* ── Status Diagnosa (ICD-10) / Dokter DPJP, Lokasi / Unit (Di atas Pencarian Kode Diagnosa) ── */}
              <Row className="g-2 mb-2">
                {inputDiagnosaType === 'diagnosa' && (
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark mb-1 d-flex align-items-center gap-1" style={{ fontSize: '0.73rem' }}>
                        <i className="bi bi-tag-fill text-warning"></i> Status Diagnosa
                      </Form.Label>
                      <Form.Select
                        size="sm"
                        value={formInputDiagnosa.ket}
                        onChange={e => {
                          const val = e.target.value;
                          if (val === 'Utama' && formInputDiagnosa.kd_icd) {
                            const curCode = formInputDiagnosa.kd_icd.trim().toLowerCase();
                            const cleanCur = curCode.replace(/\./g, '');
                            const mItem = (masterIcd10 || []).find(x => {
                              const code = String(x.icd || x.kode || x.kd_icd || '').trim().toLowerCase();
                              return code === curCode || code.replace(/\./g, '') === cleanCur;
                            });
                            if (mItem && String(mItem.accpdx || mItem.acc_pdx || '').trim().toUpperCase() === 'N') {
                              notify('warning', 'Validasi Primary Diagnosis', 'Diagnosa tidak bisa diinput sebagai primary karena accpdx = N');
                              setIcdCheckMsg({ type: 'danger', text: 'Diagnosa tidak bisa diinput sebagai primary karena accpdx = N (Diagnosa Utama tidak diizinkan, silakan pilih status Sekunder)' });
                              return;
                            }
                          }
                          setFormInputDiagnosa({ ...formInputDiagnosa, ket: val, utama: val === 'Utama' ? 1 : 0 });
                          if (val !== 'Utama' && icdCheckMsg?.text?.includes('accpdx = N')) {
                            setIcdCheckMsg({ type: 'info', text: 'Diagnosa dengan accpdx = N diizinkan diinput sebagai Diagnosa Sekunder / Tambahan' });
                          }
                        }}
                        style={{ fontSize: '0.78rem', fontWeight: 600, borderColor: formInputDiagnosa.ket === 'Utama' ? '#f59e0b' : '#d1d5db' }}
                      >
                        <option value="Utama">🟡 Diagnosa Utama (Primer)</option>
                        <option value="Sekunder">🔵 Diagnosa Sekunder (Komorbiditas)</option>
                        <option value="Tambahan">⚪ Diagnosa Tambahan</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}

                <Col md={inputDiagnosaType === 'diagnosa' ? 4 : 6}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark mb-1 d-flex align-items-center gap-1" style={{ fontSize: '0.73rem' }}>
                      <i className="bi bi-person-badge text-secondary"></i> Dokter DPJP
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      value={formInputDiagnosa.nm_dokter}
                      onChange={e => setFormInputDiagnosa({ ...formInputDiagnosa, nm_dokter: e.target.value })}
                      placeholder="Nama Dokter DPJP"
                      style={{ fontSize: '0.78rem' }}
                    />
                  </Form.Group>
                </Col>

                <Col md={inputDiagnosaType === 'diagnosa' ? 4 : 6}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark mb-1 d-flex align-items-center gap-1" style={{ fontSize: '0.73rem' }}>
                      <i className="bi bi-hospital text-info"></i> Lokasi / Unit
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={formInputDiagnosa.lokasi}
                      onChange={e => setFormInputDiagnosa({ ...formInputDiagnosa, lokasi: e.target.value })}
                      style={{ fontSize: '0.78rem' }}
                    >
                      <option value="Rawat Inap">🏥 Rawat Inap</option>
                      <option value="Rawat Jalan">🏃 Rawat Jalan</option>
                      <option value="IGD">🚑 IGD / Gawat Darurat</option>
                      <option value="Kamar Operasi">🔬 Kamar Operasi (OK)</option>
                      <option value="ICU">❤️‍🩹 ICU / HCU</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* ── Tanggal, Jam, dan Petugas Coder (Di bawah Status Diagnosa, di atas Pencarian ICD) ── */}
              <Row className="g-2 mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark mb-1 d-flex align-items-center gap-1" style={{ fontSize: '0.73rem' }}>
                      <i className="bi bi-calendar3 text-secondary"></i> Tanggal
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      value={formInputDiagnosa.tanggal}
                      onChange={e => setFormInputDiagnosa({ ...formInputDiagnosa, tanggal: e.target.value })}
                      placeholder="DD/MM/YYYY"
                      style={{ fontSize: '0.78rem', fontFamily: 'monospace' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark mb-1 d-flex align-items-center gap-1" style={{ fontSize: '0.73rem' }}>
                      <i className="bi bi-clock text-secondary"></i> Jam
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      value={formInputDiagnosa.jam}
                      onChange={e => setFormInputDiagnosa({ ...formInputDiagnosa, jam: e.target.value })}
                      placeholder="HH:mm"
                      style={{ fontSize: '0.78rem', fontFamily: 'monospace' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark mb-1 d-flex align-items-center gap-1" style={{ fontSize: '0.73rem' }}>
                      <i className="bi bi-person-check-fill text-success"></i> Petugas Coder
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      value={formInputDiagnosa.petugas || (localStorage.getItem('nama_user') || localStorage.getItem('user') || localStorage.getItem('username') || sessionStorage.getItem('nama_user') || sessionStorage.getItem('user') || sessionStorage.getItem('username') || username || 'Administrator')}
                      readOnly
                      title="Petugas Coder otomatis diisi dari session user login"
                      style={{ background: '#f8fafc', fontSize: '0.78rem', fontWeight: 600, color: '#0f172a' }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* ── Panel Pencarian ICD ── */}
              <div className="mb-3 p-3 rounded-3 border shadow-sm" style={{ background: '#ffffff', borderColor: inputDiagnosaType === 'diagnosa' ? '#93c5fd' : '#6ee7b7' }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '8px',
                    background: inputDiagnosaType === 'diagnosa' ? 'linear-gradient(135deg, #0284c7, #0369a1)' : 'linear-gradient(135deg, #059669, #047857)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    <i className={`bi ${inputDiagnosaType === 'diagnosa' ? 'bi-journal-medical' : 'bi-list-check'} text-white`} style={{ fontSize: '0.8rem' }}></i>
                  </div>
                  <div>
                    <div className="fw-bold text-dark" style={{ fontSize: '0.8rem' }}>
                      {inputDiagnosaType === 'diagnosa' ? 'Cari Kode Diagnosa ICD-10 IM' : 'Cari Kode Prosedur ICD-9-CM IM'}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.67rem' }}>
                      {inputDiagnosaType === 'diagnosa'
                        ? `Ketik kode atau nama diagnosa · ${masterIcd10.length ? masterIcd10.length.toLocaleString('id-ID') + ' data tersedia' : 'Klik field untuk memuat master'}`
                        : `Ketik kode atau nama prosedur · ${masterIcd9.length ? masterIcd9.length.toLocaleString('id-ID') + ' data tersedia' : 'Klik field untuk memuat master'}`}
                    </div>
                  </div>
                  {(inputDiagnosaType === 'diagnosa' ? masterIcd10.length : masterIcd9.length) > 0 && (
                    <Badge bg={inputDiagnosaType === 'diagnosa' ? 'primary' : 'success'} className="ms-auto" style={{ fontSize: '0.67rem' }}>
                      {inputDiagnosaType === 'diagnosa' ? masterIcd10.length.toLocaleString('id-ID') : masterIcd9.length.toLocaleString('id-ID')} item
                    </Badge>
                  )}
                </div>

                {/* Search input utama */}
                <div style={{ position: 'relative' }}>
                  <InputGroup>
                    <InputGroup.Text style={{ background: inputDiagnosaType === 'diagnosa' ? '#eff6ff' : '#f0fdf4', borderColor: inputDiagnosaType === 'diagnosa' ? '#93c5fd' : '#6ee7b7', borderRight: 'none' }}>
                      {isCheckingIcd
                        ? <Spinner animation="border" size="sm" variant={inputDiagnosaType === 'diagnosa' ? 'primary' : 'success'} />
                        : <i className={`bi bi-search ${inputDiagnosaType === 'diagnosa' ? 'text-primary' : 'text-success'}`}></i>
                      }
                    </InputGroup.Text>
                    <Form.Control
                      size="sm"
                      value={formInputDiagnosa.kd_icd}
                      onClick={() => {
                        if (inputDiagnosaType === 'diagnosa') {
                          setShowIcd10Autocomplete(true);
                          if (masterIcd10.length === 0 && !isLoadingMasterIcd) fetchMasterIcd('diagnosa');
                        } else {
                          setShowIcd9Autocomplete(true);
                          if (masterIcd9.length === 0 && !isLoadingMasterIcd) fetchMasterIcd('prosedur');
                        }
                      }}
                      onFocus={() => {
                        if (inputDiagnosaType === 'diagnosa') {
                          setShowIcd10Autocomplete(true);
                          if (masterIcd10.length === 0 && !isLoadingMasterIcd) fetchMasterIcd('diagnosa');
                        } else {
                          setShowIcd9Autocomplete(true);
                          if (masterIcd9.length === 0 && !isLoadingMasterIcd) fetchMasterIcd('prosedur');
                        }
                      }}
                      onChange={e => {
                        const val = e.target.value;
                        let mult = formInputDiagnosa.multiplicity || 1;
                        if (inputDiagnosaType === 'prosedur' && val.includes('+')) {
                          const parsed = parseInt(val.split('+')[1]);
                          if (!isNaN(parsed) && parsed > 0) {
                            mult = parsed;
                          }
                        }
                        const isHdr = (inputDiagnosaTarget === 'idrg' && inputDiagnosaType === 'diagnosa') ? isHeaderIcd10Code(val) : false;
                        const isAccNo = (inputDiagnosaTarget === 'idrg' && inputDiagnosaType === 'prosedur') ? isAccpdxNoProcedure(val) : false;
                        setFormInputDiagnosa(prev => ({ ...prev, kd_icd: val, nm_icd: '', multiplicity: mult, isHeader: isHdr, accpdx: isAccNo ? 'N' : undefined }));
                        if (isHdr) {
                          setIcdCheckMsg({ type: 'danger', text: `Diagnosa ${val} merupakan Header Code (tidak valid untuk iDRG dan tidak dapat disimpan).` });
                        } else if (isAccNo) {
                          setIcdCheckMsg({ type: 'danger', text: `Prosedur ${val} tidak bisa dipilih karena accpdx = N (tidak dapat disimpan).` });
                        } else {
                          setIcdCheckMsg(null);
                        }
                        if (inputDiagnosaType === 'diagnosa') setShowIcd10Autocomplete(true);
                        else setShowIcd9Autocomplete(true);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          setShowIcd10Autocomplete(false);
                          setShowIcd9Autocomplete(false);
                          handleCheckIcd(formInputDiagnosa.kd_icd);
                        } else if (e.key === 'Escape') {
                          setShowIcd10Autocomplete(false);
                          setShowIcd9Autocomplete(false);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          setShowIcd10Autocomplete(false);
                          setShowIcd9Autocomplete(false);
                        }, 250);
                        if (formInputDiagnosa.kd_icd && !formInputDiagnosa.nm_icd) {
                          handleCheckIcd(formInputDiagnosa.kd_icd);
                        }
                      }}
                      placeholder={inputDiagnosaType === 'diagnosa'
                        ? "🔍  Ketik kode ICD-10 atau nama diagnosa..."
                        : "🔍  Ketik kode ICD-9-CM atau nama prosedur..."}
                      required
                      style={{
                        fontWeight: formInputDiagnosa.kd_icd ? 700 : 400,
                        fontFamily: 'monospace',
                        color: inputDiagnosaType === 'diagnosa' ? '#0369a1' : '#047857',
                        fontSize: '0.85rem',
                        borderColor: inputDiagnosaType === 'diagnosa' ? '#93c5fd' : '#6ee7b7',
                        borderLeft: 'none',
                        borderRight: 'none',
                        boxShadow: 'none'
                      }}
                    />
                    {formInputDiagnosa.kd_icd && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setFormInputDiagnosa(prev => ({ ...prev, kd_icd: '', nm_icd: '' }));
                          setIcdCheckMsg(null);
                        }}
                        title="Hapus / Reset kode"
                        style={{ borderColor: '#d1d5db', borderLeft: 'none', borderRight: 'none' }}
                      >
                        <i className="bi bi-x-lg" style={{ fontSize: '0.75rem' }}></i>
                      </Button>
                    )}
                    <Button
                      variant={inputDiagnosaType === 'diagnosa' ? 'primary' : 'success'}
                      size="sm"
                      disabled={isCheckingIcd}
                      onClick={() => {
                        setShowIcd10Autocomplete(false);
                        setShowIcd9Autocomplete(false);
                        handleCheckIcd(formInputDiagnosa.kd_icd);
                      }}
                      title={inputDiagnosaType === 'prosedur' ? "Verifikasi Kode ICD-9 IM (tabel _idrg)" : "Verifikasi Kode ICD-10 IM"}
                      style={{ fontSize: '0.75rem', fontWeight: 700, borderColor: inputDiagnosaType === 'diagnosa' ? '#93c5fd' : '#6ee7b7' }}
                    >
                      {isCheckingIcd ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-patch-check me-1"></i>Verifikasi</>}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setShowIcd10Autocomplete(false);
                        setShowIcd9Autocomplete(false);
                        handleOpenMasterIcdModal(formInputDiagnosa.kd_icd);
                      }}
                      title={inputDiagnosaType === 'prosedur' ? "Buka Tabel Lengkap Master ICD-9 IM" : "Buka Tabel Lengkap Master ICD-10 IM"}
                      style={{ fontSize: '0.75rem', borderColor: '#d1d5db' }}
                    >
                      <i className="bi bi-table me-1"></i>
                      <span style={{ fontSize: '0.7rem' }}>Tabel</span>
                    </Button>
                  </InputGroup>

                  {/* Autocomplete Popup — ICD-10 (Diagnosa) */}
                  {inputDiagnosaType === 'diagnosa' && showIcd10Autocomplete && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1060,
                      maxHeight: '300px', overflowY: 'auto', background: '#ffffff',
                      border: '2px solid #3b82f6', borderRadius: '0 0 10px 10px',
                      boxShadow: '0 12px 28px rgba(59, 130, 246, 0.2)', marginTop: '-1px'
                    }}>
                      {/* Header autocomplete */}
                      <div className="px-3 py-2 d-flex justify-content-between align-items-center sticky-top" style={{ background: '#eff6ff', borderBottom: '1px solid #bfdbfe', fontSize: '0.7rem' }}>
                        <span className="text-primary fw-bold d-flex align-items-center gap-1">
                          <i className="bi bi-journal-medical"></i>
                          Master ICD-10 IM
                        </span>
                        <span className="badge bg-primary">
                          {isLoadingMasterIcd ? 'Memuat...' : (formInputDiagnosa.kd_icd ? `${filteredIcd10Suggestions.length} hasil` : `${filteredIcd10Suggestions.length} dari ${masterIcd10.length.toLocaleString('id-ID')}`)}
                        </span>
                      </div>
                      {isLoadingMasterIcd ? (
                        <div className="p-4 text-center text-muted" style={{ fontSize: '0.78rem' }}>
                          <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                          Memuat data ICD-10 IM dari server...
                        </div>
                      ) : filteredIcd10Suggestions.length > 0 ? (
                        filteredIcd10Suggestions.map((item, idx) => {
                          const code = item.icd || item.kode || item.kd_icd || '';
                          const desc = item.deskripsi || item.nm_icd || item.penyakit || '';
                          const isIm = item.im === 1 || item.im === '1';
                          const isHeader = item.validcode === 0 || item.validcode === '0' || item.validcode === false;
                          const isIdrgHeader = inputDiagnosaTarget === 'idrg' && isHeader;
                          return (
                            <div
                              key={`icd10-${code}-${idx}`}
                              onMouseDown={(e) => {
                                if (isIdrgHeader) {
                                  e.preventDefault();
                                  setIcdCheckMsg({ type: 'danger', text: `Diagnosa ${code} merupakan Header Code (tidak valid untuk iDRG dan tidak dapat dipilih).` });
                                  notify('warning', 'Header Code Tidak Dapat Dipilih', `Kode ${code} adalah Header Code dan tidak dapat dipilih untuk pengklaiman iDRG.`);
                                  return;
                                }
                                handleSelectIcd10FromDropdown(item);
                                setShowIcd10Autocomplete(false);
                              }}
                              title={isIdrgHeader ? `Header Code: ${code} tidak dapat dipilih untuk iDRG` : desc}
                              style={{
                                padding: '8px 12px',
                                cursor: isIdrgHeader ? 'not-allowed' : 'pointer',
                                borderBottom: '1px solid #f1f5f9',
                                transition: 'background 0.1s',
                                opacity: isIdrgHeader ? 0.55 : 1,
                                background: isIdrgHeader ? '#fef2f2' : '#ffffff'
                              }}
                              className="d-flex align-items-center gap-2"
                              onMouseEnter={e => e.currentTarget.style.background = isIdrgHeader ? '#fee2e2' : '#eff6ff'}
                              onMouseLeave={e => e.currentTarget.style.background = isIdrgHeader ? '#fef2f2' : '#ffffff'}
                            >
                              <span className={`badge ${isIdrgHeader ? 'bg-danger' : 'bg-primary'} font-monospace`} style={{ fontSize: '0.75rem', minWidth: '60px', textAlign: 'center' }}>{code}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className={`fw-semibold ${isIdrgHeader ? 'text-danger' : 'text-dark'} text-truncate`} style={{ fontSize: '0.78rem' }}>
                                  {desc}
                                  {isIdrgHeader && <span className="ms-1 text-danger fw-bold" style={{ fontSize: '0.68rem' }}>(Header - Tidak Dapat Dipilih)</span>}
                                </div>
                                <div className="d-flex gap-1 mt-1">
                                  {isIm && <span className="badge bg-success" style={{ fontSize: '0.6rem' }}>IM</span>}
                                  {isHeader && (
                                    <span className="badge bg-danger" style={{ fontSize: '0.6rem' }}><i className="bi bi-slash-circle me-1"></i>Header Code</span>
                                  )}
                                  {item.dtd && <span className="badge bg-secondary" style={{ fontSize: '0.6rem' }}>DTD: {item.dtd}</span>}
                                  {item.keterangan && <span className="badge bg-info text-dark" style={{ fontSize: '0.6rem' }}>{item.keterangan}</span>}
                                </div>
                              </div>
                              {!isIdrgHeader && <i className="bi bi-arrow-return-left text-primary" style={{ fontSize: '0.8rem', flexShrink: 0 }}></i>}
                              {isIdrgHeader && <i className="bi bi-slash-circle text-danger" style={{ fontSize: '0.8rem', flexShrink: 0 }}></i>}
                            </div>
                          );
                        })
                      ) : (
                        <div className="p-4 text-center" style={{ fontSize: '0.78rem' }}>
                          <i className="bi bi-search text-muted fs-4 d-block mb-2"></i>
                          <div className="text-muted">Tidak ada hasil untuk <strong>"{formInputDiagnosa.kd_icd}"</strong></div>
                          <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>Coba ketik kode atau sebagian nama diagnosa</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Autocomplete Popup — ICD-9-CM (Prosedur) */}
                  {inputDiagnosaType === 'prosedur' && showIcd9Autocomplete && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1060,
                      maxHeight: '300px', overflowY: 'auto', background: '#ffffff',
                      border: '2px solid #10b981', borderRadius: '0 0 10px 10px',
                      boxShadow: '0 12px 28px rgba(16, 185, 129, 0.2)', marginTop: '-1px'
                    }}>
                      {/* Header autocomplete */}
                      <div className="px-3 py-2 d-flex justify-content-between align-items-center sticky-top" style={{ background: '#f0fdf4', borderBottom: '1px solid #bbf7d0', fontSize: '0.7rem' }}>
                        <span className="text-success fw-bold d-flex align-items-center gap-1">
                          <i className="bi bi-list-check"></i>
                          Master ICD-9-CM IM
                        </span>
                        <span className="badge bg-success">
                          {isLoadingMasterIcd ? 'Memuat...' : (filteredIcd9Suggestions.length > 0 ? `${filteredIcd9Suggestions.length} hasil` : `${masterIcd9.length.toLocaleString('id-ID')} total`)}
                        </span>
                      </div>
                      {isLoadingMasterIcd ? (
                        <div className="p-4 text-center text-muted" style={{ fontSize: '0.78rem' }}>
                          <Spinner animation="border" size="sm" variant="success" className="me-2" />
                          Memuat data ICD-9-CM IM dari server...
                        </div>
                      ) : filteredIcd9Suggestions.length > 0 ? (
                        filteredIcd9Suggestions.map((item, idx) => {
                          const code = item.kode || item.icd || item.kd_icd || '';
                          const desc = item.deskripsi || item.nm_icd || '';
                          const isIm = item.im === 1 || item.im === '1';
                          const isAccpdxNo = String(item.accpdx || '').trim().toUpperCase() === 'N';
                          return (
                            <div
                              key={`icd9-${code}-${idx}`}
                              onMouseDown={(e) => {
                                if (isAccpdxNo && inputDiagnosaTarget === 'idrg') {
                                  e.preventDefault();
                                  setIcdCheckMsg({ type: 'danger', text: `Prosedur ${code} tidak bisa dipilih karena accpdx = N (tidak dapat disimpan).` });
                                  notify('warning', 'Prosedur Tidak Valid', `Prosedur ${code} tidak bisa dipilih karena accpdx = N`);
                                  setFormInputDiagnosa(prev => ({ ...prev, kd_icd: code, nm_icd: desc, accpdx: 'N' }));
                                  setShowIcd9Autocomplete(false);
                                  return;
                                }
                                handleSelectIcd9FromDropdown(item);
                                setShowIcd9Autocomplete(false);
                              }}
                              title={(isAccpdxNo && inputDiagnosaTarget === 'idrg') ? `Prosedur ${code} tidak bisa dipilih karena accpdx = N` : desc}
                              style={{ padding: '8px 12px', cursor: (isAccpdxNo && inputDiagnosaTarget === 'idrg') ? 'not-allowed' : 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background 0.1s', opacity: (isAccpdxNo && inputDiagnosaTarget === 'idrg') ? 0.6 : 1, background: (isAccpdxNo && inputDiagnosaTarget === 'idrg') ? '#fef2f2' : '#ffffff' }}
                              className="d-flex align-items-center gap-2"
                              onMouseEnter={e => e.currentTarget.style.background = (isAccpdxNo && inputDiagnosaTarget === 'idrg') ? '#fee2e2' : '#f0fdf4'}
                              onMouseLeave={e => e.currentTarget.style.background = (isAccpdxNo && inputDiagnosaTarget === 'idrg') ? '#fef2f2' : '#ffffff'}
                            >
                              <span className={`badge ${(isAccpdxNo && inputDiagnosaTarget === 'idrg') ? 'bg-danger' : 'bg-success'} font-monospace`} style={{ fontSize: '0.75rem', minWidth: '70px', textAlign: 'center' }}>{code}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="fw-semibold text-dark text-truncate" style={{ fontSize: '0.78rem' }}>{desc}</div>
                                <div className="d-flex gap-1 mt-1">
                                  {isIm && <span className="badge bg-warning text-dark" style={{ fontSize: '0.6rem' }}>IM — Indonesian Modification</span>}
                                  {isAccpdxNo && <span className="badge bg-danger" style={{ fontSize: '0.6rem' }}>accpdx: N (Tidak bisa dipilih)</span>}
                                </div>
                              </div>
                              <i className={`bi ${isAccpdxNo ? 'bi-slash-circle text-danger' : 'bi-arrow-return-left text-success'}`} style={{ fontSize: '0.8rem', flexShrink: 0 }}></i>
                            </div>
                          );
                        })
                      ) : masterIcd9.length === 0 ? (
                        <div className="p-4 text-center" style={{ fontSize: '0.78rem' }}>
                          <i className="bi bi-cloud-download text-muted fs-4 d-block mb-2"></i>
                          <div className="text-muted">Klik di atas untuk memuat data master ICD-9-CM</div>
                        </div>
                      ) : (
                        <div className="p-4 text-center" style={{ fontSize: '0.78rem' }}>
                          <i className="bi bi-search text-muted fs-4 d-block mb-2"></i>
                          <div className="text-muted">Tidak ada hasil untuk <strong>"{formInputDiagnosa.kd_icd}"</strong></div>
                          <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>Coba ketik kode seperti <code>87.41</code> atau nama prosedur</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Feedback validasi kode */}
                {icdCheckMsg && !/header code/i.test(icdCheckMsg.text || '') && (
                  <div className={`d-flex align-items-start gap-2 mt-2 p-2 rounded-2 ${icdCheckMsg.type === 'success' ? 'bg-success bg-opacity-10 border border-success border-opacity-25' : 'bg-danger bg-opacity-10 border border-danger border-opacity-25'}`} style={{ fontSize: '0.75rem' }}>
                    <i className={`bi ${icdCheckMsg.type === 'success' ? 'bi-check-circle-fill text-success' : 'bi-exclamation-triangle-fill text-danger'} mt-1 flex-shrink-0`}></i>
                    <span className={`fw-semibold ${icdCheckMsg.type === 'success' ? 'text-success' : 'text-danger'}`}>{icdCheckMsg.text}</span>
                  </div>
                )}

                {/* Preview kode terpilih */}
                {formInputDiagnosa.kd_icd && formInputDiagnosa.nm_icd && (
                  <div className={`d-flex align-items-center gap-2 mt-2 p-2 rounded-2 ${inputDiagnosaType === 'diagnosa' ? 'bg-primary bg-opacity-10 border border-primary border-opacity-25' : 'bg-success bg-opacity-10 border border-success border-opacity-25'}`}>
                    <i className={`bi bi-bookmark-check-fill ${inputDiagnosaType === 'diagnosa' ? 'text-primary' : 'text-success'} flex-shrink-0`}></i>
                    <div style={{ fontSize: '0.75rem' }}>
                      <span className={`badge ${inputDiagnosaType === 'diagnosa' ? 'bg-primary' : 'bg-success'} font-monospace me-2`}>{formInputDiagnosa.kd_icd}</span>
                      <span className="fw-semibold text-dark">{formInputDiagnosa.nm_icd}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Detail Deskripsi Prosedur / Diagnosa ── */}
              <div className="mb-2">
                <Form.Label className="fw-bold text-dark mb-1 d-flex align-items-center justify-content-between" style={{ fontSize: '0.75rem' }}>
                  <span className={inputDiagnosaType === 'prosedur' ? "text-success" : "text-primary"}>
                    <i className={`bi ${inputDiagnosaType === 'prosedur' ? 'bi-file-medical' : 'bi-clipboard2-pulse'} me-1`}></i>
                    {inputDiagnosaType === 'prosedur' ? 'Deskripsi Prosedur (ICD-9-CM)' : 'Nama Diagnosa (ICD-10)'}
                  </span>
                  {formInputDiagnosa.kd_icd && (
                    <span className="badge bg-light text-secondary border font-monospace" style={{ fontSize: '0.68rem' }}>
                      Kode: {formInputDiagnosa.kd_icd}
                    </span>
                  )}
                </Form.Label>
                <Form.Control
                  size="sm"
                  value={formInputDiagnosa.nm_icd}
                  onChange={e => setFormInputDiagnosa({ ...formInputDiagnosa, nm_icd: e.target.value })}
                  placeholder={inputDiagnosaType === 'prosedur' ? "Deskripsi prosedur ICD-9-CM (terisi otomatis saat verifikasi/pilih dari daftar)" : "Nama/deskripsi diagnosa ICD-10 (terisi otomatis saat verifikasi/pilih dari daftar)"}
                  required
                  style={{ fontWeight: 600, fontSize: '0.82rem' }}
                />
              </div>

              {/* ── Multiplicity (Qty) Prosedur (Posisi Di Bawah Deskripsi) ── */}
              {inputDiagnosaType === 'prosedur' && (
                <div className="mb-3 p-2 rounded-3 border" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                  <Row className="align-items-center g-2">
                    <Col md={5}>
                      <Form.Label className="fw-bold text-dark mb-0 d-flex align-items-center justify-content-between" style={{ fontSize: '0.74rem' }}>
                        <span className="d-flex align-items-center gap-1 text-success">
                          <i className="bi bi-x-octagon-fill"></i> Multiplicity (Qty Tindakan)
                        </span>
                        <span className="badge bg-success text-white" style={{ fontSize: '0.68rem', padding: '2px 8px' }}>
                          x{formInputDiagnosa.multiplicity || 1}
                        </span>
                      </Form.Label>
                      <div className="text-muted" style={{ fontSize: '0.67rem' }}>
                        Jumlah repetisi prosedur dalam 1 sesi (standar Kemenkes iDRG)
                      </div>
                    </Col>
                    <Col md={7}>
                      <div className="d-flex align-items-center gap-2">
                        <InputGroup size="sm" style={{ width: '120px' }}>
                          <Button
                            variant="outline-secondary"
                            style={{ fontSize: '0.75rem', padding: '0 8px' }}
                            onClick={() => handleMultiplicityChange(Math.max(1, (Number(formInputDiagnosa.multiplicity) || 1) - 1))}
                            title="Kurangi multiplicity"
                          >
                            <i className="bi bi-dash-lg"></i>
                          </Button>
                          <Form.Control
                            type="number"
                            min={1}
                            max={99}
                            value={formInputDiagnosa.multiplicity || 1}
                            onChange={e => handleMultiplicityChange(e.target.value)}
                            className="text-center font-monospace fw-bold"
                            style={{ fontSize: '0.82rem', borderColor: '#10b981' }}
                          />
                          <Button
                            variant="outline-secondary"
                            style={{ fontSize: '0.75rem', padding: '0 8px' }}
                            onClick={() => handleMultiplicityChange((Number(formInputDiagnosa.multiplicity) || 1) + 1)}
                            title="Tambah multiplicity"
                          >
                            <i className="bi bi-plus-lg"></i>
                          </Button>
                        </InputGroup>
                        <div className="d-flex gap-1 flex-grow-1">
                          {[1, 2, 3, 4, 5].map(n => (
                            <Button
                              key={n}
                              variant={Number(formInputDiagnosa.multiplicity || 1) === n ? "success" : "outline-secondary"}
                              size="sm"
                              className="py-1 px-1 font-monospace"
                              style={{ fontSize: '0.68rem', flex: 1 }}
                              onClick={() => handleMultiplicityChange(n)}
                            >
                              x{n}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}





              {/* ── Peringatan Header Code untuk iDRG ── */}
              {isModalIdrgHeaderDiagnosis && (
                <div className="alert alert-danger py-2 px-3 mt-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '0.75rem', borderRadius: '8px' }}>
                  <i className="bi bi-exclamation-triangle-fill text-danger flex-shrink-0 fs-6"></i>
                  <div>
                    Kode <strong>{formInputDiagnosa.kd_icd}</strong> merupakan <strong>Header Code</strong> (tidak valid untuk coding iDRG).
                    <span className="d-block text-danger fw-semibold">Tombol Simpan dinonaktifkan untuk menghindari penyimpanan kode header. Silakan pilih sub-kode diagnosa yang spesifik.</span>
                  </div>
                </div>
              )}

              {/* ── Peringatan Prosedur accpdx = N untuk iDRG ── */}
              {isModalIdrgProcedureAccpdxNo && (
                <div className="alert alert-danger py-2 px-3 mt-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '0.75rem', borderRadius: '8px' }}>
                  <i className="bi bi-exclamation-triangle-fill text-danger flex-shrink-0 fs-6"></i>
                  <div>
                    Kode Prosedur <strong>{formInputDiagnosa.kd_icd}</strong> memiliki status <strong>accpdx = N</strong> (tidak valid untuk coding iDRG).
                    <span className="d-block text-danger fw-semibold">Tombol Simpan dinonaktifkan untuk menghindari penyimpanan prosedur accpdx = N.</span>
                  </div>
                </div>
              )}

              {/* ── Footer Tombol ── */}
              <div className="d-flex justify-content-between align-items-center border-top pt-3">
                <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  {isModalIdrgHeaderDiagnosis
                    ? <span className="text-danger fw-semibold">Kode Header tidak dapat disimpan</span>
                    : isModalIdrgProcedureAccpdxNo
                      ? <span className="text-danger fw-semibold">Prosedur accpdx = N tidak dapat disimpan</span>
                      : ''}
                </div>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={() => setShowInputDiagnosaModal(false)} disabled={isSavingDiagnosa}
                    style={{ fontSize: '0.78rem' }}>
                    <i className="bi bi-x-circle me-1"></i>Batal
                  </Button>
                  <Button
                    variant={inputDiagnosaType === 'prosedur' ? 'success' : 'primary'}
                    size="sm"
                    type="submit"
                    disabled={isSavingDiagnosa || !formInputDiagnosa.kd_icd || isModalIdrgHeaderDiagnosis || isModalIdrgProcedureAccpdxNo}
                    title={isModalIdrgHeaderDiagnosis ? "Diagnosa Header Code tidak dapat disimpan pada iDRG" : isModalIdrgProcedureAccpdxNo ? "Prosedur dengan accpdx = N tidak dapat disimpan pada iDRG" : undefined}
                    className="fw-bold d-flex align-items-center gap-2 shadow-sm px-3"
                    style={{
                      background: (isModalIdrgHeaderDiagnosis || isModalIdrgProcedureAccpdxNo)
                        ? '#94a3b8'
                        : (inputDiagnosaType === 'prosedur' ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' : 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)'),
                      border: 'none', fontSize: '0.82rem',
                      cursor: (isModalIdrgHeaderDiagnosis || isModalIdrgProcedureAccpdxNo) ? 'not-allowed' : 'pointer',
                      opacity: (isModalIdrgHeaderDiagnosis || isModalIdrgProcedureAccpdxNo) ? 0.6 : 1
                    }}
                  >
                    {isSavingDiagnosa
                      ? <><Spinner animation="border" size="sm" /><span>Menyimpan...</span></>
                      : isModalIdrgHeaderDiagnosis
                        ? <><i className="bi bi-slash-circle"></i><span>Header Code (Nonaktif)</span></>
                        : isModalIdrgProcedureAccpdxNo
                          ? <><i className="bi bi-slash-circle"></i><span>accpdx=N (Nonaktif)</span></>
                          : <><i className="bi bi-check-circle-fill"></i><span>{inputDiagnosaType === 'prosedur' ? 'Simpan Prosedur' : 'Simpan Diagnosa'}</span></>
                    }
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL TABEL MASTER ICD-10 IM & ICD-9 IM (show_icdxim / show_icdixim) ══ */}
      <Modal show={showMasterIcdModal} onHide={() => setShowMasterIcdModal(false)} centered backdrop="static" size="xl">
        <Modal.Header closeButton style={{ background: inputDiagnosaType === 'prosedur' ? 'linear-gradient(135deg, #065f46 0%, #047857 100%)' : 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className={`bi ${inputDiagnosaType === 'prosedur' ? 'bi-scissors' : 'bi-journal-medical'} fs-5`}></i>
            <span>{inputDiagnosaType === 'prosedur' ? 'Pencarian Master Data Prosedur ICD-9 IM (Endpoint: /show_icdixim)' : 'Pencarian Master Data Diagnosa ICD-10 IM (Endpoint: /show_icdxim)'}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          {/* Filter search bar */}
          <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
            <InputGroup size="sm" style={{ maxWidth: '420px' }}>
              <InputGroup.Text style={{ background: '#fff' }}>
                <i className="bi bi-search text-muted"></i>
              </InputGroup.Text>
              <Form.Control
                placeholder={inputDiagnosaType === 'prosedur' ? "Filter kode ICD-9, deskripsi prosedur, IM..." : "Filter kode ICD, deskripsi diagnosa, IM..."}
                value={masterIcdModalSearch}
                onChange={e => {
                  setMasterIcdModalSearch(e.target.value);
                  setMasterIcdCurrentPage(1);
                }}
                autoFocus
              />
              {masterIcdModalSearch && (
                <Button variant="outline-secondary" onClick={() => setMasterIcdModalSearch('')}>
                  <i className="bi bi-x"></i>
                </Button>
              )}
            </InputGroup>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              Total Data: <strong>{inputDiagnosaType === 'prosedur' ? masterIcd9.length : masterIcd10.length}</strong> master item
            </div>
          </div>

          {masterIcdModalSearch && (
            <div className="alert alert-warning border-0 shadow-sm d-flex align-items-center gap-2 py-2 px-3 mb-3" style={{ fontSize: '0.78rem', borderRadius: '6px', background: '#fffbeb', borderLeft: '4px solid #f59e0b' }}>
              <i className="bi bi-exclamation-triangle-fill fs-5 text-warning"></i>
              <div>
                <strong>Pencarian Master {inputDiagnosaType === 'prosedur' ? 'Prosedur (ICD-9 IM)' : 'Diagnosa (ICD-10 IM)'}:</strong> Menampilkan hasil pencarian untuk kata kunci <span className="badge bg-warning text-dark font-monospace">{masterIcdModalSearch}</span>. Klik <strong>Pilih</strong> pada baris data yang sesuai.
              </div>
            </div>
          )}

          {isLoadingMasterIcd ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" size="sm" className="me-2" />
              <span>Memuat data master {inputDiagnosaType === 'prosedur' ? 'prosedur dari server (/show_icdixim)...' : 'ICD-10 IM dari server (/show_icdxim)...'}</span>
            </div>
          ) : (
            (() => {
              const q = masterIcdModalSearch.toLowerCase().trim();
              const sourceList = inputDiagnosaType === 'prosedur' ? masterIcd9 : masterIcd10;
              const filtered = sourceList.filter(item => {
                if (!q) return true;
                const icd = (item.icd || item.kode || item.kd_icd || '').toLowerCase();
                const nm = (item.deskripsi || item.nm_icd || item.penyakit || '').toLowerCase();
                const dtd = (item.dtd || '').toLowerCase();
                const pyk = (item.penyakit || '').toLowerCase();
                const ket = (item.keterangan || '').toLowerCase();
                const im = String(item.im || '');
                return icd.includes(q) || nm.includes(q) || dtd.includes(q) || pyk.includes(q) || ket.includes(q) || (q === 'im' && (im === '1'));
              });

              const totalRows = filtered.length;
              const totalPages = Math.ceil(totalRows / masterIcdItemsPerPage) || 1;
              const startIdx = (masterIcdCurrentPage - 1) * masterIcdItemsPerPage;
              const pageRows = filtered.slice(startIdx, startIdx + masterIcdItemsPerPage);

              return (
                <div>
                  <div className="table-responsive" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                    <Table responsive hover striped bordered size="sm" className="align-middle mb-0" style={{ fontSize: '0.75rem' }}>
                      <thead style={{ background: '#0f172a', color: '#fff', position: 'sticky', top: 0, zIndex: 1 }}>
                        {inputDiagnosaType === 'prosedur' ? (
                          <tr>
                            <th className="text-center" style={{ width: '45px' }}>No</th>
                            <th className="text-center" style={{ width: '120px' }}>Kode ICD-9</th>
                            <th>Deskripsi Prosedur (ICD-9-CM IM)</th>
                            <th className="text-center" style={{ width: '90px' }}>Status IM</th>
                            <th className="text-center" style={{ width: '85px' }}>Acc PDx</th>
                            <th className="text-center" style={{ width: '80px' }}>Aksi</th>
                          </tr>
                        ) : (
                          <tr>
                            <th className="text-center" style={{ width: '45px' }}>No</th>
                            <th className="text-center" style={{ width: '110px' }}>Kode ICD-10</th>
                            <th>Deskripsi Diagnosa (ICD-10 IM)</th>
                            <th className="text-center" style={{ width: '85px' }}>Validitas</th>
                            <th className="text-center" style={{ width: '90px' }}>Status IM</th>
                            <th className="text-center" style={{ width: '85px' }}>Acc PDx</th>
                            <th className="text-center" style={{ width: '80px' }}>Aksi</th>
                          </tr>
                        )}
                      </thead>
                      <tbody>
                        {pageRows.length > 0 ? (
                          pageRows.map((row, idx) => (
                            <tr 
                              key={idx} 
                              style={{ cursor: 'pointer' }}
                              onDoubleClick={() => handleSelectMasterIcdRow(row)}
                            >
                              <td className="text-center text-muted">{startIdx + idx + 1}</td>
                              <td className="text-center">
                                <Badge bg={inputDiagnosaType === 'prosedur' ? 'teal' : 'danger'} style={inputDiagnosaType === 'prosedur' ? { background: '#0d9488', fontSize: '0.75rem' } : { fontSize: '0.75rem' }} className="font-monospace text-white">
                                  {row.icd || row.kode || row.kd_icd}
                                </Badge>
                              </td>
                              <td className="fw-semibold text-dark">{row.deskripsi || row.nm_icd || row.penyakit}</td>
                              {inputDiagnosaType === 'diagnosa' && (
                                <td className="text-center">
                                  {(row.validcode === 0 || row.validcode === '0' || row.validcode === false) ? (
                                    <Badge bg={inputDiagnosaTarget === 'inacbg' ? 'warning' : 'danger'} text={inputDiagnosaTarget === 'inacbg' ? 'dark' : undefined} style={{ fontSize: '0.68rem' }} title={inputDiagnosaTarget === 'inacbg' ? 'Header Code (bisa dipilih untuk INACBG)' : 'Header Code (tidak bisa dipilih)'}>Header</Badge>
                                  ) : (
                                    <Badge bg="success" style={{ fontSize: '0.68rem' }}>Valid</Badge>
                                  )}
                                </td>
                              )}
                              <td className="text-center">
                                {row.im === 1 || row.im === '1' ? (
                                  <Badge bg="success" style={{ fontSize: '0.68rem' }}>Khusus IM</Badge>
                                ) : (
                                  <Badge bg="secondary" style={{ fontSize: '0.68rem' }}>WHO</Badge>
                                )}
                              </td>
                              <td className="text-center">
                                {row.accpdx === 'Y' || row.accpdx === '1' ? (
                                  <Badge bg="primary" style={{ fontSize: '0.68rem' }}>Ya (PDx)</Badge>
                                ) : String(row.accpdx || '').trim().toUpperCase() === 'N' ? (
                                  <Badge bg="danger" style={{ fontSize: '0.68rem' }} title={inputDiagnosaType === 'prosedur' ? 'accpdx = N (Tidak bisa dipilih)' : 'accpdx = N (Hanya untuk sekunder)'}>
                                    N {inputDiagnosaType === 'prosedur' ? '(Tidak Bisa)' : ''}
                                  </Badge>
                                ) : (
                                  <span className="text-muted" style={{ fontSize: '0.68rem' }}>{row.accpdx || '—'}</span>
                                )}
                              </td>
                              <td className="text-center">
                                <Button
                                  variant={inputDiagnosaType === 'prosedur'
                                    ? (String(row.accpdx || '').trim().toUpperCase() === 'N' ? 'outline-danger' : 'success')
                                    : 'primary'
                                  }
                                  size="sm"
                                  style={{ padding: '2px 8px', fontSize: '0.7rem', fontWeight: 700 }}
                                  onClick={() => handleSelectMasterIcdRow(row)}
                                >
                                  {inputDiagnosaType === 'prosedur' && String(row.accpdx || '').trim().toUpperCase() === 'N' ? (
                                    <><i className="bi bi-x-circle me-1"></i>Tidak Bisa</>
                                  ) : (
                                    <><i className="bi bi-check2 me-1"></i>Pilih</>
                                  )}
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={inputDiagnosaType === 'prosedur' ? 6 : 7} className="text-center py-4 text-muted">
                              Tidak ada data yang cocok dengan pencarian "{masterIcdModalSearch}".
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination Controls */}
                  {totalRows > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        Menampilkan <b>{startIdx + 1} - {Math.min(startIdx + masterIcdItemsPerPage, totalRows)}</b> dari <b>{totalRows}</b> data
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Pagination size="sm" className="mb-0">
                          <Pagination.First onClick={() => setMasterIcdCurrentPage(1)} disabled={masterIcdCurrentPage === 1} />
                          <Pagination.Prev onClick={() => setMasterIcdCurrentPage(p => Math.max(1, p - 1))} disabled={masterIcdCurrentPage === 1} />
                          <Pagination.Item active>{masterIcdCurrentPage} / {totalPages}</Pagination.Item>
                          <Pagination.Next onClick={() => setMasterIcdCurrentPage(p => Math.min(totalPages, p + 1))} disabled={masterIcdCurrentPage === totalPages} />
                          <Pagination.Last onClick={() => setMasterIcdCurrentPage(totalPages)} disabled={masterIcdCurrentPage === totalPages} />
                        </Pagination>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()
          )}
        </Modal.Body>
        <Modal.Footer className="py-2 bg-light">
          <Button variant="secondary" size="sm" onClick={() => setShowMasterIcdModal(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      
      {/* ══ MODAL AKSI KLAIM (HAPUS, REEDIT, STATUS) ══ */}
      <Modal show={actionModal.show} onHide={() => setActionModal({ ...actionModal, show: false })} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className={`bi ${actionModal.type === 'hapus' ? 'bi-trash text-danger' : actionModal.type === 'reedit' ? 'bi-unlock text-warning' : 'bi-info-circle text-info'} fs-5`}></i>
            <span>{actionModal.title}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-light">
          {!actionModal.result ? (
            <div className="text-center">
              <div className="mb-3">
                <i className={`bi ${actionModal.type === 'hapus' ? 'bi-exclamation-triangle-fill text-danger' : actionModal.type === 'idrg_final' ? 'bi-lock-fill text-primary' : actionModal.type === 'finalisasi' ? 'bi-lock-fill text-success' : actionModal.type === 'reedit' ? 'bi-unlock-fill text-warning' : 'bi-search text-info'} fs-1`}></i>
              </div>
              <h5 className="fw-bold mb-2 text-dark">{actionModal.title}</h5>
              <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
                Apakah Anda yakin ingin memproses <strong>{actionModal.title}</strong> untuk pasien <strong>{actionModal.item?.nm_pasien || actionModal.item?.nama}</strong> dengan No. SEP <strong>{actionModal.item?.no_sep || '-'}</strong>?
              </p>
              <div className="d-flex justify-content-center gap-2">
                <Button variant="secondary" onClick={() => setActionModal({ ...actionModal, show: false })} disabled={actionModal.loading} className="px-4 fw-semibold">
                  Batal
                </Button>
                <Button variant={actionModal.type === 'hapus' ? 'danger' : actionModal.type === 'idrg_final' ? 'primary' : actionModal.type === 'finalisasi' ? 'success' : actionModal.type === 'reedit' ? 'warning' : 'info'} onClick={handleProcessAction} disabled={actionModal.loading} className={`px-4 fw-bold ${actionModal.type !== 'hapus' ? 'text-dark' : 'text-white'} shadow-sm`}>
                  {actionModal.loading ? <Spinner size="sm" animation="border" className="me-2" /> : null}
                  {actionModal.type === 'status' ? 'Cek Sekarang' : 'Ya, Lanjutkan'}
                </Button>
              </div>
            </div>
          ) : actionModal.type === 'status' ? (
            <div>
              <div className="d-flex align-items-center gap-3 mb-3 p-3 rounded-3" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <i className="bi bi-shield-check text-success fs-1"></i>
                <div>
                  <h6 className="fw-bold text-success mb-1" style={{ fontSize: '0.92rem' }}>Pemeriksaan Status Klaim Berhasil</h6>
                  <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                    Informasi status klaim pasien di Web Service E-Klaim & Data Center Kemenkes RI
                  </p>
                </div>
              </div>

              <div className="bg-white p-3 rounded-3 border shadow-sm mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                  <span className="text-muted fw-semibold" style={{ fontSize: '0.76rem' }}>Nomor SEP</span>
                  <span className="fw-bold font-monospace text-primary" style={{ fontSize: '0.85rem' }}>{actionModal.item?.no_sep || '-'}</span>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                  <span className="text-muted fw-semibold" style={{ fontSize: '0.76rem' }}>Nama Pasien</span>
                  <span className="fw-bold text-dark" style={{ fontSize: '0.82rem' }}>{actionModal.item?.nm_pasien || actionModal.item?.nama || '-'}</span>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                  <span className="text-muted fw-semibold" style={{ fontSize: '0.76rem' }}>No. Medrec / No. Nota</span>
                  <span className="fw-semibold text-secondary font-monospace" style={{ fontSize: '0.78rem' }}>{actionModal.item?.no_medrec || '-'} / {actionModal.item?.no_nota || '-'}</span>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                  <span className="text-muted fw-semibold" style={{ fontSize: '0.76rem' }}>Status Klaim (E-Klaim / BPJS)</span>
                  <Badge bg="success" className="px-2 py-1 fw-bold d-inline-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
                    <i className="bi bi-check-circle-fill"></i>
                    <span>{actionModal.result.status_klaim || 'Klaim Terproses / Final'}</span>
                  </Badge>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="text-muted fw-semibold" style={{ fontSize: '0.76rem' }}>Status DC Kemenkes</span>
                  <Badge 
                    bg={actionModal.result.is_dc_terkirim || actionModal.result.status_dc?.toLowerCase().includes('terkirim') ? 'primary' : 'warning'} 
                    className="px-2 py-1 fw-bold d-inline-flex align-items-center gap-1" 
                    style={{ fontSize: '0.75rem' }}
                  >
                    <i className={`bi ${actionModal.result.is_dc_terkirim || actionModal.result.status_dc?.toLowerCase().includes('terkirim') ? 'bi-cloud-check-fill' : 'bi-cloud-slash'}`}></i>
                    <span>{actionModal.result.status_dc || 'Belum Terkirim ke Data Center Kemenkes'}</span>
                  </Badge>
                </div>
                {actionModal.result.tgl_kirim_dc && (
                  <div className="text-end text-muted mt-2 pt-2 border-top" style={{ fontSize: '0.7rem' }}>
                    <i className="bi bi-clock-history me-1"></i>Waktu Pengiriman DC: <span className="fw-semibold text-dark">{actionModal.result.tgl_kirim_dc}</span>
                  </div>
                )}
              </div>

              <div className="d-flex flex-wrap justify-content-end gap-2 pt-2 border-top">
                <Button
                  variant={actionModal.result?.klaim_final ? "primary" : "secondary"}
                  className="fw-bold text-white shadow-sm d-inline-flex align-items-center gap-1 px-3"
                  style={{ 
                    background: actionModal.result?.klaim_final ? '#2563eb' : '#64748b', 
                    borderColor: actionModal.result?.klaim_final ? '#1d4ed8' : '#475569', 
                    fontSize: '0.78rem', 
                    borderRadius: '7px',
                    cursor: actionModal.result?.klaim_final && !isSendingOnline ? 'pointer' : 'not-allowed',
                    opacity: actionModal.result?.klaim_final && !isSendingOnline ? 1 : 0.65
                  }}
                  onClick={() => handleSendClaimOnline(actionModal.item)}
                  disabled={!actionModal.result?.klaim_final || isSendingOnline}
                  title={actionModal.result?.klaim_final ? "Kirim Klaim ke Data Center Kemenkes RI (Poin 22)" : "Klaim belum final. Silakan lakukan Finalisasi Klaim terlebih dahulu."}
                >
                  {isSendingOnline ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-arrow-up-fill me-1"></i>}
                  <span>Kirim Klaim Online</span>
                </Button>

                {actionModal.result?.klaim_final && actionModal.item?.no_sep && actionModal.item?.no_sep !== '-' && (
                  <Button 
                    variant="dark" 
                    onClick={() => {
                      const sep = actionModal.item?.no_sep;
                      setActionModal({ ...actionModal, show: false });
                      handlePrintClaim(sep);
                    }} 
                    className="px-3 fw-bold text-white shadow-sm d-inline-flex align-items-center gap-1"
                    style={{ background: '#334155', borderColor: '#334155', fontSize: '0.78rem', borderRadius: '7px' }}
                  >
                    <i className="bi bi-printer-fill me-1"></i> Cetak Klaim
                  </Button>
                )}

                <Button variant="secondary" onClick={() => setActionModal({ ...actionModal, show: false })} className="px-3 fw-semibold shadow-sm" style={{ fontSize: '0.78rem', borderRadius: '7px' }}>
                  Tutup
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className="mb-3">
                <i className={`bi ${actionModal.result.success ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'} fs-1`}></i>
              </div>
              <h5 className={`fw-bold text-${actionModal.result.success ? 'success' : 'danger'} mb-3`}>
                {actionModal.result.success ? 'Proses Berhasil' : 'Proses Gagal'}
              </h5>
              <div className={`alert alert-${actionModal.result.success ? 'success' : 'danger'} p-3 mb-4 text-start shadow-sm`} style={{ fontSize: '0.82rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', borderRadius: '8px' }}>
                {actionModal.result.message}
              </div>
              <div className="d-flex justify-content-center gap-2">
                {actionModal.result.success && actionModal.item?.no_sep && actionModal.item?.no_sep !== '-' && (
                  <Button 
                    variant="dark" 
                    onClick={() => {
                      const sep = actionModal.item?.no_sep;
                      setActionModal({ ...actionModal, show: false });
                      handlePrintClaim(sep);
                    }} 
                    className="px-3 fw-bold text-white shadow-sm"
                    style={{ background: '#334155', borderColor: '#334155' }}
                  >
                    <i className="bi bi-printer-fill me-1"></i> Cetak Dokumen Klaim
                  </Button>
                )}
                <Button variant="secondary" onClick={() => setActionModal({ ...actionModal, show: false })} className="px-4 fw-semibold shadow-sm">
                  Tutup
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* ══ MODAL KONFIRMASI HAPUS DIAGNOSA / PROSEDUR ══ */}
      <Modal
        show={deleteConfirmModal.show}
        onHide={() => !deleteConfirmModal.isDeleting && setDeleteConfirmModal(prev => ({ ...prev, show: false }))}
        centered
        backdrop="static"
        dialogClassName="modal-dialog-centered"
      >
        <Modal.Header
          closeButton={!deleteConfirmModal.isDeleting}
          closeVariant="white"
          style={{
            background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
            color: '#fff',
            borderBottom: 'none',
            padding: '12px 18px',
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px'
          }}
        >
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: 700 }} className="d-flex align-items-center gap-2">
            <i className="bi bi-trash3-fill fs-5"></i>
            <span>Konfirmasi Hapus {deleteConfirmModal.type === 'prosedur' ? 'Prosedur (ICD-9 CM)' : 'Diagnosa (ICD-10)'} <Badge bg="light" className="text-dark ms-1" style={{ fontSize: '0.68rem', padding: '2px 7px' }}>{deleteConfirmModal.target === 'inacbg' ? 'INACBG' : 'iDRG'}</Badge></span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px 22px', background: '#f8fafc' }}>
          <div className="d-flex align-items-start gap-3">
            <div
              style={{
                width: '46px',
                height: '46px',
                borderRadius: '12px',
                background: '#fee2e2',
                color: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 2px 4px rgba(220, 38, 38, 0.15)'
              }}
            >
              <i className="bi bi-exclamation-triangle-fill fs-4"></i>
            </div>
            <div style={{ flex: 1 }}>
              <h6 className="fw-bold text-dark mb-1" style={{ fontSize: '0.88rem' }}>
                Yakin ingin menghapus item ini?
              </h6>
              <p className="text-muted mb-2" style={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
                Data {deleteConfirmModal.type === 'prosedur' ? 'prosedur' : 'diagnosa'} berikut akan dihapus dari riwayat klaim pasien:
              </p>
              {deleteConfirmModal.item && (
                <div
                  style={{
                    background: '#ffffff',
                    border: '1.5px solid #fecaca',
                    borderRadius: '8px',
                    padding: '9px 12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span
                      style={{
                        background: '#dc2626',
                        color: '#ffffff',
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        padding: '1px 8px',
                        borderRadius: '5px',
                        fontFamily: 'monospace'
                      }}
                    >
                      {deleteConfirmModal.item.kd_icd}
                    </span>
                    <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>
                      {deleteConfirmModal.type === 'prosedur' ? 'ICD-9 CM' : 'ICD-10'}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>
                    {deleteConfirmModal.item.nama_icd || deleteConfirmModal.item.nm_icd || '-'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ background: '#f1f5f9', borderTop: '1px solid #e2e8f0', padding: '10px 18px' }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDeleteConfirmModal(prev => ({ ...prev, show: false }))}
            disabled={deleteConfirmModal.isDeleting}
            className="fw-semibold px-3"
            style={{ borderRadius: '7px', fontSize: '0.8rem' }}
          >
            Batal
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleExecuteDeleteDiagnosa}
            disabled={deleteConfirmModal.isDeleting}
            className="fw-bold px-3 text-white shadow-sm"
            style={{
              borderRadius: '7px',
              fontSize: '0.8rem',
              background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
              borderColor: 'transparent'
            }}
          >
            {deleteConfirmModal.isDeleting ? (
              <><Spinner size="sm" animation="border" className="me-1" /> Menghapus...</>
            ) : (
              <><i className="bi bi-trash3-fill me-1"></i> Ya, Hapus Data</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══ MODAL EDIT KODE ICD (SESUAI NOMOR URUT DATA) ══ */}
      <Modal
        show={editIcdModal.show}
        onHide={() => !editIcdModal.isSaving && setEditIcdModal(prev => ({ ...prev, show: false }))}
        centered
        backdrop="static"
        dialogClassName="modal-dialog-centered"
      >
        <Modal.Header
          closeButton={!editIcdModal.isSaving}
          closeVariant="white"
          style={{
            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
            color: '#fff',
            borderBottom: 'none',
            padding: '12px 18px',
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px'
          }}
        >
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: 700 }} className="d-flex align-items-center gap-2">
            <i className="bi bi-pencil-square fs-5"></i>
            <span>Edit Kode ICD (No. Urut #{editIcdModal.urutan})</span>
            <Badge bg="light" className="text-dark ms-1" style={{ fontSize: '0.68rem', padding: '2px 7px' }}>
              {editIcdModal.target?.toUpperCase()}
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '18px 20px', background: '#f8fafc' }}>
          {/* Card Info Data Lama */}
          <div className="p-2 mb-3 rounded-3" style={{ background: '#ffffff', border: '1px solid #e2e8f0', fontSize: '0.76rem' }}>
            <div className="d-flex align-items-center justify-content-between mb-1 pb-1 border-bottom">
              <span className="text-muted">Nomor Urut Data: <strong>#{editIcdModal.urutan}</strong></span>
              <Badge bg={editIcdModal.type === 'prosedur' ? 'success' : (editIcdModal.item?.utama === 1 || String(editIcdModal.item?.ket || '').toLowerCase().includes('utama') ? 'primary' : 'warning')} className="text-white" style={{ fontSize: '0.65rem' }}>
                {editIcdModal.type === 'prosedur' ? 'Prosedur (ICD-9)' : (editIcdModal.item?.utama === 1 || String(editIcdModal.item?.ket || '').toLowerCase().includes('utama') ? 'Diagnosa Utama' : 'Diagnosa Sekunder')}
              </Badge>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted" style={{ minWidth: '70px' }}>Kode Lama:</span>
              <span style={{
                background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5',
                borderRadius: '4px', padding: '1px 6px', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.72rem'
              }}>
                {editIcdModal.old_kd_icd}
              </span>
              <span className="text-dark fw-semibold text-truncate" title={editIcdModal.old_nm_icd}>
                {editIcdModal.old_nm_icd}
              </span>
            </div>
          </div>

          {/* Form Pencarian Kode ICD Baru */}
          <div className="mb-3 position-relative">
            <label className="form-label fw-bold text-dark mb-1" style={{ fontSize: '0.78rem' }}>
              Cari &amp; Pilih Kode ICD Terbaru:
            </label>
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-white" style={{ borderColor: '#cbd5e1' }}>
                <i className="bi bi-search text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder={`Ketik kode atau nama ${editIcdModal.type === 'prosedur' ? 'prosedur (ICD-9)' : 'diagnosa (ICD-10)'}...`}
                value={editIcdSearchInput}
                onChange={(e) => handleSearchEditIcd(e.target.value)}
                style={{ fontSize: '0.78rem', borderColor: '#cbd5e1' }}
                autoFocus
              />
              {editIcdSearchInput && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => { setEditIcdSearchInput(''); setEditIcdSuggestions([]); }}
                >
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>

            {/* Dropdown Suggestions */}
            {editIcdSuggestions.length > 0 && (
              <div
                style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  zIndex: 1060, background: '#ffffff',
                  border: '1px solid #cbd5e1', borderRadius: '8px',
                  maxHeight: '190px', overflowY: 'auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)', marginTop: '2px'
                }}
              >
                {editIcdSuggestions.map((sug, sIdx) => {
                  const sCode = String(sug.icd || sug.kode || sug.kd_icd || '').trim();
                  const sName = String(sug.deskripsi || sug.penyakit || sug.nama_icd || sug.nm_icd || '').trim();
                  const isHeader = sug.validcode === 0 || sug.validcode === '0' || sug.validcode === false;
                  const isAccNo = String(sug.accpdx || '').toUpperCase() === 'N';
                  const isCannotSelect = (editIcdModal.target === 'idrg' && editIcdModal.type === 'diagnosa' && isHeader) ||
                    (editIcdModal.target === 'idrg' && editIcdModal.type === 'prosedur' && isAccNo);
                  return (
                    <div
                      key={sIdx}
                      onClick={() => {
                        if (isCannotSelect) {
                          if (isHeader) {
                            notify('warning', 'Header Code Tidak Dapat Dipilih', `Kode ${sCode} adalah Header Code dan tidak dapat dipilih untuk iDRG.`);
                          } else {
                            notify('warning', 'Prosedur Tidak Valid', `Prosedur ${sCode} tidak bisa dipilih karena accpdx = N.`);
                          }
                          return;
                        }
                        handleSelectEditIcdSuggestion(sug);
                      }}
                      style={{
                        padding: '6px 10px',
                        cursor: isCannotSelect ? 'not-allowed' : 'pointer',
                        borderBottom: '1px solid #f1f5f9',
                        fontSize: '0.74rem',
                        opacity: isCannotSelect ? 0.55 : 1,
                        background: isCannotSelect ? '#fef2f2' : '#ffffff'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = isCannotSelect ? '#fee2e2' : '#f0f9ff'}
                      onMouseLeave={(e) => e.currentTarget.style.background = isCannotSelect ? '#fef2f2' : '#ffffff'}
                    >
                      <div className="d-flex align-items-center justify-content-between gap-1">
                        <span className={`fw-bold font-monospace ${isCannotSelect ? 'text-danger' : 'text-primary'}`}>{sCode}</span>
                        <div className="d-flex gap-1">
                          {sug.im === 1 && <span className="badge bg-info text-dark" style={{ fontSize: '0.6rem' }}>IM</span>}
                          {isHeader && <span className="badge bg-danger" style={{ fontSize: '0.6rem' }}>Header</span>}
                          {isAccNo && <span className={`badge ${editIcdModal.target === 'idrg' && editIcdModal.type === 'prosedur' ? 'bg-danger' : 'bg-secondary'}`} style={{ fontSize: '0.6rem' }}>accpdx: N</span>}
                        </div>
                      </div>
                      <div className={`text-truncate ${isCannotSelect ? 'text-danger' : 'text-dark'}`}>
                        {sName}
                        {isCannotSelect && <span className="ms-1 fw-bold">({isHeader ? 'Header - Tidak Dapat Dipilih' : 'accpdx: N - Tidak Dapat Dipilih'})</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pratinjau Kode ICD Baru Terpilih */}
          {(() => {
            const isEditHdr = editIcdModal.target === 'idrg' && editIcdModal.type === 'diagnosa' && (
              editIcdModal.validcode === 0 || editIcdModal.validcode === '0' || editIcdModal.validcode === false || isHeaderIcd10Code(editIcdModal.new_kd_icd)
            );
            const isEditAccNo = editIcdModal.target === 'idrg' && editIcdModal.type === 'prosedur' && (
              String(editIcdModal.accpdx || '').toUpperCase() === 'N' || isAccpdxNoProcedure(editIcdModal.new_kd_icd)
            );
            const isInvalidEdit = isEditHdr || isEditAccNo;
            return (
              <>
                {isEditHdr && (
                  <div className="alert alert-danger py-1 px-2 mb-2 d-flex align-items-center gap-2" style={{ fontSize: '0.72rem', borderRadius: '6px' }}>
                    <i className="bi bi-exclamation-triangle-fill text-danger flex-shrink-0"></i>
                    <span>Kode <strong>{editIcdModal.new_kd_icd}</strong> merupakan Header Code (tidak valid untuk iDRG). Tombol simpan dinonaktifkan.</span>
                  </div>
                )}
                {isEditAccNo && (
                  <div className="alert alert-danger py-1 px-2 mb-2 d-flex align-items-center gap-2" style={{ fontSize: '0.72rem', borderRadius: '6px' }}>
                    <i className="bi bi-exclamation-triangle-fill text-danger flex-shrink-0"></i>
                    <span>Prosedur <strong>{editIcdModal.new_kd_icd}</strong> memiliki status accpdx = N (tidak valid untuk iDRG). Tombol simpan dinonaktifkan.</span>
                  </div>
                )}
                <div className="p-2 mb-2 rounded-3" style={{ background: isInvalidEdit ? '#fef2f2' : '#f0fdf4', border: `1px solid ${isInvalidEdit ? '#fca5a5' : '#86efac'}`, fontSize: '0.76rem' }}>
                  <div className="d-flex align-items-center gap-2">
                    <span className={isInvalidEdit ? "text-danger fw-bold" : "text-success fw-bold"} style={{ minWidth: '70px' }}>Kode Baru:</span>
                    <span style={{
                      background: isInvalidEdit ? '#fee2e2' : '#dcfce7',
                      color: isInvalidEdit ? '#991b1b' : '#15803d',
                      border: `1px solid ${isInvalidEdit ? '#fca5a5' : '#86efac'}`,
                      borderRadius: '4px', padding: '1px 6px', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.74rem'
                    }}>
                      {editIcdModal.new_kd_icd || '-'}
                    </span>
                    <span className="text-dark fw-semibold text-truncate" title={editIcdModal.new_nm_icd}>
                      {editIcdModal.new_nm_icd || '(Belum memilih kode baru)'}
                    </span>
                  </div>
                  {editIcdModal.type === 'prosedur' && (
                    <div className="d-flex align-items-center gap-2 mt-2 pt-2 border-top border-success-subtle">
                      <span className="text-muted" style={{ minWidth: '70px' }}>Multiplicity:</span>
                      <input
                        type="number"
                        min="1"
                        className="form-control form-control-sm"
                        style={{ width: '80px', fontSize: '0.75rem' }}
                        value={editIcdModal.multiplicity}
                        onChange={(e) => setEditIcdModal(prev => ({ ...prev, multiplicity: Math.max(1, parseInt(e.target.value) || 1) }))}
                      />
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer style={{ padding: '10px 18px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setEditIcdModal(prev => ({ ...prev, show: false }))}
            disabled={editIcdModal.isSaving}
            className="fw-semibold px-3"
            style={{ borderRadius: '7px', fontSize: '0.8rem' }}
          >
            Batal
          </Button>
          {(() => {
            const isEditHdr = editIcdModal.target === 'idrg' && editIcdModal.type === 'diagnosa' && (
              editIcdModal.validcode === 0 || editIcdModal.validcode === '0' || editIcdModal.validcode === false || isHeaderIcd10Code(editIcdModal.new_kd_icd)
            );
            const isEditAccNo = editIcdModal.target === 'idrg' && editIcdModal.type === 'prosedur' && (
              String(editIcdModal.accpdx || '').toUpperCase() === 'N' || isAccpdxNoProcedure(editIcdModal.new_kd_icd)
            );
            const isInvalidEdit = isEditHdr || isEditAccNo;
            return (
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveEditIcd}
                disabled={editIcdModal.isSaving || !editIcdModal.new_kd_icd || isInvalidEdit}
                title={isEditHdr ? "Kode Diagnosa ini adalah Header Code dan tidak dapat disimpan untuk iDRG" : isEditAccNo ? "Prosedur dengan accpdx = N tidak dapat disimpan untuk iDRG" : undefined}
                className="fw-bold px-3 text-white shadow-sm"
                style={{
                  borderRadius: '7px',
                  fontSize: '0.8rem',
                  background: isInvalidEdit ? '#94a3b8' : 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  borderColor: 'transparent',
                  cursor: isInvalidEdit ? 'not-allowed' : 'pointer',
                  opacity: isInvalidEdit ? 0.6 : 1
                }}
              >
                {editIcdModal.isSaving ? (
                  <><Spinner size="sm" animation="border" className="me-1" /> Menyimpan...</>
                ) : isEditHdr ? (
                  <><i className="bi bi-slash-circle me-1"></i> Header (Nonaktif)</>
                ) : isEditAccNo ? (
                  <><i className="bi bi-slash-circle me-1"></i> accpdx=N (Nonaktif)</>
                ) : (
                  <><i className="bi bi-check-circle-fill me-1"></i> Simpan Perubahan</>
                )}
              </Button>
            );
          })()}
        </Modal.Footer>
      </Modal>

      {/* ══ MODAL KONFIRMASI NEW CLAIM ══ */}
      <Modal show={showNewClaimConfirm} onHide={() => { setShowNewClaimConfirm(false); setNewClaimResult(null); }} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-plus-fill fs-5 text-warning"></i>
            <span>Konfirmasi New Claim E-Klaim</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 text-center bg-light">
          {!newClaimResult ? (
            <>
              <div className="mb-3">
                <i className="bi bi-cloud-arrow-up fs-1 text-primary"></i>
              </div>
              <h5 className="mb-2 fw-bold text-dark">Daftarkan ke E-Klaim?</h5>
              <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                Anda akan mendaftarkan pasien <strong>{newClaimTarget?.nm_pasien}</strong> dengan No. Medrec <strong>{newClaimTarget?.no_medrec}</strong> dan Nota <strong>{newClaimTarget?.no_nota}</strong> ke sistem E-Klaim (New Claim).
              </p>
              <div className="mb-4 text-start bg-white p-3 border rounded shadow-sm">
                <Form.Label className="fw-bold text-dark mb-1" style={{ fontSize: '0.78rem' }}>
                  <i className="bi bi-lungs-fill text-danger me-1"></i> Nomor Register SITB (Sistem Informasi Tuberkulosis)
                </Form.Label>
                <InputGroup className="shadow-sm rounded">
                  <Form.Control
                    type="text"
                    placeholder="Isi jika pasien terdiagnosa TB (A15-A19)"
                    value={inputSitb}
                    onChange={e => setInputSitb(e.target.value)}
                    style={{ fontSize: '0.85rem' }}
                  />
                  <Button 
                    variant="primary" 
                    onClick={() => handleValidateSitb(newClaimTarget?.no_sep)} 
                    disabled={isValidatingSitb}
                    className="fw-semibold px-3 text-white"
                    style={{ background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', border: 'none', zIndex: 0 }}
                  >
                    {isValidatingSitb ? (
                      <><Spinner size="sm" animation="border" className="me-1" /> Cek...</>
                    ) : (
                      <><i className="bi bi-shield-check me-1"></i> Validasi SITB</>
                    )}
                  </Button>
                </InputGroup>
                {sitbValidationResult && (
                  <div className={`alert alert-${sitbValidationResult.type} mt-2 mb-0 py-1 px-2 d-flex align-items-center gap-1`} style={{ fontSize: '0.7rem' }}>
                    <i className={`bi ${sitbValidationResult.type === 'success' ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                    <span>{sitbValidationResult.text}</span>
                  </div>
                )}
                <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                  * Wajib diisi khusus untuk diagnosa TB agar tidak ditolak E-Klaim saat finalisasi.
                </Form.Text>
              </div>
              <div className="d-flex justify-content-center gap-2">
                <Button variant="secondary" onClick={() => setShowNewClaimConfirm(false)} className="px-4 fw-semibold" disabled={isProcessingNewClaim}>
                  Batal
                </Button>
                <Button variant="primary" onClick={handleProcessNewClaim} className="px-4 fw-semibold" disabled={isProcessingNewClaim}>
                  {isProcessingNewClaim ? <Spinner size="sm" animation="border" className="me-2" /> : null}
                  Ya, Daftarkan
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3">
                <i className={`bi ${newClaimResult.type === 'success' ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'} fs-1`}></i>
              </div>
              <h5 className={`mb-2 fw-bold text-${newClaimResult.type === 'success' ? 'success' : 'danger'}`}>
                {newClaimResult.type === 'success' ? 'Berhasil' : 'Gagal'}
              </h5>
              <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
                {newClaimResult.text}
              </p>
              <Button variant="secondary" onClick={() => { setShowNewClaimConfirm(false); setNewClaimResult(null); }} className="px-4 fw-semibold">
                Tutup
              </Button>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* ══ MODAL GROUPER E-CLAIM ══ */}
      <Modal show={showGrouperModal} onHide={() => setShowGrouperModal(false)} centered backdrop="static" size="xl">
        <Modal.Header style={{
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
          color: '#fff',
          padding: '8px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.15)',
          position: 'sticky',
          top: 0,
          zIndex: 1050,
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
        }}>
          <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
            {/* Title & Subtitle */}
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '32px', height: '32px', borderRadius: '8px',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <i className="bi bi-cpu-fill fs-6 text-white"></i>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: '0.92rem', lineHeight: '1.2' }}>Proses Grouper E-Claim BPJS</div>
                <div style={{ fontSize: '0.68rem', fontWeight: '400', opacity: 0.88 }}>Verifikasi Diagnosa &amp; Penentuan Tarif INA-CBG</div>
              </div>
            </div>

            {/* Action Buttons (Standarisasi 25 Poin Kemenkes) */}
            <div className="d-flex align-items-center gap-1 flex-wrap">
              {/* Tombol Utama: Jalankan Grouper E-Claim di-hide karena sudah ada tombol Grouper iDRG dan Grouper INACBG terpisah */}
              {/*
              <Button
                size="sm"
                className="fw-bold text-white shadow-sm d-inline-flex align-items-center gap-1"
                style={{
                  background: isGrouping ? '#d97706' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  border: 'none', borderRadius: '7px', fontSize: '0.74rem', padding: '4px 10px',
                  boxShadow: '0 2px 5px rgba(217, 119, 6, 0.35)'
                }}
                onClick={handleProcessGrouper}
                disabled={isGrouping || isInvalidSep(grouperTarget?.no_sep)}
                title="Menjalankan siklus grouping iDRG dan INA-CBG"
              >
                {isGrouping ? (
                  <><Spinner size="sm" animation="border" className="me-1" /> Memproses...</>
                ) : (
                  <><i className="bi bi-play-circle-fill"></i> Jalankan Grouper</>
                )}
              </Button>
              */}

              {/* Tahap 2: Finalisasi iDRG (Poin 7, 9) - Nonaktif jika error MDC 36 / Ungroupable */}
              {idrgStatus.isGrouped && !idrgStatus.isFinal && (
                <Button
                  size="sm"
                  className="fw-bold text-white shadow-sm d-inline-flex align-items-center gap-1"
                  style={{
                    background: (idrgStatus.isUngroupable || String(idrgStatus.code).startsWith('36')) ? '#94a3b8' : '#0284c7',
                    border: 'none', borderRadius: '7px', fontSize: '0.74rem', padding: '4px 10px',
                    cursor: (isFinalizingIdrg || idrgStatus.isUngroupable || String(idrgStatus.code).startsWith('36')) ? 'not-allowed' : 'pointer',
                    opacity: (isFinalizingIdrg || idrgStatus.isUngroupable || String(idrgStatus.code).startsWith('36')) ? 0.65 : 1
                  }}
                  onClick={() => handleFinalisasiIdrg()}
                  disabled={isFinalizingIdrg || idrgStatus.isUngroupable || String(idrgStatus.code).startsWith('36') || String(idrgStatus.desc || '').toLowerCase().includes('ungroupable')}
                  title={
                    (idrgStatus.isUngroupable || String(idrgStatus.code).startsWith('36'))
                      ? "Tombol Dinonaktifkan: Hasil Grouping iDRG Ungroupable (MDC 36). Silakan perbaiki koding terlebih dahulu."
                      : "Kemenkes Poin 7 & 9: Finalisasi Koding iDRG"
                  }
                >
                  {isFinalizingIdrg ? <Spinner size="sm" animation="border" /> : <i className="bi bi-shield-lock-fill"></i>}
                  <span>Finalisasi iDRG</span>
                </Button>
              )}

              {/* Tahap 2: Edit Ulang iDRG (Poin 11) - Muncul jika iDRG final, tapi klaim belum final */}
              {idrgStatus.isFinal && !claimFinalStatus && (
                <Button
                  size="sm"
                  variant="outline-light"
                  className="fw-bold shadow-sm d-inline-flex align-items-center gap-1"
                  style={{ borderRadius: '7px', fontSize: '0.74rem', padding: '4px 8px', borderColor: 'rgba(255,255,255,0.7)', background: 'rgba(0,0,0,0.15)' }}
                  onClick={() => handleReeditIdrg()}
                  title="Kemenkes Poin 11: Edit Ulang / Buka Kunci iDRG"
                >
                  <i className="bi bi-unlock text-warning"></i>
                  <span>Edit Ulang iDRG</span>
                </Button>
              )}

              {/* Tahap 3: Impor Koding ke INA-CBG (Poin 13) - Aktif saat iDRG sudah final */}
              {idrgStatus.isFinal && !claimFinalStatus && (
                <Button
                  size="sm"
                  className="fw-bold text-dark shadow-sm d-inline-flex align-items-center gap-1"
                  style={{ background: '#a7f3d0', border: '1px solid #34d399', borderRadius: '7px', fontSize: '0.74rem', padding: '4px 10px' }}
                  onClick={() => handleImportIdrgToInacbg()}
                  disabled={isImportingInacbg}
                  title="Kemenkes Poin 13: Salin Diagnosis & Prosedur iDRG ke INA-CBG"
                >
                  {isImportingInacbg ? <Spinner size="sm" animation="border" /> : <i className="bi bi-arrow-left-right text-success"></i>}
                  <span>Impor ke INA-CBG</span>
                </Button>
              )}

              {/* Tahap 3.5: Grouper INA-CBG - Aktif saat iDRG sudah final dan belum final INA-CBG */}
              {idrgStatus.isFinal && !inacbgStatus.isFinal && !claimFinalStatus && (
                <Button
                  size="sm"
                  className="fw-bold text-white shadow-sm d-inline-flex align-items-center gap-1"
                  style={{ background: '#6366f1', border: 'none', borderRadius: '7px', fontSize: '0.74rem', padding: '4px 10px' }}
                  onClick={() => handleProcessGrouperInacbg()}
                  disabled={isGroupingInacbg}
                  title="Jalankan Engine Grouper INA-CBG untuk menghitung kode & tarif INA-CBG"
                >
                  {isGroupingInacbg ? <Spinner size="sm" animation="border" /> : <i className="bi bi-gear-wide-connected"></i>}
                  <span>Grouper INA-CBG</span>
                </Button>
              )}

              {/* Tahap 4: Finalisasi INA-CBG (Poin 17, 18) - Nonaktif jika error (berawalan X, misal X-0-99-X) */}
              {idrgStatus.isFinal && !inacbgStatus.isFinal && !claimFinalStatus && (
                <Button
                  size="sm"
                  className="fw-bold text-white shadow-sm d-inline-flex align-items-center gap-1"
                  style={{
                    background: (!inacbgStatus.isGrouped || inacbgStatus.isUngroupable || String(inacbgStatus.code).startsWith('X')) ? '#94a3b8' : '#16a34a',
                    border: 'none', borderRadius: '7px', fontSize: '0.74rem', padding: '4px 10px',
                    cursor: (isFinalizingInacbg || !inacbgStatus.isGrouped || inacbgStatus.isUngroupable || String(inacbgStatus.code).startsWith('X')) ? 'not-allowed' : 'pointer',
                    opacity: (!inacbgStatus.isGrouped || inacbgStatus.isUngroupable || String(inacbgStatus.code).startsWith('X')) ? 0.65 : 1
                  }}
                  onClick={() => handleFinalisasiInacbg()}
                  disabled={isFinalizingInacbg || !inacbgStatus.isGrouped || inacbgStatus.isUngroupable || String(inacbgStatus.code).startsWith('X') || String(inacbgStatus.desc || '').toLowerCase().includes('ungroupable')}
                  title={
                    !inacbgStatus.isGrouped
                      ? "Tombol Dinonaktifkan: Belum dilakukan Grouper INA-CBG."
                      : (inacbgStatus.isUngroupable || String(inacbgStatus.code).startsWith('X'))
                        ? `Tombol Dinonaktifkan: Hasil Grouping INA-CBG Error / Ungroupable (${inacbgStatus.code || 'X-0-99-X'}). Perbaiki koding sebelum finalisasi.`
                        : "Kemenkes Poin 17 & 18: Finalisasi Koding INA-CBG"
                  }
                >
                  {isFinalizingInacbg ? <Spinner size="sm" animation="border" /> : <i className="bi bi-check2-circle"></i>}
                  <span>Finalisasi INA-CBG</span>
                </Button>
              )}

              {/* Tahap 4: Edit Ulang INA-CBG (Poin 19) - Muncul jika INA-CBG final tapi klaim belum final */}
              {inacbgStatus.isFinal && !claimFinalStatus && (
                <Button
                  size="sm"
                  variant="outline-light"
                  className="fw-bold shadow-sm d-inline-flex align-items-center gap-1"
                  style={{ borderRadius: '7px', fontSize: '0.74rem', padding: '4px 8px', borderColor: 'rgba(255,255,255,0.7)', background: 'rgba(0,0,0,0.15)' }}
                  onClick={() => handleReeditInacbg()}
                  title="Kemenkes Poin 19: Edit Ulang / Buka Kunci INA-CBG"
                >
                  <i className="bi bi-unlock text-warning"></i>
                  <span>Edit Ulang INA-CBG</span>
                </Button>
              )}

              {/* Tahap 5: Finalisasi Klaim (Poin 20, 21) - Hanya aktif jika INA-CBG sudah difinalkan */}
              {!claimFinalStatus && (
                <Button
                  size="sm"
                  variant="success"
                  className="fw-bold text-white shadow-sm d-inline-flex align-items-center gap-1"
                  onClick={() => { setShowGrouperModal(false); handleOpenAction(grouperTarget, 'finalisasi', 'Finalisasi Klaim (Kunci)'); }}
                  disabled={!inacbgStatus.isFinal || claimFinalStatus}
                  style={{
                    borderRadius: '7px', fontSize: '0.74rem', padding: '4px 12px',
                    background: inacbgStatus.isFinal ? '#059669' : '#94a3b8',
                    borderColor: inacbgStatus.isFinal ? '#047857' : '#94a3b8',
                    cursor: !inacbgStatus.isFinal ? 'not-allowed' : 'pointer',
                    opacity: !inacbgStatus.isFinal ? 0.65 : 1
                  }}
                  title={
                    !inacbgStatus.isFinal
                      ? "Tombol Dinonaktifkan: Finalisasi Klaim hanya dapat dilakukan setelah INA-CBG difinalkan."
                      : "Kemenkes Poin 20 & 21: Finalisasi Klaim Eksternal dengan NIK Koder"
                  }
                >
                  <i className="bi bi-lock-fill"></i> Finalisasi Klaim
                </Button>
              )}

              {/* Tahap 5: Kirim Online Kemenkes (Poin 22) - Default Disable, Enable jika Klaim Final */}
              <Button
                size="sm"
                className="fw-bold text-white shadow-sm d-inline-flex align-items-center gap-1"
                style={{ 
                  background: claimFinalStatus ? '#2563eb' : '#64748b', 
                  border: 'none', 
                  borderRadius: '7px', 
                  fontSize: '0.74rem', 
                  padding: '4px 10px',
                  cursor: claimFinalStatus && !isSendingOnline ? 'pointer' : 'not-allowed',
                  opacity: claimFinalStatus && !isSendingOnline ? 1 : 0.6
                }}
                onClick={() => handleSendClaimOnline()}
                disabled={!claimFinalStatus || isSendingOnline}
                title={claimFinalStatus ? "Kemenkes Poin 22: Kirim Klaim Individual ke Data Center Kemenkes" : "Tombol dinonaktifkan: Silakan selesaikan Finalisasi Klaim terlebih dahulu"}
              >
                {isSendingOnline ? <Spinner size="sm" animation="border" /> : <i className="bi bi-cloud-arrow-up-fill"></i>}
                <span>Kirim Klaim Online</span>
              </Button>

              {/* Cetak Klaim (Poin 23) - Default Hide, Hanya Tampil Jika Klaim Final */}
              {claimFinalStatus && (
                <Button
                  variant="light"
                  size="sm"
                  className="fw-bold text-dark shadow-sm d-inline-flex align-items-center gap-1"
                  onClick={() => handlePrintClaim(grouperTarget?.no_sep)}
                  disabled={isInvalidSep(grouperTarget?.no_sep)}
                  style={{ borderRadius: '7px', fontSize: '0.74rem', padding: '4px 10px', background: '#ffffff', borderColor: '#e2e8f0' }}
                  title="Kemenkes Poin 23: Cetak Dokumen Klaim E-Klaim"
                >
                  <i className="bi bi-printer-fill text-secondary"></i> Cetak Klaim
                </Button>
              )}

              <Button
                variant="outline-light"
                size="sm"
                onClick={() => setShowGrouperModal(false)}
                disabled={isGrouping}
                style={{ borderRadius: '7px', fontSize: '0.74rem', padding: '4px 10px', borderColor: 'rgba(255,255,255,0.45)' }}
              >
                <i className="bi bi-x-lg me-1"></i> Tutup
              </Button>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-0" style={{ background: '#f8fafc' }}>
          {grouperTarget && (
            <div>
              {/* ── INFO PASIEN STRIP ── */}
              <div style={{
                background: 'linear-gradient(135deg, #e0f2fe 0%, #e0fdf4 100%)',
                borderBottom: '1px solid #cbd5e1',
                padding: '8px 16px'
              }}>
                <div className="d-flex flex-wrap align-items-center gap-2" style={{ fontSize: '0.75rem' }}>
                  <div style={{ minWidth: '150px' }}>
                    <div className="text-muted" style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Nama Pasien</div>
                    <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.88rem', lineHeight: '1.2' }}>{grouperTarget.nm_pasien || grouperTarget.nama}</div>
                  </div>
                  <div style={{ borderLeft: '1px solid #94a3b8', paddingLeft: '10px', minWidth: '100px' }}>
                    <div className="text-muted" style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>No. Medrec</div>
                    <div className="fw-semibold font-monospace text-dark" style={{ fontSize: '0.78rem' }}>{grouperTarget.no_medrec}</div>
                  </div>
                  <div style={{ borderLeft: '1px solid #94a3b8', paddingLeft: '10px', minWidth: '100px' }}>
                    <div className="text-muted" style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>No. Nota</div>
                    <div className="fw-semibold font-monospace text-dark" style={{ fontSize: '0.78rem' }}>{grouperTarget.no_nota || '-'}</div>
                  </div>
                  <div style={{ borderLeft: '1px solid #94a3b8', paddingLeft: '10px', minWidth: '140px' }}>
                    <div className="text-muted" style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Nomor SEP</div>
                    <div className="fw-bold font-monospace text-primary" style={{ fontSize: '0.78rem' }}>{grouperTarget.no_sep || '-'}</div>
                  </div>
                  <div style={{ borderLeft: '1px solid #94a3b8', paddingLeft: '10px', minWidth: '110px' }}>
                    <div className="text-muted" style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Total Tagihan</div>
                    <div className="fw-bold text-success" style={{ fontSize: '0.8rem' }}>
                      {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(grouperTarget.total || 0)}
                    </div>
                  </div>
                  <div style={{ borderLeft: '1px solid #94a3b8', paddingLeft: '10px', minWidth: '130px' }}>
                    <div className="text-muted" style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Status DC Kemenkes</div>
                    <Badge 
                      bg={isSentOnline ? "primary" : "secondary"}
                      style={{ fontSize: '0.72rem', fontWeight: '600' }}
                    >
                      <i className={`bi ${isSentOnline ? 'bi-cloud-check-fill' : 'bi-cloud-slash'} me-1`}></i>
                      {isSentOnline ? "Terkirim ke DC" : "Belum Terkirim"}
                    </Badge>
                  </div>
                  {(() => {
                    const isRanapItem = !isRawatJalanItem(grouperTarget);
                    const tglMasukTable = grouperTarget.tgl_masuk || '-';
                    const tglPulangTable = grouperTarget.tgl_pulang || '-';

                    return (
                      <div style={{ borderLeft: '1px solid #94a3b8', paddingLeft: '10px', minWidth: '150px' }}>
                        <div className="d-flex align-items-center gap-1">
                          <span className="text-muted" style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>
                            {isRanapItem ? 'Periode Rawat Inap' : 'Tanggal Kunjungan'}
                          </span>
                        </div>
                        <div className="fw-bold text-dark font-monospace" style={{ fontSize: '0.76rem' }}>
                          {isRanapItem ? (
                            <>
                              <span className="text-success" title="Tanggal Masuk (Tgl. Masuk di Tabel)">{tglMasukTable}</span>
                              <span className="text-muted mx-1">s/d</span>
                              <span className="text-danger" title="Tanggal Pulang">{tglPulangTable}</span>
                            </>
                          ) : (
                            <span className="text-success d-inline-flex align-items-center gap-1" title="Tanggal Kunjungan (Tgl. Masuk di Tabel)">
                              <i className="bi bi-calendar2-check text-primary"></i>
                              <span>{tglMasukTable}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  {grouperTarget.diagnosa && (
                    <div style={{ borderLeft: '1px solid #94a3b8', paddingLeft: '10px', flex: 1, minWidth: '140px' }}>
                      <div className="text-muted" style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Diagnosa Utama</div>
                      <div className="fw-semibold text-dark text-truncate" title={grouperTarget.diagnosa} style={{ fontSize: '0.74rem' }}>{grouperTarget.diagnosa}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── SIKLUS BRIDGING KEMENKES (25 POIN SERTIFIKASI) STEPPER STRIP ── */}
              <div style={{
                background: '#ffffff',
                borderBottom: '1px solid #e2e8f0',
                padding: '6px 16px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
              }}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ fontSize: '0.72rem' }}>
                  <div className="d-flex align-items-center gap-1">
                    <span className="fw-bold text-dark me-1" style={{ fontSize: '0.74rem' }}>
                      <i className="bi bi-diagram-3-fill me-1" style={{ color: '#0d9488' }}></i>
                      Siklus Kemenkes:
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {/* Stage 1 & 2: iDRG */}
                    <div className="d-flex align-items-center gap-1 px-2 py-1 rounded" style={{
                      background: idrgStatus.isFinal ? '#ecfdf5' : (idrgStatus.isGrouped ? (idrgStatus.isUngroupable ? '#fef2f2' : '#eff6ff') : '#f8fafc'),
                      border: `1px solid ${idrgStatus.isFinal ? '#86efac' : (idrgStatus.isGrouped ? (idrgStatus.isUngroupable ? '#fca5a5' : '#bfdbfe') : '#e2e8f0')}`
                    }}>
                      <span className="fw-bold text-muted">1. iDRG:</span>
                      {idrgStatus.isFinal ? (
                        <Badge bg="success" className="d-inline-flex align-items-center gap-1" style={{ fontSize: '0.65rem' }}>
                          <i className="bi bi-lock-fill"></i> Final ({idrgStatus.code || 'Valid'})
                        </Badge>
                      ) : idrgStatus.isGrouped ? (
                        idrgStatus.isUngroupable ? (
                          <Badge bg="danger" style={{ fontSize: '0.65rem' }}>Ungroupable (MDC 36)</Badge>
                        ) : (
                          <Badge bg="primary" style={{ fontSize: '0.65rem' }}>Tergroup ({idrgStatus.code || '-'})</Badge>
                        )
                      ) : (
                        <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>Draft</Badge>
                      )}
                    </div>

                    <i className="bi bi-chevron-right text-muted" style={{ fontSize: '0.65rem' }}></i>

                    {/* Stage 3: Koding INA-CBG */}
                    <div className="d-flex align-items-center gap-1 px-2 py-1 rounded" style={{
                      background: idrgStatus.isFinal ? '#f0fdf4' : '#f8fafc',
                      border: `1px solid ${idrgStatus.isFinal ? '#bbf7d0' : '#e2e8f0'}`
                    }}>
                      <span className="fw-bold text-muted">2. Impor INA-CBG:</span>
                      {idrgStatus.isFinal ? (
                        <Badge bg="teal" className="text-white" style={{ background: '#0d9488', fontSize: '0.65rem' }}>
                          <i className="bi bi-check2-all me-1"></i>Koding Terbuka
                        </Badge>
                      ) : (
                        <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>
                          <i className="bi bi-lock-fill me-1"></i>Terkunci
                        </Badge>
                      )}
                    </div>

                    <i className="bi bi-chevron-right text-muted" style={{ fontSize: '0.65rem' }}></i>

                    {/* Stage 4: INA-CBG Grouping */}
                    <div className="d-flex align-items-center gap-1 px-2 py-1 rounded" style={{
                      background: inacbgStatus.isFinal ? '#ecfdf5' : (inacbgStatus.isGrouped ? (inacbgStatus.isUngroupable ? '#fef2f2' : '#f5f3ff') : '#f8fafc'),
                      border: `1px solid ${inacbgStatus.isFinal ? '#86efac' : (inacbgStatus.isGrouped ? (inacbgStatus.isUngroupable ? '#fca5a5' : '#ddd6fe') : '#e2e8f0')}`
                    }}>
                      <span className="fw-bold text-muted">3. INA-CBG:</span>
                      {inacbgStatus.isFinal ? (
                        <Badge bg="success" className="d-inline-flex align-items-center gap-1" style={{ fontSize: '0.65rem' }}>
                          <i className="bi bi-lock-fill"></i> Final ({inacbgStatus.code || 'Valid'})
                        </Badge>
                      ) : inacbgStatus.isGrouped ? (
                        inacbgStatus.isUngroupable ? (
                          <Badge bg="danger" style={{ fontSize: '0.65rem' }}>X-0-99-X / Ungroupable</Badge>
                        ) : (
                          <Badge bg="indigo" className="text-white" style={{ background: '#6366f1', fontSize: '0.65rem' }}>Tergroup ({inacbgStatus.code})</Badge>
                        )
                      ) : (
                        <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>Belum</Badge>
                      )}
                    </div>

                    <i className="bi bi-chevron-right text-muted" style={{ fontSize: '0.65rem' }}></i>

                    {/* Stage 5: Klaim Final & Kirim */}
                    <div className="d-flex align-items-center gap-1 px-2 py-1 rounded" style={{
                      background: claimFinalStatus ? '#f0fdf4' : '#f8fafc',
                      border: `1px solid ${claimFinalStatus ? '#86efac' : '#e2e8f0'}`
                    }}>
                      <span className="fw-bold text-muted">4. Klaim:</span>
                      {claimFinalStatus ? (
                        <Badge bg="success" style={{ fontSize: '0.65rem' }}>
                          <i className="bi bi-check-circle-fill me-1"></i>Final Klaim
                        </Badge>
                      ) : inacbgStatus.isFinal ? (
                        <Badge bg="warning" className="text-dark" style={{ fontSize: '0.65rem' }}>Siap Finalisasi</Badge>
                      ) : (
                        <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>Draft</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>

                {/* ── PERINGATAN SEP KOSONG / TIDAK VALID ── */}
                {isInvalidSep(grouperTarget?.no_sep) && (
                  <div style={{
                    background: '#fef2f2', border: '1px solid #fca5a5',
                    borderRadius: '10px', padding: '10px 14px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                    fontSize: '0.76rem'
                  }}>
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-exclamation-triangle-fill text-danger fs-5"></i>
                      <div>
                        <div className="fw-bold text-danger" style={{ fontSize: '0.78rem' }}>Nomor SEP Belum Diisi atau Format SEP Salah</div>
                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                          Nilai saat ini: <strong>"{grouperTarget?.no_sep || '-'}"</strong>. Tidak bisa melanjutkan grouper, silakan input SEP dulu.
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="warning"
                      className="fw-bold text-dark flex-shrink-0"
                      style={{ fontSize: '0.72rem', borderRadius: '6px', whiteSpace: 'nowrap' }}
                      onClick={() => { setShowGrouperModal(false); handleOpenEditSep(grouperTarget); }}
                    >
                      <i className="bi bi-pencil-square me-1"></i> Input / Ubah SEP
                    </Button>
                  </div>
                )}

                {/* ══ SECTION: DIAGNOSA & PROSEDUR ══ */}
                <div style={{
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  overflow: 'visible',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                }}>
                  {/* Section Header */}
                  <div style={{
                    padding: '8px 12px',
                    background: '#f1f5f9',
                    borderBottom: '1px solid #e2e8f0',
                    borderRadius: '11px 11px 0 0',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px'
                  }}>
                    <div className="d-flex align-items-center gap-2">
                      <div style={{
                        width: '24px', height: '24px', borderRadius: '6px',
                        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <i className="bi bi-clipboard2-pulse-fill text-white" style={{ fontSize: '0.65rem' }}></i>
                      </div>
                      <span className="fw-bold text-dark" style={{ fontSize: '0.82rem' }}>Diagnosa dan Prosedur iDRG</span>

                      {/* Tab Toggle Pills */}
                      <div style={{
                        display: 'flex', background: '#e2e8f0', borderRadius: '8px', padding: '2px', gap: '2px'
                      }}>
                        <button
                          onClick={() => {
                            setActiveTabDiagnosa('diagnosa');
                            handleOpenInputDiagnosa('diagnosa', 'idrg');
                          }}
                          style={{
                            border: 'none', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer',
                            fontSize: '0.72rem', fontWeight: '600', transition: 'all 0.15s',
                            background: activeTabDiagnosa === 'diagnosa' ? '#ffffff' : 'transparent',
                            color: activeTabDiagnosa === 'diagnosa' ? '#1d4ed8' : '#64748b',
                            boxShadow: activeTabDiagnosa === 'diagnosa' ? '0 1px 3px rgba(0,0,0,0.15)' : 'none'
                          }}
                        >
                          <i className="bi bi-clipboard2-pulse-fill me-1"></i>
                          ICD-10
                          <span style={{
                            marginLeft: '4px', background: activeTabDiagnosa === 'diagnosa' ? '#dbeafe' : '#e2e8f0',
                            color: activeTabDiagnosa === 'diagnosa' ? '#1d4ed8' : '#64748b',
                            borderRadius: '10px', padding: '0 6px', fontSize: '0.65rem', fontWeight: '700'
                          }}>
                            {diagnosaData.list_diagnosa?.length || 0}
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTabDiagnosa('prosedur');
                            handleOpenInputDiagnosa('prosedur', 'idrg');
                          }}
                          style={{
                            border: 'none', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer',
                            fontSize: '0.72rem', fontWeight: '600', transition: 'all 0.15s',
                            background: activeTabDiagnosa === 'prosedur' ? '#ffffff' : 'transparent',
                            color: activeTabDiagnosa === 'prosedur' ? '#059669' : '#64748b',
                            boxShadow: activeTabDiagnosa === 'prosedur' ? '0 1px 3px rgba(0,0,0,0.15)' : 'none'
                          }}
                        >
                          <i className="bi bi-scissors me-1"></i>
                          ICD-9-CM
                          <span style={{
                            marginLeft: '4px', background: activeTabDiagnosa === 'prosedur' ? '#d1fae5' : '#e2e8f0',
                            color: activeTabDiagnosa === 'prosedur' ? '#059669' : '#64748b',
                            borderRadius: '10px', padding: '0 6px', fontSize: '0.65rem', fontWeight: '700'
                          }}>
                            {diagnosaData.list_prosedur?.length || 0}
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTabDiagnosa('tarif');
                            setShowInlineInputIdrg(false);
                          }}
                          style={{
                            border: 'none', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer',
                            fontSize: '0.72rem', fontWeight: '600', transition: 'all 0.15s',
                            background: activeTabDiagnosa === 'tarif' ? '#ffffff' : 'transparent',
                            color: activeTabDiagnosa === 'tarif' ? '#b45309' : '#64748b',
                            boxShadow: activeTabDiagnosa === 'tarif' ? '0 1px 3px rgba(0,0,0,0.15)' : 'none'
                          }}
                        >
                          <i className="bi bi-calculator-fill me-1 text-warning"></i>
                          Tarif SIMRS (hasil_tarif)
                          <span style={{
                            marginLeft: '4px', background: activeTabDiagnosa === 'tarif' ? '#fef3c7' : '#e2e8f0',
                            color: activeTabDiagnosa === 'tarif' ? '#b45309' : '#64748b',
                            borderRadius: '10px', padding: '0 6px', fontSize: '0.65rem', fontWeight: '700'
                          }}>
                            {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format((grouperResult?.data?.hasil_tarif?.total_rs || hasilTarifData?.total_rs || grouperTarget?.total) || 0)}
                          </span>
                        </button>
                      </div>
                    </div>

                    {activeTabDiagnosa === 'tarif' ? (
                      <div className="d-flex align-items-center gap-2">
                        <button
                          onClick={() => fetchHasilTarif(grouperTarget)}
                          disabled={isLoadingHasilTarif}
                          title="Refresh Rincian Tarif dari SIMRS"
                          style={{
                            border: '1px solid #d97706', borderRadius: '7px', padding: '4px 10px', cursor: 'pointer',
                            fontSize: '0.72rem', fontWeight: '700', color: '#b45309', background: '#fef3c7',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                        >
                          <i className={`bi bi-arrow-clockwise ${isLoadingHasilTarif ? 'spin' : ''}`}></i>
                          <span>Muat Ulang Tarif</span>
                        </button>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center gap-2">
                        {(idrgStatus.isFinal || claimFinalStatus) && (
                          <span className="badge bg-secondary d-inline-flex align-items-center gap-1" style={{ fontSize: '0.68rem', padding: '5px 8px' }}>
                            <i className="bi bi-lock-fill"></i> Terkunci ({claimFinalStatus ? 'Klaim Final' : 'iDRG Final'})
                          </span>
                        )}
                        {/* Tombol input di-hide karena otomatis muncul saat tab diklik */}

                        <button
                          onClick={handleProcessGrouper}
                          disabled={isGrouping || idrgStatus.isFinal || !grouperTarget?.no_sep || grouperTarget.no_sep === '-'}
                          style={{
                            border: 'none', borderRadius: '7px', padding: '4px 12px',
                            cursor: (isGrouping || idrgStatus.isFinal || !grouperTarget?.no_sep || grouperTarget.no_sep === '-') ? 'not-allowed' : 'pointer',
                            opacity: (isGrouping || idrgStatus.isFinal || !grouperTarget?.no_sep || grouperTarget.no_sep === '-') ? 0.6 : 1,
                            fontSize: '0.72rem', fontWeight: '700', color: '#ffffff',
                            background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                          title={idrgStatus.isFinal ? "iDRG sudah difinalisasi" : "Jalankan Grouper Coding iDRG langsung dari tabel diagnosa & prosedur"}
                        >
                          {isGrouping ? (
                            <><Spinner size="sm" animation="border" style={{ width: '12px', height: '12px', borderWidth: '1.5px' }} /> Proses...</>
                          ) : (
                            <><i className="bi bi-cpu-fill"></i> Grouper Coding iDRG</>
                          )}
                        </button>

                        {/* Tombol Finalisasi iDRG di header tabel iDRG - Nonaktif jika MDC 36 / Ungroupable */}
                        {idrgStatus.isGrouped && !idrgStatus.isFinal && (
                          <button
                            onClick={() => handleFinalisasiIdrg()}
                            disabled={isFinalizingIdrg || idrgStatus.isUngroupable || String(idrgStatus.code).startsWith('36') || String(idrgStatus.desc || '').toLowerCase().includes('ungroupable')}
                            style={{
                              border: 'none', borderRadius: '7px', padding: '4px 12px',
                              cursor: (isFinalizingIdrg || idrgStatus.isUngroupable || String(idrgStatus.code).startsWith('36')) ? 'not-allowed' : 'pointer',
                              opacity: (isFinalizingIdrg || idrgStatus.isUngroupable || String(idrgStatus.code).startsWith('36')) ? 0.6 : 1,
                              fontSize: '0.72rem', fontWeight: '700', color: '#ffffff',
                              background: (idrgStatus.isUngroupable || String(idrgStatus.code).startsWith('36')) ? '#94a3b8' : 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                              boxShadow: '0 1px 4px rgba(2,132,199,0.3)',
                              display: 'flex', alignItems: 'center', gap: '4px'
                            }}
                            title={
                              (idrgStatus.isUngroupable || String(idrgStatus.code).startsWith('36'))
                                ? "Kasus Ungroupable (MDC 36): Tombol Dinonaktifkan"
                                : "Finalisasi Koding iDRG untuk menyelesaikan proses iDRG dan membuka proses INACBG"
                            }
                          >
                            {isFinalizingIdrg ? (
                              <><Spinner size="sm" animation="border" style={{ width: '12px', height: '12px', borderWidth: '1.5px' }} /> Finalisasi iDRG...</>
                            ) : (
                              <><i className="bi bi-shield-lock-fill"></i> Finalisasi iDRG</>
                            )}
                          </button>
                        )}

                        {/* Tombol Edit Ulang iDRG jika sudah grouper atau final */}
                        {isIdrgGrouperLocked && !claimFinalStatus && (
                          <button
                            onClick={() => handleReeditIdrg()}
                            style={{
                              border: '1px solid #f59e0b', borderRadius: '7px', padding: '4px 10px',
                              cursor: 'pointer',
                              fontSize: '0.72rem', fontWeight: '700', color: '#b45309',
                              background: '#fffbeb',
                              boxShadow: '0 1px 3px rgba(245,158,11,0.2)',
                              display: 'flex', alignItems: 'center', gap: '4px'
                            }}
                            title="Buka kunci Grouper iDRG untuk edit ulang koding diagnosa & prosedur"
                          >
                            <i className="bi bi-unlock-fill text-warning"></i>
                            <span>Edit Ulang iDRG</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Inline Form Input Diagnosa / Prosedur (iDRG) ── */}
                  {showInlineInputIdrg && (
                    <div style={{
                      background: inputDiagnosaType === 'diagnosa'
                        ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
                        : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                      borderBottom: `2px solid ${inputDiagnosaType === 'diagnosa' ? '#93c5fd' : '#6ee7b7'}`,
                      padding: '10px 12px'
                    }}>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <i className={`bi ${inputDiagnosaType === 'diagnosa' ? 'bi-clipboard2-pulse-fill text-primary' : 'bi-scissors text-success'}`} style={{ fontSize: '0.85rem' }}></i>
                        <span style={{ fontSize: '0.78rem', fontWeight: '700', color: inputDiagnosaType === 'diagnosa' ? '#1d4ed8' : '#047857' }}>
                          {inputDiagnosaType === 'diagnosa' ? 'Input Diagnosa ICD-10 IM (iDRG)' : 'Input Prosedur ICD-9-CM IM (iDRG)'}
                        </span>
                        {isIdrgGrouperLocked && (
                          <span className="badge bg-warning text-dark px-2 py-1" style={{ fontSize: '0.65rem' }}>
                            <i className="bi bi-lock-fill me-1"></i>Terkunci Grouper
                          </span>
                        )}
                        {isLoadingMasterIcd && (
                          <span style={{ fontSize: '0.68rem', color: '#64748b' }} className="d-flex align-items-center gap-1">
                            <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px', borderWidth: '1.5px' }} /> Memuat master...
                          </span>
                        )}
                        <button
                          onClick={() => { setShowInlineInputIdrg(false); setIcdCheckMsg(null); closeAutocomplete(); }}
                          style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', padding: '2px 6px', borderRadius: '4px', fontSize: '0.8rem' }}
                          title="Tutup form input"
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </div>
                      <div className="d-flex align-items-start gap-2 flex-wrap">
                        {/* Status Diagnosa (hanya untuk diagnosa, bukan prosedur) */}
                        {inputDiagnosaType === 'diagnosa' && (
                          <div style={{ minWidth: '120px' }}>
                            <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: '600', marginBottom: '3px' }}>Status</div>
                            <select
                              disabled={isIdrgGrouperLocked}
                              size="1"
                              value={formInputDiagnosaIdrg.ket}
                              onChange={e => {
                                const val = e.target.value;
                                setFormInputDiagnosaIdrg(prev => ({ ...prev, ket: val, utama: val === 'Utama' ? 1 : 0 }));
                                setFormInputDiagnosa(prev => ({ ...prev, ket: val, utama: val === 'Utama' ? 1 : 0 }));
                              }}
                              style={{ fontSize: '0.75rem', fontWeight: '600', border: '1px solid #93c5fd', borderRadius: '6px', padding: '3px 6px', width: '100%', background: '#fff' }}
                            >
                              <option value="Utama">🟡 Utama</option>
                              <option value="Sekunder">🔵 Sekunder</option>
                              <option value="Tambahan">⚪ Tambahan</option>
                            </select>
                          </div>
                        )}
                        {/* Kode ICD dengan autocomplete */}
                        <div style={{ flex: '0 0 140px', position: 'relative' }}>
                          <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: '600', marginBottom: '3px' }}>
                            {inputDiagnosaType === 'diagnosa' ? 'Kode ICD-10' : 'Kode ICD-9-CM'}
                          </div>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <input
                              disabled={isIdrgGrouperLocked}
                              type="text"
                              value={formInputDiagnosaIdrg.kd_icd}
                              placeholder={inputDiagnosaType === 'diagnosa' ? 'Cari kode/nama...' : 'Cari kode/nama...'}
                              onClick={() => {
                                if (isIdrgGrouperLocked) return;
                                setInputDiagnosaTarget('idrg');
                                openAutocomplete('idrg', inputDiagnosaType === 'diagnosa' ? 'icd10' : 'icd9');
                                if (inputDiagnosaType === 'diagnosa') {
                                  if (masterIcd10.length === 0 && !isLoadingMasterIcd) fetchMasterIcd('diagnosa');
                                } else {
                                  if (masterIcd9.length === 0 && !isLoadingMasterIcd) fetchMasterIcd('prosedur');
                                }
                              }}
                              onFocus={() => {
                                if (isIdrgGrouperLocked) return;
                                setInputDiagnosaTarget('idrg');
                                openAutocomplete('idrg', inputDiagnosaType === 'diagnosa' ? 'icd10' : 'icd9');
                                if (inputDiagnosaType === 'diagnosa') {
                                  if (masterIcd10.length === 0 && !isLoadingMasterIcd) fetchMasterIcd('diagnosa');
                                } else {
                                  if (masterIcd9.length === 0 && !isLoadingMasterIcd) fetchMasterIcd('prosedur');
                                }
                              }}
                              onChange={e => {
                                const val = e.target.value;
                                setInputDiagnosaTarget('idrg');
                                const isHdr = inputDiagnosaType === 'diagnosa' ? isHeaderIcd10Code(val) : false;
                                const isAccNo = inputDiagnosaType === 'prosedur' ? isAccpdxNoProcedure(val) : false;
                                setFormInputDiagnosaIdrg(prev => ({ ...prev, kd_icd: val, nm_icd: '', isHeader: isHdr, accpdx: isAccNo ? 'N' : undefined }));
                                setFormInputDiagnosa(prev => ({ ...prev, kd_icd: val, nm_icd: '', isHeader: isHdr, accpdx: isAccNo ? 'N' : undefined }));
                                if (isHdr) {
                                  setIcdCheckMsg({ type: 'danger', text: `Diagnosa ${val} merupakan Header Code (tidak valid untuk iDRG dan tidak dapat disimpan).` });
                                } else if (isAccNo) {
                                  setIcdCheckMsg({ type: 'danger', text: `Prosedur ${val} memiliki status accpdx = N (tidak valid untuk iDRG dan tidak dapat disimpan).` });
                                } else {
                                  setIcdCheckMsg(null);
                                }
                                openAutocomplete('idrg', inputDiagnosaType === 'diagnosa' ? 'icd10' : 'icd9');
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  closeAutocomplete();
                                  handleCheckIcd(formInputDiagnosaIdrg.kd_icd, 'idrg');
                                } else if (e.key === 'Escape') {
                                  closeAutocomplete();
                                }
                              }}
                              onBlur={() => {
                                setTimeout(() => { closeAutocomplete(); }, 250);
                                if (formInputDiagnosaIdrg.kd_icd && !formInputDiagnosaIdrg.nm_icd) handleCheckIcd(formInputDiagnosaIdrg.kd_icd, 'idrg');
                              }}
                              style={{
                                flex: 1, fontSize: '0.78rem', fontWeight: formInputDiagnosaIdrg.kd_icd ? '700' : '400',
                                fontFamily: 'monospace', border: `1px solid ${inputDiagnosaType === 'diagnosa' ? '#93c5fd' : '#6ee7b7'}`,
                                borderRadius: '6px', padding: '3px 8px', outline: 'none', width: '100%',
                                color: inputDiagnosaType === 'diagnosa' ? '#1d4ed8' : '#047857'
                              }}
                            />
                          </div>
                          {/* Autocomplete dropdown — ICD-10 */}
                          {inputDiagnosaType === 'diagnosa' && activeAutocomplete?.target === 'idrg' && activeAutocomplete?.type === 'icd10' && (
                            <div style={{
                              position: 'absolute', top: '100%', left: 0, width: '560px', maxWidth: '85vw', zIndex: 9999,
                              maxHeight: '280px', overflowY: 'auto', background: '#ffffff',
                              border: '2px solid #3b82f6', borderRadius: '0 0 8px 8px',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.22)', marginTop: '-1px'
                            }}>
                              <div className="px-2 py-1 sticky-top" style={{ background: '#eff6ff', borderBottom: '1px solid #bfdbfe', fontSize: '0.68rem', color: '#1d4ed8', fontWeight: '700' }}>
                                Master ICD-10 IM (iDRG) — {isLoadingMasterIcd ? 'Memuat...' : `${filteredIcd10SuggestionsIdrg.length} hasil`}
                              </div>
                              {isLoadingMasterIcd ? (
                                <div className="p-3 text-center text-muted" style={{ fontSize: '0.75rem' }}>
                                  <Spinner animation="border" size="sm" variant="primary" className="me-2" /> Memuat...
                                </div>
                              ) : filteredIcd10SuggestionsIdrg.slice(0, 50).map((item, idx) => {
                                const code = item.icd || item.kode || item.kd_icd || '';
                                const desc = item.deskripsi || item.nm_icd || item.penyakit || '';
                                const isHeader = item.validcode === 0 || item.validcode === '0' || item.validcode === false;
                                const isAccpdxNo = String(item.accpdx || '').trim().toUpperCase() === 'N';
                                return (
                                  <div key={`il10-idrg-${code}-${idx}`}
                                    onMouseDown={(e) => {
                                      if (isHeader) {
                                        e.preventDefault();
                                        setIcdCheckMsg({ type: 'danger', text: `Diagnosa ${code} merupakan Header Code (tidak valid untuk iDRG dan tidak dapat dipilih).` });
                                        notify('warning', 'Header Code Tidak Dapat Dipilih', `Kode ${code} adalah Header Code dan tidak dapat dipilih untuk pengklaiman iDRG.`);
                                        return;
                                      }
                                      handleSelectIcd10FromDropdown(item, 'idrg');
                                      closeAutocomplete();
                                    }}
                                    title={isHeader ? `Header Code: ${code} tidak dapat dipilih untuk iDRG` : desc}
                                    style={{
                                      padding: '6px 12px',
                                      cursor: isHeader ? 'not-allowed' : 'pointer',
                                      opacity: isHeader ? 0.55 : 1,
                                      borderBottom: '1px solid #f1f5f9',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      background: isHeader ? '#fef2f2' : '#ffffff'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = isHeader ? '#fee2e2' : '#eff6ff'}
                                    onMouseLeave={e => e.currentTarget.style.background = isHeader ? '#fef2f2' : '#fff'}
                                  >
                                    <span className={`badge ${isHeader ? 'bg-danger' : 'bg-primary'} font-monospace flex-shrink-0`} style={{ fontSize: '0.72rem', minWidth: '60px', textAlign: 'center' }}>{code}</span>
                                    <span style={{ fontSize: '0.76rem', color: isHeader ? '#991b1b' : '#1e293b', flex: 1, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.25' }}>
                                      {desc}
                                      {isHeader && <span className="ms-1 text-danger fw-bold" style={{ fontSize: '0.68rem' }}>(Header - Tidak Dapat Dipilih)</span>}
                                    </span>
                                    {isHeader && (
                                      <span className="badge bg-danger" style={{ fontSize: '0.62rem' }}><i className="bi bi-slash-circle me-1"></i>Header</span>
                                    )}
                                    {isAccpdxNo && !isHeader && (
                                      <span className="badge bg-warning text-dark" style={{ fontSize: '0.62rem' }}>accpdx: N (Sekunder)</span>
                                    )}
                                  </div>
                                );
                              })}
                              {!isLoadingMasterIcd && filteredIcd10SuggestionsIdrg.length === 0 && (
                                <div className="p-3 text-center text-muted" style={{ fontSize: '0.73rem' }}>Tidak ada hasil untuk "{formInputDiagnosaIdrg.kd_icd}"</div>
                              )}
                            </div>
                          )}
                          {/* Autocomplete dropdown — ICD-9 */}
                          {inputDiagnosaType === 'prosedur' && activeAutocomplete?.target === 'idrg' && activeAutocomplete?.type === 'icd9' && (
                            <div style={{
                              position: 'absolute', top: '100%', left: 0, width: '560px', maxWidth: '85vw', zIndex: 9999,
                              maxHeight: '280px', overflowY: 'auto', background: '#ffffff',
                              border: '2px solid #10b981', borderRadius: '0 0 8px 8px',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.22)', marginTop: '-1px'
                            }}>
                              <div className="px-2 py-1 sticky-top" style={{ background: '#f0fdf4', borderBottom: '1px solid #bbf7d0', fontSize: '0.68rem', color: '#047857', fontWeight: '700' }}>
                                Master ICD-9-CM IM (iDRG) — {isLoadingMasterIcd ? 'Memuat...' : `${filteredIcd9SuggestionsIdrg.length} hasil`}
                              </div>
                              {isLoadingMasterIcd ? (
                                <div className="p-3 text-center text-muted" style={{ fontSize: '0.75rem' }}>
                                  <Spinner animation="border" size="sm" variant="success" className="me-2" /> Memuat...
                                </div>
                              ) : filteredIcd9SuggestionsIdrg.slice(0, 50).map((item, idx) => {
                                const code = item.kode || item.icd || item.kd_icd || '';
                                const desc = item.deskripsi || item.nm_icd || '';
                                const isAccpdxNo = String(item.accpdx || '').trim().toUpperCase() === 'N';
                                return (
                                  <div key={`il9-idrg-${code}-${idx}`}
                                    onMouseDown={(e) => {
                                      if (isAccpdxNo) {
                                        e.preventDefault();
                                        setIcdCheckMsg({ type: 'danger', text: `Prosedur ${code} tidak bisa dipilih karena accpdx = N (tidak dapat disimpan).` });
                                        notify('warning', 'Prosedur Tidak Valid', `Prosedur ${code} tidak bisa dipilih karena accpdx = N.`);
                                        return;
                                      }
                                      handleSelectIcd9FromDropdown(item, 'idrg');
                                      closeAutocomplete();
                                    }}
                                    title={isAccpdxNo ? `Prosedur ${code} memiliki status accpdx = N (tidak dapat dipilih untuk iDRG)` : desc}
                                    style={{
                                      padding: '6px 12px',
                                      cursor: isAccpdxNo ? 'not-allowed' : 'pointer',
                                      opacity: isAccpdxNo ? 0.55 : 1,
                                      borderBottom: '1px solid #f1f5f9',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      background: isAccpdxNo ? '#fef2f2' : '#ffffff'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = isAccpdxNo ? '#fee2e2' : '#f0fdf4'}
                                    onMouseLeave={e => e.currentTarget.style.background = isAccpdxNo ? '#fef2f2' : '#fff'}
                                  >
                                    <span className={`badge ${isAccpdxNo ? 'bg-danger' : 'bg-success'} font-monospace flex-shrink-0`} style={{ fontSize: '0.72rem', minWidth: '60px', textAlign: 'center' }}>{code}</span>
                                    <span style={{ fontSize: '0.76rem', color: isAccpdxNo ? '#991b1b' : '#1e293b', flex: 1, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.25' }}>
                                      {desc}
                                      {isAccpdxNo && <span className="ms-1 text-danger fw-bold" style={{ fontSize: '0.68rem' }}>(accpdx: N - Tidak Dapat Dipilih)</span>}
                                    </span>
                                    {isAccpdxNo && (
                                      <span className="badge bg-danger" style={{ fontSize: '0.62rem' }}><i className="bi bi-slash-circle me-1"></i>accpdx: N</span>
                                    )}
                                  </div>
                                );
                              })}
                              {!isLoadingMasterIcd && filteredIcd9SuggestionsIdrg.length === 0 && (
                                <div className="p-3 text-center text-muted" style={{ fontSize: '0.73rem' }}>Tidak ada hasil untuk "{formInputDiagnosaIdrg.kd_icd}"</div>
                              )}
                            </div>
                          )}
                        </div>
                        {/* Nama Diagnosa / Prosedur */}
                        <div style={{ flex: 1, minWidth: '160px' }}>
                          <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: '600', marginBottom: '3px' }}>
                            {inputDiagnosaType === 'diagnosa' ? 'Nama Diagnosa' : 'Nama Prosedur'}
                          </div>
                          <input
                            disabled={isIdrgGrouperLocked}
                            type="text"
                            value={formInputDiagnosaIdrg.nm_icd}
                            onChange={e => {
                              const v = e.target.value;
                              setFormInputDiagnosaIdrg(prev => ({ ...prev, nm_icd: v }));
                              setFormInputDiagnosa(prev => ({ ...prev, nm_icd: v }));
                            }}
                            placeholder="Terisi otomatis saat pilih dari daftar..."
                            style={{
                              width: '100%', fontSize: '0.75rem', fontWeight: formInputDiagnosaIdrg.nm_icd ? '600' : '400',
                              border: `1px solid ${inputDiagnosaType === 'diagnosa' ? '#93c5fd' : '#6ee7b7'}`,
                              borderRadius: '6px', padding: '3px 8px', outline: 'none'
                            }}
                          />
                        </div>
                        {/* Multiplicity (untuk prosedur saja) */}
                        {inputDiagnosaType === 'prosedur' && (
                          <div style={{ minWidth: '80px' }}>
                            <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: '600', marginBottom: '3px' }}>Qty (x)</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <button disabled={isIdrgGrouperLocked} onClick={() => handleMultiplicityChange(Math.max(1, (Number(formInputDiagnosaIdrg.multiplicity) || 1) - 1), 'idrg')}
                                style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', background: '#f8fafc', fontSize: '0.75rem' }}>−</button>
                              <input disabled={isIdrgGrouperLocked} type="number" min={1} max={99} value={formInputDiagnosaIdrg.multiplicity || 1}
                                onChange={e => handleMultiplicityChange(e.target.value, 'idrg')}
                                style={{ width: '38px', textAlign: 'center', fontSize: '0.78rem', fontWeight: '700', fontFamily: 'monospace', border: '1px solid #6ee7b7', borderRadius: '4px', padding: '2px 4px' }}
                              />
                              <button disabled={isIdrgGrouperLocked} onClick={() => handleMultiplicityChange((Number(formInputDiagnosaIdrg.multiplicity) || 1) + 1, 'idrg')}
                                style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', background: '#f8fafc', fontSize: '0.75rem' }}>+</button>
                            </div>
                          </div>
                        )}
                        {/* Tombol Simpan & Batal */}
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', paddingBottom: '0' }}>
                          <button
                            onClick={(e) => handleSaveInputDiagnosa(e, { target: 'idrg' })}
                            disabled={isIdrgGrouperLocked || isSavingDiagnosa || !formInputDiagnosaIdrg.kd_icd || isIdrgHeaderDiagnosis || isIdrgProcedureAccpdxNo}
                            title={isIdrgHeaderDiagnosis ? "Diagnosa Header Code tidak dapat disimpan pada iDRG" : isIdrgProcedureAccpdxNo ? "Prosedur dengan accpdx = N tidak dapat disimpan pada iDRG" : (inputDiagnosaType === 'diagnosa' ? "Simpan Diagnosa" : "Simpan Prosedur")}
                            style={{
                              border: 'none', borderRadius: '6px', padding: '4px 14px',
                              fontSize: '0.75rem', fontWeight: '700', color: '#ffffff',
                              cursor: (isIdrgGrouperLocked || isSavingDiagnosa || !formInputDiagnosaIdrg.kd_icd || isIdrgHeaderDiagnosis || isIdrgProcedureAccpdxNo) ? 'not-allowed' : 'pointer',
                              opacity: (isIdrgGrouperLocked || isSavingDiagnosa || !formInputDiagnosaIdrg.kd_icd || isIdrgHeaderDiagnosis || isIdrgProcedureAccpdxNo) ? 0.5 : 1,
                              background: (isIdrgHeaderDiagnosis || isIdrgProcedureAccpdxNo)
                                ? '#94a3b8'
                                : (inputDiagnosaType === 'diagnosa' ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'linear-gradient(135deg, #059669 0%, #047857 100%)'),
                              display: 'flex', alignItems: 'center', gap: '5px', marginTop: '17px'
                            }}
                          >
                            {isSavingDiagnosa
                              ? <><Spinner animation="border" size="sm" style={{ width: '11px', height: '11px', borderWidth: '1.5px' }} /> Simpan...</>
                              : isIdrgHeaderDiagnosis
                                ? <><i className="bi bi-slash-circle"></i> Header (Nonaktif)</>
                                : isIdrgProcedureAccpdxNo
                                  ? <><i className="bi bi-slash-circle"></i> accpdx=N (Nonaktif)</>
                                  : <><i className="bi bi-check-circle-fill"></i> Simpan</>
                            }
                          </button>
                          <button
                            onClick={() => {
                              setShowInlineInputIdrg(false);
                              setIcdCheckMsg(null);
                              closeAutocomplete();
                              setFormInputDiagnosaIdrg(prev => ({ ...prev, kd_icd: '', nm_icd: '', multiplicity: 1, isHeader: false, validcode: undefined }));
                              setFormInputDiagnosa(prev => ({ ...prev, kd_icd: '', nm_icd: '', multiplicity: 1, isHeader: false, validcode: undefined }));
                            }}
                            style={{
                              border: '1px solid #d1d5db', borderRadius: '6px', padding: '4px 10px',
                              fontSize: '0.75rem', fontWeight: '600', color: '#64748b', cursor: 'pointer',
                              background: '#ffffff', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '17px'
                            }}
                          >
                            <i className="bi bi-x-circle"></i> Batal
                          </button>
                        </div>
                      </div>
                      {/* Feedback validasi kode */}
                      {isIdrgHeaderDiagnosis ? (
                        <div className="d-flex align-items-center gap-2 mt-2 p-2 rounded-2 bg-danger bg-opacity-10 border border-danger border-opacity-25" style={{ fontSize: '0.72rem' }}>
                          <i className="bi bi-exclamation-triangle-fill text-danger flex-shrink-0 fs-6"></i>
                          <span className="fw-semibold text-danger">
                            Kode <strong>{formInputDiagnosaIdrg.kd_icd}</strong> merupakan <strong>Header Code</strong> (tidak valid untuk coding iDRG). Tombol Simpan dinonaktifkan untuk menghindari penyimpanan. Silakan pilih sub-kode diagnosa yang spesifik.
                          </span>
                        </div>
                      ) : isIdrgProcedureAccpdxNo ? (
                        <div className="d-flex align-items-center gap-2 mt-2 p-2 rounded-2 bg-danger bg-opacity-10 border border-danger border-opacity-25" style={{ fontSize: '0.72rem' }}>
                          <i className="bi bi-exclamation-triangle-fill text-danger flex-shrink-0 fs-6"></i>
                          <span className="fw-semibold text-danger">
                            Prosedur <strong>{formInputDiagnosaIdrg.kd_icd}</strong> memiliki status <strong>accpdx = N</strong> (tidak valid untuk coding iDRG). Tombol Simpan dinonaktifkan untuk menghindari penyimpanan.
                          </span>
                        </div>
                      ) : (
                        icdCheckMsg && (
                          <div className={`d-flex align-items-center gap-2 mt-2 p-2 rounded-2 ${icdCheckMsg.type === 'success' ? 'bg-success bg-opacity-10 border border-success border-opacity-25' : icdCheckMsg.type === 'info' ? 'bg-info bg-opacity-10 border border-info border-opacity-25' : 'bg-danger bg-opacity-10 border border-danger border-opacity-25'}`} style={{ fontSize: '0.72rem' }}>
                            <i className={`bi ${icdCheckMsg.type === 'success' ? 'bi-check-circle-fill text-success' : icdCheckMsg.type === 'info' ? 'bi-info-circle-fill text-info' : 'bi-exclamation-triangle-fill text-danger'} flex-shrink-0`}></i>
                            <span className={`fw-semibold ${icdCheckMsg.type === 'success' ? 'text-success' : icdCheckMsg.type === 'info' ? 'text-info' : 'text-danger'}`}>{icdCheckMsg.text}</span>
                          </div>
                        )
                      )}
                    </div>
                  )}

                  {/* Table Content */}
                  {activeTabDiagnosa === 'tarif' ? (
                    renderTabTarifContent()
                  ) : isLoadingDiagnosa ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" style={{ color: '#6366f1' }} className="me-2" />
                      <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Memuat data diagnosa &amp; prosedur...</span>
                    </div>
                  ) : activeTabDiagnosa === 'diagnosa' ? (
                    <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr style={{ background: '#1e293b', color: '#e2e8f0' }}>
                            <th style={{ padding: '6px 8px', textAlign: 'center', width: '28px', fontWeight: '600' }}>#</th>
                            <th style={{ padding: '6px 8px', textAlign: 'center', width: '75px', fontWeight: '600' }}>Kode ICD</th>
                            <th style={{ padding: '6px 8px', textAlign: 'center', width: '80px', fontWeight: '600' }}>Status</th>
                            <th style={{ padding: '6px 8px', fontWeight: '600' }}>Nama Diagnosa (ICD-10)</th>
                            <th style={{ padding: '6px 10px', textAlign: 'right', width: '135px', fontWeight: '600' }}>Aksi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diagnosaData.list_diagnosa?.length > 0 ? (
                            diagnosaData.list_diagnosa.map((diag, idx) => {
                              const isUtama = diag.utama === 1 || String(diag.utama) === '1' || String(diag.ket || '').toLowerCase().includes('utama');
                              return (
                              <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                <td style={{ padding: '5px 8px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>{diag.urutan && parseInt(diag.urutan) > 0 ? diag.urutan : (idx + 1)}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                  <span style={{
                                    display: 'inline-block', background: '#fef2f2', color: '#b91c1c',
                                    border: '1px solid #fca5a5', borderRadius: '5px',
                                    padding: '1px 6px', fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: '700'
                                  }}>{diag.kd_icd}</span>
                                </td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                  {isUtama ? (
                                    <span style={{
                                      display: 'inline-flex', alignItems: 'center', gap: '3px',
                                      background: '#eff6ff', color: '#1d4ed8', borderRadius: '6px',
                                      padding: '2px 7px', fontSize: '0.65rem', fontWeight: '700',
                                      border: '1px solid #bfdbfe'
                                    }}>
                                      <i className="bi bi-star-fill text-warning" style={{ fontSize: '0.6rem' }}></i> Utama
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => handleSetDiagnosaUtama(diag, 'idrg')}
                                      disabled={isIdrgGrouperLocked}
                                      title={isIdrgGrouperLocked ? "Terkunci (iDRG Final)" : "Klik untuk jadikan Diagnosa Utama & Urutan Pertama"}
                                      style={{
                                        border: '1px solid #fde68a', background: '#fffbeb', color: '#92400e',
                                        borderRadius: '6px', padding: '2px 7px', fontSize: '0.65rem', fontWeight: '600',
                                        cursor: isIdrgGrouperLocked ? 'not-allowed' : 'pointer',
                                        opacity: isIdrgGrouperLocked ? 0.5 : 1,
                                        display: 'inline-flex', alignItems: 'center', gap: '3px'
                                      }}
                                    >
                                      <i className="bi bi-arrow-up-circle text-warning"></i> Sekunder
                                    </button>
                                  )}
                                </td>
                                <td style={{ padding: '5px 8px', fontWeight: '600', color: '#1e293b' }} title={diag.nama_icd || diag.nm_icd}>{diag.nama_icd || diag.nm_icd}</td>
                                <td style={{ padding: '5px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                  <div className="d-flex align-items-center justify-content-end gap-1">
                                    {!isUtama && (
                                      <button
                                        onClick={() => handleSetDiagnosaUtama(diag, 'idrg')}
                                        disabled={isIdrgGrouperLocked}
                                        title={isIdrgGrouperLocked ? "Terkunci (iDRG Final)" : "Jadikan Diagnosa Utama & Urutan Pertama"}
                                        style={{
                                          border: '1px solid #fde047', background: '#fefce8', color: '#a16207',
                                          borderRadius: '5px', padding: '2px 6px',
                                          cursor: isIdrgGrouperLocked ? 'not-allowed' : 'pointer',
                                          opacity: isIdrgGrouperLocked ? 0.35 : 1,
                                          fontSize: '0.65rem',
                                          lineHeight: 1,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '2px'
                                        }}
                                      >
                                        <i className="bi bi-arrow-up-circle-fill text-warning"></i>
                                        <span style={{ fontSize: '0.62rem', fontWeight: '700' }}>Utama</span>
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleOpenEditIcd(diag, 'diagnosa', 'idrg', diag.urutan && parseInt(diag.urutan) > 0 ? diag.urutan : (idx + 1))}
                                      disabled={isIdrgGrouperLocked}
                                      title={isIdrgGrouperLocked ? "Terkunci (iDRG Final)" : `Edit Kode ICD (No. Urut #${diag.urutan && parseInt(diag.urutan) > 0 ? diag.urutan : (idx + 1)})`}
                                      style={{
                                        border: '1px solid #93c5fd', background: '#eff6ff', color: '#1d4ed8',
                                        borderRadius: '5px', padding: '2px 6px',
                                        cursor: isIdrgGrouperLocked ? 'not-allowed' : 'pointer',
                                        opacity: isIdrgGrouperLocked ? 0.35 : 1,
                                        fontSize: '0.65rem',
                                        lineHeight: 1
                                      }}
                                    >
                                      <i className="bi bi-pencil-square"></i>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteDiagnosa(diag, 'diagnosa')}
                                      disabled={isIdrgGrouperLocked}
                                      title={idrgStatus.isFinal || claimFinalStatus ? "Terkunci (iDRG Final)" : "Hapus Diagnosa"}
                                      style={{
                                        border: '1px solid #fca5a5', background: '#fff5f5', color: '#dc2626',
                                        borderRadius: '5px', padding: '2px 6px',
                                        cursor: isIdrgGrouperLocked ? 'not-allowed' : 'pointer',
                                        opacity: isIdrgGrouperLocked ? 0.35 : 1,
                                        fontSize: '0.65rem',
                                        lineHeight: 1
                                      }}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                            })
                          ) : (
                            <tr>
                              <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>
                                <i className="bi bi-clipboard2-x d-block mb-1" style={{ fontSize: '1.3rem', opacity: 0.4 }}></i>
                                Belum ada diagnosa. Klik <strong style={{ color: '#3b82f6' }}>Input Diagnosa</strong> untuk menambahkan.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div>
                      {/* Banner Terverifikasi iDRG jika data response tersedia */}
                      {idrgProcedureList && idrgProcedureList.length > 0 && (
                        <div className="mb-2 px-3 py-2 rounded-3 d-flex align-items-center justify-content-between shadow-xs" style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', fontSize: '0.72rem' }}>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-patch-check-fill text-success fs-6"></i>
                            <span className="text-dark">
                              <strong>{idrgProcedureList.length} Prosedur</strong> telah berhasil terverifikasi oleh server Engine iDRG Kemenkes.
                            </span>
                          </div>
                          <Badge bg="success" className="px-2 py-1 shadow-xs fw-medium d-inline-flex align-items-center gap-1" style={{ fontSize: '0.66rem' }}>
                            <i className="bi bi-check2-all"></i> idrg_procedure_set 200 Ok
                          </Badge>
                        </div>
                      )}

                      <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr style={{ background: '#1e293b', color: '#e2e8f0' }}>
                              <th style={{ padding: '6px 8px', textAlign: 'center', width: '28px', fontWeight: '600' }}>#</th>
                              <th style={{ padding: '6px 8px', textAlign: 'center', width: '75px', fontWeight: '600' }}>Kode ICD</th>
                              <th style={{ padding: '6px 8px', fontWeight: '600' }}>Nama Prosedur (ICD-9-IM &amp; Display)</th>
                              <th style={{ padding: '6px 8px', textAlign: 'center', width: '75px', fontWeight: '600' }}>Multiplicity</th>
                              <th style={{ padding: '6px 8px', textAlign: 'center', width: '75px', fontWeight: '600' }}>Status iDRG</th>
                              <th style={{ padding: '6px 10px', textAlign: 'right', width: '85px', fontWeight: '600' }}>Aksi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {diagnosaData.list_prosedur?.length > 0 ? (
                              diagnosaData.list_prosedur.map((proc, idx) => {
                                const matchedIdrg = idrgProcedureList?.find(p => String(p.code).trim() === String(proc.kd_icd).trim());
                                return (
                                  <tr key={proc.id ? `proc-${proc.id}` : idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                    <td style={{ padding: '5px 8px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>{proc.urutan && parseInt(proc.urutan) > 0 ? proc.urutan : (idx + 1)}</td>
                                    <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                      <span style={{
                                        display: 'inline-block', background: '#f0fdf4', color: '#047857',
                                        border: '1px solid #86efac', borderRadius: '5px',
                                        padding: '1px 6px', fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: '700'
                                      }}>{proc.kd_icd}</span>
                                    </td>
                                    <td style={{ padding: '5px 8px', color: '#1e293b' }}>
                                      <div className="fw-semibold text-truncate" title={matchedIdrg?.display || proc.nama_icd || proc.nm_icd}>
                                        {matchedIdrg?.display || proc.nama_icd || proc.nm_icd}
                                      </div>
                                      {matchedIdrg?.display && (proc.nama_icd || proc.nm_icd) && matchedIdrg.display !== (proc.nama_icd || proc.nm_icd) && (
                                        <div className="text-muted text-truncate" style={{ fontSize: '0.65rem' }}>
                                          SIMRS: {proc.nama_icd || proc.nm_icd}
                                        </div>
                                      )}
                                    </td>
                                    <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                      <Badge bg="light" className="text-secondary border font-monospace" style={{ fontSize: '0.67rem' }}>
                                        x{proc.qty || proc.multiplicity || (proc.kd_icd?.includes('+') ? proc.kd_icd.split('+')[1] : (matchedIdrg?.multiplicity || 1))}
                                      </Badge>
                                    </td>
                                    <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                      {matchedIdrg ? (
                                        <Badge bg="success" className="px-2 py-0 fw-medium shadow-xs" style={{ fontSize: '0.65rem' }}>
                                          <i className="bi bi-check-circle-fill me-1"></i>Valid
                                        </Badge>
                                      ) : (
                                        <span className="text-muted" style={{ fontSize: '0.65rem' }}>Lokal</span>
                                      )}
                                    </td>
                                    <td style={{ padding: '5px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                      <div className="d-flex align-items-center justify-content-end gap-1">
                                        <button
                                          onClick={() => handleOpenEditIcd(proc, 'prosedur', 'idrg', proc.urutan && parseInt(proc.urutan) > 0 ? proc.urutan : (idx + 1))}
                                          disabled={isIdrgGrouperLocked}
                                          title={isIdrgGrouperLocked ? "Terkunci (iDRG Final)" : `Edit Kode ICD Prosedur (No. Urut #${proc.urutan && parseInt(proc.urutan) > 0 ? proc.urutan : (idx + 1)})`}
                                          style={{
                                            border: '1px solid #93c5fd', background: '#eff6ff', color: '#1d4ed8',
                                            borderRadius: '5px', padding: '2px 6px',
                                            cursor: isIdrgGrouperLocked ? 'not-allowed' : 'pointer',
                                            opacity: isIdrgGrouperLocked ? 0.35 : 1,
                                            fontSize: '0.65rem',
                                            lineHeight: 1
                                          }}
                                        >
                                          <i className="bi bi-pencil-square"></i>
                                        </button>
                                        <button
                                          onClick={() => handleDeleteDiagnosa(proc, 'prosedur')}
                                          disabled={isIdrgGrouperLocked}
                                          title={idrgStatus.isFinal || claimFinalStatus ? "Terkunci (iDRG Final)" : "Hapus Prosedur"}
                                          style={{
                                            border: '1px solid #fca5a5', background: '#fff5f5', color: '#dc2626',
                                            borderRadius: '5px', padding: '2px 6px',
                                            cursor: (idrgStatus.isFinal || claimFinalStatus) ? 'not-allowed' : 'pointer',
                                            opacity: (idrgStatus.isFinal || claimFinalStatus) ? 0.35 : 1,
                                            fontSize: '0.65rem',
                                            lineHeight: 1
                                          }}
                                        >
                                          <i className="bi bi-trash"></i>
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : idrgProcedureList && idrgProcedureList.length > 0 ? (
                              idrgProcedureList.map((pItem, pIdx) => (
                                <tr key={pIdx} style={{ borderBottom: '1px solid #f1f5f9', background: pIdx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                  <td style={{ padding: '5px 8px', textAlign: 'center', color: '#94a3b8' }}>{pItem.no || pIdx + 1}</td>
                                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                    <span style={{
                                      display: 'inline-block', background: '#ecfdf5', color: '#047857',
                                      border: '1px solid #a7f3d0', borderRadius: '5px',
                                      padding: '1px 6px', fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: '700'
                                    }}>{pItem.code}</span>
                                  </td>
                                  <td style={{ padding: '5px 8px', fontWeight: '600', color: '#1e293b' }}>
                                    {pItem.display}
                                  </td>
                                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                    <Badge bg="light" className="text-secondary border font-monospace" style={{ fontSize: '0.67rem' }}>
                                      x{pItem.multiplicity || 1}
                                    </Badge>
                                  </td>
                                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                    <Badge bg="success" className="px-2 py-0 fw-medium shadow-xs" style={{ fontSize: '0.65rem' }}>
                                      <i className="bi bi-check-circle-fill me-1"></i>Valid (200)
                                    </Badge>
                                  </td>
                                  <td style={{ padding: '5px 10px', textAlign: 'right' }}>-</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>
                                  <i className="bi bi-scissors d-block mb-1" style={{ fontSize: '1.3rem', opacity: 0.4 }}></i>
                                  Belum ada prosedur. Klik <strong style={{ color: '#10b981' }}>Input Prosedur</strong> untuk menambahkan.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

                {/* ══ SECTION: DIAGNOSA & PROSEDUR INACBG (Default hidden, unhidden saat final iDRG sukses) ══ */}
                {Boolean(showTableInacbg && idrgStatus.isFinal) && (
                  <div style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    overflow: 'visible',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                  }}>
                  {/* Section Header */}
                  <div style={{
                    padding: '8px 12px',
                    background: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    borderRadius: '11px 11px 0 0',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px'
                  }}>
                    <div className="d-flex align-items-center gap-2">
                      <div style={{
                        width: '24px', height: '24px', borderRadius: '6px',
                        background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <i className="bi bi-hospital-fill text-white" style={{ fontSize: '0.65rem' }}></i>
                      </div>
                      <span className="fw-bold text-dark" style={{ fontSize: '0.82rem' }}>Diagnosa dan Prosedur INACBG</span>

                      {/* Tab Toggle Pills */}
                      <div style={{
                        display: 'flex', background: '#e2e8f0', borderRadius: '8px', padding: '2px', gap: '2px'
                      }}>
                        <button
                          onClick={() => {
                            setActiveTabDiagnosaInacbg('diagnosa');
                            if (idrgStatus.isFinal) {
                              handleOpenInputDiagnosa('diagnosa', 'inacbg');
                            }
                          }}
                          style={{
                            border: 'none', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer',
                            fontSize: '0.72rem', fontWeight: '600', transition: 'all 0.15s',
                            background: activeTabDiagnosaInacbg === 'diagnosa' ? '#ffffff' : 'transparent',
                            color: activeTabDiagnosaInacbg === 'diagnosa' ? '#0369a1' : '#64748b',
                            boxShadow: activeTabDiagnosaInacbg === 'diagnosa' ? '0 1px 3px rgba(0,0,0,0.15)' : 'none'
                          }}
                        >
                          <i className="bi bi-clipboard2-pulse-fill me-1"></i>
                          ICD-10
                          <span style={{
                            marginLeft: '4px', background: activeTabDiagnosaInacbg === 'diagnosa' ? '#e0f2fe' : '#e2e8f0',
                            color: activeTabDiagnosaInacbg === 'diagnosa' ? '#0369a1' : '#64748b',
                            borderRadius: '10px', padding: '0 6px', fontSize: '0.65rem', fontWeight: '700'
                          }}>
                            {diagnosaInacbgData.list_diagnosa?.length || 0}
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTabDiagnosaInacbg('prosedur');
                            if (idrgStatus.isFinal) {
                              handleOpenInputDiagnosa('prosedur', 'inacbg');
                            }
                          }}
                          style={{
                            border: 'none', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer',
                            fontSize: '0.72rem', fontWeight: '600', transition: 'all 0.15s',
                            background: activeTabDiagnosaInacbg === 'prosedur' ? '#ffffff' : 'transparent',
                            color: activeTabDiagnosaInacbg === 'prosedur' ? '#059669' : '#64748b',
                            boxShadow: activeTabDiagnosaInacbg === 'prosedur' ? '0 1px 3px rgba(0,0,0,0.15)' : 'none'
                          }}
                        >
                          <i className="bi bi-scissors me-1"></i>
                          ICD-9-CM
                          <span style={{
                            marginLeft: '4px', background: activeTabDiagnosaInacbg === 'prosedur' ? '#d1fae5' : '#e2e8f0',
                            color: activeTabDiagnosaInacbg === 'prosedur' ? '#059669' : '#64748b',
                            borderRadius: '10px', padding: '0 6px', fontSize: '0.65rem', fontWeight: '700'
                          }}>
                            {diagnosaInacbgData.list_prosedur?.length || 0}
                          </span>
                        </button>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      {!showInlineInputInacbg && (
                        <button
                          onClick={() => handleOpenInputDiagnosa(activeTabDiagnosaInacbg, 'inacbg')}
                          style={{
                            border: 'none', borderRadius: '7px', padding: '4px 10px', cursor: 'pointer',
                            fontSize: '0.72rem', fontWeight: '700', color: '#ffffff',
                            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                            boxShadow: '0 1px 3px rgba(2,132,199,0.3)',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                        >
                          <i className="bi bi-plus-circle"></i> Input {activeTabDiagnosaInacbg === 'diagnosa' ? 'Diagnosa (ICD-10)' : 'Prosedur (ICD-9)'}
                        </button>
                      )}

                      <button
                        onClick={() => fetchDiagnosaInacbgData(grouperTarget)}
                        disabled={isLoadingDiagnosaInacbg}
                        title="Muat Ulang Diagnosa & Prosedur INACBG"
                        style={{
                          border: '1px solid #cbd5e1', borderRadius: '7px', padding: '4px 8px', cursor: 'pointer',
                          fontSize: '0.72rem', fontWeight: '600', color: '#475569', background: '#f8fafc',
                          display: 'flex', alignItems: 'center', gap: '4px'
                        }}
                      >
                        <i className={`bi bi-arrow-clockwise ${isLoadingDiagnosaInacbg ? 'spin' : ''}`}></i>
                        <span>Refresh</span>
                      </button>

                      <button
                        onClick={handleImportToInacbg}
                        disabled={isImportingInacbg || !grouperTarget}
                        style={{
                          border: 'none', borderRadius: '7px', padding: '4px 12px',
                          cursor: (isImportingInacbg || !grouperTarget) ? 'not-allowed' : 'pointer',
                          opacity: (isImportingInacbg || !grouperTarget) ? 0.7 : 1,
                          fontSize: '0.72rem', fontWeight: '700', color: '#ffffff',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          boxShadow: '0 1px 4px rgba(16,185,129,0.3)',
                          display: 'flex', alignItems: 'center', gap: '5px'
                        }}
                        title="Salin semua data diagnosa dan prosedur iDRG ke tabel INACBG (hdiagnosa_icd & hdiagnosa_icd2)"
                      >
                        {isImportingInacbg ? (
                          <><Spinner size="sm" animation="border" style={{ width: '12px', height: '12px', borderWidth: '1.5px' }} /> Mengimport...</>
                        ) : (
                          <><i className="bi bi-box-arrow-in-down"></i> Import untuk Grouper INACBG</>
                        )}
                      </button>

                      <button
                        onClick={() => handleProcessGrouperInacbg()}
                        disabled={isGroupingInacbg || !grouperTarget?.no_sep || grouperTarget.no_sep === '-'}
                        style={{
                          border: 'none', borderRadius: '7px', padding: '4px 12px',
                          cursor: (isGroupingInacbg || !grouperTarget?.no_sep || grouperTarget.no_sep === '-') ? 'not-allowed' : 'pointer',
                          opacity: (isGroupingInacbg || !grouperTarget?.no_sep || grouperTarget.no_sep === '-') ? 0.7 : 1,
                          fontSize: '0.72rem', fontWeight: '700', color: '#ffffff',
                          background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                          boxShadow: '0 1px 4px rgba(99,102,241,0.3)',
                          display: 'flex', alignItems: 'center', gap: '5px'
                        }}
                        title="Jalankan Grouper INA-CBG untuk mendapatkan kode INA-CBG dari E-KLAIM"
                      >
                        {isGroupingInacbg ? (
                          <><Spinner size="sm" animation="border" style={{ width: '12px', height: '12px', borderWidth: '1.5px' }} /> Grouper INACBG...</>
                        ) : (
                          <><i className="bi bi-cpu-fill"></i> Grouper INACBG</>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* ── Inline Form Input Diagnosa / Prosedur (INACBG) ── */}
                  {showInlineInputInacbg && idrgStatus.isFinal && (
                    <div style={{
                      background: inputDiagnosaType === 'diagnosa'
                        ? 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
                        : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                      borderBottom: `2px solid ${inputDiagnosaType === 'diagnosa' ? '#7dd3fc' : '#6ee7b7'}`,
                      padding: '10px 12px'
                    }}>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <i className={`bi ${inputDiagnosaType === 'diagnosa' ? 'bi-clipboard2-pulse-fill text-info' : 'bi-scissors text-success'}`} style={{ fontSize: '0.85rem' }}></i>
                        <span style={{ fontSize: '0.78rem', fontWeight: '700', color: inputDiagnosaType === 'diagnosa' ? '#0369a1' : '#047857' }}>
                          {inputDiagnosaType === 'diagnosa' ? 'Input Diagnosa ICD-10 IM (INACBG)' : 'Input Prosedur ICD-9-CM IM (INACBG)'}
                        </span>
                        {isLoadingMasterIcd && (
                          <span style={{ fontSize: '0.68rem', color: '#64748b' }} className="d-flex align-items-center gap-1">
                            <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px', borderWidth: '1.5px' }} /> Memuat master...
                          </span>
                        )}
                        <button
                          onClick={() => { setShowInlineInputInacbg(false); setIcdCheckMsg(null); closeAutocomplete(); }}
                          style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', padding: '2px 6px', borderRadius: '4px', fontSize: '0.8rem' }}
                          title="Tutup form input"
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </div>
                      <div className="d-flex align-items-start gap-2 flex-wrap">
                        {/* Status Diagnosa (hanya untuk diagnosa, bukan prosedur) */}
                        {inputDiagnosaType === 'diagnosa' && (
                          <div style={{ minWidth: '120px' }}>
                            <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: '600', marginBottom: '3px' }}>Status</div>
                            <select
                              size="1"
                              value={formInputDiagnosaInacbg.ket}
                              onChange={e => {
                                const val = e.target.value;
                                setFormInputDiagnosaInacbg(prev => ({ ...prev, ket: val, utama: val === 'Utama' ? 1 : 0 }));
                                setFormInputDiagnosa(prev => ({ ...prev, ket: val, utama: val === 'Utama' ? 1 : 0 }));
                              }}
                              style={{ fontSize: '0.75rem', fontWeight: '600', border: '1px solid #7dd3fc', borderRadius: '6px', padding: '3px 6px', width: '100%', background: '#fff' }}
                            >
                              <option value="Utama">🟡 Utama</option>
                              <option value="Sekunder">🔵 Sekunder</option>
                              <option value="Tambahan">⚪ Tambahan</option>
                            </select>
                          </div>
                        )}
                        {/* Kode ICD dengan autocomplete */}
                        <div style={{ flex: '0 0 140px', position: 'relative' }}>
                          <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: '600', marginBottom: '3px' }}>
                            {inputDiagnosaType === 'diagnosa' ? 'Kode ICD-10' : 'Kode ICD-9-CM'}
                          </div>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <input
                              type="text"
                              value={formInputDiagnosaInacbg.kd_icd}
                              placeholder={inputDiagnosaType === 'diagnosa' ? 'Cari kode/nama...' : 'Cari kode/nama...'}
                              onClick={() => {
                                setInputDiagnosaTarget('inacbg');
                                openAutocomplete('inacbg', inputDiagnosaType === 'diagnosa' ? 'icd10' : 'icd9');
                                if (inputDiagnosaType === 'diagnosa') {
                                  if (masterIcd10.length === 0 && !isLoadingMasterIcd) fetchMasterIcd('diagnosa');
                                } else {
                                  if (masterIcd9.length === 0 && !isLoadingMasterIcd) fetchMasterIcd('prosedur');
                                }
                              }}
                              onFocus={() => {
                                setInputDiagnosaTarget('inacbg');
                                openAutocomplete('inacbg', inputDiagnosaType === 'diagnosa' ? 'icd10' : 'icd9');
                                if (inputDiagnosaType === 'diagnosa') {
                                  if (masterIcd10.length === 0 && !isLoadingMasterIcd) fetchMasterIcd('diagnosa');
                                } else {
                                  if (masterIcd9.length === 0 && !isLoadingMasterIcd) fetchMasterIcd('prosedur');
                                }
                              }}
                              onChange={e => {
                                const val = e.target.value;
                                setInputDiagnosaTarget('inacbg');
                                setFormInputDiagnosaInacbg(prev => ({ ...prev, kd_icd: val, nm_icd: '' }));
                                setFormInputDiagnosa(prev => ({ ...prev, kd_icd: val, nm_icd: '' }));
                                setIcdCheckMsg(null);
                                openAutocomplete('inacbg', inputDiagnosaType === 'diagnosa' ? 'icd10' : 'icd9');
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  closeAutocomplete();
                                  handleCheckIcd(formInputDiagnosaInacbg.kd_icd, 'inacbg');
                                } else if (e.key === 'Escape') {
                                  closeAutocomplete();
                                }
                              }}
                              onBlur={() => {
                                setTimeout(() => { closeAutocomplete(); }, 250);
                                if (formInputDiagnosaInacbg.kd_icd && !formInputDiagnosaInacbg.nm_icd) handleCheckIcd(formInputDiagnosaInacbg.kd_icd, 'inacbg');
                              }}
                              style={{
                                flex: 1, fontSize: '0.78rem', fontWeight: formInputDiagnosaInacbg.kd_icd ? '700' : '400',
                                fontFamily: 'monospace', border: `1px solid ${inputDiagnosaType === 'diagnosa' ? '#7dd3fc' : '#6ee7b7'}`,
                                borderRadius: '6px', padding: '3px 8px', outline: 'none', width: '100%',
                                color: inputDiagnosaType === 'diagnosa' ? '#0369a1' : '#047857'
                              }}
                            />
                          </div>
                          {/* Autocomplete dropdown — ICD-10 */}
                          {inputDiagnosaType === 'diagnosa' && activeAutocomplete?.target === 'inacbg' && activeAutocomplete?.type === 'icd10' && (
                            <div style={{
                              position: 'absolute', top: '100%', left: 0, width: '560px', maxWidth: '85vw', zIndex: 9999,
                              maxHeight: '280px', overflowY: 'auto', background: '#ffffff',
                              border: '2px solid #0284c7', borderRadius: '0 0 8px 8px',
                              boxShadow: '0 8px 24px rgba(2,132,199,0.22)', marginTop: '-1px'
                            }}>
                              <div className="px-2 py-1 sticky-top" style={{ background: '#f0f9ff', borderBottom: '1px solid #bae6fd', fontSize: '0.68rem', color: '#0369a1', fontWeight: '700' }}>
                                Master ICD-10 IM (INACBG) — {isLoadingMasterIcd ? 'Memuat...' : `${filteredIcd10SuggestionsInacbg.length} hasil`}
                              </div>
                              {isLoadingMasterIcd ? (
                                <div className="p-3 text-center text-muted" style={{ fontSize: '0.75rem' }}>
                                  <Spinner animation="border" size="sm" variant="info" className="me-2" /> Memuat...
                                </div>
                              ) : filteredIcd10SuggestionsInacbg.slice(0, 50).map((item, idx) => {
                                const code = item.icd || item.kode || item.kd_icd || '';
                                const desc = item.deskripsi || item.nm_icd || item.penyakit || '';
                                const isHeader = item.validcode === 0 || item.validcode === '0' || item.validcode === false;
                                const isAccpdxNo = String(item.accpdx || '').trim().toUpperCase() === 'N';
                                return (
                                  <div key={`il10-inacbg-${code}-${idx}`}
                                    onMouseDown={() => {
                                      handleSelectIcd10FromDropdown(item, 'inacbg');
                                      closeAutocomplete();
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      cursor: 'pointer',
                                      opacity: 1,
                                      borderBottom: '1px solid #f1f5f9',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                  >
                                    <span className={`badge ${isHeader ? 'bg-warning text-dark' : 'bg-info text-dark'} font-monospace flex-shrink-0`} style={{ fontSize: '0.72rem', minWidth: '60px', textAlign: 'center' }}>{code}</span>
                                    <span style={{ fontSize: '0.76rem', color: '#1e293b', flex: 1, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.25' }}>{desc}</span>
                                    {isHeader && (
                                      <span className="badge bg-warning text-dark" style={{ fontSize: '0.62rem' }}>Header</span>
                                    )}
                                    {isAccpdxNo && !isHeader && (
                                      <span className="badge bg-warning text-dark" style={{ fontSize: '0.62rem' }}>accpdx: N (Sekunder)</span>
                                    )}
                                  </div>
                                );
                              })}
                              {!isLoadingMasterIcd && filteredIcd10SuggestionsInacbg.length === 0 && (
                                <div className="p-3 text-center text-muted" style={{ fontSize: '0.73rem' }}>Tidak ada hasil untuk "{formInputDiagnosaInacbg.kd_icd}"</div>
                              )}
                            </div>
                          )}
                          {/* Autocomplete dropdown — ICD-9 */}
                          {inputDiagnosaType === 'prosedur' && activeAutocomplete?.target === 'inacbg' && activeAutocomplete?.type === 'icd9' && (
                            <div style={{
                              position: 'absolute', top: '100%', left: 0, width: '560px', maxWidth: '85vw', zIndex: 9999,
                              maxHeight: '280px', overflowY: 'auto', background: '#ffffff',
                              border: '2px solid #10b981', borderRadius: '0 0 8px 8px',
                              boxShadow: '0 8px 24px rgba(16,185,129,0.22)', marginTop: '-1px'
                            }}>
                              <div className="px-2 py-1 sticky-top" style={{ background: '#f0fdf4', borderBottom: '1px solid #bbf7d0', fontSize: '0.68rem', color: '#047857', fontWeight: '700' }}>
                                Master ICD-9-CM IM (INACBG) — {isLoadingMasterIcd ? 'Memuat...' : `${filteredIcd9SuggestionsInacbg.length} hasil`}
                              </div>
                              {isLoadingMasterIcd ? (
                                <div className="p-3 text-center text-muted" style={{ fontSize: '0.75rem' }}>
                                  <Spinner animation="border" size="sm" variant="success" className="me-2" /> Memuat...
                                </div>
                              ) : filteredIcd9SuggestionsInacbg.slice(0, 50).map((item, idx) => {
                                const code = item.kode || item.icd || item.kd_icd || '';
                                const desc = item.deskripsi || item.nm_icd || '';
                                return (
                                  <div key={`il9-inacbg-${code}-${idx}`}
                                    onMouseDown={() => {
                                      handleSelectIcd9FromDropdown(item, 'inacbg');
                                      closeAutocomplete();
                                    }}
                                    style={{ padding: '6px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '10px' }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                  >
                                    <span className="badge bg-success font-monospace flex-shrink-0" style={{ fontSize: '0.72rem', minWidth: '60px', textAlign: 'center' }}>{code}</span>
                                    <span style={{ fontSize: '0.76rem', color: '#1e293b', flex: 1, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.25' }}>{desc}</span>
                                  </div>
                                );
                              })}
                              {!isLoadingMasterIcd && filteredIcd9SuggestionsInacbg.length === 0 && (
                                <div className="p-3 text-center text-muted" style={{ fontSize: '0.73rem' }}>Tidak ada hasil untuk "{formInputDiagnosaInacbg.kd_icd}"</div>
                              )}
                            </div>
                          )}
                        </div>
                        {/* Nama Diagnosa / Prosedur */}
                        <div style={{ flex: 1, minWidth: '160px' }}>
                          <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: '600', marginBottom: '3px' }}>
                            {inputDiagnosaType === 'diagnosa' ? 'Nama Diagnosa' : 'Nama Prosedur'}
                          </div>
                          <input
                            type="text"
                            value={formInputDiagnosaInacbg.nm_icd}
                            onChange={e => {
                              const v = e.target.value;
                              setFormInputDiagnosaInacbg(prev => ({ ...prev, nm_icd: v }));
                              setFormInputDiagnosa(prev => ({ ...prev, nm_icd: v }));
                            }}
                            placeholder="Terisi otomatis saat pilih dari daftar..."
                            style={{
                              width: '100%', fontSize: '0.75rem', fontWeight: formInputDiagnosaInacbg.nm_icd ? '600' : '400',
                              border: `1px solid ${inputDiagnosaType === 'diagnosa' ? '#7dd3fc' : '#6ee7b7'}`,
                              borderRadius: '6px', padding: '3px 8px', outline: 'none'
                            }}
                          />
                        </div>
                        {/* Multiplicity (untuk prosedur saja) */}
                        {inputDiagnosaType === 'prosedur' && (
                          <div style={{ minWidth: '80px' }}>
                            <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: '600', marginBottom: '3px' }}>Qty (x)</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <button onClick={() => handleMultiplicityChange(Math.max(1, (Number(formInputDiagnosaInacbg.multiplicity) || 1) - 1), 'inacbg')}
                                style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', background: '#f8fafc', fontSize: '0.75rem' }}>−</button>
                              <input type="number" min={1} max={99} value={formInputDiagnosaInacbg.multiplicity || 1}
                                onChange={e => handleMultiplicityChange(e.target.value, 'inacbg')}
                                style={{ width: '38px', textAlign: 'center', fontSize: '0.78rem', fontWeight: '700', fontFamily: 'monospace', border: '1px solid #6ee7b7', borderRadius: '4px', padding: '2px 4px' }}
                              />
                              <button onClick={() => handleMultiplicityChange((Number(formInputDiagnosaInacbg.multiplicity) || 1) + 1, 'inacbg')}
                                style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', background: '#f8fafc', fontSize: '0.75rem' }}>+</button>
                            </div>
                          </div>
                        )}
                        {/* Tombol Simpan & Batal */}
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', paddingBottom: '0' }}>
                          <button
                            onClick={(e) => handleSaveInputDiagnosa(e, { target: 'inacbg' })}
                            disabled={isSavingDiagnosa || !formInputDiagnosaInacbg.kd_icd}
                            style={{
                              border: 'none', borderRadius: '6px', padding: '4px 14px',
                              fontSize: '0.75rem', fontWeight: '700', color: '#ffffff', cursor: isSavingDiagnosa || !formInputDiagnosaInacbg.kd_icd ? 'not-allowed' : 'pointer',
                              opacity: isSavingDiagnosa || !formInputDiagnosaInacbg.kd_icd ? 0.6 : 1,
                              background: inputDiagnosaType === 'diagnosa' ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                              display: 'flex', alignItems: 'center', gap: '5px', marginTop: '17px'
                            }}
                          >
                            {isSavingDiagnosa
                              ? <><Spinner animation="border" size="sm" style={{ width: '11px', height: '11px', borderWidth: '1.5px' }} /> Simpan...</>
                              : <><i className="bi bi-check-circle-fill"></i> Simpan</>
                            }
                          </button>
                          <button
                            onClick={() => {
                              setShowInlineInputInacbg(false);
                              setIcdCheckMsg(null);
                              closeAutocomplete();
                              setFormInputDiagnosaInacbg(prev => ({ ...prev, kd_icd: '', nm_icd: '', multiplicity: 1 }));
                              setFormInputDiagnosa(prev => ({ ...prev, kd_icd: '', nm_icd: '', multiplicity: 1 }));
                            }}
                            style={{
                              border: '1px solid #d1d5db', borderRadius: '6px', padding: '4px 10px',
                              fontSize: '0.75rem', fontWeight: '600', color: '#64748b', cursor: 'pointer',
                              background: '#ffffff', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '17px'
                            }}
                          >
                            <i className="bi bi-x-circle"></i> Batal
                          </button>
                        </div>
                      </div>
                      {/* Feedback validasi kode */}
                      {icdCheckMsg && !/header code/i.test(icdCheckMsg.text || '') && (
                        <div className={`d-flex align-items-center gap-2 mt-2 p-2 rounded-2 ${icdCheckMsg.type === 'success' ? 'bg-success bg-opacity-10 border border-success border-opacity-25' : icdCheckMsg.type === 'info' ? 'bg-info bg-opacity-10 border border-info border-opacity-25' : 'bg-danger bg-opacity-10 border border-danger border-opacity-25'}`} style={{ fontSize: '0.72rem' }}>
                          <i className={`bi ${icdCheckMsg.type === 'success' ? 'bi-check-circle-fill text-success' : icdCheckMsg.type === 'info' ? 'bi-info-circle-fill text-info' : 'bi-exclamation-triangle-fill text-danger'} flex-shrink-0`}></i>
                          <span className={`fw-semibold ${icdCheckMsg.type === 'success' ? 'text-success' : icdCheckMsg.type === 'info' ? 'text-info' : 'text-danger'}`}>{icdCheckMsg.text}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Table Content */}
                  {isLoadingDiagnosaInacbg ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" style={{ color: '#0284c7' }} className="me-2" />
                      <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Memuat data diagnosa &amp; prosedur INACBG...</span>
                    </div>
                  ) : activeTabDiagnosaInacbg === 'diagnosa' ? (
                    <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr style={{ background: '#1e293b', color: '#e2e8f0' }}>
                            <th style={{ padding: '6px 8px', textAlign: 'center', width: '28px', fontWeight: '600' }}>#</th>
                            <th style={{ padding: '6px 8px', textAlign: 'center', width: '75px', fontWeight: '600' }}>Kode ICD</th>
                            <th style={{ padding: '6px 8px', textAlign: 'center', width: '80px', fontWeight: '600' }}>Status</th>
                            <th style={{ padding: '6px 8px', fontWeight: '600' }}>Nama Diagnosa (ICD-10)</th>
                            <th style={{ padding: '6px 10px', textAlign: 'right', width: '135px', fontWeight: '600' }}>Aksi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diagnosaInacbgData.list_diagnosa?.length > 0 ? (
                            diagnosaInacbgData.list_diagnosa.map((diag, idx) => {
                              const isUtama = diag.utama === 1 || String(diag.utama) === '1' || String(diag.ket || '').toLowerCase().includes('utama');
                              return (
                              <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                <td style={{ padding: '5px 8px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>{diag.urutan && parseInt(diag.urutan) > 0 ? diag.urutan : (idx + 1)}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    background: (diag.is_im === 1 || diag.im_tidak_berlaku || diag.im === 1) ? '#fef2f2' : '#e0f2fe',
                                    color: (diag.is_im === 1 || diag.im_tidak_berlaku || diag.im === 1) ? '#dc2626' : '#0369a1',
                                    border: `1px solid ${(diag.is_im === 1 || diag.im_tidak_berlaku || diag.im === 1) ? '#fca5a5' : '#7dd3fc'}`,
                                    borderRadius: '5px',
                                    padding: '1px 6px', fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: '700'
                                  }}>{diag.kd_icd}</span>
                                </td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                  {isUtama ? (
                                    <span style={{
                                      display: 'inline-flex', alignItems: 'center', gap: '3px',
                                      background: '#eff6ff', color: '#1d4ed8', borderRadius: '6px',
                                      padding: '2px 7px', fontSize: '0.65rem', fontWeight: '700',
                                      border: '1px solid #bfdbfe'
                                    }}>
                                      <i className="bi bi-star-fill text-warning" style={{ fontSize: '0.6rem' }}></i> Utama
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => handleSetDiagnosaUtama(diag, 'inacbg')}
                                      disabled={claimFinalStatus}
                                      title={claimFinalStatus ? "Terkunci (Klaim Final)" : "Klik untuk jadikan Diagnosa Utama & Urutan Pertama (INACBG)"}
                                      style={{
                                        border: '1px solid #fde68a', background: '#fffbeb', color: '#92400e',
                                        borderRadius: '6px', padding: '2px 7px', fontSize: '0.65rem', fontWeight: '600',
                                        cursor: claimFinalStatus ? 'not-allowed' : 'pointer',
                                        opacity: claimFinalStatus ? 0.5 : 1,
                                        display: 'inline-flex', alignItems: 'center', gap: '3px'
                                      }}
                                    >
                                      <i className="bi bi-arrow-up-circle text-warning"></i> Sekunder
                                    </button>
                                  )}
                                </td>
                                <td style={{ padding: '5px 8px', fontWeight: '600', color: '#1e293b' }} title={diag.nama_icd || diag.nm_icd}>
                                  <span>{diag.nama_icd || diag.nm_icd}</span>
                                  {(diag.is_im === 1 || diag.im_tidak_berlaku || diag.im === 1 || diag.status_im === 'IM tidak berlaku') && (
                                    <span style={{
                                      marginLeft: '8px',
                                      color: '#dc2626',
                                      backgroundColor: '#fef2f2',
                                      border: '1px solid #fca5a5',
                                      borderRadius: '4px',
                                      padding: '2px 6px',
                                      fontSize: '0.65rem',
                                      fontWeight: '700',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      verticalAlign: 'middle'
                                    }}>
                                      <i className="bi bi-exclamation-octagon-fill text-danger" style={{ fontSize: '0.62rem' }}></i>
                                      IM tidak berlaku
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '5px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                  <div className="d-flex align-items-center justify-content-end gap-1">
                                    {!isUtama && (
                                      <button
                                        onClick={() => handleSetDiagnosaUtama(diag, 'inacbg')}
                                        disabled={claimFinalStatus}
                                        title={claimFinalStatus ? "Terkunci (Klaim Final)" : "Jadikan Diagnosa Utama & Urutan Pertama (INACBG)"}
                                        style={{
                                          border: '1px solid #fde047', background: '#fefce8', color: '#a16207',
                                          borderRadius: '5px', padding: '2px 6px',
                                          cursor: claimFinalStatus ? 'not-allowed' : 'pointer',
                                          opacity: claimFinalStatus ? 0.35 : 1,
                                          fontSize: '0.65rem',
                                          lineHeight: 1,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '2px'
                                        }}
                                      >
                                        <i className="bi bi-arrow-up-circle-fill text-warning"></i>
                                        <span style={{ fontSize: '0.62rem', fontWeight: '700' }}>Utama</span>
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleOpenEditIcd(diag, 'diagnosa', 'inacbg', diag.urutan && parseInt(diag.urutan) > 0 ? diag.urutan : (idx + 1))}
                                      disabled={claimFinalStatus}
                                      title={claimFinalStatus ? "Terkunci (Klaim Final)" : `Edit Kode ICD (No. Urut #${diag.urutan && parseInt(diag.urutan) > 0 ? diag.urutan : (idx + 1)})`}
                                      style={{
                                        border: '1px solid #93c5fd', background: '#eff6ff', color: '#1d4ed8',
                                        borderRadius: '5px', padding: '2px 6px',
                                        cursor: claimFinalStatus ? 'not-allowed' : 'pointer',
                                        opacity: claimFinalStatus ? 0.35 : 1,
                                        fontSize: '0.65rem',
                                        lineHeight: 1
                                      }}
                                    >
                                      <i className="bi bi-pencil-square"></i>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteDiagnosa(diag, 'diagnosa', 'inacbg')}
                                      disabled={claimFinalStatus}
                                      title={claimFinalStatus ? "Terkunci (Klaim Final)" : "Hapus Diagnosa INACBG"}
                                      style={{
                                        border: '1px solid #fca5a5', background: '#fff5f5', color: '#dc2626',
                                        borderRadius: '5px', padding: '2px 6px',
                                        cursor: claimFinalStatus ? 'not-allowed' : 'pointer',
                                        opacity: claimFinalStatus ? 0.35 : 1,
                                        fontSize: '0.65rem',
                                        lineHeight: 1
                                      }}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                            })
                          ) : (
                            <tr>
                              <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>
                                <i className="bi bi-box-arrow-in-down d-block mb-1" style={{ fontSize: '1.3rem', opacity: 0.4 }}></i>
                                Belum ada diagnosa INACBG di tabel <code>hdiagnosa_icd</code>.<br />
                                Klik <strong style={{ color: '#0284c7' }}>Input Diagnosa</strong> atau tombol <strong>Import untuk Grouper INACBG</strong> di atas untuk menambahkan.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr style={{ background: '#1e293b', color: '#e2e8f0' }}>
                            <th style={{ padding: '6px 8px', textAlign: 'center', width: '28px', fontWeight: '600' }}>#</th>
                            <th style={{ padding: '6px 8px', textAlign: 'center', width: '75px', fontWeight: '600' }}>Kode ICD</th>
                            <th style={{ padding: '6px 8px', fontWeight: '600' }}>Nama Prosedur (ICD-9-CM)</th>
                            <th style={{ padding: '6px 8px', textAlign: 'center', width: '75px', fontWeight: '600' }}>Multiplicity</th>
                            <th style={{ padding: '6px 10px', textAlign: 'right', width: '85px', fontWeight: '600' }}>Aksi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diagnosaInacbgData.list_prosedur?.length > 0 ? (
                            diagnosaInacbgData.list_prosedur.map((proc, idx) => (
                              <tr key={proc.id ? `proc-inacbg-${proc.id}` : idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                <td style={{ padding: '5px 8px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>{proc.urutan && parseInt(proc.urutan) > 0 ? proc.urutan : (idx + 1)}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    background: (proc.is_im === 1 || proc.im_tidak_berlaku || proc.im === 1) ? '#fef2f2' : '#f0fdf4',
                                    color: (proc.is_im === 1 || proc.im_tidak_berlaku || proc.im === 1) ? '#dc2626' : '#15803d',
                                    border: `1px solid ${(proc.is_im === 1 || proc.im_tidak_berlaku || proc.im === 1) ? '#fca5a5' : '#86efac'}`,
                                    borderRadius: '5px',
                                    padding: '1px 6px', fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: '700'
                                  }}>{proc.kd_icd}</span>
                                </td>
                                <td style={{ padding: '5px 8px', fontWeight: '600', color: '#1e293b' }} title={proc.nama_icd || proc.nm_icd}>
                                  <span>{proc.nama_icd || proc.nm_icd}</span>
                                  {(proc.is_im === 1 || proc.im_tidak_berlaku || proc.im === 1 || proc.status_im === 'IM tidak berlaku') && (
                                    <span style={{
                                      marginLeft: '8px',
                                      color: '#dc2626',
                                      backgroundColor: '#fef2f2',
                                      border: '1px solid #fca5a5',
                                      borderRadius: '4px',
                                      padding: '2px 6px',
                                      fontSize: '0.65rem',
                                      fontWeight: '700',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      verticalAlign: 'middle'
                                    }}>
                                      <i className="bi bi-exclamation-octagon-fill text-danger" style={{ fontSize: '0.62rem' }}></i>
                                      IM tidak berlaku
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                  <Badge bg="light" className="text-secondary border font-monospace" style={{ fontSize: '0.67rem' }}>
                                    x{proc.qty || proc.multiplicity || (proc.kd_icd?.includes('+') ? proc.kd_icd.split('+')[1] : 1)}
                                  </Badge>
                                </td>
                                <td style={{ padding: '5px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                  <div className="d-flex align-items-center justify-content-end gap-1">
                                    <button
                                      onClick={() => handleOpenEditIcd(proc, 'prosedur', 'inacbg', proc.urutan && parseInt(proc.urutan) > 0 ? proc.urutan : (idx + 1))}
                                      disabled={claimFinalStatus}
                                      title={claimFinalStatus ? "Terkunci (Klaim Final)" : `Edit Kode ICD Prosedur (No. Urut #${proc.urutan && parseInt(proc.urutan) > 0 ? proc.urutan : (idx + 1)})`}
                                      style={{
                                        border: '1px solid #93c5fd', background: '#eff6ff', color: '#1d4ed8',
                                        borderRadius: '5px', padding: '2px 6px',
                                        cursor: claimFinalStatus ? 'not-allowed' : 'pointer',
                                        opacity: claimFinalStatus ? 0.35 : 1,
                                        fontSize: '0.65rem',
                                        lineHeight: 1
                                      }}
                                    >
                                      <i className="bi bi-pencil-square"></i>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteDiagnosa(proc, 'prosedur', 'inacbg')}
                                      disabled={claimFinalStatus}
                                      title={claimFinalStatus ? "Terkunci (Klaim Final)" : "Hapus Prosedur INACBG"}
                                      style={{
                                        border: '1px solid #fca5a5', background: '#fff5f5', color: '#dc2626',
                                        borderRadius: '5px', padding: '2px 6px',
                                        cursor: claimFinalStatus ? 'not-allowed' : 'pointer',
                                        opacity: claimFinalStatus ? 0.35 : 1,
                                        fontSize: '0.65rem',
                                        lineHeight: 1
                                      }}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>
                                <i className="bi bi-scissors d-block mb-1" style={{ fontSize: '1.3rem', opacity: 0.4 }}></i>
                                Belum ada prosedur INACBG di tabel <code>hdiagnosa_icd2</code>.<br />
                                Klik <strong style={{ color: '#10b981' }}>Input Prosedur</strong> atau tombol <strong>Import untuk Grouper INACBG</strong> di atas untuk menambahkan.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Tampilan Hasil Grouping INACBG langsung di Tab INACBG (YANG PERTAMA) */}
                  {(hasRunGrouperInacbg || inacbgGroupingData || inacbgStatus.isGrouped || grouperTarget?.kd_inacbg || grouperTarget?.cbg) && (
                    <div className="mt-3 pt-3 border-top">
                      {renderHasilGroupingInacbgTable()}
                    </div>
                  )}
                </div>
              )}

                {/* ══ SECTION: HASIL GROUPER ══ */}
                {isGrouping ? (
                  <div style={{
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px',
                    padding: '24px 20px', textAlign: 'center',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                  }}>
                    <Spinner animation="border" style={{ width: '2.2rem', height: '2.2rem', color: '#0d9488', borderWidth: '3px' }} />
                    <div className="mt-3 fw-bold text-dark" style={{ fontSize: '0.85rem' }}>Menghubungkan ke Engine Grouper E-Claim BPJS...</div>
                    <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>Verifikasi data klaim, diagnosa, dan penentuan tarif INA-CBG</div>
                  </div>
                ) : grouperResult ? (
                  <div style={{
                    background: grouperResult.type === 'success' ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${grouperResult.type === 'success' ? '#86efac' : '#fca5a5'}`,
                    borderRadius: '12px', overflow: 'hidden',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                  }}>
                    {/* Result Header */}
                    <div style={{
                      padding: '9px 14px',
                      background: grouperResult.type === 'success' ? '#dcfce7' : '#fee2e2',
                      borderBottom: `1px solid ${grouperResult.type === 'success' ? '#86efac' : '#fca5a5'}`,
                      display: 'flex', alignItems: 'center', gap: '8px'
                    }}>
                      <i className={`bi ${grouperResult.type === 'success' ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'} fs-5`}></i>
                      <span className="fw-bold" style={{ fontSize: '0.82rem', color: grouperResult.type === 'success' ? '#15803d' : '#b91c1c' }}>
                        {grouperResult.type === 'success' ? 'Hasil Grouper E-Claim Berhasil' : 'Proses Grouper Gagal'}
                      </span>
                    </div>
                    <div style={{ padding: '12px 14px' }}>
                      <div className="text-dark mb-2" style={{ fontSize: '0.77rem' }}>{grouperResult.text}</div>
                      {grouperResult.data && (
                        <div style={{
                          background: '#ffffff', border: '1px solid #e2e8f0',
                          borderRadius: '8px', padding: '10px 12px'
                        }}>
                          <Row className="g-2">
                            {(grouperResult.data.idrg_code || grouperResult.data.drg_code || grouperResult.data.eklaim_response?.response_inacbg?.idrg_code || grouperResult.data.eklaim_response?.response_idrg?.drg_code) && (
                              <Col md={12}>
                                <div className="text-muted" style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '2px' }}>Kode iDRG &amp; Deskripsi (Poin 6)</div>
                                <div className="fw-bold text-dark d-flex align-items-center flex-wrap gap-1" style={{ fontSize: '0.78rem' }}>
                                  <Badge bg={grouperResult.type === 'success' ? 'success' : 'danger'} className="px-2 py-1 shadow-sm font-monospace" style={{ background: grouperResult.type === 'success' ? '#059669' : '#dc2626', fontSize: '0.75rem' }}>
                                    <i className={`bi ${grouperResult.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} me-1`}></i>
                                    {grouperResult.data.idrg_code || grouperResult.data.drg_code || grouperResult.data.eklaim_response?.response_inacbg?.idrg_code || grouperResult.data.eklaim_response?.response_idrg?.drg_code}
                                  </Badge>
                                  <span className={grouperResult.type === 'danger' ? 'text-danger fw-bold' : ''} style={{ fontSize: '0.78rem' }}>
                                    {grouperResult.data.idrg_desc || grouperResult.data.drg_description || grouperResult.data.eklaim_response?.response_inacbg?.idrg_desc || grouperResult.data.eklaim_response?.response_idrg?.drg_description || '-'}
                                  </span>

                                  {/* Bobot Biaya (Cost Weight) iDRG: DRG Cost Weight, Top Up Cost Weight, Total Cost Weight */}
                                  {(idrgCostWeightData.drgCostWeight || idrgCostWeightData.topupCostWeight || idrgCostWeightData.totalCostWeight || grouperResult.data.idrg_cost_weight || grouperResult.data.cost_weight || idrgStatus.costWeight) ? (
                                    <div className="d-flex align-items-center flex-wrap gap-2 mt-2 pt-2 border-top w-100">
                                      <span className="text-muted d-inline-flex align-items-center gap-1" style={{ fontSize: '0.72rem', fontWeight: '600' }}>
                                        <i className="bi bi-calculator text-primary"></i>Kalkulasi Bobot Biaya:
                                      </span>
                                      <Badge bg="light" className="text-dark border font-monospace px-2 py-1 shadow-xs" style={{ fontSize: '0.72rem', background: '#f8fafc' }}>
                                        DRG Cost Weight: <strong>{idrgCostWeightData.drgCostWeight || grouperResult.data.drg_cost_weight || grouperResult.data.idrg_cost_weight || grouperResult.data.cost_weight || idrgStatus.costWeight || '0'}</strong>
                                      </Badge>
                                      <Badge bg="light" className="text-dark border font-monospace px-2 py-1 shadow-xs" style={{ fontSize: '0.72rem', background: '#f8fafc' }}>
                                        Top Up Cost Weight: <strong className="text-primary">{idrgCostWeightData.topupCostWeight || grouperResult.data.topup_cost_weight || '0'}</strong>
                                      </Badge>
                                      <Badge bg="info" className="text-dark font-monospace px-2 py-1 shadow-xs ms-auto" style={{ background: '#bae6fd', border: '1px solid #7dd3fc', fontSize: '0.73rem' }}>
                                        <i className="bi bi-speedometer2 me-1"></i>Total Cost Weight: <strong>{idrgCostWeightData.totalCostWeight || grouperResult.data.total_cost_weight || grouperResult.data.cost_weight || idrgStatus.costWeight || '0'}</strong>
                                      </Badge>
                                    </div>
                                  ) : null}
                                </div>

                                {/* Opsi Top-Up Payment iDRG Stage 2 (Dikelompokkan Berdasarkan Tipe) */}
                                {idrgTopupOptions && idrgTopupOptions.length > 0 && (
                                  <div className="mt-2 pt-2 border-top">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                      <div className="d-flex align-items-center gap-1">
                                        <i className="bi bi-layers-fill text-primary" style={{ fontSize: '0.82rem' }}></i>
                                        <span className="fw-bold text-dark" style={{ fontSize: '0.76rem' }}>
                                          Opsi Top-Up Payment iDRG (Stage 2)
                                        </span>
                                        <Badge bg="primary" style={{ fontSize: '0.65rem' }}>{idrgTopupOptions.length} Opsi Tersedia</Badge>
                                      </div>
                                      {isGroupingIdrgStage2 && (
                                        <span className="text-primary fw-semibold d-inline-flex align-items-center gap-1" style={{ fontSize: '0.72rem' }}>
                                          <Spinner size="sm" animation="border" style={{ width: '12px', height: '12px', borderWidth: '1.5px' }} />
                                          <span>Memproses Top-Up...</span>
                                        </span>
                                      )}
                                    </div>

                                    {(() => {
                                      const groupedTopup = idrgTopupOptions.reduce((acc, opt) => {
                                        const t = (opt.type || 'Lainnya').toLowerCase();
                                        if (!acc[t]) acc[t] = [];
                                        acc[t].push(opt);
                                        return acc;
                                      }, {});
                                      const groupKeys = Object.keys(groupedTopup);

                                      return (
                                        <div className="p-2 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                          <Row className="g-2">
                                            {groupKeys.map(typeKey => {
                                              const opts = groupedTopup[typeKey];
                                              const typeLabel = typeKey.charAt(0).toUpperCase() + typeKey.slice(1);
                                              const curVal = selectedIdrgTopupMap[typeKey] || '';
                                              return (
                                                <Col md={groupKeys.length > 1 ? 6 : 12} key={typeKey}>
                                                  <label className="form-label text-muted mb-1" style={{ fontSize: '0.68rem', fontWeight: '600', textTransform: 'uppercase' }}>
                                                    Top-Up {typeLabel}
                                                  </label>
                                                  <select
                                                    className="form-select form-select-sm"
                                                    style={{ fontSize: '0.74rem', borderRadius: '6px' }}
                                                    value={curVal}
                                                    disabled={isGroupingIdrgStage2 || idrgStatus.isFinal || claimFinalStatus}
                                                    onChange={(e) => handleIdrgTopupChange(typeKey, e.target.value)}
                                                  >
                                                    <option value="">- Tidak Ada Top-Up (Reset) -</option>
                                                    {opts.map((opt, optIdx) => (
                                                      <option key={optIdx} value={opt.code}>
                                                        [{opt.code}] {opt.description || opt.desc || opt.code} {opt.cost_weight ? `(CW: +${opt.cost_weight})` : ''}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </Col>
                                              );
                                            })}
                                          </Row>
                                          <div className="d-flex align-items-center justify-content-between mt-2 pt-1 border-top">
                                            <span className="text-muted" style={{ fontSize: '0.67rem' }}>
                                              * Pilih opsi untuk memicu Grouping Stage 2 Top-up iDRG. Pilih "- Tidak Ada Top-Up (Reset) -" untuk membatalkan (mengirim <code>#</code>).
                                            </span>
                                            {Object.values(selectedIdrgTopupMap).some(Boolean) && (
                                              <Button
                                                size="sm"
                                                variant="outline-danger"
                                                style={{ fontSize: '0.67rem', padding: '2px 8px', borderRadius: '5px' }}
                                                disabled={isGroupingIdrgStage2 || idrgStatus.isFinal || claimFinalStatus}
                                                onClick={() => {
                                                  setSelectedIdrgTopupMap({});
                                                  handleProcessGrouperIdrgStage2(grouperTarget, '#');
                                                }}
                                              >
                                                <i className="bi bi-x-circle me-1"></i>Reset Semua Top-Up
                                              </Button>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                )}

                                {/* Poin 8: Peringatan Ungroupable iDRG (MDC 36) */}
                                {(String(grouperResult.data.idrg_mdc_number) === '36' || idrgStatus.isUngroupable || String(grouperResult.data.idrg_code || '').startsWith('36') || String(grouperResult.data.idrg_desc || '').toLowerCase().includes('ungroupable')) && (
                                  <div className="mt-2 p-2 rounded d-flex align-items-center gap-2 shadow-xs" style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: '0.72rem' }}>
                                    <i className="bi bi-shield-x-fill text-danger fs-5"></i>
                                    <div>
                                      <strong>Kasus Ungroupable (MDC 36 / 3613029):</strong> Berdasarkan regulasi Kemenkes (Poin 8), kasus ini tidak boleh difinalisasi. Tombol Finalisasi iDRG dinonaktifkan secara otomatis sampai koding diperbaiki.
                                    </div>
                                  </div>
                                )}
                              </Col>
                            )}

                            {/* Poin 14: Peringatan Kode Indonesian Modification (IM) pada INA-CBG */}
                            {imWarningList && imWarningList.length > 0 && (
                              <Col md={12}>
                                <div className="p-2 rounded d-flex align-items-center justify-content-between shadow-xs" style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '0.72rem' }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-exclamation-triangle-fill text-warning fs-5"></i>
                                    <div>
                                      <strong>Peringatan Standardisasi INA-CBG (Poin 14):</strong> Ditemukan kode diagnosis khusus Indonesian Modification (IM): <strong>{imWarningList.map(x => x.kd_icd).join(', ')}</strong>. Kode IM berlaku pada iDRG. Pastikan kode dipetakan ke kode standar 3-5 karakter sebelum finalisasi INA-CBG.
                                    </div>
                                  </div>
                                  <Badge bg="warning" className="text-dark">Poin 14 Kemenkes</Badge>
                                </div>
                              </Col>
                            )}


                            <Col md={12}>
                              <div className="text-muted" style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '2px' }}>Tarif RS SIMRS (Rp)</div>
                              <div className="fw-bold text-secondary" style={{ fontSize: '0.85rem' }}>
                                {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(grouperResult.data?.tarif_rs || grouperTarget?.total || 0)}
                              </div>
                            </Col>

                            {/* Banner Ringkas Hasil Grouping Tarif SIMRS (Variable hasil_tarif) */}
                            {(grouperResult?.data?.hasil_tarif || grouperResult?.data?.hasil_tarif_str || hasilTarifData || hasilTarifStr) && (
                              <Col md={12} className="mt-2 pt-2 border-top">
                                {renderHasilTarifBanner()}
                              </Col>
                            )}
                          </Row>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  (hasilTarifData || hasilTarifStr || isLoadingHasilTarif) ? (
                    <div className="mb-2">
                      {renderHasilTarifBanner()}
                    </div>
                  ) : null
                )}

                {/* ══ SECTION: SITB ══ */}
                <div style={{
                  background: '#ffffff', border: '1px solid #e2e8f0',
                  borderRadius: '10px', padding: '10px 12px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '6px',
                      background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <i className="bi bi-lungs-fill text-white" style={{ fontSize: '0.65rem' }}></i>
                    </div>
                    <span className="fw-bold text-dark" style={{ fontSize: '0.8rem' }}>Nomor Register SITB</span>
                    <span className="text-muted" style={{ fontSize: '0.68rem' }}>(Sistem Informasi Tuberkulosis)</span>
                  </div>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Isi jika pasien terdiagnosa TB (A15-A19)"
                      value={inputSitb}
                      onChange={e => setInputSitb(e.target.value)}
                      style={{ fontSize: '0.82rem', borderRadius: '8px 0 0 8px', border: '1px solid #d1d5db' }}
                    />
                    <Button
                      onClick={() => handleValidateSitb(grouperTarget?.no_sep)}
                      disabled={isValidatingSitb}
                      className="fw-semibold px-3 text-white"
                      style={{
                        background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
                        border: 'none', borderRadius: '0 8px 8px 0', fontSize: '0.8rem'
                      }}
                    >
                      {isValidatingSitb ? (
                        <><Spinner size="sm" animation="border" className="me-1" /> Cek...</>
                      ) : (
                        <><i className="bi bi-shield-check me-1"></i> Validasi SITB</>
                      )}
                    </Button>
                  </InputGroup>
                  {sitbValidationResult && (
                    <div className={`mt-2 py-1 px-2 d-flex align-items-center gap-1`}
                      style={{
                        fontSize: '0.7rem', borderRadius: '6px',
                        background: sitbValidationResult.type === 'success' ? '#f0fdf4' : '#fef2f2',
                        color: sitbValidationResult.type === 'success' ? '#15803d' : '#b91c1c',
                        border: `1px solid ${sitbValidationResult.type === 'success' ? '#86efac' : '#fca5a5'}`
                      }}>
                      <i className={`bi ${sitbValidationResult.type === 'success' ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                      <span>{sitbValidationResult.text}</span>
                    </div>
                  )}
                  <div className="text-muted mt-1" style={{ fontSize: '0.68rem' }}>
                    * Wajib diisi khusus untuk diagnosa TB. Akan divalidasi oleh BPJS saat proses Grouper / Finalisasi.
                  </div>
                </div>

              </div>{/* end padding wrapper */}

              {/* ── FOOTER ACTIONS (RINGKAS) ── */}
              <div style={{
                padding: '8px 16px',
                borderTop: '1px solid #e2e8f0',
                background: '#f8fafc',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px'
              }}>
                <div className="text-muted d-flex align-items-center gap-1" style={{ fontSize: '0.71rem' }}>
                  <i className="bi bi-info-circle text-primary"></i>
                  <span>Aksi utama Grouper, Cetak Klaim, dan Tutup kini berada di <strong>bilah atas (header)</strong> modal.</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowGrouperModal(false)}
                    disabled={isGrouping}
                    style={{ borderRadius: '6px', fontSize: '0.75rem', padding: '4px 14px' }}
                  >
                    <i className="bi bi-x-lg me-1"></i> Tutup
                  </Button>
                </div>
              </div>

            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* ══ MODAL PERINGATAN NOMOR SEP KOSONG / TIDAK VALID (CONFIRMATION ALERT) ══ */}
      <Modal show={showSepWarningModal} onHide={() => setShowSepWarningModal(false)} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-exclamation-triangle-fill fs-5 text-warning"></i>
            <span>Peringatan: Nomor SEP Belum Diisi atau Format SEP Salah</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          {warningPatientTarget && (
            <div>
              <div className="alert alert-warning border-0 shadow-sm p-3 mb-3" style={{ background: '#fffbeb', borderLeft: '4px solid #f59e0b', borderRadius: '10px' }}>
                <div className="fw-bold text-dark mb-1" style={{ fontSize: '0.85rem' }}>
                  <i className="bi bi-person-exclamation me-1 text-warning"></i> Pasien: <strong>{warningPatientTarget.nm_pasien || warningPatientTarget.nama}</strong>
                </div>
                <div className="text-muted mb-2" style={{ fontSize: '0.76rem' }}>
                  No. Medrec: <strong className="font-monospace text-dark">{warningPatientTarget.no_medrec}</strong> | No. Nota: <span className="font-monospace">{warningPatientTarget.no_nota || '-'}</span> | Status SEP: <span className="badge bg-danger">{warningPatientTarget.no_sep || 'KOSONG'}</span>
                </div>
                <div className="text-danger fw-bold border-top pt-2" style={{ fontSize: '0.82rem' }}>
                  <i className="bi bi-x-circle-fill me-1"></i> Nomor SEP belum diisi atau Format SEP Salah
                </div>
                <div className="text-dark mt-1" style={{ fontSize: '0.78rem' }}>
                  Pasien tidak bisa melanjutkan proses Grouper E-Claim karena Nomor SEP masih bernilai <strong>"{warningPatientTarget.no_sep || '-'}"</strong>. Harap input atau lengkapi Nomor SEP yang valid terlebih dahulu.
                </div>
              </div>

              <p className="fw-semibold text-center text-dark mb-2" style={{ fontSize: '0.84rem' }}>
                Apakah Anda ingin menginput / mengubah Nomor SEP pasien ini sekarang?
              </p>

              <div className="d-flex justify-content-center gap-2 mt-3 pt-2 border-top">
                <Button
                  variant="secondary"
                  size="sm"
                  style={{ borderRadius: '6px', padding: '0.35rem 1rem', fontSize: '0.78rem' }}
                  onClick={() => setShowSepWarningModal(false)}
                >
                  Batal / Nanti Saja
                </Button>
                <Button
                  variant="warning"
                  size="sm"
                  className="fw-bold text-dark shadow-sm d-flex align-items-center gap-1"
                  style={{ borderRadius: '6px', padding: '0.35rem 1rem', fontSize: '0.78rem' }}
                  onClick={() => {
                    const target = warningPatientTarget;
                    setShowSepWarningModal(false);
                    handleOpenEditSep(target);
                  }}
                >
                  <i className="bi bi-pencil-square"></i>
                  <span>Input / Ubah Nomor SEP Sekarang</span>
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>



      {/* ── MODAL CETAK KLAIM (PDF) ── */}
      <Modal show={showPrintModal} onHide={() => setShowPrintModal(false)} size="xl" centered>
        <Modal.Header closeButton style={{ background: '#334155', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.95rem', fontWeight: '700' }}>
            <i className="bi bi-printer-fill me-2"></i> Pratinjau Dokumen Klaim
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: '85vh', backgroundColor: '#525659' }}>
          {printPdfUrl ? (
            <iframe
              src={printPdfUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title="Pratinjau PDF Klaim"
            />
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100 text-white">
              <Spinner animation="border" className="me-2" /> Memuat dokumen...
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* MODAL EXPORT NCC */}
      <Modal show={showExportModal} onHide={() => !isExporting && setShowExportModal(false)} centered backdrop="static">
        <Modal.Header closeButton={!isExporting} style={{ background: '#059669', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.95rem', fontWeight: '700' }}>
            <i className="bi bi-file-earmark-excel-fill me-2"></i> Export Data Klaim (NCC)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light p-3">
          <div className="alert alert-info border-0 shadow-sm p-3 mb-3" style={{ fontSize: '0.8rem' }}>
            Fitur ini akan menarik data `pull_claim` langsung dari server NCC untuk pasien yang <strong>sudah di-Grouper iDRG</strong> pada rentang tanggal pulang yang Anda pilih.
          </div>
          
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="text-muted fw-bold" style={{ fontSize: '0.75rem' }}>Periode Awal (Tgl Pulang)</Form.Label>
                <Form.Control 
                  type="date" 
                  size="sm"
                  value={exportStartDate}
                  onChange={e => setExportStartDate(e.target.value)}
                  disabled={isExporting}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="text-muted fw-bold" style={{ fontSize: '0.75rem' }}>Sampai Dengan</Form.Label>
                <Form.Control 
                  type="date" 
                  size="sm"
                  value={exportEndDate}
                  onChange={e => setExportEndDate(e.target.value)}
                  disabled={isExporting}
                />
              </Form.Group>
            </Col>
          </Row>

          {isExporting && (
            <div className="mt-4">
              <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.75rem' }}>
                <span className="fw-bold text-dark">Proses Menarik Data NCC...</span>
                <span className="fw-bold text-success">{exportProgress.current} / {exportProgress.total}</span>
              </div>
              <ProgressBar 
                animated 
                variant="success" 
                now={exportProgress.total > 0 ? (exportProgress.current / exportProgress.total) * 100 : 0} 
                style={{ height: '12px' }} 
              />
              <div className="text-muted mt-2 text-center" style={{ fontSize: '0.7rem' }}>
                Mohon jangan tutup halaman ini sampai proses selesai.
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-white p-2">
          <Button variant="secondary" size="sm" onClick={() => setShowExportModal(false)} disabled={isExporting}>
            Batal
          </Button>
          <Button variant="success" size="sm" className="fw-bold px-3" onClick={handleExportNcc} disabled={isExporting}>
            {isExporting ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-download me-1"></i>}
            Proses Download
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══ MODAL LAPORAN KLAIM INDIVIDUAL (FILTER E-KLAIM) ══ */}
      <Modal 
        show={showFilterLaporanModal} 
        onHide={() => !isExportingExcel && setShowFilterLaporanModal(false)} 
        size="xl"
        centered
        backdrop="static"
        dialogClassName="modal-klaim-individual"
      >
        <style>{`
          .modal-dialog.modal-klaim-individual,
          .modal-klaim-individual.modal-dialog {
            max-width: 960px !important;
            width: 960px !important;
            min-width: min(960px, 95vw) !important;
            --bs-modal-width: 960px !important;
          }
        `}</style>
        {/* Title Bar / Header */}
        <Modal.Header 
          closeButton={!isExportingExcel} 
          style={{ 
            background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)', 
            padding: '12px 20px',
            borderBottom: '1px solid #cbd5e1'
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-primary bg-opacity-10 text-primary p-1 rounded-2">
              <i className="bi bi-funnel-fill fs-6"></i>
            </span>
            <div>
              <div className="fw-bold text-dark lh-1" style={{ fontSize: '0.92rem' }}>Filter Laporan Klaim Individual</div>
              <small className="text-muted" style={{ fontSize: '0.72rem' }}>Format Baku Ekspor E-Klaim Kemenkes RI</small>
            </div>
          </div>
        </Modal.Header>

        {/* Modal Body / Table Form */}
        <Modal.Body className="p-0 bg-white" style={{ overflowX: 'visible' }}>
          <div style={{ overflowX: 'visible' }}>
            <table className="table mb-0 align-middle" style={{ borderColor: '#edf2f7' }}>
              <tbody>
                {/* 1. Jenis Rawat */}
                <tr style={{ borderBottom: '1px solid #edf2f7' }}>
                  <td 
                    style={{ 
                      width: '160px', 
                      minWidth: '150px', 
                      textAlign: 'right', 
                      paddingRight: '16px', 
                      backgroundColor: '#fbfcfd', 
                      color: '#475569', 
                      fontWeight: '600', 
                      fontSize: '0.8rem',
                      borderRight: '1px solid #f1f5f9'
                    }}
                  >
                    Jenis Rawat
                  </td>
                  <td style={{ padding: '8px 20px', backgroundColor: '#ffffff' }}>
                    <Form.Select 
                      size="sm" 
                      value={filterDialog.jenisRawat}
                      onChange={e => setFilterDialog({ ...filterDialog, jenisRawat: e.target.value })}
                      style={{ width: '220px', height: '32px', fontSize: '0.8rem', borderColor: '#cbd5e1' }}
                      disabled={isExportingExcel}
                    >
                      <option value="Semua">Semua</option>
                      <option value="RI">Rawat Inap</option>
                      <option value="RJ">Rawat Jalan</option>
                    </Form.Select>
                  </td>
                </tr>

                {/* 2. Periode */}
                <tr style={{ borderBottom: '1px solid #edf2f7' }}>
                  <td 
                    style={{ 
                      width: '160px',
                      minWidth: '150px',
                      textAlign: 'right', 
                      paddingRight: '16px', 
                      backgroundColor: '#fbfcfd', 
                      color: '#475569', 
                      fontWeight: '600', 
                      fontSize: '0.8rem',
                      borderRight: '1px solid #f1f5f9'
                    }}
                  >
                    Periode
                  </td>
                  <td style={{ padding: '8px 20px', backgroundColor: '#ffffff' }}>
                    <div className="d-flex align-items-center gap-2 flex-nowrap" style={{ whiteSpace: 'nowrap' }}>
                      <Form.Select 
                        size="sm" 
                        value={filterDialog.periodeTipe}
                        onChange={e => setFilterDialog({ ...filterDialog, periodeTipe: e.target.value })}
                        style={{ width: '140px', minWidth: '130px', height: '32px', fontSize: '0.8rem', borderColor: '#cbd5e1' }}
                        disabled={isExportingExcel}
                      >
                        <option value="Waktu Pulang">Waktu Pulang</option>
                        <option value="Waktu Masuk">Waktu Masuk</option>
                        <option value="Waktu Grouping">Waktu Grouping</option>
                      </Form.Select>

                      <div className="d-inline-flex align-items-center bg-white border rounded px-2 shadow-none flex-shrink-0" style={{ height: '32px', borderColor: '#cbd5e1' }}>
                        <i className="bi bi-calendar3 text-secondary me-1" style={{ fontSize: '0.75rem' }}></i>
                        <input 
                          type="date" 
                          value={filterDialog.periodeTglAwal}
                          onChange={e => setFilterDialog({ ...filterDialog, periodeTglAwal: e.target.value })}
                          style={{ border: 'none', outline: 'none', fontSize: '0.8rem', color: '#334155', background: 'transparent' }}
                          disabled={isExportingExcel}
                        />
                        <span className="text-secondary opacity-50 mx-1">|</span>
                        <i className="bi bi-clock text-secondary me-1" style={{ fontSize: '0.75rem' }}></i>
                        <input 
                          type="time" 
                          value={filterDialog.periodeJamAwal}
                          onChange={e => setFilterDialog({ ...filterDialog, periodeJamAwal: e.target.value })}
                          style={{ border: 'none', outline: 'none', fontSize: '0.8rem', color: '#334155', background: 'transparent', width: '58px' }}
                          disabled={isExportingExcel}
                        />
                      </div>

                      <span className="text-muted fw-semibold px-1 flex-shrink-0" style={{ fontSize: '0.78rem' }}>s/d</span>

                      <div className="d-inline-flex align-items-center bg-white border rounded px-2 shadow-none flex-shrink-0" style={{ height: '32px', borderColor: '#cbd5e1' }}>
                        <i className="bi bi-calendar3 text-secondary me-1" style={{ fontSize: '0.75rem' }}></i>
                        <input 
                          type="date" 
                          value={filterDialog.periodeTglAkhir}
                          onChange={e => setFilterDialog({ ...filterDialog, periodeTglAkhir: e.target.value })}
                          style={{ border: 'none', outline: 'none', fontSize: '0.8rem', color: '#334155', background: 'transparent' }}
                          disabled={isExportingExcel}
                        />
                        <span className="text-secondary opacity-50 mx-1">|</span>
                        <i className="bi bi-clock text-secondary me-1" style={{ fontSize: '0.75rem' }}></i>
                        <input 
                          type="time" 
                          value={filterDialog.periodeJamAkhir}
                          onChange={e => setFilterDialog({ ...filterDialog, periodeJamAkhir: e.target.value })}
                          style={{ border: 'none', outline: 'none', fontSize: '0.8rem', color: '#334155', background: 'transparent', width: '58px' }}
                          disabled={isExportingExcel}
                        />
                      </div>
                    </div>
                  </td>
                </tr>

                {/* 3. Metode Pembayaran */}
                <tr style={{ borderBottom: '1px solid #edf2f7' }}>
                  <td 
                    style={{ 
                      width: '160px',
                      minWidth: '150px',
                      textAlign: 'right', 
                      paddingRight: '16px', 
                      backgroundColor: '#fbfcfd', 
                      color: '#475569', 
                      fontWeight: '600', 
                      fontSize: '0.8rem',
                      borderRight: '1px solid #f1f5f9'
                    }}
                  >
                    Metode Pembayaran
                  </td>
                  <td style={{ padding: '8px 20px', backgroundColor: '#ffffff' }}>
                    <Form.Select
                      size="sm"
                      value={filterDialog.metodePembayaran}
                      onChange={e => setFilterDialog({ ...filterDialog, metodePembayaran: e.target.value })}
                      style={{ width: '240px', height: '32px', fontSize: '0.8rem', borderColor: '#cbd5e1' }}
                      disabled={isExportingExcel}
                    >
                      <option value="Semua">Semua</option>
                      <option value="JKN">JKN / BPJS Kesehatan</option>
                      <option value="Asuransi Lain">Asuransi Lain</option>
                      <option value="Umum / Pribadi">Umum / Pribadi</option>
                    </Form.Select>
                  </td>
                </tr>

                {/* 4. Kelas Rawat */}
                <tr style={{ borderBottom: '1px solid #edf2f7' }}>
                  <td 
                    style={{ 
                      width: '160px',
                      minWidth: '150px',
                      textAlign: 'right', 
                      paddingRight: '16px', 
                      backgroundColor: '#fbfcfd', 
                      color: '#475569', 
                      fontWeight: '600', 
                      fontSize: '0.8rem',
                      borderRight: '1px solid #f1f5f9'
                    }}
                  >
                    Kelas Rawat
                  </td>
                  <td style={{ padding: '8px 20px', backgroundColor: '#ffffff' }}>
                    <div className="d-flex align-items-center gap-4 py-1">
                      {['Kelas 1', 'Kelas 2', 'Kelas 3', 'Semua Kelas'].map((kls) => {
                        const isChecked = filterDialog.kelasRawat === kls;
                        return (
                          <label 
                            key={kls}
                            className={`d-inline-flex align-items-center gap-2 mb-0 ${isChecked ? 'text-primary fw-bold' : 'text-secondary'}`}
                            style={{ fontSize: '0.82rem', cursor: 'pointer', userSelect: 'none' }}
                          >
                            <input
                              type="radio"
                              name="filterKelasRawat"
                              value={kls}
                              checked={isChecked}
                              onChange={() => setFilterDialog({ ...filterDialog, kelasRawat: kls })}
                              disabled={isExportingExcel}
                              style={{ accentColor: '#0284c7', cursor: 'pointer' }}
                            />
                            <span>{kls}</span>
                          </label>
                        );
                      })}
                    </div>
                  </td>
                </tr>

                {/* 5. Cara Pulang */}
                <tr style={{ borderBottom: '1px solid #edf2f7' }}>
                  <td 
                    style={{ 
                      width: '160px',
                      minWidth: '150px',
                      textAlign: 'right', 
                      paddingRight: '16px', 
                      backgroundColor: '#fbfcfd', 
                      color: '#475569', 
                      fontWeight: '600', 
                      fontSize: '0.8rem',
                      borderRight: '1px solid #f1f5f9'
                    }}
                  >
                    Cara Pulang
                  </td>
                  <td style={{ padding: '8px 20px', backgroundColor: '#ffffff' }}>
                    <Form.Select 
                      size="sm" 
                      value={filterDialog.caraPulang}
                      onChange={e => setFilterDialog({ ...filterDialog, caraPulang: e.target.value })}
                      style={{ width: '240px', height: '32px', fontSize: '0.8rem', borderColor: '#cbd5e1' }}
                      disabled={isExportingExcel}
                    >
                      <option value="Semua">Semua</option>
                      <option value="Atas Persetujuan Dokter">Atas Persetujuan Dokter</option>
                      <option value="Atas Permintaan Sendiri">Atas Permintaan Sendiri</option>
                      <option value="Meninggal">Meninggal</option>
                      <option value="Dirujuk">Dirujuk</option>
                      <option value="Lain-lain">Lain-lain</option>
                    </Form.Select>
                  </td>
                </tr>

                {/* 6. Severity */}
                <tr style={{ borderBottom: '1px solid #edf2f7' }}>
                  <td 
                    style={{ 
                      width: '160px',
                      minWidth: '150px',
                      textAlign: 'right', 
                      paddingRight: '16px', 
                      backgroundColor: '#fbfcfd', 
                      color: '#475569', 
                      fontWeight: '600', 
                      fontSize: '0.8rem',
                      borderRight: '1px solid #f1f5f9'
                    }}
                  >
                    Severity
                  </td>
                  <td style={{ padding: '8px 20px', backgroundColor: '#ffffff' }}>
                    <Form.Select 
                      size="sm" 
                      value={filterDialog.severity}
                      onChange={e => setFilterDialog({ ...filterDialog, severity: e.target.value })}
                      style={{ width: '180px', height: '32px', fontSize: '0.8rem', borderColor: '#cbd5e1' }}
                      disabled={isExportingExcel}
                    >
                      <option value="Semua">Semua</option>
                      <option value="Rawat Jalan / 0">Rawat Jalan / 0</option>
                      <option value="I">Severity I</option>
                      <option value="II">Severity II</option>
                      <option value="III">Severity III</option>
                    </Form.Select>
                  </td>
                </tr>

                {/* 7. Jenis Tarif */}
                <tr style={{ borderBottom: '1px solid #edf2f7' }}>
                  <td 
                    style={{ 
                      width: '160px',
                      minWidth: '150px',
                      textAlign: 'right', 
                      paddingRight: '16px', 
                      backgroundColor: '#fbfcfd', 
                      color: '#475569', 
                      fontWeight: '600', 
                      fontSize: '0.8rem',
                      borderRight: '1px solid #f1f5f9'
                    }}
                  >
                    Jenis Tarif
                  </td>
                  <td style={{ padding: '8px 20px', backgroundColor: '#ffffff' }}>
                    <Form.Select 
                      size="sm" 
                      value={filterDialog.jenisTarif}
                      onChange={e => setFilterDialog({ ...filterDialog, jenisTarif: e.target.value })}
                      style={{ width: '280px', height: '32px', fontSize: '0.8rem', borderColor: '#cbd5e1' }}
                      disabled={isExportingExcel}
                    >
                      <option value="TARIF RS KELAS C SWASTA">TARIF RS KELAS C SWASTA</option>
                      <option value="TARIF RS KELAS C PEMERINTAH">TARIF RS KELAS C PEMERINTAH</option>
                      <option value="TARIF RS KELAS B SWASTA">TARIF RS KELAS B SWASTA</option>
                      <option value="TARIF RS KELAS B PEMERINTAH">TARIF RS KELAS B PEMERINTAH</option>
                      <option value="TARIF RS KELAS A SWASTA">TARIF RS KELAS A SWASTA</option>
                      <option value="TARIF RS KELAS D SWASTA">TARIF RS KELAS D SWASTA</option>
                    </Form.Select>
                  </td>
                </tr>

                {/* 8. COB */}
                <tr style={{ borderBottom: '1px solid #edf2f7' }}>
                  <td 
                    style={{ 
                      width: '160px',
                      minWidth: '150px',
                      textAlign: 'right', 
                      paddingRight: '16px', 
                      backgroundColor: '#fbfcfd', 
                      color: '#475569', 
                      fontWeight: '600', 
                      fontSize: '0.8rem',
                      borderRight: '1px solid #f1f5f9'
                    }}
                  >
                    COB
                  </td>
                  <td style={{ padding: '8px 20px', backgroundColor: '#ffffff' }}>
                    <Form.Select 
                      size="sm" 
                      value={filterDialog.cob}
                      onChange={e => setFilterDialog({ ...filterDialog, cob: e.target.value })}
                      style={{ width: '180px', height: '32px', fontSize: '0.8rem', borderColor: '#cbd5e1' }}
                      disabled={isExportingExcel}
                    >
                      <option value="Semua">Semua</option>
                      <option value="Ya">Ya</option>
                      <option value="Tidak">Tidak</option>
                    </Form.Select>
                  </td>
                </tr>

                {/* 9. Waktu Grouping */}
                <tr>
                  <td 
                    style={{ 
                      width: '160px',
                      minWidth: '150px',
                      textAlign: 'right', 
                      paddingRight: '16px', 
                      backgroundColor: '#fbfcfd', 
                      color: '#475569',  
                      fontWeight: '600', 
                      fontSize: '0.8rem',
                      borderRight: '1px solid #f1f5f9'
                    }}
                  >
                    Waktu Grouping
                  </td>
                  <td style={{ padding: '8px 20px', backgroundColor: '#ffffff' }}>
                    <div className="d-flex align-items-center gap-2 flex-nowrap" style={{ whiteSpace: 'nowrap' }}>
                      <div className="d-inline-flex align-items-center bg-white border rounded px-2 shadow-none flex-shrink-0" style={{ height: '32px', borderColor: '#cbd5e1' }}>
                        <i className="bi bi-calendar3 text-secondary me-1" style={{ fontSize: '0.75rem' }}></i>
                        <input 
                          type="date" 
                          value={filterDialog.groupingTglAwal}
                          onChange={e => setFilterDialog({ ...filterDialog, groupingTglAwal: e.target.value })}
                          style={{ border: 'none', outline: 'none', fontSize: '0.8rem', color: '#334155', background: 'transparent' }}
                          disabled={isExportingExcel}
                        />
                        <span className="text-secondary opacity-50 mx-1">|</span>
                        <i className="bi bi-clock text-secondary me-1" style={{ fontSize: '0.75rem' }}></i>
                        <input 
                          type="time" 
                          value={filterDialog.groupingJamAwal}
                          onChange={e => setFilterDialog({ ...filterDialog, groupingJamAwal: e.target.value })}
                          style={{ border: 'none', outline: 'none', fontSize: '0.8rem', color: '#334155', background: 'transparent', width: '58px' }}
                          disabled={isExportingExcel}
                        />
                      </div>

                      <span className="text-muted fw-semibold px-1 flex-shrink-0" style={{ fontSize: '0.78rem' }}>s/d</span>

                      <div className="d-inline-flex align-items-center bg-white border rounded px-2 shadow-none flex-shrink-0" style={{ height: '32px', borderColor: '#cbd5e1' }}>
                        <i className="bi bi-calendar3 text-secondary me-1" style={{ fontSize: '0.75rem' }}></i>
                        <input 
                          type="date" 
                          value={filterDialog.groupingTglAkhir}
                          onChange={e => setFilterDialog({ ...filterDialog, groupingTglAkhir: e.target.value })}
                          style={{ border: 'none', outline: 'none', fontSize: '0.8rem', color: '#334155', background: 'transparent' }}
                          disabled={isExportingExcel}
                        />
                        <span className="text-secondary opacity-50 mx-1">|</span>
                        <i className="bi bi-clock text-secondary me-1" style={{ fontSize: '0.75rem' }}></i>
                        <input 
                          type="time" 
                          value={filterDialog.groupingJamAkhir}
                          onChange={e => setFilterDialog({ ...filterDialog, groupingJamAkhir: e.target.value })}
                          style={{ border: 'none', outline: 'none', fontSize: '0.8rem', color: '#334155', background: 'transparent', width: '58px' }}
                          disabled={isExportingExcel}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Catatan Informasi Resmi E-Klaim */}
          <div 
            className="px-3 py-2 border-top d-flex align-items-center gap-2" 
            style={{ 
              backgroundColor: '#f8fafc', 
              fontSize: '0.75rem', 
              color: '#334155',
              borderLeft: '4px solid #0284c7'
            }}
          >
            <i className="bi bi-info-circle-fill text-primary" style={{ fontSize: '0.95rem' }}></i>
            <div>
              Data yang ditampilkan di laporan adalah data klaim final dengan status <strong className="text-primary">Terkirim ke Pusat Data Kementerian Kesehatan</strong>.
            </div>
          </div>

          {/* Export Progress Status Indicator */}
          {isExportingExcel && (
            <div className="p-3 bg-light border-top text-center">
              <div className="d-flex align-items-center justify-content-center gap-2 text-success fw-bold" style={{ fontSize: '0.85rem' }}>
                <Spinner animation="border" size="sm" variant="success" />
                <span>{exportExcelStatusMsg || 'Sedang menyusun dan mengunduh berkas Excel E-Klaim...'}</span>
              </div>
              <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>
                Harap tunggu, berkas akan diunduh secara otomatis setelah siap.
              </div>
            </div>
          )}
        </Modal.Body>

        {/* Modal Footer */}
        <Modal.Footer className="bg-light px-3 py-2 d-flex align-items-center justify-content-between border-top">
          {/* Quick Preset Buttons */}
          <div className="d-flex align-items-center gap-1">
            <span className="text-muted me-1 fw-semibold" style={{ fontSize: '0.72rem' }}>Pilihan Cepat:</span>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              className="py-0 px-2 fw-medium" 
              style={{ fontSize: '0.72rem', height: '26px' }}
              onClick={() => handleSetPresetDate('today')}
              disabled={isExportingExcel}
            >
              Hari Ini
            </Button>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              className="py-0 px-2 fw-medium" 
              style={{ fontSize: '0.72rem', height: '26px' }}
              onClick={() => handleSetPresetDate('thisMonth')}
              disabled={isExportingExcel}
            >
              Bulan Ini
            </Button>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              className="py-0 px-2 fw-medium" 
              style={{ fontSize: '0.72rem', height: '26px' }}
              onClick={() => handleSetPresetDate('reset')}
              disabled={isExportingExcel}
              title="Reset semua filter ke default"
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i>Reset
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="d-flex align-items-center gap-2">
            <Button 
              variant="light" 
              size="sm" 
              className="px-3 border fw-semibold text-secondary"
              onClick={() => setShowFilterLaporanModal(false)} 
              disabled={isExportingExcel}
              style={{ fontSize: '0.8rem', height: '32px' }}
            >
              Batal
            </Button>
            <Button 
              variant="success" 
              size="sm" 
              className="px-3 fw-bold d-flex align-items-center shadow-sm text-white"
              style={{ background: '#059669', borderColor: '#059669', fontSize: '0.8rem', height: '32px' }}
              onClick={() => handleExportEklaimExcel(filterDialog)} 
              disabled={isExportingExcel}
            >
              {isExportingExcel ? (
                <><Spinner size="sm" animation="border" className="me-2" /> Menyusun Berkas...</>
              ) : (
                <><i className="bi bi-file-earmark-excel-fill me-2 fs-6"></i> Ekspor Excel E-Klaim</>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ══ GLOBAL FLOATING TOAST NOTIFICATION ══ */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 99999, position: 'fixed' }}>
        <Toast
          show={toastNotif.show}
          onClose={() => setToastNotif(prev => ({ ...prev, show: false }))}
          delay={4500}
          autohide
          style={{
            minWidth: '320px',
            maxWidth: '420px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            border: 'none',
            overflow: 'hidden'
          }}
        >
          <Toast.Header
            style={{
              background: toastNotif.type === 'success' ? '#10b981'
                : toastNotif.type === 'warning' ? '#f59e0b'
                : toastNotif.type === 'danger' ? '#ef4444'
                : '#0284c7',
              color: '#fff',
              borderBottom: 'none',
              padding: '8px 12px'
            }}
          >
            <i className={`bi ${
              toastNotif.type === 'success' ? 'bi-check-circle-fill'
              : toastNotif.type === 'warning' ? 'bi-exclamation-triangle-fill'
              : toastNotif.type === 'danger' ? 'bi-x-circle-fill'
              : 'bi-info-circle-fill'
            } me-2 fs-6`}></i>
            <strong className="me-auto" style={{ fontSize: '0.85rem' }}>{toastNotif.title || 'Pemberitahuan'}</strong>
            <small style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.7rem' }}>Baru saja</small>
          </Toast.Header>
          <Toast.Body style={{ fontSize: '0.78rem', background: '#fff', color: '#1e293b', padding: '10px 12px' }}>
            {toastNotif.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

    </div>
  );
};

// ==========================================
// STYLES
// ==========================================
const styles = {
  appContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f0f4f8',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  },
  navbarCustom: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    borderBottom: '2px solid #0d9488',
    padding: '0 1rem',
    height: '48px'
  },
  navLinkCustom: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: '0.75rem',
    fontWeight: '500',
    padding: '0.25rem 0.6rem',
    borderRadius: '6px',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    textDecoration: 'none'
  },
  navLinkActive: {
    color: '#fff',
    backgroundColor: 'rgba(255,255,255,0.1)',
    fontWeight: '600',
  },
  mainContent: {
    flex: 1,
    overflow: 'hidden',
  }
};

export default DataClaimPasien;
